import { inpOutlierNote } from './inpOutlierNote.js';
import { embedAuditEvidenceDigest } from './auditEvidenceDigest.js';
import { comparableLabSummary } from './comparableLabSummary.js';
import { renderDiagnosticAuditContext, preventiveReportCopy } from './diagnosticAuditContext.js';

import { formatSavingsValue, formatSavingsRange, savingsBadgeKey, getSavingsLabels } from './diagnosticsExport.js';
import { renderEvidenceHTML } from './diagnosticEvidence.js';
import { renderAuditEvidenceContractHTML, renderInpScenarioScopesHTML, renderInpScenarioFailureNotice } from './auditEvidencePresentation.js';
import { sameOwner } from './domainOwnership.js';
import { ownerForRecommendation, ownerForCategory, ownerForClsCause, buildFollowUpSchedule, formatFollowUpDate, followUpLabels } from './findingOwners.js';
import { detectSgtm, gtmAdviceSentence, gtmMigrationTitle, hasGtmContainer, scopeTagRecommendations } from './gtmAdvice.js';
import { metricCopy } from './metricCopy.js';
import {
  lcpCauseContent,
  inpCauseContent,
  severityLabels,
  genericCodeExampleLabel,
  inpGenericCodeExampleLabel,
  longTaskScopeDetail,
  longTaskCountDiscrepancy,
  inpCauseDescription,
  inpCauseRecommendationsFor,
  inpDiagnosticSeverity,
  normalizeInpDiagnosticCause
} from './causeContent.js';
import { METRIC_THRESHOLDS } from './metricThresholds.js';
import { getDeepFailurePresentation } from './deepFailurePresentation.js';
import { renderInteractionDiagnosticsHtml, renderMultiPageInteractionDiagnosticsHtml } from './inpInteractionDiagnostics.js';
import { hasValidInpMeasurement } from './inpMeasurementValidity.js';
import { canonicalFindingSentences, deriveCanonicalFinding, evaluateInpEvidence } from './canonicalFinding.js';
import { formatConsentState } from './consentState.js';

// =========================================================================
// LOW-LEVEL UTILITIES
// =========================================================================
const escapeHtml = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const fmtMs = (v) => {
  if (v == null || isNaN(v)) return null;
  if (v >= 1000) return `${(v / 1000).toFixed(2)} s`;
  return `${Math.round(v)} ms`;
};

const fmtKb = (bytes) => {
  if (bytes == null || isNaN(bytes)) return null;
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const fmtNum = (v, digits = 0) => {
  if (v == null || isNaN(v)) return null;
  return Number(v).toLocaleString('cs-CZ', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

const formatMetricValue = (m, v) => {
  if (v == null || isNaN(v)) return null;
  const ml = (m || '').toLowerCase();
  if (ml === 'cls') return Number(v).toFixed(3);
  return fmtMs(v);
};

const orDash = (s) => (s == null || s === '' ? '—' : s);

const metricThresholds = METRIC_THRESHOLDS;

const statusOf = (metric, value) => {
  const t = metricThresholds[(metric || '').toLowerCase()];
  if (!t || value == null) return 'warning';
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'warning';
  return 'critical';
};

const statusLabel = (status, lang) => {
  if (lang === 'cs') {
    return status === 'good' ? 'Dobré' : status === 'warning' ? 'Vyžaduje zlepšení' : 'Špatné';
  }
  return status === 'good' ? 'Good' : status === 'warning' ? 'Needs improvement' : 'Poor';
};

const formatDateNow = (lang) => {
  const d = new Date();
  if (lang === 'cs') {
    return d.toLocaleString('cs-CZ', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }
  return d.toLocaleString('en-US', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatMonthYear = (lang, when) => {
  // Exact audit run date, not just the month — the report elsewhere works
  // with a precise CrUX window; a coarse month would contradict it.
  const d = (when && !isNaN(new Date(when))) ? new Date(when) : new Date();
  const monthsCsGen = ['ledna','února','března','dubna','května','června','července','srpna','září','října','listopadu','prosince'];
  if (lang === 'cs') return `${d.getDate()}. ${monthsCsGen[d.getMonth()]} ${d.getFullYear()}`;
  return d.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// Human phrase for the header's priority metric — "#1 CLS" is internal
// shorthand; the reader gets "hlavní problém: stabilita layoutu (CLS)".
const metricHumanPhrase = (mUp, cs) => ({
  CLS: cs ? 'stabilita layoutu (CLS)' : 'layout stability (CLS)',
  LCP: cs ? 'rychlost načtení (LCP)' : 'loading speed (LCP)',
  INP: cs ? 'odezva na interakci (INP)' : 'interaction response (INP)',
  TTFB: cs ? 'odezva serveru (TTFB)' : 'server response (TTFB)',
  FCP: cs ? 'rychlost prvního vykreslení (FCP)' : 'first paint speed (FCP)'
}[String(mUp).toUpperCase()] || mUp);

// Shared human-readable header meta line (both templates).
const headerMetaText = (r, lang) => {
  const cs = lang === 'cs';
  const bits = [r.domain];
  bits.push(r.unified
    ? (cs ? `Core Web Vitals · hlavní problém: ${metricHumanPhrase(r.metricUpper, cs)}` : `Core Web Vitals · main problem: ${metricHumanPhrase(r.metricUpper, cs)}`)
    : metricHumanPhrase(r.metricUpper, cs));
  if (r.device) bits.push(cs ? `hlavní zařízení: ${r.device}` : `primary device: ${r.device}`);
  // A geo-disabled CMP means no banner ever rendered — the header must not
  // claim consent was given/withheld; that state simply did not apply.
  if (r.deepCLSResults?.cmpState === 'disabled') bits.push(cs ? 'stav souhlasu se neuplatnil' : 'consent state not applicable');
  else if (r.cmpMode) bits.push(formatConsentState(r.consentEvidence, lang, { prefix: false }));
  bits.push(r.monthYear);
  return bits.map(escapeHtml).join(' &nbsp;·&nbsp; ');
};

// First-occurrence jargon annotation on the final HTML: "main thread" gets a
// one-line plain-language parenthetical the first time it appears in body
// text. Runs on rendered output because the phrase originates from dozens of
// copy templates — annotating each would bloat every scenario.
const annotateFirstJargon = (html, lang) => {
  // Case-insensitive, VISIBLE-TEXT-ONLY: only text segments between tags and
  // only after </head> (skips <style>/<head> content and all attributes).
  const pats = lang === 'cs'
    ? [
      [/hlavní vlákno/i, ' (kde prohlížeč zpracovává vše, co uživatel dělá)'],
      [/hlavního vlákna/i, ' (tam prohlížeč zpracovává vše, co uživatel dělá)']
    ]
    : [[/main thread/i, ' (where the browser processes everything the user does)']];
  const cut = html.indexOf('</head>');
  const head = cut >= 0 ? html.slice(0, cut) : '';
  let body = cut >= 0 ? html.slice(cut) : html;
  let done = false;
  let inSkip = false; // inside <script>/<style> body
  body = body.replace(/(<[^>]*>)|([^<]+)/g, (m, tag, text) => {
    if (tag) {
      if (/^<(script|style)\b/i.test(tag)) inSkip = true;
      else if (/^<\/(script|style)/i.test(tag)) inSkip = false;
      return tag;
    }
    if (done || inSkip) return m;
    let best = null;
    for (const [re, suffix] of pats) {
      const mm = text.match(re);
      if (mm && (best == null || mm.index < best.index)) best = { re, suffix, matched: mm[0] };
    }
    if (!best) return m;
    done = true;
    return text.replace(best.re, best.matched + best.suffix);
  });
  return head + body;
};

// Server diagnostics build their descriptions in English (Lighthouse-style
// strings). A Czech report must never show raw English technical text —
// translate every known pattern; unknown strings pass through unchanged.
const translateCauseDescription = (desc, lang) => {
  if (lang !== 'cs' || !desc) return desc || '';
  const rules = [
    [/^Input delay of (\d+)ms \(main thread blocked\)$/, (m) => `Zpoždění vstupu ${m[1]} ms (hlavní vlákno bylo obsazené)`],
    [/^Event handler takes (\d+)ms\.?$/, (m) => `Zpracování kliknutí trvá ${m[1]} ms.`],
    [/^Presentation delay of (\d+)ms$/, (m) => `Vykreslení odpovědi trvá ${m[1]} ms`],
    [/^DOM has (\d+) elements \(recommended: <1500\)$/, (m) => `Stránka má ${m[1]} prvků (nadpisy, obrázky, boxy) — doporučeno do 1 500. Čím víc prvků, tím víc práce má prohlížeč a tím pomaleji stránka reaguje.`],
    [/^(\d+) DOM elements detected\.$/, (m) => `Stránka má ${m[1]} prvků — čím víc, tím víc práce má prohlížeč.`],
    [/^JS heap size is (\d+)MB$/, (m) => `Skripty drží v paměti ${m[1]} MB`],
    [/^Heap size is (\d+)MB\.$/, (m) => `Skripty drží v paměti ${m[1]} MB.`],
    [/^(\d+) long tasks may block interactions$/, (m) => `${m[1]} dlouhých úloh může blokovat odezvu na kliknutí`],
    [/^(\d+) long tasks block the main thread — (\d+)% of attributed script time comes from third-party code(?: \(longest: (.+)\))?\.$/, (m) => `${m[1]} dlouhých úloh blokuje hlavní vlákno — ${m[2]} % času skriptů pochází z kódu třetích stran${m[3] ? ` (nejdelší: ${m[3]})` : ''}.`],
    [/^(\d+) long tasks block the main thread\.$/, (m) => `${m[1]} dlouhých úloh blokuje hlavní vlákno.`],
    [/^(\d+) long tasks detected during page load\.$/, (m) => `${m[1]} dlouhých úloh během načítání stránky.`],
    [/^(\d+)ms delay before handler starts\.$/, (m) => `${m[1]} ms čekání, než se kliknutí vůbec začne zpracovávat.`],
    [/^(\d+)ms to update the screen\.$/, (m) => `${m[1]} ms trvá překreslení obrazovky.`],
    [/^Server response time \(TTFB\) is (\d+)ms$/, (m) => `Odpověď serveru (TTFB) trvá ${m[1]} ms`],
    [new RegExp(`^TTFB of (\\d+)ms is too slow\\. Target is under ${metricThresholds.ttfb.good}ms\\.$`), (m) => `Odpověď serveru ${m[1]} ms je pomalá. Cíl je do ${metricThresholds.ttfb.good} ms.`],
    [/^Redirect chain adds (\d+)ms$/, (m) => `Řetěz přesměrování přidává ${m[1]} ms`],
    [/^Redirects add (\d+)ms to page load\.$/, (m) => `Přesměrování přidávají ${m[1]} ms k načtení stránky.`],
    [/^(\d+) render-blocking CSS files in <head>$/, (m) => `${m[1]} CSS souborů blokuje vykreslení stránky`],
    [/^(\d+) CSS files block initial render\.$/, (m) => `${m[1]} CSS souborů blokuje první vykreslení.`],
    [/^(\d+) synchronous scripts blocking render$/, (m) => `${m[1]} synchronních skriptů blokuje vykreslení`],
    [/^(\d+) synchronous scripts in <head> block rendering\.$/, (m) => `${m[1]} synchronních skriptů v <head> blokuje vykreslení.`],
    [/^(\d+) third-party resources blocking render$/, (m) => `${m[1]} zdrojů třetích stran blokuje vykreslení`],
    [/^(\d+) external resources block render\.$/, (m) => `${m[1]} externích zdrojů blokuje vykreslení.`],
    [/^LCP image has loading="lazy" but is above the fold$/, () => `Hlavní obrázek má loading="lazy", přestože je ve viditelné části stránky (bez scrollování) — má se načíst hned`],
    [/^LCP image should load eagerly, not lazily\.$/, () => `Hlavní obrázek se má načíst hned, ne odloženě (lazy).`],
    [/^LCP image is (\d+)KB$/, (m) => `Hlavní obrázek má ${m[1]} kB`],
    [/^Image is (\d+)KB\. Should be under 200KB\.$/, (m) => `Obrázek má ${m[1]} kB. Má být do 200 kB.`],
    [/^LCP resource took (\d+)ms to load$/, (m) => `Hlavní obrázek se stahoval ${m[1]} ms`],
    [/^LCP resource takes (\d+)ms to load\.$/, (m) => `Stažení hlavního obrázku trvá ${m[1]} ms.`],
    [/^(\d+) fonts without font-display: swap may block text rendering$/, (m) => `${m[1]} fontů bez font-display: swap může blokovat vykreslení textu`],
    [/^(\d+) fonts may block text rendering\.$/, (m) => `${m[1]} fontů může blokovat vykreslení textu.`],
    [/^(\d+)ms delay between resource load and render$/, (m) => `${m[1]} ms prodleva mezi stažením a vykreslením`],
    [/^(\d+)ms delay after resource load\.$/, (m) => `${m[1]} ms prodleva po stažení zdroje.`],
    [/^Add preload hint for faster LCP image loading\.$/, () => `Přidejte preload, ať se hlavní obrázek načítá dřív.`]
  ];
  for (const [re, fn] of rules) {
    const m = String(desc).match(re);
    if (m) return fn(m);
  }
  return desc;
};

const getDomain = (url) => {
  if (!url) return '';
  try {
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(normalized).hostname.replace(/^www\./, '');
  } catch {
    return String(url);
  }
};

const getHostname = (url) => {
  if (!url) return '';
  try {
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(normalized).hostname;
  } catch {
    return String(url);
  }
};

const shortenUrl = (url, max = 60) => {
  if (!url) return '';
  let s = String(url).replace(/^https?:\/\//, '');
  if (s.length > max) s = s.slice(0, max - 1) + '…';
  return s;
};

const deviceLabel = (device, lang) => {
  if (!device) return '';
  const d = String(device).toLowerCase();
  if (d === 'phone' || d === 'mobile') return lang === 'cs' ? 'Mobil' : 'Mobile';
  if (d === 'tablet') return 'Tablet';
  if (d === 'desktop') return 'Desktop';
  if (d === 'all') return lang === 'cs' ? 'Všechna zařízení' : 'All devices';
  return device;
};

// =========================================================================
// VENDOR CLASSIFICATION (3rd-party attribution)
// =========================================================================
// Mirrors server/services/performance/thirdPartyAnalyzer.js — kept
// client-side so HTML exports can attribute long tasks without an API call.
const VENDOR_MAP = {
  'googletagmanager.com':   { category: 'tag-manager', entity: 'Google Tag Manager' },
  'google-analytics.com':   { category: 'analytics',   entity: 'Google Analytics' },
  'googlesyndication.com':  { category: 'ads',         entity: 'Google Ads' },
  'doubleclick.net':        { category: 'ads',         entity: 'Google Ad Manager' },
  'googleadservices.com':   { category: 'ads',         entity: 'Google Ads' },
  'gstatic.com':            { category: 'cdn',         entity: 'Google Static' },
  'connect.facebook.net':   { category: 'social',      entity: 'Facebook Pixel' },
  'facebook.net':           { category: 'social',      entity: 'Facebook' },
  'facebook.com':           { category: 'social',      entity: 'Facebook' },
  'amazon-adsystem.com':    { category: 'ads',         entity: 'Amazon Ads' },
  'sdk.privacy-center.org': { category: 'cmp',         entity: 'Didomi CMP' },
  'gemius.pl':              { category: 'analytics',   entity: 'Gemius' },
  'r2b2.cz':                { category: 'ads',         entity: 'R2B2 (header bidding)' },
  'cpex.cz':                { category: 'ads',         entity: 'CPEx Prebid' },
  'adactive.cz':            { category: 'ads',         entity: 'SAS AdActive' },
  'aimatch.com':            { category: 'ads',         entity: 'aiMatch (ad server)' },
  'pubmatic.com':           { category: 'ads',         entity: 'PubMatic' },
  'ssp.seznam.cz':          { category: 'ads',         entity: 'Seznam SSP' },
  'cookielaw.org':          { category: 'cmp',         entity: 'OneTrust' },
  'cookiebot.com':          { category: 'cmp',         entity: 'Cookiebot' },
  'usercentrics.eu':        { category: 'cmp',         entity: 'Usercentrics' },
  'iubenda.com':            { category: 'cmp',         entity: 'Iubenda' },
  'cmp.inmobi.com':         { category: 'cmp',         entity: 'Quantcast Choice' },
  'trustarc.com':           { category: 'cmp',         entity: 'TrustArc' },
  'hotjar.com':             { category: 'analytics',   entity: 'Hotjar' },
  'clarity.ms':             { category: 'analytics',   entity: 'Microsoft Clarity' },
  'youtube.com':            { category: 'video',       entity: 'YouTube' },
  'youtube-nocookie.com':   { category: 'video',       entity: 'YouTube' },
  'ytimg.com':              { category: 'video',       entity: 'YouTube' },
  'vimeo.com':              { category: 'video',       entity: 'Vimeo' },
  'twitter.com':            { category: 'social',      entity: 'Twitter' },
  'x.com':                  { category: 'social',      entity: 'X (Twitter)' },
  'instagram.com':          { category: 'social',      entity: 'Instagram' },
  'sklik.cz':               { category: 'ads',         entity: 'Sklik' },
  'seznam.cz':              { category: 'analytics',   entity: 'Seznam' },
  'tiktok.com':             { category: 'social',      entity: 'TikTok' },
  'linkedin.com':           { category: 'social',      entity: 'LinkedIn' },
  'licdn.com':              { category: 'social',      entity: 'LinkedIn Insight' },
  'pinterest.com':          { category: 'social',      entity: 'Pinterest' },
  'snapchat.com':           { category: 'social',      entity: 'Snapchat' },
  'bing.com':               { category: 'ads',         entity: 'Microsoft Advertising' },
  'criteo.net':             { category: 'ads',         entity: 'Criteo' },
  'criteo.com':             { category: 'ads',         entity: 'Criteo' },
  'adform.net':             { category: 'ads',         entity: 'Adform' },
  'rtbhouse.com':           { category: 'ads',         entity: 'RTB House' },
  'segment.com':            { category: 'analytics',   entity: 'Segment' },
  'segment.io':             { category: 'analytics',   entity: 'Segment' },
  'mixpanel.com':           { category: 'analytics',   entity: 'Mixpanel' },
  'amplitude.com':          { category: 'analytics',   entity: 'Amplitude' },
  'fullstory.com':          { category: 'analytics',   entity: 'FullStory' },
  'smartlook.com':          { category: 'analytics',   entity: 'Smartlook' },
  'mouseflow.com':          { category: 'analytics',   entity: 'Mouseflow' },
  'intercom.io':            { category: 'chat',        entity: 'Intercom' },
  'intercomcdn.com':        { category: 'chat',        entity: 'Intercom' },
  'zendesk.com':            { category: 'chat',        entity: 'Zendesk' },
  'zopim.com':              { category: 'chat',        entity: 'Zendesk Chat' },
  'tawk.to':                { category: 'chat',        entity: 'Tawk.to' },
  'livechatinc.com':        { category: 'chat',        entity: 'LiveChat' },
  'smartsupp.com':          { category: 'chat',        entity: 'Smartsupp' },
  'cdn.shopify.com':        { category: 'cdn',         entity: 'Shopify CDN' },
  'cloudflare.com':         { category: 'cdn',         entity: 'Cloudflare' },
  'cloudfront.net':         { category: 'cdn',         entity: 'CloudFront' },
  'akamaihd.net':           { category: 'cdn',         entity: 'Akamai' },
  'jsdelivr.net':           { category: 'cdn',         entity: 'jsDelivr' },
  'unpkg.com':              { category: 'cdn',         entity: 'unpkg' },
  'cdnjs.cloudflare.com':   { category: 'cdn',         entity: 'cdnjs' },
};

const SUFFIX_VENDORS = Object.keys(VENDOR_MAP).sort((a, b) => b.length - a.length);

const classifyVendor = (url, primaryHost = '') => {
  const host = getHostname(url);
  if (!host) return null;
  if (primaryHost && sameOwner(primaryHost, host)) return null;
  if (host === 'googletagmanager.com' || host === 'www.googletagmanager.com') {
    if (/\/gtag\/js(?:[?#]|$)/i.test(url)) {
      return { category: 'analytics', entity: 'Google tag (gtag.js)', host, suffix: 'googletagmanager.com' };
    }
    if (/\/gtag\/destination(?:[?#]|$)/i.test(url)) {
      return { category: 'analytics', entity: 'Google tag destination', host, suffix: 'googletagmanager.com' };
    }
  }
  for (const suffix of SUFFIX_VENDORS) {
    if (host === suffix || host.endsWith('.' + suffix)) {
      return { ...VENDOR_MAP[suffix], host, suffix };
    }
  }
  return { category: 'third-party', entity: host, host, suffix: host };
};

const isAdsTrackingCategory = (cat) => ['ads', 'tag-manager', 'social', 'analytics', 'cmp'].includes(cat);

// =========================================================================
// RICH DIAGNOSTICS EXTRACTION
// =========================================================================
const extractServerTiming = (st) => {
  if (!st) return null;
  const bd = st.breakdown || {};
  const dns = bd.dns ?? st.dns ?? 0;
  const tcp = bd.connection ?? st.tcp ?? 0;
  const tls = bd.tls ?? st.tls ?? 0;
  const ttfb = bd.waiting ?? st.ttfb ?? 0;
  const download = bd.receiving ?? st.download ?? 0;
  const total = bd.total ?? st.responseTime ?? (dns + tcp + tls + ttfb + download);
  // TTFB of 0 means Navigation Timing data was not captured for this run —
  // interpreting all-zero values as "fast response" is a false positive.
  // Sub-10 ms values are equally bogus (cache hit / measurement artifact —
  // even a same-city origin needs >10 ms); rendering them would produce a
  // false "backend fine" verdict, so the section is omitted instead.
  if (!total || !(ttfb >= 10)) return null;
  return {
    dns: Math.round(dns),
    tcp: Math.round(tcp),
    tls: Math.round(tls),
    ttfb: Math.round(ttfb),
    download: Math.round(download),
    total: Math.round(total),
    isFast: ttfb > 0 && ttfb < 200,
    isSlow: ttfb > metricThresholds.ttfb.good,
    phasesAvailable: bd.phasesAvailable !== false
  };
};

const extractLongTasks = (lt, primaryHost) => {
  if (!lt || !lt.count) return null;
  // Defense in depth: older saved payloads may still contain Lighthouse's own
  // injected probe (`_lighthouse-eval.js`) — never attribute probe overhead
  // to the audited site.
  const tasks = (lt.tasks || lt.items || []).filter(t => !String(t.url || t.script || '').includes('_lighthouse-eval.js'));
  const items = tasks.map((t) => {
    const url = t.url || t.script || t.attribution?.url || '';
    const vendor = classifyVendor(url, primaryHost);
    const host = getHostname(url);
    const isFirstParty = !!host && !!primaryHost && sameOwner(primaryHost, host);
    let category;
    if (vendor?.category) category = vendor.category;
    else if (!url) category = 'first-party';
    else if (isFirstParty) category = 'first-party';
    else category = 'third-party';
    return {
      url,
      // A url-less task ("self") is usually inline/eval code injected by a tag
      // manager — labeling it plainly stops a third of blocking time from
      // hiding behind a cryptic "self" row.
      name: url ? shortenUrl(url, 60) : ((t.name && t.name !== 'self') ? t.name : 'inline/eval (nepřiřazeno — typicky inline kód z tag manageru)'),
      unattributed: !url,
      duration: Math.round(t.duration || 0),
      blockingTime: Math.round(t.blockingTime || Math.max(0, (t.duration || 0) - 50)),
      vendor: vendor?.entity || null,
      vendorCategory: category,
      vendorHost: vendor?.host || host || null
    };
  }).sort((a, b) => b.blockingTime - a.blockingTime);

  // Aggregate by vendor (entity-level)
  const byVendor = new Map();
  items.forEach((it) => {
    const key = it.vendor || (it.vendorCategory === 'first-party'
      ? (primaryHost || 'first-party')
      : (it.vendorHost || 'unknown'));
    const existing = byVendor.get(key) || {
      vendor: key,
      category: it.vendorCategory,
      totalBlockingMs: 0,
      totalDurationMs: 0,
      count: 0
    };
    existing.totalBlockingMs += it.blockingTime;
    existing.totalDurationMs += it.duration;
    existing.count += 1;
    byVendor.set(key, existing);
  });
  const vendorBreakdown = Array.from(byVendor.values())
    .sort((a, b) => b.totalBlockingMs - a.totalBlockingMs);

  const totalBlockingTime = lt.totalBlockingTime ?? items.reduce((s, t) => s + t.blockingTime, 0);
  const adsTrackingMs = vendorBreakdown
    .filter(v => isAdsTrackingCategory(v.category))
    .reduce((s, v) => s + v.totalBlockingMs, 0);
  // Share must be computed against the SAME sum the numerator came from
  // (per-item blocking times, which include first-party and unattributed
  // tasks). Dividing by the server's independently-rounded TBT could yield
  // impossible values like 101 %.
  const itemsBlockingSum = items.reduce((s, t) => s + t.blockingTime, 0);
  const shareDenominator = itemsBlockingSum > 0 ? itemsBlockingSum : totalBlockingTime;

  return {
    count: lt.count,
    totalBlockingTime: Math.round(totalBlockingTime),
    avgDuration: Math.round(lt.avgDuration || (items.length ? items.reduce((s, t) => s + t.duration, 0) / items.length : 0)),
    items,
    vendorBreakdown,
    adsTrackingMs: Math.round(adsTrackingMs),
    adsTrackingShare: shareDenominator > 0 ? Math.min(1, adsTrackingMs / shareDenominator) : 0
  };
};

const extractResourceTiming = (rt) => {
  if (!rt) return null;
  const summary = rt.summary || rt.byType || {};
  const tp = rt.thirdPartyImpact || {};
  // Lighthouse resource-summary used to include a 'total' aggregate row —
  // summing it together with the per-type rows double-counts every request
  // and byte. Exclude it defensively (newer server payloads already strip it
  // into rt.totals).
  const typeEntries = Object.entries(summary).filter(([type]) => String(type).toLowerCase() !== 'total');
  const totalRequests = rt.totals?.count || typeEntries.reduce((s, [, d]) => s + (d?.count || 0), 0);
  const totalBytes = rt.totals?.size || typeEntries.reduce((s, [, d]) => s + (d?.size || 0), 0);
  const byType = typeEntries.map(([type, d]) => ({
    type,
    count: d?.count || 0,
    bytes: d?.size || 0
  })).sort((a, b) => b.bytes - a.bytes);

  // Measurement-context guardrail for the "total vs third-party subset"
  // comparison. `totalRequests` comes from Lighthouse resource-summary,
  // `thirdParty.count` from Lighthouse network-requests — two different
  // request taxonomies. They must NEVER be phrased as total + subset
  // ("z toho X"): on real pages the network-requests subset can exceed the
  // resource-summary total (observed: 231 total vs 249 "z toho").
  //
  // Newer server payloads include rt.networkTotals — a total derived from
  // the SAME network-requests array as the subset. Only that pair is
  // compatible.
  //   'compatible'   — networkTotals present and subset <= total → subset
  //                    phrasing and share % allowed against networkTotals.
  //   'error'        — same declared context, yet subset > total →
  //                    mathematically impossible; suppress the comparison
  //                    (data bug — never render it).
  //   'incompatible' — old payload without networkTotals → different or
  //                    unverifiable measurement context; values may be shown
  //                    separately but never as total + subset, and no share %.
  const finiteNonNeg = (v) => Number.isFinite(v) && v >= 0;
  const netTotals = rt.networkTotals && finiteNonNeg(rt.networkTotals.count)
    ? { count: rt.networkTotals.count, bytes: finiteNonNeg(rt.networkTotals.size) ? rt.networkTotals.size : 0 }
    : null;
  const tpCount = finiteNonNeg(tp.count) ? tp.count : 0;
  const tpBytes = finiteNonNeg(tp.totalSize) ? tp.totalSize : 0;
  // Subset relation must hold for BOTH counts and bytes within the same
  // declared context — an impossible byte subset must trigger suppression,
  // never be clamped into a "100%" share.
  const tpComparison = netTotals
    ? ((tpCount <= netTotals.count && tpBytes <= netTotals.bytes) ? 'compatible' : 'error')
    : 'incompatible';

  return {
    totalRequests,
    totalBytes,
    byType,
    networkTotals: netTotals,
    tpComparison,
    thirdParty: {
      count: tpCount,
      totalBytes: tp.totalSize || 0,
      // Lighthouse request timings are already milliseconds.
      totalTimeMs: tp.totalTime ? Math.round(tp.totalTime) : 0
    }
  };
};

const MTW_LABELS = {
  cs: {
    scriptEvaluation: 'Skripty',
    scriptParseCompile: 'Parse / Compile',
    styleLayout: 'Styly a layout',
    paintCompositeRender: 'Render',
    garbageCollection: 'Garbage collection',
    parseHTML: 'HTML parsing',
    other: 'Ostatní',
    Script: 'Skripty',
    'Parse/Compile': 'Parse / Compile',
    Other: 'Ostatní',
    GC: 'Garbage collection',
    Style: 'Styly',
    Render: 'Render'
  },
  en: {
    scriptEvaluation: 'Scripts',
    scriptParseCompile: 'Parse / Compile',
    styleLayout: 'Style & layout',
    paintCompositeRender: 'Render',
    garbageCollection: 'Garbage collection',
    parseHTML: 'HTML parsing',
    other: 'Other',
    Script: 'Scripts',
    'Parse/Compile': 'Parse / Compile',
    Other: 'Other',
    GC: 'Garbage collection',
    Style: 'Style',
    Render: 'Render'
  }
};

const MTW_COLORS = {
  scriptEvaluation: '#fbbf24',
  scriptParseCompile: '#f97316',
  styleLayout: '#a855f7',
  paintCompositeRender: '#22c55e',
  garbageCollection: '#ef4444',
  parseHTML: '#3b82f6',
  other: '#64748b',
  Script: '#fbbf24',
  'Parse/Compile': '#f97316',
  Style: '#a855f7',
  Render: '#22c55e',
  GC: '#ef4444',
  Other: '#64748b'
};

const extractMainThread = (mtw, lang) => {
  if (!mtw) return null;
  const total = mtw.total || 0;
  if (!total) return null;

  let categories = [];
  if (Array.isArray(mtw.categories) && mtw.categories.length) {
    categories = mtw.categories.map(c => ({
      category: c.category,
      label: MTW_LABELS[lang]?.[c.category] || c.category,
      color: MTW_COLORS[c.category] || '#64748b',
      percentage: c.percentage,
      ms: Math.round((c.percentage / 100) * total)
    }));
  } else if (mtw.breakdown && typeof mtw.breakdown === 'object') {
    categories = Object.entries(mtw.breakdown).map(([cat, ms]) => ({
      category: cat,
      label: MTW_LABELS[lang]?.[cat] || cat,
      color: MTW_COLORS[cat] || '#64748b',
      percentage: Math.round((ms / total) * 100),
      ms: Math.round(ms)
    })).sort((a, b) => b.ms - a.ms);
  }

  const topScripts = (mtw.topScripts || []).slice(0, 5).map(s => ({
    url: s.url || '',
    name: s.url ? shortenUrl(s.url, 50) : 'Unattributable',
    ms: Math.round(s.total || s.duration || s.time || 0)
  }));

  return { totalMs: Math.round(total), categories, topScripts };
};

const extractFontLoading = (fl) => {
  if (!fl) return null;
  const totalFonts = fl.totalFonts || (Array.isArray(fl.fontDetails) ? fl.fontDetails.length : 0);
  if (!totalFonts && !fl.fontsWithoutSwap?.length) return null;
  return {
    totalFonts,
    withoutSwapCount: fl.fontsWithoutSwap?.length || 0,
    foitRisk: !!fl.foitRisk,
    foutRisk: !!fl.foutRisk,
    items: (fl.fontDetails || []).slice(0, 5).map(f => ({
      name: f.url ? shortenUrl(f.url, 45) : (f.name || 'font'),
      bytes: f.size || f.transferSize || 0,
      durationMs: f.duration ? Math.round(f.duration * 1000) : 0
    }))
  };
};

const BUDGET_LABELS = {
  cs: {
    total: 'Celkem',
    script: 'JavaScript',
    stylesheet: 'CSS',
    image: 'Obrázky',
    font: 'Fonty',
    document: 'HTML',
    other: 'Ostatní',
    media: 'Média'
  },
  en: {
    total: 'Total',
    script: 'JavaScript',
    stylesheet: 'CSS',
    image: 'Images',
    font: 'Fonts',
    document: 'HTML',
    other: 'Other',
    media: 'Media'
  }
};

const extractBudget = (pb, lang) => {
  if (!pb) return null;
  const items = (pb.budgetStatus || []).map(b => {
    const actual = b.current || b.actual || 0;
    const budget = b.limit || b.budget || 0;
    const pct = b.percentage ?? (budget > 0 ? Math.min(200, (actual / budget) * 100) : 0);
    return {
      type: b.type,
      label: BUDGET_LABELS[lang]?.[b.type] || b.type,
      actual,
      budget,
      percentage: Math.round(pct),
      overBudget: !!b.overBudget,
      ratio: budget > 0 ? actual / budget : 0
    };
  }).sort((a, b) => (b.percentage || 0) - (a.percentage || 0));

  const totalItem = items.find(i => i.type === 'total');
  return {
    items,
    overBudgetCount: items.filter(i => i.overBudget).length,
    totalActualBytes: totalItem?.actual || 0,
    totalBudgetBytes: totalItem?.budget || 0,
    totalRatio: totalItem?.ratio || 0,
    unusedJsBytes: pb.unusedJs?.wastedBytes
      || pb.unusedJsBytes
      || pb.unusedJavaScriptBytes
      || pb.unusedJs?.bytes
      || 0,
    unusedCssBytes: pb.unusedCss?.wastedBytes
      || pb.unusedCssBytes
      || pb.unusedStylesheetBytes
      || pb.unusedCss?.bytes
      || 0
  };
};

const extractRenderBlocking = (rb, crp) => {
  const data = rb || crp;
  if (!data) return null;
  const resources = data.resources || data.renderBlockingResources || [];
  const totalBlockingTime = data.totalBlockingTime || 0;
  if (!resources.length && !totalBlockingTime) return null;
  return {
    count: resources.length,
    totalBlockingMs: Math.round(totalBlockingTime),
    totalBytes: data.totalBlockingBytes || 0,
    items: resources.slice(0, 6).map(r => ({
      name: r.url ? shortenUrl(r.url, 50) : 'unknown',
      type: r.type || 'other',
      bytes: r.totalBytes || 0,
      wastedMs: Math.round(r.wastedMs || 0)
    }))
  };
};

const extractRichDiagnostics = (diagnostics, primaryHost, lang, cmpUntested = false) => {
  if (!diagnostics) diagnostics = {};
  const rich = {
    serverTiming: extractServerTiming(diagnostics.serverTiming),
    longTasks: extractLongTasks(diagnostics.longTasks, primaryHost),
    resourceTiming: extractResourceTiming(diagnostics.resourceTiming),
    mainThreadWork: extractMainThread(diagnostics.mainThreadWork || diagnostics.mainThreadBreakdown, lang),
    fontLoading: extractFontLoading(diagnostics.fontLoading),
    performanceBudget: extractBudget(diagnostics.performanceBudget, lang),
    renderBlocking: extractRenderBlocking(diagnostics.renderBlocking, diagnostics.criticalRenderingPath),
    cmpWarning: !!cmpUntested,
    cmpVendor: null,
    labStability: diagnostics.labStability || null,
    tbtSeedValue: typeof diagnostics.tbtSample?.value === 'number'
      && Number.isFinite(diagnostics.tbtSample.value) && diagnostics.tbtSample.value >= 0
      ? diagnostics.tbtSample.value : null,
    tbtRunSpread: diagnostics.labRunSpread?.metric === 'TBT' ? diagnostics.labRunSpread : null
  };

  // Try to detect CMP vendor present in resource timing
  if (rich.longTasks?.vendorBreakdown) {
    const cmp = rich.longTasks.vendorBreakdown.find(v => v.category === 'cmp');
    if (cmp) rich.cmpVendor = cmp.vendor;
  }

  return rich;
};

// =========================================================================
// CULPRIT BUILDER — pulls best-available "top 5 things to blame"
// =========================================================================
const buildCulprits = (rich, deepLCPResults, deepCLSResults, deepINPResults, lang, metricGood = false) => {
  // When the audited metric's verdict is GOOD, long tasks affect page load,
  // not the metric being graded — cap their severity at "medium" so the
  // report does not scream "high" next to a passing verdict.
  const capSeverity = (sev) => metricGood && (sev === 'critical' || sev === 'high') ? 'medium' : sev;
  // Priority 1: long tasks vendor breakdown (most actionable for site owners)
  if (rich.longTasks?.vendorBreakdown?.length) {
    const top = rich.longTasks.vendorBreakdown.slice(0, 6);
    return {
      kind: 'vendor',
      label: lang === 'cs' ? 'Hlavní viníci dlouhých úloh' : 'Main long-task culprits',
      items: top.map((v, i) => {
        const exemplar = rich.longTasks.items.find(it => (it.vendor || it.vendorHost) === v.vendor);
        return {
          rank: String(i + 1).padStart(2, '0'),
          title: v.vendor,
          subtitle: vendorCategoryLabel(v.category, lang),
          url: exemplar?.url || '',
          ms: v.totalDurationMs,
          blockingMs: v.totalBlockingMs,
          severity: capSeverity(v.totalBlockingMs > 500 ? 'critical' : v.totalBlockingMs > 200 ? 'high' : 'medium'),
          count: v.count,
          unit: 'ms'
        };
      })
    };
  }

  // Priority 2: deep LCP recs
  if (deepLCPResults?.recommendations?.length) {
    const recs = deepLCPResults.recommendations.slice(0, 5);
    return {
      kind: 'lcp',
      label: lang === 'cs' ? 'Hlavní příčiny LCP' : 'Top LCP causes',
      items: recs.map((r, i) => ({
        rank: String(i + 1).padStart(2, '0'),
        title: r.title || r.cause || (lang === 'cs' ? 'Příčina' : 'Cause'),
        subtitle: translateCauseDescription(r.description || r.detail || '', lang),
        url: (r.resources && r.resources[0]) || '',
        ms: r.impactMs || r.estimatedImpact || null,
        severity: r.severity || 'medium',
        unit: 'ms'
      }))
    };
  }

  // Priority 3: CLS elements
  if (deepCLSResults?.elements?.length) {
    // Affected containers are victims, not culprits — keep them out of the
    // "Biggest layout shifts" culprit ranking whenever real culprits exist.
    const nonContainers = deepCLSResults.elements.filter(el => !el.isContainer);
    const pool = nonContainers.length ? nonContainers : deepCLSResults.elements;
    // Culprit ranking follows FREQUENCY across stored runs when available
    // (labRunSpread.culpritFrequency) — the headline culprit must be the one
    // seen in most runs, not whichever element happened to shift most in one
    // noisy run (single-run CLS spread on the same page is huge).
    const freqList = Array.isArray(deepCLSResults?.labRunSpread?.culpritFrequency)
      ? deepCLSResults.labRunSpread.culpritFrequency : [];
    const freqBySelector = new Map(freqList.map(f => [f.selector, f]));
    const totalSpreadRuns = deepCLSResults?.labRunSpread?.runs || null;
    const seenOf = (el) => freqBySelector.get(el.selector)?.seenIn || 0;
    const sorted = [...pool].sort((a, b) =>
      seenOf(b) - seenOf(a) || (b.totalShift || 0) - (a.totalShift || 0)).slice(0, 5);
    return {
      kind: 'cls',
      label: lang === 'cs' ? 'Největší posuny obsahu' : 'Biggest layout shifts',
      items: sorted.map((el, i) => {
        const f = freqBySelector.get(el.selector);
        const freqNote = (f && totalSpreadRuns >= 2)
          ? (lang === 'cs' ? ` · viděno v ${f.seenIn} z ${totalSpreadRuns} běhů` : ` · seen in ${f.seenIn} of ${totalSpreadRuns} runs`)
          : '';
        // When frequency data exists, the shown shift is the MEDIAN across
        // runs for that element — same basis as the headline lab value.
        const shiftVal = (f && f.medianShift != null) ? f.medianShift : el.totalShift;
        return {
          rank: String(i + 1).padStart(2, '0'),
          title: el.tagName || (lang === 'cs' ? 'Element' : 'Element'),
          subtitle: (el.cause || '') + freqNote,
          url: el.selector || '',
          ms: shiftVal ? Math.round(shiftVal * 1000) : null,
          severity: 'high',
          unit: 'CLS×1000'
        };
      })
    };
  }

  // Priority 4: INP interactions
  if (deepINPResults?.interactions?.length) {
    const ints = [...deepINPResults.interactions].sort((a, b) => (b.duration || 0) - (a.duration || 0)).slice(0, 5);
    return {
      kind: 'inp',
      label: lang === 'cs' ? 'Nejpomalejší interakce' : 'Slowest interactions',
      items: ints.map((it, i) => ({
        rank: String(i + 1).padStart(2, '0'),
        title: it.target || it.type || 'Interaction',
        subtitle: it.type || '',
        url: it.selector || '',
        ms: it.duration || null,
        severity: 'high',
        unit: 'ms'
      }))
    };
  }

  return { kind: 'none', label: '', items: [] };
};

const vendorCategoryLabel = (cat, lang) => {
  const map = {
    cs: {
      'tag-manager': 'Tag manager',
      'ads': 'Reklamní síť',
      'analytics': 'Analytika',
      'social': 'Sociální síť / pixel',
      'cmp': 'Cookie banner (CMP)',
      'video': 'Video embed',
      'chat': 'Chat widget',
      'cdn': 'CDN',
      'first-party': 'Vlastní kód',
      'third-party': 'Externí skript'
    },
    en: {
      'tag-manager': 'Tag manager',
      'ads': 'Ad network',
      'analytics': 'Analytics',
      'social': 'Social pixel',
      'cmp': 'Cookie banner (CMP)',
      'video': 'Video embed',
      'chat': 'Chat widget',
      'cdn': 'CDN',
      'first-party': 'First-party',
      'third-party': 'Third-party script'
    }
  };
  return map[lang]?.[cat] || cat;
};

// =========================================================================
// SCENARIO DETECTION — picks the dominant story for narrative copy
// =========================================================================
// Lab interaction breakdown for cause claims. Prefers component MEDIANS
// across all stored runs (labRunSpread.components) — a single run either
// collides with a load-phase long task or not, so one run's breakdown is a
// weak basis for "rendering is the bottleneck". Falls back to the single
// worst-interaction run only when no multi-run spread exists at all.
const getLabBreakdown = (r) => {
  const sp = r.deepINPResults?.labRunSpread;
  const mc = sp?.components;
  if (mc && labSeriesIsVerified(sp)
      && [mc.inputDelay, mc.processingTime, mc.presentationDelay].every(v => Number.isFinite(Number(v)))) {
    return {
      inputDelay: Math.round(Number(mc.inputDelay)),
      processingTime: Math.round(Number(mc.processingTime)),
      presentationDelay: Math.round(Number(mc.presentationDelay)),
      basis: 'median',
      runs: sp.runs,
      // Cross-run dominance variability (computed server-side over ALL stored
      // runs with complete breakdowns): when the dominant component flips
      // between runs, the report must admit both profiles instead of leading
      // a single cause from the median run.
      dominance: mc.dominance || null
    };
  }
  const wi = r.deepINPResults?.worstInteraction;
  if (!wi) return null;
  return {
    inputDelay: Math.round(wi.inputDelay || 0),
    processingTime: Math.round(wi.processingTime || 0),
    presentationDelay: Math.round(wi.presentationDelay || 0),
    basis: 'single',
    // Spread of totals exists but has no component medians (older stored
    // runs) — a single-run cause claim would contradict the spread, mark it.
    weak: !!(sp && sp.runs >= 2),
    runs: 1
  };
};

const detectScenario = (r) => {
  const rich = r.rich;
  const status = r.status;

  // If the deep layer of the PRIORITY #1 metric failed (timeout/error), the
  // report must say exactly that — it must NOT rescue itself with another
  // metric's narrative (the banned TBT/"ads & tracking" fallback). Same rule
  // as 'inp-not-measured', lifted to the unified CWV level.
  {
    const prio = ((r.priorityTop?.metric) || r.metricLower || '').toUpperCase();
    const deepByMetric = { CLS: r.deepCLSResults, INP: r.deepINPResults, LCP: r.deepLCPResults };
    if ((r.failedDeepMetrics || []).includes(prio) && prio in deepByMetric && !deepByMetric[prio]) {
      return 'deep-failed-priority';
    }
  }

  // The headline must follow the metric that the device-priority ranking puts
  // at #1 — a report where the priority table says "CLS is the problem" while
  // the headline blames INP rendering contradicts itself. Cause claims about
  // a metric are only allowed when that metric IS the top priority.
  if (r.priorityTop && r.priorityTop.metric !== r.metricLower) {
    const m = r.priorityTop.metric;
    if (m === 'cls') return 'priority-cls';
    if (m === 'lcp' || m === 'fcp') return 'priority-lcp';
    if (m === 'ttfb') return 'priority-ttfb';
  }

  // INP reports MUST NOT fall back to load-time (TBT) narratives. When no
  // valid interaction breakdown exists (pointerover-only, 0 ms interaction,
  // navigated away, DOM sanity failure), the honest headline is "interaction
  // not measured" — the same input must never produce a tracking-blame
  // headline one run and a clean one the next.
  if (r.metricLower === 'inp') {
    const deep = r.deepINPResults;
    const wiCheck = deep?.worstInteraction;
    const breakdownTotal = (wiCheck?.inputDelay || 0)
      + (wiCheck?.processingTime || 0)
      + (wiCheck?.presentationDelay || 0);
    if (!r.validDeepInp || deep.attributionValid === false || !wiCheck || breakdownTotal <= 0) {
      return 'inp-not-measured';
    }
    return evaluateInpEvidence(r).variable ? 'inp-variable-profile' : 'inp-evidence-limited';
  }

  if (status === 'good' && (!rich.longTasks || rich.longTasks.totalBlockingTime < 300)) {
    return 'fast-and-clean';
  }

  // CLS report with measured shift sources: the headline is the concrete
  // finding ("what shifts the layout"), not a generic load-time story.
  if (r.metricLower === 'cls' && r.deepCLSResults?.elements?.some(el => (el.totalShift || 0) > 0.001)) {
    // Container guard IN THE HEADLINE branch too: a pushed container is the
    // victim of a shift, never its source. If the run captured ONLY
    // containers, the headline must say "culprit not determined", not
    // "pushed by the affected container".
    const hasSpecific = r.deepCLSResults.elements.some(el => (el.totalShift || 0) > 0.001 && !el.isContainer);
    return hasSpecific ? 'cls-shift-sources' : 'cls-shift-container-only';
  }

  // INP reports: the headline must follow the worst-interaction breakdown
  // (input delay / processing / presentation), not the load-time TBT vendor
  // ranking. When presentation delay dominates and processing is negligible,
  // scripts do not delay the interaction — rendering does.
  // Three-branch cause logic (input delay / processing / presentation) based
  // on the MEDIAN breakdown across stored runs — a single run whose spread
  // contradicts it (e.g. lab median in the good band) must not drive a cause
  // headline. Processing-dominant intentionally falls through to the
  // script/vendor scenarios below — that IS the "scripts delay it" story.
  if (r.metricLower === 'inp' && r.deepINPResults?.attributionValid !== false) {
    const bd = getLabBreakdown(r);
    if (bd && !bd.weak) {
      // Evidence-strength guard: when the dominant component FLIPS between
      // stored runs (dominance.variable), no single-cause headline is
      // supported by the data — "the strength of the claim must not exceed
      // the strength of the evidence". The headline must present both
      // profiles instead of leading the median run's cause as definitive.
      if (bd.dominance?.variable && bd.dominance.secondary) {
        return 'inp-variable-profile';
      }
      const { inputDelay: id, processingTime: pt, presentationDelay: pd } = bd;
      const tot = id + pt + pd;
      if (tot > 0 && pd >= tot * 0.5 && pd > pt && pd > id) {
        return 'inp-presentation-bound';
      }
      if (tot > 0 && id >= tot * 0.5 && id > pt && id > pd) {
        return 'inp-input-delay-bound';
      }
    }
  }

  // ----- Metric-mechanism gate for the generic scenario tail -----
  // The headline's claim must concern the MECHANISM of the metric that leads
  // the report (which, thanks to the priority guard above, is the priority-#1
  // metric). Page weight has nothing to do with TTFB (TTFB ends before the
  // body downloads); script/tracking stories are load/interaction mechanisms,
  // not layout-stability ones. A scenario outside its metric's mechanism is
  // skipped and the chain falls through to the next relevant one.
  const mechMetric = r.metricLower === 'fcp' ? 'lcp' : r.metricLower;
  const SCENARIO_METRICS = {
    'tracking-within-limits': ['inp', 'lcp'],
    'cmp-burden': ['inp', 'lcp'],
    'gtm-dominant': ['inp', 'lcp'],
    'social-tracker-heavy': ['inp', 'lcp'],
    'ads-heavy': ['inp', 'lcp'],
    'tracking-heavy': ['inp', 'lcp'],
    'slow-server': ['ttfb', 'lcp'],
    'large-page': ['lcp'],
    'unused-js': ['lcp'],
    'font-blocking': ['lcp'],
    'mainthread-overload': ['inp', 'lcp'],
    'cmp-untested': ['inp', 'lcp', 'cls', 'ttfb'] // methodological, metric-agnostic
  };
  const relevant = (s) => (SCENARIO_METRICS[s] || []).includes(mechMetric);

  // Data-driven headline: a high third-party SHARE of a small TBT is not a
  // "the problem is ads" story. When blocking time is within the 300ms
  // limit, use the neutral scenario regardless of CMP warnings.
  if (relevant('tracking-within-limits') && rich.longTasks?.adsTrackingShare > 0.5 && (rich.longTasks.totalBlockingTime || 0) < 300) {
    return 'tracking-within-limits';
  }

  // CMP is the trigger story when present + tracking is non-trivial — check
  // before raw tracking-heavy because the banner is the decisional moment for
  // the site owner (consent flow), not the individual tracker.
  if (relevant('cmp-burden') && rich.cmpWarning && rich.longTasks?.adsTrackingShare > 0.25) {
    return 'cmp-burden';
  }

  if (relevant('tracking-heavy') && rich.longTasks?.adsTrackingShare > 0.5) {
    // "The problem is not your site" headline is only honest when the site's
    // own code is a negligible blocker. If first-party code contributes a
    // significant share, use the neutral tracking headline instead.
    const firstPartyMs = (rich.longTasks.vendorBreakdown || [])
      .filter(v => v.category === 'first-party')
      .reduce((s, v) => s + (v.totalBlockingMs || 0), 0);
    const topSingleTask = rich.longTasks.items?.[0];
    if (firstPartyMs >= 150 || topSingleTask?.vendorCategory === 'first-party') return 'tracking-heavy';
    const top = rich.longTasks.vendorBreakdown[0];
    if (top?.category === 'tag-manager' && r.hasGtmContainer) return 'gtm-dominant';
    if (top?.category === 'social') return 'social-tracker-heavy';
    if (top?.category === 'ads') return 'ads-heavy';
    return 'tracking-heavy';
  }

  if (relevant('slow-server') && rich.serverTiming?.isSlow) return 'slow-server';

  // A TTFB-led report's headline mechanism IS the server response — even when
  // Server-Timing headers were absent, the honest story is the slow first
  // byte, never page weight or scripts (they play after the first byte).
  if (mechMetric === 'ttfb' && status !== 'good') return 'slow-server';

  if (relevant('large-page') && rich.performanceBudget?.totalActualBytes > 3 * 1024 * 1024) return 'large-page';

  if (relevant('unused-js') && rich.performanceBudget?.unusedJsBytes > 300 * 1024) return 'unused-js';

  if (relevant('font-blocking') && rich.fontLoading?.withoutSwapCount > 0 && (r.metricLower === 'lcp' || r.metricLower === 'fcp')) {
    return 'font-blocking';
  }

  if (relevant('mainthread-overload') && rich.mainThreadWork && rich.mainThreadWork.totalMs > 3000) return 'mainthread-overload';

  if (relevant('cmp-untested') && rich.cmpWarning) return 'cmp-untested';

  return 'balanced';
};

// =========================================================================
// SCENARIO COPY — Czech-first narrative templates with token substitution
// =========================================================================
// Tokens: {domain}, {metric}, {value}, {tbt}, {pageMb}, {budgetMb},
//         {unusedJsKb}, {topVendor}, {topVendorMs}, {ttfbMs}, {trackingShare},
//         {trackingMs}, {mainThreadS}, {fontsCount}
const SCENARIO_COPY = {
  cs: {
    'fast-and-clean': {
      headline: ['Web', 'běží', 'jak má.'],
      headlineEm: 1,
      subhead: 'Měření neukázalo žádný významný problém. {metric} je {value}, hlavní vlákno není přetížené, third-party skripty drží svá místa.',
      body: [
        'Při auditu jsme nenašli nic, co by se vyplatilo měnit kvůli rychlosti. {metric} ({value}) sedí pod doporučenou hranicí, server odpovídá rychle a obsah se vykresluje plynule.',
        'Doporučujeme zachovat současnou disciplínu při přidávání skriptů a obrázků. Drobné optimalizace v sekci „Doporučení" jsou volitelné — žádná z nich není kritická.'
      ],
      pullquote: 'Bez problémů je nejlepší výsledek auditu, který může klient dostat.'
    },
    'inp-not-measured': {
      headline: ['Interakce', 'nezměřena.', 'Analýza odezvy', 'neproběhla.'],
      headlineEm: 1,
      subhead: 'Měření nezachytilo žádnou validní interakci — hodnota INP a její rozpad (vstup / zpracování / vykreslení) proto nejsou k dispozici. Tento report nemůže o odezvě na kliknutí nic tvrdit.',
      body: [
        'Deep test se pokusil vyvolat a změřit reálnou interakci, ale výsledek nelze použít: zachycená událost buď nebyla skutečná interakce (např. pouhý pohyb myši), trvala 0 ms, nebo stránka během měření přešla jinam. Bez validního rozpadu interakce nelze určit, co odezvu brzdí — a bylo by nepoctivé místo toho vyprávět příběh o načítání stránky.',
        'Doporučujeme test zopakovat. Metriky načtení (dlouhé úlohy, váha skriptů) v tomto reportu zůstávají — popisují rychlost načtení, ne odezvu na interakci, a závěry o INP z nich vyvozovat nelze.'
      ],
      pullquote: 'Nezměřená interakce není dobrá ani špatná zpráva. Jsou to chybějící data.'
    },
    'deep-failed-priority': {
      headline: ['{metric} je priorita č. 1,', 'ale jeho hloubková analýza', 'selhala.'],
      headlineEm: 2,
      subhead: 'Prioritizace podle reálných dat určila jako hlavní problém {metric} ({value}), jenže hloubkový laboratorní test této metriky se nezdařil. Field verdikt se nemění a laboratorní hodnota není doplněna odhadem.',
      body: [
        'Pravděpodobná příčina: {failureCause}',
        '{failureAvailable} Ostatní naměřené vrstvy popisují jen vedlejší témata — žádný z těchto nálezů není odpovědí na to, proč má {metric} hodnotu {value}.',
        'Další krok: {failureAction}'
      ],
      pullquote: 'Report bez analýzy hlavního problému není hotový report. Je to nekompletní report.'
    },
    'gtm-dominant': {
      headline: ['Problém', 'není web.', 'Problém je', 'reklama a tracking.'],
      headlineEm: 3,
      subhead: 'V jednom výchozím běhu Lighthouse z {tbt} ms blokujícího času připadá {trackingMs} ms ({trackingShare}) na třetí strany; nejde o rozpad mediánu série. Nejtěžší jednotlivá úloha: {topTaskName} ({topTaskMs} ms). {siteVerdict} V součtu podle vlastníka nasčítá nejvíce {topVendor} ({topVendorMs} ms z {topVendorCount} úloh).',
      body: [
        'Hlavní vlákno prohlížeče zaměstnává především kód třetích stran. Žebříček dlouhých úloh vede {topTaskName} s jedinou úlohou o {topTaskMs} ms; v součtu podle vlastníka nasčítá nejvíce {topVendor} ({topVendorMs} ms rozložených do {topVendorCount} úloh) — to jsou dva různé pohledy na stejná data. Zbytek tvoří samostatně nasazené skripty — consent management, pixely, reklamní systémy — které se sčítají do dlouhých úloh a brzdí interakci.',
        '{siteVerdict} To, co dělá web pomalým, je vrstva přidávaná postupem času — jednotlivé tracking kódy, konverzní pixely, reklamní zdroje. {gtmAdvice}'
      ],
      pullquote: 'Tag manager se chová jako další web uvnitř webu. Jen ho nikdo nevidí.'
    },
    'inp-presentation-bound': {
      headline: ['INP:', 'změřený rozpad,', 'ne potvrzená', 'příčina.'],
      headlineEm: 3,
      subhead: 'Rozpad laboratorní interakce ({inpMs} ms{labRangeSuffix}): zpoždění vstupu {inputDelayMs} ms, zpracování {processingMs} ms, presentation delay {presentationMs} ms. Tato fáze není profilem potvrzenou příčinou.',
      body: [
        'V této interakci zpracování trvalo {processingMs} ms a fáze presentation delay {presentationMs} ms. Rozpad Event Timing sám neurčuje, zda čas způsobilo layout, paint, DOM nebo jiná práce; ani neprokazuje vliv třetích stran na interakci.{processingReproNote}',
        'Nejprve pořiďte Performance profil pomalé interakce a určete konkrétní práci v jejím okně. Teprve podle profilu volte zásah a ověřte jej opakovanými srovnatelnými běhy. {loadTrackingNote}'
      ],
      pullquote: 'Dlouhá fáze presentation delay je pozorování, nikoli potvrzený viník.'
    },
    'inp-variable-profile': {
      headline: ['INP má', 'dva profily problému:', 'vykreslení a', 'obsazené vlákno.'],
      headlineEm: 3,
      subhead: 'Uložené laboratorní běhy ({inpMs} ms{labRangeSuffix}) se neshodují na jediné příčině — dominantní složka rozpadu interakce se mezi běhy střídá. Data proto nedovolují označit jednu definitivní příčinu; report vede oba profily.',
      body: [
        'V běhu nejblíže mediánu vypadá rozpad takto: zpoždění vstupu {inputDelayMs} ms, zpracování {processingMs} ms, vykreslení {presentationMs} ms. V dalších uložených bězích se ale dominantní složka mění — část interakcí brzdí vykreslení výsledku, část čekání na uvolnění hlavního vlákna (dlouhé úlohy skriptů, včetně třetích stran).',
        'Prvním krokem proto není jedna izolovaná optimalizace, ale profilování nejpomalejších interakcí v Chrome DevTools (panel Performance): teprve profil rozhodne, zda převažuje vykreslení (→ zjednodušit DOM, CSS contain) nebo obsazené vlákno (→ rozbít dlouhé úlohy, odložit nekritické skripty). {loadTrackingNote}'
      ],
      pullquote: 'Dva střídající se profily znamenají dva možné viníky — o pořadí oprav rozhodne profilování interakcí.'
    },
    'inp-input-delay-bound': {
      headline: ['Brzdou odezvy', 'není handler.', 'Je to', 'obsazené vlákno.'],
      headlineEm: 3,
      subhead: 'Rozpad laboratorní interakce ({inpMs} ms{labRangeSuffix}): zpoždění vstupu {inputDelayMs} ms, zpracování {processingMs} ms, vykreslení {presentationMs} ms. Hlavní příčinou pomalé odezvy je čekání na volné hlavní vlákno — kliknutí trefilo běžící úlohu.',
      body: [
        'Samotné zpracování interakce trvalo {processingMs} ms a vykreslení {presentationMs} ms — kód navázaný na kliknutí není problém. Většina času ({inputDelayMs} ms) uplyne ještě PŘED spuštěním handleru: hlavní vlákno bylo v okamžiku interakce obsazené jinou úlohou (dlouhé úlohy skriptů, hydratace, tracking).',
        'Cesta k lepšímu INP proto vede přes rozbití dlouhých úloh: rozdělit těžké skripty (yield přes setTimeout/scheduler.yield), odložit nekritické third-party skripty a přesunout těžkou práci mimo hlavní vlákno (web worker). {loadTrackingNote}'
      ],
      pullquote: 'Handler byl rychlý. Kliknutí jen čekalo, až na něj přijde řada.'
    },
    'cls-shift-sources': {
      headline: ['Layout', 'neposkakuje sám.', 'Posouvá ho', '{clsTopCause}.'],
      headlineEm: 3,
      subhead: '{clsValueLine} Měření zachytilo konkrétní elementy při posunu — největší podíl má {clsTopCause}: {clsTopSelector} s posunem {clsTopShift}.',
      body: [
        'Posuny layoutu se sbíraly po celou životnost stránky včetně scrollu, u každého posunu měření zaznamenalo konkrétní element a jeho zdroje. Sekce „Co přesně posouvá layout" níže uvádí každý nález s příčinou, vlastníkem a opravou.',
        'Obecný princip oprav: každý prvek, který se dotahuje později (reklama, obrázek, embed, font), musí mít místo rezervované předem — min-height u reklamních slotů, width/height či aspect-ratio u obrázků a embedů. Obsah se nikdy nesmí vkládat nad již vykreslený text.'
      ],
      pullquote: 'Každý posun má konkrétního viníka. Tenhle report ho jmenuje.'
    },
    'cls-shift-container-only': {
      headline: ['Layout', 'se posouvá.', 'Konkrétní viník se', 'v tomto běhu neurčil.'],
      headlineEm: 3,
      subhead: '{clsValueLine} Lab běh zachytil posuny layoutu, ale jen jako posunuté kontejnery ({clsTopSelector}, posun {clsTopShift}) — to je oběť posunu, ne jeho zdroj. Co přesně kontejner posunulo (reklama, obrázek, font, vkládaný obsah), se v tomto běhu určit nepodařilo.',
      body: [
        'Posunutý kontejner je důkaz, že se NAD ním nebo V něm něco dotáhlo později a odtlačilo obsah — sám o sobě ale viníkem není. Typické skutečné zdroje: reklamní slot bez rezervované výšky, obrázek bez rozměrů, pozdě dotažený font nebo dynamicky vkládaný banner.',
        'Obecný princip oprav platí i bez jmenovitého viníka: každý prvek, který se dotahuje později, musí mít místo rezervované předem — min-height u reklamních slotů, width/height či aspect-ratio u obrázků a embedů. Obsah se nikdy nesmí vkládat nad již vykreslený text.'
      ],
      pullquote: 'Posunutý kontejner není viník. Je to důkaz, že ho něco posunulo.'
    },
    'priority-cls': {
      headline: ['Hlavní problém', 'není odezva.', 'Je to', 'stabilita layoutu.'],
      headlineEm: 3,
      subhead: 'Prioritizace podle reálných dat určila jako problém č. 1 CLS: {priorityP75} na zařízení {priorityDevice} (práh dobré hodnoty {priorityThreshold}). Stránka se uživatelům posouvá pod rukama — a to je teď důležitější než cokoli jiného v tomto reportu.',
      body: [
        'CLS (Cumulative Layout Shift) měří, jak moc obsah poskakuje během života stránky: reklamní sloty bez rezervovaného místa, obrázky bez rozměrů, pozdě dotažené fonty a vkládané bannery posouvají text ve chvíli, kdy ho uživatel čte nebo na něj kliká.',
        'První kroky: rezervovat rozměry pro reklamní sloty (min-height), doplnit width/height nebo aspect-ratio u obrázků a embedů a nevkládat obsah nad již vykreslený text. Detailní diagnostika odezvy (INP) v tomto reportu zůstává platná, ale podle prioritizace není tím, co řešit první.'
      ],
      pullquote: 'Uživatele teď nejvíc bolí uskakující layout, ne pomalá odezva.'
    },
    'priority-lcp': {
      headline: ['Hlavní problém', 'není odezva.', 'Je to', 'načítání.'],
      headlineEm: 3,
      subhead: 'Prioritizace podle reálných dat určila jako problém č. 1 {priorityMetric}: {priorityP75} na zařízení {priorityDevice} (práh dobré hodnoty {priorityThreshold}). Uživatelé čekají na zobrazení hlavního obsahu — a to je teď důležitější než cokoli jiného v tomto reportu.',
      body: [
        'Pomalé vykreslení hlavního obsahu má typicky tři zdroje: pomalou odpověď serveru, blokující zdroje v hlavičce (CSS, fonty, synchronní skripty) a těžký hlavní obrázek či pozdě objevený LCP element.',
        'První kroky: prověřit velikost a prioritu LCP elementu (fetchpriority="high", preload), odblokovat render-blocking zdroje a zkontrolovat odpověď serveru. Detailní diagnostika odezvy (INP) v tomto reportu zůstává platná, ale podle prioritizace není tím, co řešit první.'
      ],
      pullquote: 'Uživatele teď nejvíc bolí čekání na obsah, ne pomalá odezva.'
    },
    'priority-ttfb': {
      headline: ['Hlavní problém', 'není odezva.', 'Je to', 'server.'],
      headlineEm: 3,
      subhead: 'Prioritizace podle reálných dat určila jako problém č. 1 TTFB: {priorityP75} na zařízení {priorityDevice} (práh dobré hodnoty {priorityThreshold}). Než prohlížeč dostane první bajt, uživatel jen čeká — a to je teď důležitější než cokoli jiného v tomto reportu.',
      body: [
        'Pomalý první bajt zdržuje úplně všechno ostatní: žádná optimalizace frontendu nedožene čas, který stránka prostojí čekáním na odpověď serveru. Typické příčiny: chybějící cache, pomalé databázové dotazy, vzdálený či poddimenzovaný hosting, redirect řetězce.',
        'První kroky: zapnout/prověřit cachování odpovědí (CDN, full-page cache), změřit čas backendu na generování stránky a odstranit zbytečné přesměrování. Detailní diagnostika odezvy (INP) v tomto reportu zůstává platná, ale podle prioritizace není tím, co řešit první.'
      ],
      pullquote: 'Uživatele teď nejvíc bolí čekání na server, ne pomalá odezva.'
    },
    'social-tracker-heavy': {
      headline: ['Sociální pixely', 'sčítají', 'sekundy.'],
      headlineEm: 1,
      subhead: 'Třetí strany ze sociálních sítí ({topVendor}) blokují hlavní vlákno {topVendorMs} ms. Dohromady se sociální tracking podílí na {trackingShare} celkového zpoždění.',
      body: [
        'Ve výchozím běhu Lighthouse připadá {trackingMs} ms blokujícího času z {tbt} ms na tuto skupinu. Jde o atribuci tohoto jednoho běhu, nikoli o rozpad mediánu opakovaných měření.',
        'Pixely se zpravidla dají odložit přes server-side conversions API nebo načíst až po prvním interakčním eventu. Snížíte tím TBT bez ztráty sledovatelnosti konverzí.'
      ],
      pullquote: 'Pixel je skript jako každý jiný. Jen má krásnější jméno.'
    },
    'ads-heavy': {
      headline: ['Reklamní sítě', 'platí', 'výkonem.'],
      headlineEm: 1,
      subhead: 'Reklamní skripty ({topVendor} a další) blokují hlavní vlákno {topVendorMs} ms. {siteVerdict}',
      body: [
        'Audit ukazuje, že čisté metriky stránky (HTML, CSS, vlastní JS) by zvládly cíl pod doporučenou hranicí. Co je drží nad ní, jsou reklamní pixely a aukční scripty: {trackingMs} ms ({trackingShare}) hlavního vlákna.',
        'Server-side bidding nebo lazy load reklam níže na stránce (mimo viditelnou část bez scrollování) může uvolnit {trackingMs} ms okamžitě. Pokud reklama financuje provoz, stojí za to spočítat, kolik vás stojí v konverzích.'
      ],
      pullquote: 'Reklama platí provoz. Někdy taky platí Core Web Vitals.'
    },
    'tracking-heavy': {
      headline: ['Tracking', 'zatěžuje', 'rychlost.'],
      headlineEm: 1,
      subhead: 'Sledovací skripty třetích stran zabírají {trackingShare} blokujícího času. {siteVerdict}',
      body: [
        'Audit identifikoval {trackingMs} ms blokujícího času na třetích stranách. Žebříček dlouhých úloh vede {topTaskName} ({topTaskMs} ms v jediné úloze); v součtu podle vlastníka nasčítá nejvíce {topVendor} ({topVendorMs} ms z {topVendorCount} úloh).',
        'Doporučujeme udělat audit aktivních tagů — část jich už není potřeba a běží jen ze setrvačnosti. Druhá vlna optimalizace patří přesunu klíčových sledovacích skriptů na server-side.'
      ],
      pullquote: 'Většina tracking kódu nepřináší rozhodnutí. Jen šum.'
    },
    'cmp-burden': {
      headline: ['Cookie banner', 'startuje', 'celý řetěz.'],
      headlineEm: 1,
      subhead: 'Měření zachytilo {trackingMs} ms blokujícího času od trackerů a reálný uživatel uvidí ještě více — testovací scénář neproběhl s kliknutím na „Přijmout vše".',
      body: [
        'Cookie consent banner ({topVendor}) je první skript, který spouští všechen marketingový tracking. Po souhlasu se na hlavní vlákno přidá ještě další várka pixelů a remarketingových kódů.',
        'Optimalizace zde znamená dvě věci: zjednodušit consent flow tak, aby banner sám nebyl nákladný, a u trackerů, které spouští, používat lazy load nebo server-side variantu.'
      ],
      pullquote: 'Banner se ptá na souhlas. Skripty pod ním nepustí brzdu.'
    },
    'cmp-untested': {
      headline: ['Test', 'proběhl', 'bez souhlasu.'],
      headlineEm: 2,
      subhead: 'Měření bylo provedeno bez kliknutí na cookie lištu. Skutečný dopad na uživatele bude vyšší, než ukazuje tento report.',
      body: [
        'Většina nasazených webů má pomalejší skutečný start než syntetický test, protože reálný uživatel projde nejprve consent flow. Tato analýza ukazuje výchozí stav před souhlasem.',
        'Pro úplnou představu doporučujeme spustit druhé měření s povolenými marketing cookies a porovnat metriky. Rozdíl ukáže, kolik vás cookie consent fakticky stojí.'
      ],
      pullquote: 'Test bez souhlasu ukazuje strop, ne realitu.'
    },
    'slow-server': {
      headline: ['Server', 'odpovídá', 'pomalu.'],
      headlineEm: 1,
      subhead: 'TTFB {ttfbMs} ms — server potřebuje příliš dlouho, než vrátí první bajt. Vše ostatní na něj jen čeká.',
      body: [
        `Klientské optimalizace nemají na co navazovat, dokud server nepošle HTML. Hodnota TTFB nad ${metricThresholds.ttfb.good} ms je signál pro analýzu serverové části: pomalé databázové dotazy, chybějící cache, nebo geografická vzdálenost serveru.`,
        'První krok je server-side timing breakdown a profilace databáze. CDN s edge cachem nebo přesun do bližšího regionu zpravidla přináší okamžité zlepšení.'
      ],
      pullquote: 'Frontend nemůže být rychlejší než server pod ním.'
    },
    'large-page': {
      headline: ['Web', 'váží', 'příliš mnoho.'],
      headlineEm: 1,
      subhead: 'Stránka má {pageMb} MB při rozpočtu {budgetMb} MB. Načítání na mobilní síti je pomalé prostě proto, že je co načítat.',
      body: [
        'Performance budget (interní doporučený benchmark ControlVitals, ne limit Google) je překročen v několika kategoriích. Největší podíl mají obvykle obrázky a JavaScript. Audit jich identifikoval konkrétní seznam — viz sekce „Performance budget".',
        'Snížit váhu lze ve dvou krocích: přechodem na moderní formáty (WebP/AVIF pro obrázky, brotli komprese pro text) a code splittingem JS, aby se počáteční bundle načítal v menší dávce.'
      ],
      pullquote: 'Mobilní uživatel na 4G platí každou megabajtu sekundami.'
    },
    'unused-js': {
      headline: ['Stránka', 'stahuje', 'kód, který nepoužije.'],
      headlineEm: 2,
      subhead: 'Až {unusedJsKb} KB JavaScriptu se stáhne, ale při studeném načtení se nespustí (horní odhad dle Lighthouse — část kódu běží až po interakcích). Prohlížeč ho přesto musí parsovat a kompilovat.',
      body: [
        'Knihovny a frameworky se často přidávají v plné velikosti, i když stránka používá jen malou část. Code splitting (dynamic import) a tree shaking dokáží odstranit většinu tohoto balastu.',
        'Druhý zdroj je sdílený bundle pro celý web, kde každá stránka stáhne i kód patřící jiným routám. Per-route splitting tento problém řeší beze změn v aplikační logice.'
      ],
      pullquote: 'Nepoužitý kód je nejdražší, protože za něj prohlížeč pracuje pro nic.'
    },
    'font-blocking': {
      headline: ['Fonty', 'blokují', 'první zobrazení.'],
      headlineEm: 1,
      subhead: 'Stránka načítá {fontsCount} vlastních fontů a část z nich nemá nastaveno font-display: swap. Text čeká, dokud se font nestáhne.',
      body: [
        'Vlastní fonty bez `font-display: swap` způsobují FOIT (Flash of Invisible Text) — text na stránce je do okamžiku načtení fontu neviditelný. To přímo zvyšuje LCP.',
        'Řešení je trojí: 1) přidat `font-display: swap` (nebo `optional`) do @font-face, 2) preload kritických fontů přes <link rel="preload">, 3) zvážit systémový font-stack jako fallback. Implementace zabere zpravidla méně než hodinu.'
      ],
      pullquote: 'Hezký font, který vidíte až za sekundu, není hezký font.'
    },
    'mainthread-overload': {
      headline: ['Hlavní vlákno', 'pracuje', 'příliš dlouho.'],
      headlineEm: 1,
      subhead: 'Prohlížeč stráví {mainThreadS} aktivní prací při načítání stránky. To je nad rámec běžného webu a zpomaluje vše, co následuje.',
      body: [
        'Largest contributor je obvykle JavaScript — buď jeho zpracování (parse/compile), nebo dlouhé synchronní operace ve scriptech samých. Sekce „Práce hlavního vlákna" ukazuje rozložení podle kategorií.',
        'Konkrétní úlohy nad 50 ms jsou v sekci „Dlouhé úlohy" včetně původce. Nejúčinnější optimalizace je rozdělení dlouhých úloh přes `scheduler.yield()` nebo přesun těžké práce do Web Workeru.'
      ],
      pullquote: 'Hlavní vlákno je jednovlákno. Když pracuje, uživatel čeká.'
    },
    'balanced': {
      headline: ['Web', 'má', 'více menších brzd.'],
      headlineEm: 1,
      subhead: 'Žádná jednotlivá příčina nedominuje, ale součet menších problémů drží metriku {metric} nad doporučenou hranicí.',
      body: [
        'Audit nenašel jednoho velkého viníka. Místo toho je to kombinace několika faktorů: třetí strany, velikost stránky, hlavní vlákno. Žádný z nich sám o sobě není kritický, dohromady ale tvoří měřitelný problém.',
        'Doporučujeme postupovat podle sekce „Doporučení" — položky jsou seřazeny podle poměru dopadu k náročnosti. Každá z prvních tří položek by měla přinést měřitelné zlepšení, žádná z nich není dílem dnů.'
      ],
      pullquote: 'Když nedominuje jediná příčina, pomůže pár drobných změn.'
    },
    'tracking-within-limits': {
      headline: ['Zátěž třetích stran', 'je', 'v limitu.'],
      headlineEm: 2,
      subhead: 'V jednom výchozím běhu Lighthouse je blokující čas v limitu ({tbt} ms). Hlavní zátěž v tomto běhu je na straně třetích stran ({trackingShare}); nejde o rozpad mediánu série ani měření INP.',
      body: [
        'Většinu blokujícího času hlavního vlákna tvoří skripty třetích stran ({trackingMs} ms, největší zdroj {topVendor}). Celkový blokující čas ale zůstává pod hranicí 300 ms, takže reálný dopad na odezvu stránky je malý.',
        'Není důvod k okamžitému zásahu. Doporučujeme podíl třetích stran sledovat — každý nově přidaný tag se přičítá k této vrstvě a rezerva vůči limitu není velká.'
      ],
      pullquote: 'Tracking je vidět v číslech, ale zatím nebrzdí uživatele.'
    }
  },
  en: {
    'fast-and-clean': {
      headline: ['Site', 'is running', 'as it should.'],
      headlineEm: 1,
      subhead: 'No significant issue detected. {metric} is {value}, the main thread is not overloaded, third-party scripts behave.',
      body: [
        'The audit found nothing worth changing for performance. {metric} ({value}) sits below the recommended threshold, server responds quickly, and content renders smoothly.',
        'We recommend keeping the current discipline when adding scripts and images. The minor optimizations in the recommendations section are optional — none of them is critical.'
      ],
      pullquote: 'A clean audit is the best result a client can receive.'
    },
    'inp-not-measured': {
      headline: ['Interaction', 'not measured.', 'Responsiveness analysis', 'did not run.'],
      headlineEm: 1,
      subhead: 'The measurement captured no valid interaction — the INP value and its breakdown (input / processing / presentation) are therefore unavailable. This report can make no claims about click responsiveness.',
      body: [
        'The deep test attempted to trigger and measure a real interaction, but the result is unusable: the captured event either was not a real interaction (e.g. a mere mouse move), lasted 0 ms, or the page navigated away during measurement. Without a valid interaction breakdown, the cause of slow responsiveness cannot be determined — and telling a page-load story instead would be dishonest.',
        'We recommend re-running the test. Load-time metrics (long tasks, script weight) remain in this report — they describe load speed, not interaction responsiveness, and no INP conclusions can be drawn from them.'
      ],
      pullquote: 'An unmeasured interaction is neither good nor bad news. It is missing data.'
    },
    'deep-failed-priority': {
      headline: ['{metric} is priority #1,', 'but its deep analysis', 'failed.'],
      headlineEm: 2,
      subhead: 'Real-user prioritization identified {metric} ({value}) as the main problem, but the deep lab test of this metric did not complete. The field verdict remains unchanged and no lab value is estimated.',
      body: [
        'Likely cause: {failureCause}',
        '{failureAvailable} The remaining measured layers cover side topics only — none of these findings explains why {metric} is at {value}.',
        'Next step: {failureAction}'
      ],
      pullquote: 'A report without an analysis of the main problem is not a finished report. It is an incomplete one.'
    },
    'gtm-dominant': {
      headline: ['The problem', 'isn\'t the site.', 'It\'s', 'ads and tracking.'],
      headlineEm: 3,
      subhead: 'In one seed Lighthouse run, out of {tbt} ms of blocking time, {trackingMs} ms ({trackingShare}) belongs to third parties; this is not a breakdown of the series median. Heaviest single task: {topTaskName} ({topTaskMs} ms). {siteVerdict} Aggregated by owner, {topVendor} adds up to the most ({topVendorMs} ms across {topVendorCount} tasks).',
      body: [
        'The browser main thread is busy mostly with third-party code. The long-task ranking is led by {topTaskName} with a single {topTaskMs} ms task; aggregated by owner, {topVendor} adds up to the most ({topVendorMs} ms spread across {topVendorCount} tasks) — two different views of the same data. The rest comes from independently deployed scripts — consent management, pixels, ad systems — that pile up into long tasks and slow interactions.',
        '{siteVerdict} What makes it slow is the layer added over time — individual tracking codes, conversion pixels, ad sources. {gtmAdvice}'
      ],
      pullquote: 'A tag manager behaves like another website inside the website.'
    },
    'inp-presentation-bound': {
      headline: ['INP:', 'measured breakdown,', 'not a confirmed', 'cause.'],
      headlineEm: 3,
      subhead: 'Lab interaction breakdown ({inpMs} ms{labRangeSuffix}): input delay {inputDelayMs} ms, processing {processingMs} ms, presentation delay {presentationMs} ms. This phase is not a profile-confirmed cause.',
      body: [
        'In this interaction processing took {processingMs} ms and presentation delay {presentationMs} ms. Event Timing phases alone cannot tell whether layout, paint, DOM or other work caused this time, nor establish third-party contribution to the interaction.{processingReproNote}',
        'First capture a Performance profile of the slow interaction and identify work in its interaction window. Only then choose a change and verify it with comparable repeated runs. {loadTrackingNote}'
      ],
      pullquote: 'A long presentation-delay phase is an observation, not a confirmed culprit.'
    },
    'inp-variable-profile': {
      headline: ['INP has', 'two problem profiles:', 'rendering and', 'a busy thread.'],
      headlineEm: 3,
      subhead: 'The stored lab runs ({inpMs} ms{labRangeSuffix}) do not agree on a single cause — the dominant breakdown component flips between runs. The data therefore does not support one definitive cause; this report leads both profiles.',
      body: [
        'In the run closest to the median the breakdown is: input delay {inputDelayMs} ms, processing {processingMs} ms, presentation {presentationMs} ms. In other stored runs, however, the dominant component changes — some interactions are slowed by rendering the result, others by waiting for a free main thread (long script tasks, including third parties).',
        'The first step is therefore not one isolated optimization but profiling the slowest interactions in Chrome DevTools (Performance panel): only the profile decides whether rendering dominates (→ simplify the DOM, CSS contain) or a busy thread does (→ break up long tasks, defer non-critical scripts). {loadTrackingNote}'
      ],
      pullquote: 'When the runs disagree on the cause, an honest report leads both. And sends the developer profiling.'
    },
    'inp-input-delay-bound': {
      headline: ['The bottleneck', 'isn\'t the handler.', 'It\'s', 'a busy thread.'],
      headlineEm: 3,
      subhead: 'Lab interaction breakdown ({inpMs} ms{labRangeSuffix}): input delay {inputDelayMs} ms, processing {processingMs} ms, presentation {presentationMs} ms. The main cause of slow response is waiting for a free main thread — the click landed on a running task.',
      body: [
        'Processing the interaction took {processingMs} ms and presentation {presentationMs} ms — the code attached to the click is not the problem. Most of the time ({inputDelayMs} ms) passes BEFORE the handler even starts: the main thread was busy with another task at the moment of interaction (long script tasks, hydration, tracking).',
        'The path to a better INP therefore leads through breaking up long tasks: split heavy scripts (yield via setTimeout/scheduler.yield), defer non-critical third parties and move heavy work off the main thread (web worker). {loadTrackingNote}'
      ],
      pullquote: 'The handler was fast. The click just waited for its turn.'
    },
    'cls-shift-sources': {
      headline: ['The layout', 'doesn\'t jump by itself.', 'It\'s pushed by', '{clsTopCause}.'],
      headlineEm: 3,
      subhead: '{clsValueLine} The measurement captured concrete elements while shifting — the biggest contributor is {clsTopCause}: {clsTopSelector} with a {clsTopShift} shift.',
      body: [
        'Layout shifts were collected over the whole page lifetime including scroll, and for every shift the measurement recorded the concrete element and its sources. The "What exactly shifts the layout" section below lists each finding with a cause, an owner and a fix.',
        'The general principle of the fixes: anything that arrives late (ads, images, embeds, fonts) must have its space reserved up front — min-height on ad slots, width/height or aspect-ratio on images and embeds. Content must never be inserted above already-rendered text.'
      ],
      pullquote: 'Every shift has a concrete culprit. This report names it.'
    },
    'cls-shift-container-only': {
      headline: ['The layout', 'shifts.', 'This run could not', 'pin down the culprit.'],
      headlineEm: 3,
      subhead: '{clsValueLine} The lab run captured layout shifts, but only as pushed containers ({clsTopSelector}, {clsTopShift} shift) — that is the victim of the shift, not its source. What exactly pushed the container (an ad, an image, a font, injected content) could not be determined in this run.',
      body: [
        'A pushed container is evidence that something above or inside it arrived late and displaced the content — but it is not the culprit itself. Typical real sources: an ad slot without a reserved height, an image without dimensions, a late-loading font or a dynamically injected banner.',
        'The general principle of the fixes applies even without a named culprit: anything that arrives late must have its space reserved up front — min-height on ad slots, width/height or aspect-ratio on images and embeds. Content must never be inserted above already-rendered text.'
      ],
      pullquote: 'A pushed container is not the culprit. It is proof something pushed it.'
    },
    'priority-cls': {
      headline: ['The main problem', 'isn\'t responsiveness.', 'It\'s', 'layout stability.'],
      headlineEm: 3,
      subhead: 'Prioritization based on real-user data puts CLS at #1: {priorityP75} on {priorityDevice} (good threshold {priorityThreshold}). The page shifts under users\' hands — and that matters more right now than anything else in this report.',
      body: [
        'CLS (Cumulative Layout Shift) measures how much content jumps during the page\'s lifetime: ad slots without reserved space, images without dimensions, late-loading fonts and injected banners shift text exactly when the user is reading or clicking it.',
        'First steps: reserve space for ad slots (min-height), add width/height or aspect-ratio to images and embeds, and never insert content above already-rendered text. The detailed responsiveness (INP) diagnostics in this report remain valid, but per the prioritization they are not what to fix first.'
      ],
      pullquote: 'What hurts users most right now is the jumping layout, not slow response.'
    },
    'priority-lcp': {
      headline: ['The main problem', 'isn\'t responsiveness.', 'It\'s', 'loading.'],
      headlineEm: 3,
      subhead: 'Prioritization based on real-user data puts {priorityMetric} at #1: {priorityP75} on {priorityDevice} (good threshold {priorityThreshold}). Users wait for the main content to appear — and that matters more right now than anything else in this report.',
      body: [
        'Slow rendering of the main content typically has three sources: a slow server response, render-blocking resources in the head (CSS, fonts, synchronous scripts) and a heavy or late-discovered LCP element.',
        'First steps: check the LCP element\'s size and priority (fetchpriority="high", preload), unblock render-blocking resources and review the server response. The detailed responsiveness (INP) diagnostics in this report remain valid, but per the prioritization they are not what to fix first.'
      ],
      pullquote: 'What hurts users most right now is waiting for content, not slow response.'
    },
    'priority-ttfb': {
      headline: ['The main problem', 'isn\'t responsiveness.', 'It\'s', 'the server.'],
      headlineEm: 3,
      subhead: 'Prioritization based on real-user data puts TTFB at #1: {priorityP75} on {priorityDevice} (good threshold {priorityThreshold}). Until the browser gets the first byte, the user just waits — and that matters more right now than anything else in this report.',
      body: [
        'A slow first byte delays everything else: no frontend optimization can recover the time the page spends waiting for the server. Typical causes: missing caching, slow database queries, distant or undersized hosting, redirect chains.',
        'First steps: enable/verify response caching (CDN, full-page cache), measure backend page-generation time and remove unnecessary redirects. The detailed responsiveness (INP) diagnostics in this report remain valid, but per the prioritization they are not what to fix first.'
      ],
      pullquote: 'What hurts users most right now is waiting for the server, not slow response.'
    },
    'social-tracker-heavy': {
      headline: ['Social pixels', 'add up', 'to seconds.'],
      headlineEm: 1,
      subhead: 'Third parties from social networks ({topVendor}) block the main thread {topVendorMs} ms. Together social tracking accounts for {trackingShare} of the delay.',
      body: [
        'In the seed Lighthouse run, this group accounts for {trackingMs} ms of blocking time out of {tbt} ms. This attribution belongs to that single run, not the median across repeated measurements.',
        'Pixels can usually be deferred via server-side conversions APIs or loaded after the first interaction event. This reduces TBT without losing conversion tracking.'
      ],
      pullquote: 'A pixel is just a script. With a nicer name.'
    },
    'ads-heavy': {
      headline: ['Ad networks', 'are paid', 'in performance.'],
      headlineEm: 1,
      subhead: 'Ad scripts ({topVendor} and others) block the main thread {topVendorMs} ms. {siteVerdict}',
      body: [
        'The audit shows the page\'s clean metrics (HTML, CSS, own JS) would meet the target. What keeps them above it are ad pixels and auction scripts: {trackingMs} ms ({trackingShare}) of main thread.',
        'Server-side bidding or lazy-loading below-the-fold ads can free up {trackingMs} ms instantly. If ads finance the operation, it\'s worth calculating their cost in conversions.'
      ],
      pullquote: 'Ads pay the bills. Sometimes also Core Web Vitals.'
    },
    'tracking-heavy': {
      headline: ['Tracking', 'weighs on', 'speed.'],
      headlineEm: 1,
      subhead: 'Third-party tracking scripts take {trackingShare} of blocking time. {siteVerdict}',
      body: [
        'The audit identified {trackingMs} ms of blocking time on third parties. The long-task ranking is led by {topTaskName} ({topTaskMs} ms in a single task); aggregated by owner, {topVendor} adds up to the most ({topVendorMs} ms across {topVendorCount} tasks).',
        'We recommend a tag audit — some are no longer needed and run by inertia. The second wave of optimization is moving key tracking scripts to server-side.'
      ],
      pullquote: 'Most tracking code drives no decisions. Just noise.'
    },
    'cmp-burden': {
      headline: ['Cookie banner', 'starts', 'the chain.'],
      headlineEm: 1,
      subhead: 'The measurement caught {trackingMs} ms of blocking time from trackers and a real user will see more — the test did not click "Accept all".',
      body: [
        'The CMP ({topVendor}) is the first script that triggers all marketing tracking. After consent, more pixels and remarketing codes pile onto the main thread.',
        'Optimization here means two things: simplify the consent flow so the banner itself is not expensive, and lazy-load or server-side the trackers it triggers.'
      ],
      pullquote: 'The banner asks for consent. The scripts under it never brake.'
    },
    'cmp-untested': {
      headline: ['Test ran', 'without', 'the banner.'],
      headlineEm: 2,
      subhead: 'The measurement was made without clicking the cookie banner. Real-world impact will be higher than this report shows.',
      body: [
        'Most deployed sites have a slower real-world start than synthetic tests, because real users go through the consent flow first. This analysis shows the baseline before consent.',
        'For a complete picture we recommend a second measurement with marketing cookies allowed and comparing metrics. The difference shows what the cookie consent actually costs.'
      ],
      pullquote: 'A test without the banner shows the ceiling, not reality.'
    },
    'slow-server': {
      headline: ['Server', 'responds', 'slowly.'],
      headlineEm: 1,
      subhead: 'TTFB {ttfbMs} ms — the server takes too long to return the first byte. Everything else just waits on it.',
      body: [
        `Client-side optimizations have nothing to build on until the server returns HTML. TTFB above ${metricThresholds.ttfb.good} ms signals the need for a server-side analysis: slow database queries, missing cache, or geographic distance.`,
        'The first step is a server-timing breakdown and database profiling. A CDN with edge cache or moving to a closer region usually delivers immediate improvement.'
      ],
      pullquote: 'The frontend can\'t be faster than the server underneath.'
    },
    'large-page': {
      headline: ['The site', 'weighs', 'too much.'],
      headlineEm: 1,
      subhead: 'The page is {pageMb} MB on a budget of {budgetMb} MB. Loading on mobile is slow simply because there is a lot to load.',
      body: [
        'The performance budget (ControlVitals\' internal recommended benchmark, not a Google limit) is exceeded in several categories. Images and JavaScript usually take the biggest share. The audit identified the specific list — see "Performance budget".',
        'Reducing weight goes in two steps: switching to modern formats (WebP/AVIF for images, brotli for text) and code splitting JS so the initial bundle loads in smaller chunks.'
      ],
      pullquote: 'A mobile user on 4G pays for every megabyte in seconds.'
    },
    'unused-js': {
      headline: ['The page', 'downloads', 'code it never runs.'],
      headlineEm: 2,
      subhead: 'Up to {unusedJsKb} KB of JavaScript downloads but does not execute during the cold load (upper bound per Lighthouse — some code runs only after interactions). The browser still has to parse and compile it.',
      body: [
        'Libraries and frameworks are often added in full size even when only a fraction is used. Code splitting (dynamic import) and tree shaking can remove most of this overhead.',
        'A second source is the shared bundle for the whole site where each page downloads code belonging to other routes. Per-route splitting solves this without changing application logic.'
      ],
      pullquote: 'Unused code is the most expensive — the browser works for nothing.'
    },
    'font-blocking': {
      headline: ['Fonts', 'block', 'the first paint.'],
      headlineEm: 1,
      subhead: 'The page loads {fontsCount} custom fonts and some have no font-display: swap. Text waits until the font finishes loading.',
      body: [
        'Custom fonts without `font-display: swap` cause FOIT (Flash of Invisible Text) — text is invisible until the font loads. This directly raises LCP.',
        'Three fixes: 1) add `font-display: swap` (or `optional`) to @font-face, 2) preload critical fonts via <link rel="preload">, 3) consider a system font fallback. Usually under an hour of work.'
      ],
      pullquote: 'A nice font you see after a second is not a nice font.'
    },
    'mainthread-overload': {
      headline: ['Main thread', 'works', 'too long.'],
      headlineEm: 1,
      subhead: 'The browser spends {mainThreadS} actively working during page load. That is beyond a typical site and slows everything that follows.',
      body: [
        'The largest contributor is usually JavaScript — its parse/compile, or long synchronous operations inside scripts. The "Main Thread Work" section shows the breakdown by category.',
        'Tasks longer than 50 ms are listed in "Long Tasks" with their origin. The most effective optimization is splitting them via `scheduler.yield()` or moving heavy work to a Web Worker.'
      ],
      pullquote: 'The main thread is single-threaded. While it works, the user waits.'
    },
    'balanced': {
      headline: ['Site', 'has', 'many small brakes.'],
      headlineEm: 1,
      subhead: 'No single cause dominates, but the sum of smaller problems keeps {metric} above the recommended threshold.',
      body: [
        'The audit found no single big culprit. Instead it is a combination of several factors: third parties, page size, main thread. None of them is critical alone, but together they form a measurable problem.',
        'We recommend following the recommendations section — items are ordered by impact-to-effort ratio. Each of the first three items should bring a measurable improvement, none requires days of work.'
      ],
      pullquote: 'When no single cause dominates, a few small changes do the trick.'
    },
    'tracking-within-limits': {
      headline: ['Third-party load', 'is', 'within limits.'],
      headlineEm: 2,
      subhead: 'Blocking time in one seed Lighthouse run is within limits ({tbt} ms). Most of this run’s load sits with third parties ({trackingShare}); this is not a breakdown of the series median or an INP measurement.',
      body: [
        'Most of the main-thread blocking time comes from third-party scripts ({trackingMs} ms, largest source {topVendor}). Total blocking time however stays below the 300 ms threshold, so the real impact on page responsiveness is small.',
        'No immediate action is needed. We recommend monitoring the third-party share — every newly added tag adds to this layer and the headroom is not large.'
      ],
      pullquote: 'Tracking shows up in the numbers, but it is not yet slowing users down.'
    }
  }
};

const renderTemplate = (tpl, ctx) => {
  if (!tpl) return '';
  return String(tpl).replace(/\{(\w+)\}/g, (_, key) => {
    const v = ctx[key];
    return v == null ? '' : String(v);
  });
};

const buildScenarioContext = (r) => {
  const rich = r.rich;
  const tbt = rich.longTasks?.totalBlockingTime || 0;
  const trackingMs = rich.longTasks?.adsTrackingMs || 0;
  const trackingShare = rich.longTasks?.adsTrackingShare || 0;
  const top = rich.longTasks?.vendorBreakdown?.[0];
  const topTask = rich.longTasks?.items?.[0] || null;
  const firstPartyMs = (rich.longTasks?.vendorBreakdown || [])
    .filter(v => v.category === 'first-party')
    .reduce((s, v) => s + (v.totalBlockingMs || 0), 0);
  const cs = r.lang !== 'en';
  // Server-side GTM already deployed? Recommending "migrate to sGTM" on a
  // site that already runs it is instantly dismissed by the client's devs.
  // Detection + wording come from the shared gtmAdvice module (single source
  // for dashboard AND export).
  const sgtmDetected = r.sgtmDetected != null ? r.sgtmDetected : detectSgtm(r.diagnostics);
  const gtmAdvice = gtmAdviceSentence(sgtmDetected, cs ? 'cs' : 'en');
  const wi = r.deepINPResults?.worstInteraction || null;
  const topTaskIsFirstParty = topTask?.vendorCategory === 'first-party';
  const topTaskMsRaw = topTask ? Math.round(topTask.blockingTime || topTask.duration || 0) : 0;
  // "The site itself is fine" is only allowed when first-party code is
  // neither the heaviest single blocker nor a significant combined one.
  const siteVerdict = topTaskIsFirstParty
    ? (cs
      ? `Nejtěžší úloha (${topTask?.vendor || topTask?.vendorHost || topTask?.name || 'vlastní kód'}, ${fmtNum(topTaskMsRaw)} ms) přitom spadá pod vlastní správu webu — tuto část máte přímo ve svých rukou.`
      : `The heaviest task (${topTask?.vendor || topTask?.vendorHost || topTask?.name || 'own code'}, ${fmtNum(topTaskMsRaw)} ms) falls under the site's own control — that part is fully in your hands.`)
    : firstPartyMs >= 150
      ? (cs
        ? `Vlastní kód webu se ale na blokování podílí ${fmtNum(Math.round(firstPartyMs))} ms — tuto část máte přímo ve svých rukou.`
        : `The site's own code however contributes ${fmtNum(Math.round(firstPartyMs))} ms of blocking — that part is fully in your hands.`)
      : (cs ? 'Samotná stránka je v pořádku.' : 'The site itself is fine.');
  return {
    siteVerdict,
    gtmAdvice,
    loadTrackingNote: trackingShare > 0
      ? (cs
        ? `Zátěž třetích stran (${Math.round(trackingShare * 100)} % blokujícího času při načtení) popisuje fázi načtení stránky — o podílu třetích stran na konkrétních interakcích ani na field INP z ní nelze usuzovat; to ukazuje jen atribuce interakčního okna.`
        : `Third-party load (${Math.round(trackingShare * 100)} % of load-time blocking) describes the page-load phase — it does not tell how much third parties contribute to specific interactions or to field INP; only the interaction-window attribution shows that.`)
      : (cs
        ? 'Při načtení nebyl třetím stranám přiřazen žádný blokující čas — o jejich vlivu na konkrétní interakce to samo o sobě nevypovídá.'
        : 'No load-time blocking was attributed to third parties — by itself that says nothing about their effect on specific interactions.'),
    // "Processing was negligible" upgrades from a one-run finding to a
    // reproduced one only when independent compare runs confirm it.
    processingReproNote: (() => {
      const runs = (r.inpCompareResults?.summary || [])
        .filter(s => (s.valid != null ? !!s.valid : !!s.success) && s.processingMs != null);
      const deepPt = wi ? Math.round(wi.processingTime || 0) : null;
      const vals = runs.map(s => Math.round(s.processingMs));
      const allVals = deepPt != null ? [deepPt, ...vals] : vals;
      if (allVals.length >= 2 && allVals.every(v => v <= 16)) {
        return cs
          ? ` Zanedbatelné zpracování není jednorázový výsledek — opakovaně se potvrdilo napříč ${allVals.length} nezávislými běhy (${allVals.join(', ')} ms).`
          : ` Negligible processing is not a one-off result — it reproduced across ${allVals.length} independent runs (${allVals.join(', ')} ms).`;
      }
      return '';
    })(),
    // Headline INP number must be the SAME basis as the breakdown next to it:
    // the median across stored runs when a spread exists, never the one deep
    // run that happens to be attached (which may be the collision-tail worst).
    inpMs: (() => {
      const sp = r.deepINPResults?.labRunSpread;
      if (sp && labSeriesDisplayValue(sp) != null) return fmtNum(Math.round(labSeriesDisplayValue(sp)));
      return r.deepINPResults?.inp != null ? fmtNum(Math.round(r.deepINPResults.inp)) : null;
    })(),
    // Spread across ACTUALLY STORED deep runs (from the DB, never synthesized).
    // When component medians exist, the breakdown shown IS the median across
    // runs; otherwise it is one specific run and must be labeled as such.
    labRangeSuffix: (() => {
      const sp = r.deepINPResults?.labRunSpread;
      const bd = getLabBreakdown(r);
      if (!sp || sp.runs <= 1) return cs ? ', jeden běh, orientační' : ', single run, indicative';
      if (!labSeriesIsVerified(sp)) {
        return cs
          ? `; medián ${formatMetricValue('inp', labSeriesDisplayValue(sp)) || '—'} · n=${sp.runs} · jistota diagnostiky omezená`
          : `; median ${formatMetricValue('inp', labSeriesDisplayValue(sp)) || '—'} · n=${sp.runs} · limited diagnostic confidence`;
      }
      if (sp.min === sp.max) return cs
        ? `; ${sp.runs} shodných validních běhů`
        : `; ${sp.runs} matching valid runs`;
      if (bd?.basis === 'median') {
        // Component medians may come from a complete-data subset of the
        // stored runs — say the actual count, not "all runs".
        const compRuns = sp.components?.runs || sp.runs;
        return cs
          ? `; rozptyl všech ${sp.runs} uložených běhů ${fmtNum(sp.min)}–${fmtNum(sp.max)} ms, medián ~${fmtNum(sp.median)} ms — rozpad níže je z běhu nejblíže mediánu (z ${compRuns} ${compRuns >= 5 ? 'běhů' : 'běhů'} s úplným rozpadem), ne z nejhoršího běhu`
          : `; spread across all ${sp.runs} stored runs ${fmtNum(sp.min)}–${fmtNum(sp.max)} ms, median ~${fmtNum(sp.median)} ms — the breakdown below is from the run closest to the median (of ${compRuns} runs with a complete breakdown), not the worst run`;
      }
      return cs
        ? `; rozptyl všech ${sp.runs} uložených běhů ${fmtNum(sp.min)}–${fmtNum(sp.max)} ms, medián ~${fmtNum(sp.median)} ms — tento rozpad je z jednoho konkrétního běhu, ne typická hodnota`
        : `; spread across all ${sp.runs} stored runs ${fmtNum(sp.min)}–${fmtNum(sp.max)} ms, median ~${fmtNum(sp.median)} ms — this breakdown is one specific run, not a typical value`;
    })(),
    // Cause-branch scenarios (presentation/input-delay bound) are chosen from
    // the median breakdown — the numbers in the copy must be the SAME basis.
    inputDelayMs: (() => { const bd = getLabBreakdown(r); return bd ? fmtNum(bd.inputDelay) : (wi ? fmtNum(Math.round(wi.inputDelay || 0)) : null); })(),
    processingMs: (() => { const bd = getLabBreakdown(r); return bd ? fmtNum(bd.processingTime) : (wi ? fmtNum(Math.round(wi.processingTime || 0)) : null); })(),
    presentationMs: (() => { const bd = getLabBreakdown(r); return bd ? fmtNum(bd.presentationDelay) : (wi ? fmtNum(Math.round(wi.presentationTime || wi.presentationDelay || 0)) : null); })(),
    // Tokens for the CLS shift-sources headline (top measured shifter).
    ...(() => {
      if (r.metricLower !== 'cls') return {};
      // The headline names a CULPRIT — an affected container ("pushed by
      // body-wrapper") would blame the victim, so prefer the top specific one.
      const findings = buildClsFindings(r);
      const f = findings.find(x => !x.isContainer) || findings[0];
      if (!f) return {};
      return {
        clsValueLine: clsFieldFirstLine(r),
        clsTopCause: f.causeLabel.toLowerCase(),
        clsTopSelector: f.displayName || f.selector || f.name,
        clsTopShift: fmtShift(f.shift).replace('.', cs ? ',' : '.')
      };
    })(),
    // Tokens for priority-driven headlines (metric #1 from the ranking).
    priorityMetric: r.priorityTop ? r.priorityTop.metric.toUpperCase() : null,
    priorityDevice: r.priorityTop ? deviceLabel(r.priorityTop.device, r.lang) : null,
    priorityP75: r.priorityTop
      ? (r.priorityTop.metric === 'cls'
        ? Number(r.priorityTop.p75).toFixed(3).replace('.', cs ? ',' : '.')
        : `${fmtNum(Math.round(r.priorityTop.p75))} ms`)
      : null,
    priorityThreshold: r.priorityTop
      ? (r.priorityTop.metric === 'cls'
        ? Number(r.priorityTop.threshold).toFixed(2).replace('.', cs ? ',' : '.')
        : `${fmtNum(Math.round(r.priorityTop.threshold))} ms`)
      : null,
    priorityShare: r.priorityTop?.deviceShare != null ? `${fmtNum(r.priorityTop.deviceShare, 1)} %` : null,
    topVendorCount: top?.count || 0,
    topTaskName: topTask ? (topTask.vendor || topTask.vendorHost || topTask.name || '—') : '—',
    topTaskMs: topTask ? fmtNum(topTask.blockingTime || topTask.duration || 0) : null,
    domain: r.domain,
    metric: r.metricUpper,
    value: r.formattedMetricValue,
    failureCause: r.deepFailurePresentation?.cause || (cs
      ? 'Hloubkový test skončil technickou chybou bez spolehlivé klasifikace.'
      : 'The deep test ended with a technical error without a reliable classification.'),
    failureAvailable: r.deepFailurePresentation?.available || (cs
      ? 'Field p75 zůstává autoritativní; laboratorní hodnota není k dispozici.'
      : 'Field p75 remains authoritative; no lab value is available.'),
    failureAction: r.deepFailurePresentation?.action || (cs
      ? 'Spusťte komplexní audit znovu.'
      : 'Run the complete audit again.'),
    tbt: tbt ? fmtNum(tbt) : null,
    trackingMs: trackingMs ? fmtNum(trackingMs) : null,
    trackingShare: trackingShare ? `${Math.round(trackingShare * 100)} %` : null,
    topVendor: top?.vendor || '—',
    topVendorMs: top ? fmtNum(top.totalBlockingMs) : null,
    // A TTFB-led report's headline must quote the TTFB that IS the problem —
    // the field p75 (real users). The unthrottled lab Server-Timing value can
    // be fast (e.g. 125 ms) while real users wait 1+ s; quoting it under a
    // "server is slow" headline contradicts the claim. Non-TTFB reports keep
    // the lab Server-Timing value (their slow-server scenario is triggered by
    // a measured slow lab response).
    ttfbMs: (r.metricLower === 'ttfb' && Number.isFinite(Number(r.fieldValue)))
      ? fmtNum(Math.round(Number(r.fieldValue)))
      : (r.metricLower === 'ttfb' && Number.isFinite(Number(r.metricValue)))
        ? fmtNum(Math.round(Number(r.metricValue)))
        : (rich.serverTiming?.ttfb != null ? fmtNum(rich.serverTiming.ttfb) : null),
    pageMb: rich.performanceBudget?.totalActualBytes
      ? (rich.performanceBudget.totalActualBytes / 1024 / 1024).toFixed(2).replace('.', ',')
      : null,
    budgetMb: rich.performanceBudget?.totalBudgetBytes
      ? (rich.performanceBudget.totalBudgetBytes / 1024 / 1024).toFixed(2).replace('.', ',')
      : null,
    unusedJsKb: rich.performanceBudget?.unusedJsBytes
      ? Math.round(rich.performanceBudget.unusedJsBytes / 1024)
      : null,
    mainThreadS: rich.mainThreadWork
      ? `${(rich.mainThreadWork.totalMs / 1000).toFixed(2).replace('.', ',')} s`
      : null,
    fontsCount: rich.fontLoading?.totalFonts || 0
  };
};

// Cross-run INP breakdown variability sentence. On some sites the bottleneck
// flips between runs (one run presentation-bound, another processing-bound);
// the headline stays tied to the median run, but the report must admit the
// second profile exists instead of hiding it behind one cause.
const INP_COMPONENT_LABELS = {
  cs: {
    input: 'čekání na volné hlavní vlákno (input delay)',
    processing: 'zpracování skriptů (processing)',
    presentation: 'vykreslování (presentation delay)'
  },
  en: {
    input: 'waiting for a free main thread (input delay)',
    processing: 'script processing',
    presentation: 'rendering (presentation delay)'
  }
};

const inpVariabilityNote = (r) => {
  if (r.metricLower !== 'inp') return null;
  const bd = getLabBreakdown(r);
  const d = bd?.basis === 'median' ? bd.dominance : null;
  if (!d || !d.variable || !d.secondary) return null;
  const cs = r.lang !== 'en';
  const L = INP_COMPONENT_LABELS[cs ? 'cs' : 'en'];
  const primary = L[d.primary] || d.primary;
  const secondary = L[d.secondary] || d.secondary;
  return cs
    ? `INP je variabilní — dominantní složka rozpadu se mezi uloženými běhy liší: v části interakcí zdržuje ${primary}, v jiné části ${secondary}. Hlavní příčina výše vychází z běhu nejblíže mediánu, druhý profil se ale objevuje také. Pro definitivní určení profilujte konkrétní pomalé interakce v Chrome DevTools (panel Performance).`
    : `INP is variable — the dominant breakdown component differs between stored runs: some interactions are slowed by ${primary}, others by ${secondary}. The main cause above comes from the run closest to the median, but the second profile also occurs. For a definitive answer, profile the specific slow interactions in Chrome DevTools (Performance panel).`;
};

// Scenarios where NO single INP cause is being led — the variability
// admission belongs everywhere else on an INP report (incl. the
// processing-dominant script/vendor scenarios like gtm-dominant,
// social-tracker-heavy or cmp-burden). Exclusion list, not allowlist:
// a new cause scenario is covered by default, a new hand-off must opt out.
const INP_NO_CAUSE_SCENARIOS = new Set([
  'inp-not-measured', 'deep-failed-priority', 'fast-and-clean',
  'priority-cls', 'priority-lcp', 'priority-ttfb',
  // The variable-profile scenario IS the variability admission — appending
  // the note again would duplicate it.
  'inp-variable-profile'
]);

const normalizeReportMeasurementProvenance = (input) => {
  const source = input && typeof input === 'object' ? input : {};
  const collector = source.collector && typeof source.collector === 'object'
    ? source.collector
    : {};
  const verification = source.verification && typeof source.verification === 'object'
    ? source.verification
    : {};
  const status = String(verification.status || '').toLowerCase() === 'verified'
    ? 'verified'
    : 'unverified';
  return {
    collector: {
      id: String(collector.id || 'legacy-unknown'),
      provider: String(collector.provider || 'unknown'),
      region: String(collector.region || 'unknown').toLowerCase(),
      country: collector.country ? String(collector.country).toUpperCase() : null,
      zone: collector.zone ? String(collector.zone) : null
    },
    verification: {
      status,
      method: String(verification.method || 'missing-or-unverified')
    }
  };
};

const getScenarioCopy = (scenario, r) => {
  const lang = r.lang;
  if (['inp-variable-profile', 'inp-evidence-limited'].includes(scenario)) {
    const finding = deriveCanonicalFinding({ ...r, metric: 'INP', language: lang });
    const sentences = canonicalFindingSentences(finding);
    const cs = lang === 'cs';
    const profileStatus = r.deepINPResults?.interactionDiagnostics?.profile?.status
      ?? r.deepINPResults?.profile?.status;
    return {
      key: scenario,
      headline: profileStatus === 'completed'
        ? (cs ? ['INP:', 'měřený profil,', 'ne potvrzená příčina.'] : ['INP:', 'measured profile,', 'not a confirmed cause.'])
        : (cs ? ['INP:', 'omezené důkazy,', 'ne potvrzená příčina.'] : ['INP:', 'limited evidence,', 'not a confirmed cause.']),
      headlineEm: 2,
      subhead: sentences[0],
      body: sentences.slice(1),
      pullquote: cs ? 'Síla závěru nesmí překročit sílu důkazů.' : 'Claim strength must not exceed evidence strength.'
    };
  }
  const all = SCENARIO_COPY[lang] || SCENARIO_COPY.cs;
  const tpl = all[scenario] || all.balanced;
  const ctx = buildScenarioContext(r);
  return {
    key: scenario,
    // Headline words may carry tokens too (e.g. the CLS top-cause headline).
    headline: tpl.headline.map(w => renderTemplate(w, ctx)),
    headlineEm: tpl.headlineEm,
    subhead: renderTemplate(tpl.subhead, ctx),
    body: (() => {
      const body = tpl.body.map(p => renderTemplate(p, ctx));
      // Cross-run variability admission: appended as its own paragraph to
      // every scenario that leads an INP cause (incl. processing-dominant
      // vendor scenarios) — never to "not measured"/failed hand-offs.
      const note = INP_NO_CAUSE_SCENARIOS.has(scenario) ? null : inpVariabilityNote(r);
      return note ? [...body, note] : body;
    })(),
    pullquote: tpl.pullquote
  };
};

// A sweep run is a launched browser call. The ledger also records planned
// attempts skipped when the time budget ran out; those are not failed calls.
// A call with an internal retry is still one ledger entry, not two sweep runs.
const sweepRunCounts = page => {
  const ledger = Array.isArray(page?.runAttempts) ? page.runAttempts : null;
  if (!ledger?.length) return {
    calls: Number(page?.runs) || null,
    valid: Number(page?.validRuns) || null,
    invalid: Array.isArray(page?.invalidRuns) ? page.invalidRuns.length : null,
    skipped: 0, retries: 0, ledger: false
  };
  const launched = ledger.filter(a => a.attempted === true);
  return {
    calls: launched.length,
    valid: launched.filter(a => a.measurementValid === true).length,
    invalid: launched.filter(a => a.measurementValid !== true).length,
    skipped: ledger.filter(a => a.attempted === false).length,
    retries: launched.filter(a => a.retried === true).length,
    ledger: true
  };
};

// =========================================================================
// REPORT DATA — central object passed to both templates
// =========================================================================
const buildReportData = (data) => {
  const {
    url, metric, value, fieldValue = null, diagnostics,
    deepCLSResults: rawDeepCLSResults, multiPageResults,
    deepINPResults, inpCompareResults = null, multiPageINPResults,
    multiPageLCPResults, deepLCPResults,
    deepTTFBResults = null, multiPageTTFBResults = null,
    tasks = [], language = 'cs', device,
    savingsRecommendations = [], savingsSummary = null,
    aiNarrative = null, devicePriority = null, fieldDataUnavailable = false,
    auditDate = null, failedDeepMetrics = [], deepFailures = {},
    deepDeviceRuns = [], sweepDeepRuns = [],
    sweepDeepSkipReason = null,
    deepAnalyzedUrl = null, entryDeep = null,
    lcpFieldEvidence = null,
    measurementProvenance = null
  } = data;

  const lang = language === 'en' ? 'en' : 'cs';
  const metricUpper = (metric || '').toUpperCase();
  const metricLower = (metric || '').toLowerCase();
  const validDeepInp = hasValidInpMeasurement(deepINPResults);

  // Normalize CLS attribution ONCE for every consumer below (culprit ranking,
  // findings, handover) — no surface may see a pushed container as a culprit.
  const deepCLSResults = (rawDeepCLSResults && Array.isArray(rawDeepCLSResults.elements) && rawDeepCLSResults.elements.length)
    ? { ...rawDeepCLSResults, elements: mergeClsContainerElements(rawDeepCLSResults.elements) }
    : rawDeepCLSResults;

  // ----- Improved metric value fallback (fixes the '—' bug for RTT data) -----
  const metricValue = (() => {
    // Legacy INP payloads used zero for "no interaction captured". It is not
    // a perfect response and must remain unavailable even when loaded from a
    // stored report. (Zero remains valid for unrelated CLS.)
    if (value != null && !isNaN(value) && (metricLower !== 'inp' || Number(value) > 0)) return value;
    if (metricLower === 'lcp' && deepLCPResults?.lcp != null) return deepLCPResults.lcp;
    if (metricLower === 'cls' && deepCLSResults?.cls != null) return deepCLSResults.cls;
    if (metricLower === 'inp' && validDeepInp) return deepINPResults.inp;
    if (metricLower === 'ttfb' && deepTTFBResults?.ttfb != null) return deepTTFBResults.ttfb;
    if (diagnostics?.[metricLower]?.value != null
        && (metricLower !== 'inp' || Number(diagnostics[metricLower].value) > 0)) return diagnostics[metricLower].value;
    if (diagnostics?.[metricUpper]?.value != null
        && (metricLower !== 'inp' || Number(diagnostics[metricUpper].value) > 0)) return diagnostics[metricUpper].value;
    if (diagnostics?.[metricLower]?.p75 != null
        && (metricLower !== 'inp' || Number(diagnostics[metricLower].p75) > 0)) return diagnostics[metricLower].p75;
    if (data?.metrics?.[metricUpper]?.p75 != null
        && (metricLower !== 'inp' || Number(data.metrics[metricUpper].p75) > 0)) return data.metrics[metricUpper].p75;
    return null;
  })();

  const status = statusOf(metric, metricValue);
  const primaryHost = getHostname(url);

  // ----- CMP detection from any of the three RTT runs (LCP / CLS / INP) -----
  // skipCmp=true means the lab test auto-clicked "Accept all" on the cookie
  // banner (consent granted, post-consent trackers measured). The "reality
  // will be worse" warning applies to the OPPOSITE case: a deep test that ran
  // without consent, so the post-consent tracker layer was not measured.
  const skipCmp = !!(deepLCPResults?.skipCmp ?? deepCLSResults?.skipCmp ?? deepINPResults?.skipCmp ?? diagnostics?.skipCmp);
  // Newer deep runs carry an explicit cmpMode ('accept' | 'reject' | 'none')
  // instead of the legacy skipCmp boolean. A run that clicked "Accept all"
  // (cmpMode 'accept') measured the post-consent tracker layer — it must be
  // labeled "consent given", not "banner untouched".
  const cmpAccepted = skipCmp || [deepLCPResults, deepCLSResults, deepINPResults]
    .some(o => o?.cmpMode === 'accept' && o?.cmpClicked !== false);
  // A deep run counts only when it produced a concrete metric value (or is
  // explicitly marked successful) — a failed/empty deep object must not
  // trigger CMP labels or the "reality will be worse" warning.
  const deepRan = deepLCPResults?.lcp != null || deepCLSResults?.cls != null || deepINPResults?.inp != null
    || deepLCPResults?.success === true || deepCLSResults?.success === true || deepINPResults?.success === true;
  const cmpUntested = deepRan && !cmpAccepted;

  // ----- Rich diagnostics extraction -----
  const rich = extractRichDiagnostics(diagnostics, primaryHost, lang, cmpUntested);
  if (!rich.tbtRunSpread && data.labRunEvidence?.TBT) rich.tbtRunSpread = data.labRunEvidence.TBT;

  // ----- Build culprits (vendor-attributed when long tasks available) -----
  const culpritData = buildCulprits(rich, deepLCPResults, deepCLSResults, deepINPResults, lang, status === 'good');

  // ----- Multi-page summary -----
  // Two shapes exist: legacy { results: [{ value }] } and the multi-page
  // INP/CLS endpoints' { pages: [{ inp | cls | lcp }], summary: {...} }.
  // Prefer the dataset that matches the audited metric so an LCP crawl
  // never drives an INP headline.
  let multiPage = null;
  const mpByMetric = metricLower === 'inp' ? multiPageINPResults
    : metricLower === 'lcp' ? multiPageLCPResults
    : metricLower === 'cls' ? multiPageResults
    : metricLower === 'ttfb' ? multiPageTTFBResults
    : null;
  // Strictly same-metric: an LCP crawl must never render (or cap) an INP
  // report — thresholds and units differ per metric.
  const mp = mpByMetric;
  const mpValueOf = (p) => {
    const v = p?.value ?? p?.inp ?? p?.lcp ?? p?.cls ?? p?.ttfb;
    return (v == null || isNaN(v)) ? null : Number(v);
  };
  const mpPageIsValid = (p) => {
    const value = mpValueOf(p);
    if (p?.error || p?.measurementValid === false || p?.valid === false
        || p?.invalid === true || p?.status === 'unavailable') return false;
    // INP zero is the historic "no interaction" sentinel. New sweep rows also
    // carry explicit evidence flags; reject any explicit attribution/target
    // failure while retaining positive legacy rows that predate those fields.
    if (metricLower === 'inp') {
      if (!(value > 0) || p?.noValidInteraction === true || p?.interactionMeasured === false
          || p?.attributionValid === false || p?.targetsFound === 0) return false;
    }
    return value != null;
  };
  // multi-page-ttfb reports its pages under `pageResults`.
  const mpRawPages = mp?.results?.length ? mp.results : (mp?.pages?.length ? mp.pages : (mp?.pageResults?.length ? mp.pageResults : null));
  if (mpRawPages) {
    const validPages = mpRawPages.filter(mpPageIsValid);
    const unmeasuredPages = mpRawPages.length - validPages.length;
    if (validPages.length) {
      const avg = validPages.reduce((s, p) => s + mpValueOf(p), 0) / validPages.length;
      // Median is the honest central value for skewed page samples — a single
      // slow page drags the average, so both are reported side by side.
      const sortedVals = validPages.map(mpValueOf).sort((a, b) => a - b);
      const mid = Math.floor(sortedVals.length / 2);
      const median = sortedVals.length % 2 ? sortedVals[mid] : (sortedVals[mid - 1] + sortedVals[mid]) / 2;
      const worstP = validPages.reduce((w, p) => mpValueOf(p) > mpValueOf(w) ? p : w, validPages[0]);
      const bestP = validPages.reduce((b, p) => mpValueOf(p) < mpValueOf(b) ? p : b, validPages[0]);
      // Wide spread (worst > 2× best, or ≥ 3× median) means the average is a
      // weak summary — the report must say so instead of implying stability.
      const bestV = mpValueOf(bestP);
      const worstV = mpValueOf(worstP);
      // Warn already at >2× spread — at 2× the average/median stops being a
      // trustworthy single number for the site (e.g. 64–320 ms range).
      const wideRange = (bestV > 0 && worstV / bestV > 2) || (median > 0 && worstV / median >= 3);
      const pageStatuses = validPages.map(p => statusOf(metric, mpValueOf(p)));
      // A "poor" page counts as confirmed only with ≥3 valid runs of that
      // page (the server re-measures the worst page 5× and reports a
      // median). Single-run poor pages stay visible but are indicative.
      const confirmedPoor = validPages.filter(p =>
        statusOf(metric, mpValueOf(p)) === 'critical' && Number(p.validRuns) >= 3).length;
      multiPage = {
        pages: validPages.length,
        attemptedPages: mpRawPages.length,
        validPages: validPages.length,
        unmeasuredPages,
        unavailable: false,
        worst: { url: worstP.url, value: worstV },
        worstConfidence: Number(worstP.validRuns) >= 3 ? 'median-confirmed'
          : Number(worstP.runs) > 1 ? 'repeated-incomplete'
          : Number(worstP.runs) === 1 ? 'single-run' : null,
        worstRuns: sweepRunCounts(worstP).calls,
        worstValidRuns: sweepRunCounts(worstP).valid,
        worstInvalidRuns: sweepRunCounts(worstP).invalid,
        worstSkippedRuns: sweepRunCounts(worstP).skipped,
        worstInternalRetries: sweepRunCounts(worstP).retries,
        worstHasLedger: sweepRunCounts(worstP).ledger,
        repeatedPages: validPages.filter(p => Number(p.runs) > 1).length,
        best: { url: bestP.url, value: bestV },
        avg,
        median,
        wideRange,
        goodCount: pageStatuses.filter(s => s === 'good').length,
        warnCount: pageStatuses.filter(s => s === 'warning').length,
        poorCount: pageStatuses.filter(s => s === 'critical').length,
        confirmedPoorCount: confirmedPoor,
        siteStatus: statusOf(metric, avg),
        matchesMetric: mp === mpByMetric
      };
    } else {
      multiPage = {
        pages: 0,
        attemptedPages: mpRawPages.length,
        validPages: 0,
        unmeasuredPages: mpRawPages.length,
        unavailable: true,
        unavailableReason: mp?.summary?.noValidMeasurements
          ? 'no-valid-interaction'
          : 'no-valid-measurements',
        goodCount: 0,
        warnCount: 0,
        poorCount: 0,
        confirmedPoorCount: 0,
        avg: null,
        median: null,
        best: null,
        worst: null,
        siteStatus: null,
        matchesMetric: mp === mpByMetric
      };
    }
  }

  // ----- Lab value for the analyzed metric (deep RTT run) -----
  // CLS: prefer the median across stored runs (labRunSpread) over the single
  // fresh run — observed run-to-run spread on the same page is 0.9–1.9, so a
  // single run is as noisy as single-run INP was. Same rule as INP.
  const clsSpread = deepCLSResults?.labRunSpread;
  const ttfbSpread = deepTTFBResults?.labRunSpread;
  const labValue = metricLower === 'cls'
    ? labSeriesDisplayValue(clsSpread, deepCLSResults?.cls ?? null)
    // INP: same median rule — verdict cards and same-page comparison must
    // share the basis of the headline/breakdown (median across stored runs),
    // never the one attached run that may be the collision-tail worst.
    : metricLower === 'inp'
      ? (validDeepInp ? labSeriesDisplayValue(deepINPResults?.labRunSpread, deepINPResults.inp) : null)
    : metricLower === 'lcp'
      ? labSeriesDisplayValue(deepLCPResults?.labRunSpread, deepLCPResults?.lcp ?? null)
    // TTFB: same median rule as CLS/INP — one run is one network moment.
    : metricLower === 'ttfb'
      ? labSeriesDisplayValue(ttfbSpread, deepTTFBResults?.ttfb ?? null)
      : null;

  // If the multi-page "worst page" is the SAME URL as the main deep test,
  // the reader sees two different values for one page in one report — that
  // difference is run-to-run variance, and the report must say so instead of
  // letting it look like a contradiction.
  // The main deep test may have run on the sweep's worst page (promoted by
  // the orchestration), not on the entry URL — compare against the URL the
  // deep diagnostics ACTUALLY ran on, and remember it so the note can name it.
  const normUrlForCompare = (u) => String(u || '').replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();
  const analyzedDeepUrl = deepAnalyzedUrl || url;
  if (multiPage && labValue != null && multiPage.worst?.url
      && normUrlForCompare(multiPage.worst.url) === normUrlForCompare(analyzedDeepUrl)) {
    multiPage.worstIsAnalyzedPage = true;
    multiPage.analyzedLabValue = labValue;
    multiPage.analyzedDeepUrl = analyzedDeepUrl;
  }

  // ----- Headline severity cap from multi-page data -----
  // If the site-wide crawl for the SAME metric shows zero "poor" pages, the
  // single-page/field headline must not scream "critical" — the product would
  // contradict its own multi-page section. Cap severity at "warning" and
  // record the cap so the report can explain it.
  let effectiveStatus = status;
  let multiPageCapApplied = false;
  if (multiPage && !multiPage.unavailable && multiPage.validPages > 0
      && multiPage.unmeasuredPages === 0
      && multiPage.matchesMetric && multiPage.poorCount === 0
      && status === 'critical' && multiPage.siteStatus !== 'critical') {
    effectiveStatus = 'warning';
    multiPageCapApplied = true;
  }
  // Symmetric escalation: at least one genuinely "poor" page site-wide must
  // never be softened into "partial headroom" by a good single-page headline.
  // The verdict reacts to 1× poor — confirmed poor pages escalate the
  // headline to "warning" and the report names the page.
  let multiPageEscalationApplied = false;
  if (multiPage && multiPage.matchesMetric && multiPage.confirmedPoorCount > 0
      && effectiveStatus === 'good') {
    effectiveStatus = 'warning';
    multiPageEscalationApplied = true;
  }

  // ----- Verdict cards (3 cards as per visual reference) -----
  // ----- Field (CrUX) matrix per metric × device from device-priority items -----
  // These are real-user p75 values (28-day window) — the most authoritative
  // numbers in the report. When present, load-side verdicts must follow them:
  // TBT / JS budget are LAB proxies and must not override green field LCP/TTFB.
  const dpItems = Array.isArray(devicePriority?.items) ? devicePriority.items : [];
  const fieldMatrix = dpItems.length ? dpItems : null;
  const rankSev = (s) => (s === 'poor' || s === 'critical') ? 2 : (s === 'needs-improvement' || s === 'warning') ? 1 : 0;
  // CLS belongs here too: the loading/stability box lists CLS cards, so a
  // poor field CLS must escalate the box verdict — green LCP/TTFB alone must
  // not mask it.
  const fieldLoadItems = dpItems.filter(i => ['lcp', 'ttfb', 'fcp', 'cls'].includes(i.metric));
  const fieldLoadStatus = fieldLoadItems.length
    ? ['good', 'warning', 'critical'][Math.max(...fieldLoadItems.map(i => rankSev(i.status)))]
    : null;

  // Measuring a device other than the #1 priority device must be said out
  // loud: the report saying "Tablet" is not enough when 94 % of users are on
  // mobile — the reader needs an explicit warning that the measurement may
  // not represent the users who matter.
  const normDev = (d) => { const s = String(d || '').toLowerCase(); return s === 'phone' ? 'mobile' : s; };
  const topPriorityDevice = dpItems.length ? normDev(dpItems[0].device) : null;
  const measuredDevice = normDev(device);
  const devicePriorityMismatch = (topPriorityDevice && measuredDevice && measuredDevice !== 'all' && measuredDevice !== topPriorityDevice)
    ? { measured: measuredDevice, priority: topPriorityDevice }
    : null;

  // sGTM detection + wording live in the shared gtmAdvice module (single
  // source for dashboard AND export — see that file's header).
  const sgtmDetected = detectSgtm(diagnostics);
  // gtag.js/destination are Google tag endpoints, not evidence of a
  // publishable GTM container. Do not assign container changes without gtm.js.
  const adjustedSavingsRecs = scopeTagRecommendations(savingsRecommendations, diagnostics)
    .map(rec => {
    if (sgtmDetected && rec?.id === 'gtm-server-side') {
      return {
        ...rec,
        title: gtmMigrationTitle(lang),
        titleCs: gtmMigrationTitle('cs'),
        titleEn: gtmMigrationTitle('en')
      };
    }
    return rec;
  });

  // The verdict card must use the SAME spread the labValue came from, or the
  // card label ("median of N runs") and the number would disagree.
  const verdictCards = buildVerdictCards(metric, metricValue, rich, lang, fieldValue, labValue, fieldLoadStatus,
    metricLower === 'inp' ? (deepINPResults?.labRunSpread || null)
      : metricLower === 'lcp' ? (deepLCPResults?.labRunSpread || null)
      : metricLower === 'ttfb' ? (deepTTFBResults?.labRunSpread || null)
      : null, fieldDataUnavailable);
  // Propagate the multi-page severity cap to the visible primary-metric
  // card(s) — otherwise the card would still render "critical" while the
  // headline says "warning".
  if (multiPageCapApplied) {
    verdictCards.forEach(c => {
      if (c.label && c.label.startsWith(metricUpper) && c.severity === 'critical') {
        c.severity = 'warning';
        // The note must be attributed to the LAB SWEEP — appended bare next
        // to a field value 2×+ over threshold it read as reassurance about
        // the field number itself (ulekare.cz finding).
        const capNote = lang === 'cs'
          ? ' · verdikt zmírněn podle laboratorního sweepu: žádná stránka webu v pásmu „špatné" (netýká se field hodnoty v této kartě)'
          : ' · verdict softened by the lab sweep: no page of the site in the "poor" band (does not refer to the field value on this card)';
        c.target = (c.target || '') + capNote;
      }
    });
  }

  const r = {
    lang,
    fieldDevice: device,
    url,
    domain: getDomain(url),
    device: deviceLabel(device, lang),
    metric,
    metricUpper,
    metricLower,
    metricValue,
    // Raw field p75 for the audited metric — the CLS narrative needs it to
    // stay field-first even where verdict cards are not in scope.
    fieldValue: (fieldValue != null && !isNaN(fieldValue)) ? Number(fieldValue) : null,
    status: effectiveStatus,
    rawStatus: status,
    multiPageCapApplied,
    multiPageEscalationApplied,
    statusLabel: statusLabel(effectiveStatus, lang),
    formattedMetricValue: formatMetricValue(metric, metricValue) || '—',
    culprits: culpritData.items,
    culpritKindLabel: culpritData.label,
    culpritKind: culpritData.kind,
    rich,
    verdictCards,
    multiPage,
    // Per-(metric, device) deep lab runs from the complex audit — feeds the
    // device-comparison section (mobile vs tablet vs desktop, same run batch).
    deviceRuns: Array.isArray(deepDeviceRuns) ? deepDeviceRuns.filter(d => d && d.metric) : [],
    // Sweep→deep runs: the primary metric's deep layer re-run directly on the
    // worst sweep pages (up to 2), so culprits belong to the page the sweep
    // points at — not to the homepage.
    sweepDeepRuns: Array.isArray(sweepDeepRuns) ? sweepDeepRuns.filter(s => s && s.url && s.metric) : [],
    evidenceContract: data.evidenceContract?.version === 2 ? data.evidenceContract : null,
    sweepDeepSkipReason: sweepDeepSkipReason || null,
    // URL the primary metric's main deep diagnostics actually ran on + the
    // entry-page run kept as a reference (set when the orchestration promoted
    // the sweep's worst page to the main deep story).
    deepAnalyzedUrl: analyzedDeepUrl,
    entryDeep: (entryDeep && entryDeep.url) ? entryDeep : null,
    lcpFieldEvidence: (lcpFieldEvidence && typeof lcpFieldEvidence === 'object')
      ? lcpFieldEvidence
      : null,
    measurementProvenance: normalizeReportMeasurementProvenance(
      measurementProvenance
        || deepLCPResults?.measurementProvenance
        || deepCLSResults?.measurementProvenance
        || deepINPResults?.measurementProvenance
        || deepTTFBResults?.measurementProvenance
    ),
    // Multi-page sweeps kept per metric so carried (non-primary) sections can
    // reference their own metric's crawl, not the primary's.
    multiPageByMetric: {
      CLS: multiPageResults || null,
      INP: multiPageINPResults || null,
      LCP: multiPageLCPResults || null,
      TTFB: multiPageTTFBResults || null
    },
    tasks: tasks || [],
    deepLCPResults,
    deepCLSResults,
    deepINPResults,
    validDeepInp,
    deepTTFBResults,
    inpCompareResults,
    // Deep layers that FAILED during the unified run (uppercase metric names)
    // — drives the honest "priority analysis failed" scenario + banner.
    failedDeepMetrics: Array.isArray(failedDeepMetrics)
      ? failedDeepMetrics.map(m => String(m).toUpperCase())
      : [],
    // Failure types are classified by the backend's shared contract. This
    // renderer only reads the contract and translates it for presentation.
    deepFailures: (deepFailures && typeof deepFailures === 'object')
      ? deepFailures
      : {},
    diagnostics,
    savingsRecommendations: adjustedSavingsRecs,
    hasGtmContainer: hasGtmContainer(diagnostics),
    savingsSummary,
    devicePriority: (devicePriority && Array.isArray(devicePriority.items)) ? devicePriority : null,
    // Metric #1 from the priority ranking (highest score above 0) — the
    // headline scenario must follow it; a report whose priority table says
    // CLS while the headline blames INP contradicts itself.
    // dpItems arrive already ranked by the server — take the first actionable
    // item as-is so the headline uses EXACTLY the same ordering (incl. tie
    // breaks) as the rendered priority table; re-sorting could disagree on ties.
    priorityTop: dpItems.find(i => Number(i.score) > 0) || null,
    // Unified CWV run: one run measured all metrics; deep layers of every
    // above-threshold metric belong in this single report (presence-gated),
    // ordered by the priority ranking. Metrics whose deep layer doesn't exist
    // yet (TTFB/LCP/FCP) get an explicit "depth pending" slot.
    unified: !!data.unified,
    fieldMatrix,
    fieldLoadStatus,
    devicePriorityMismatch,
    sgtmDetected,
    aiNarrative: (aiNarrative && typeof aiNarrative === 'object' && aiNarrative.headline)
      ? {
          headline: String(aiNarrative.headline || '').trim(),
          paragraphs: Array.isArray(aiNarrative.paragraphs)
            ? aiNarrative.paragraphs.map(p => String(p || '').trim()).filter(Boolean).slice(0, 2)
            : []
        }
      : null,
    dateNow: formatDateNow(lang),
    monthYear: formatMonthYear(lang, auditDate),
    auditDate: (auditDate && !isNaN(new Date(auditDate))) ? new Date(auditDate) : new Date(),
    // Consent state is a property of the RUN (the banner was or was not
    // clicked during page load), not of whether the interaction measurement
    // later succeeded — so the header label must not depend on deepRan.
    cmpMode: (deepRan || [deepLCPResults, deepCLSResults, deepINPResults, diagnostics].some(o => o && (o.skipCmp != null || o.cmpMode != null)))
      ? (cmpAccepted ? 'accepted' : 'untested') : null,
    effectiveCmpMode: [
      deepINPResults?.labRunSpread?.conditions?.effectiveCmpMode,
      deepINPResults?.conditions?.effectiveCmpMode,
      deepCLSResults?.labRunSpread?.conditions?.effectiveCmpMode,
      deepCLSResults?.conditions?.effectiveCmpMode,
      deepLCPResults?.labRunSpread?.conditions?.effectiveCmpMode,
      deepLCPResults?.conditions?.effectiveCmpMode
    ].find(value => value != null) || null,
    consentEvidence: deepINPResults || deepCLSResults || deepLCPResults || diagnostics || {}
  };

  const primaryFailure = r.deepFailures?.[r.metricUpper] || null;
  r.deepFailurePresentation = primaryFailure
    ? getDeepFailurePresentation(primaryFailure, lang, {
        metric: r.metricUpper,
        hasSweep: !!r.multiPageByMetric?.[r.metricUpper],
        hasDiagnostics: !!diagnostics
      })
    : null;

  // Detect scenario AFTER r is composed (needs status, rich, metricLower)
  r.scenario = detectScenario(r);
  r.copy = getScenarioCopy(r.scenario, r);
  const preventiveCopy = preventiveReportCopy(data, lang);
  if (preventiveCopy && !r.deepFailurePresentation && r.scenario !== 'inp-not-measured') {
    r.copy = { ...r.copy, ...preventiveCopy };
  }

  // In an "interaction not measured" INP report the vendor ranking is
  // load-time blocking attribution, NOT an answer about responsiveness —
  // relabel it so it doesn't look like the answer the report just refused
  // to give.
  if (r.scenario === 'inp-not-measured' && r.culprits.length) {
    r.culpritKindLabel = lang === 'cs'
      ? 'Blokující čas při načtení (nesouvisí s odezvou na interakci)'
      : 'Blocking time during load (not interaction responsiveness)';
  }

  return r;
};

// Single-lab-run caveat must be bound to the metric's mechanism. The text
// lives in the shared metric-copy map (client/src/utils/metricCopy.js) —
// no call site composes its own wording.
const singleLabRunNote = (metric, lang) => metricCopy(metric, 'singleLabRun', lang);

// Recompute from retained comparable samples, not a legacy labStability label.
const tbtSeriesMedian = (spread) => {
  if (spread?.metric !== 'TBT') return null;
  const fields = ['url', 'device', 'lighthouseVersion', 'throttlingMethod',
    'throttling', 'screenEmulation', 'userAgent', 'networkProfile', 'captureMethod'];
  const canonical = value => Array.isArray(value) ? value.map(canonical)
    : value && typeof value === 'object'
      ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value;
  const stable = value => JSON.stringify(canonical(value));
  const expected = spread.conditions;
  if (fields.some(key => expected?.[key] == null)) return null;
  if (!['pagespeed-lighthouse', 'controlled-lighthouse'].includes(expected.networkProfile) || expected.captureMethod !== 'lighthouse-navigation'
    || ['throttling', 'screenEmulation'].some(key => !expected[key] || typeof expected[key] !== 'object'
      || Array.isArray(expected[key]) || !Object.keys(expected[key]).length)) return null;
  const values = (spread.attempts || []).filter(a => a.status === 'ok' && !a.error && !a.duplicate
    && typeof a.value === 'number' && Number.isFinite(a.value) && a.value >= 0
    && fields.every(key => stable(a.conditions?.[key]) === stable(expected[key])))
    .map(a => a.value).sort((a, b) => a - b);
  return values.length >= 2
    ? (values[Math.floor((values.length - 1) / 2)] + values[Math.floor(values.length / 2)]) / 2 : null;
};

const labSeriesIsVerified = (spread) => {
  if (!spread) return false;
  if (spread.metric === 'TBT') return spread.verified === true
    && spread.runs >= 3 && tbtSeriesMedian(spread) != null;
  // Repeated timings do not verify unknown effective test conditions, even
  // when an older serialized spread still carries a "verified" flag.
  const required = ['networkProfile', 'protocol', 'captureMethod', 'effectiveCmpMode', 'cpuThrottling'];
  const missing = (conditions) => required.some(key => conditions?.[key] == null);
  if (spread.attemptConditionsComplete === false
    || (Array.isArray(spread.incompleteConditionAttemptIds) && spread.incompleteConditionAttemptIds.length > 0)
    || (spread.conditions && missing(spread.conditions))
    || spread.attempts?.some(a => a.conditionsIncomplete === true
      || (Array.isArray(a.missingConditions) && a.missingConditions.length > 0)
      || (a.conditions && missing(a.conditions)))) return false;
  const hasMixedIds = Array.isArray(spread.mixedConditionAttemptIds)
    && spread.mixedConditionAttemptIds.length > 0;
  const hasMixedAttempts = Array.isArray(spread.attempts)
    && spread.attempts.some(attempt => attempt?.conditionMismatch === true);
  const hasMultipleConditionGroups = Array.isArray(spread.conditionGroups)
    && spread.conditionGroups.length > 1;
  if (spread.conditionsConsistent === false || hasMixedIds || hasMixedAttempts || hasMultipleConditionGroups) {
    return false;
  }
  return spread.verified === true || (spread.verified == null && Number(spread.runs) >= 5);
};

const labSeriesDisplayValue = (spread, fallback = null) => {
  if (!spread) return fallback;
  if (spread.metric === 'TBT') return tbtSeriesMedian(spread) ?? spread.displayValue ?? fallback;
  const summary = comparableLabSummary(spread);
  if (summary) return summary.median;
  if (spread.attempts?.length) return null;
  if (labSeriesIsVerified(spread) && spread.median != null) return Number(spread.median);
  if (spread.displayValue != null) return Number(spread.displayValue);
  if (spread.runs >= 2 && spread.average != null) return Number(spread.average);
  return fallback;
};

const labSeriesSummaryText = (spread, metric, lang) => {
  if (!spread || !Number(spread.runs)) return lang === 'cs' ? 'bez validního běhu' : 'no valid run';
  const value = formatMetricValue(metric, labSeriesDisplayValue(spread)) || '—';
  if (spread.metric === 'TBT' && tbtSeriesMedian(spread) != null) {
    return `${lang === 'cs' ? 'medián srovnatelných běhů' : 'comparable-run median'} ${value} · n=${spread.runs}`;
  }
  if (labSeriesIsVerified(spread)) {
    return lang === 'cs'
      ? `medián ${value} · n=${spread.runs}`
      : `median ${value} · n=${spread.runs}`;
  }
  if (spread.runs === 1) return lang === 'cs'
    ? `${value} · jeden běh, orientační`
    : `${value} · single run, indicative`;
  return lang === 'cs'
    ? `medián ${value} · n=${spread.runs} · jistota diagnostiky omezená`
    : `median ${value} · n=${spread.runs} · limited diagnostic confidence`;
};

const buildVerdictCards = (metric, metricValue, rich, lang, fieldValue = null, labValue = null, fieldLoadStatus = null, labRunSpread = null, fieldDataUnavailable = false) => {
  const cards = [];
  const isFieldPrimary = fieldValue != null && !isNaN(fieldValue) && Number(fieldValue) === Number(metricValue);

  // Card 1: the primary metric itself
  if (metricValue != null) {
    cards.push({
      label: (metric || 'Metric').toUpperCase()
        + (isFieldPrimary ? (lang === 'cs' ? ' · reální uživatelé' : ' · real users')
          : fieldDataUnavailable && labValue != null && Number(metricValue) === Number(labValue)
            ? (lang === 'cs' ? ' · laboratorní test' : ' · lab test') : ''),
      value: formatMetricValue(metric, metricValue) || '—',
      target: metricThresholds[(metric || '').toLowerCase()]
        ? (lang === 'cs' ? `Cíl: pod ${formatMetricValue(metric, metricThresholds[(metric || '').toLowerCase()].good)}` : `Target: under ${formatMetricValue(metric, metricThresholds[(metric || '').toLowerCase()].good)}`)
        : '',
      severity: statusOf(metric, metricValue)
    });
  }

  // Card 1b: lab-measured value when it tells a different story than real users.
  // With ≥2 STORED runs, show the spread (min–max, median) instead of one
  // number — a single interaction either hits a load-phase long task
  // (collision tail) or not, so any single lab run over- or under-states.
  const hasSpread = labRunSpread && labRunSpread.runs >= 2 && labRunSpread.min !== labRunSpread.max;
  const verifiedSpread = labSeriesIsVerified(labRunSpread);
  if (isFieldPrimary && labValue != null && !isNaN(labValue)
    && (hasSpread || statusOf(metric, labValue) !== statusOf(metric, fieldValue))) {
    cards.push({
      label: (metric || 'Metric').toUpperCase() + (lang === 'cs' ? ' · laboratorní test' : ' · lab test'),
      value: hasSpread
        ? (verifiedSpread
          ? `${fmtNum(labRunSpread.min)}–${fmtNum(labRunSpread.max)} ms`
          : `~${formatMetricValue(metric, labSeriesDisplayValue(labRunSpread, labValue)) || '—'}`)
        : (formatMetricValue(metric, labValue) || '—'),
      target: hasSpread
        ? (verifiedSpread ? (lang === 'cs'
          ? `Medián ~${fmtNum(labRunSpread.median)} ms z ${labRunSpread.runs} uložených běhů. Reprezentativní je field p75, ne kterýkoli jednotlivý lab běh.`
          : `Median ~${fmtNum(labRunSpread.median)} ms across ${labRunSpread.runs} stored runs. Field p75 is representative, not any single lab run.`)
          : labSeriesSummaryText(labRunSpread, metric, lang))
        : singleLabRunNote(metric, lang),
      severity: verifiedSpread ? statusOf(metric, labRunSpread.median) : (hasSpread ? '' : statusOf(metric, labValue))
    });
  }

  // Card 2: TBT or page weight
  if (rich.tbtSeedValue != null || rich.longTasks?.totalBlockingTime != null || tbtSeriesMedian(rich.tbtRunSpread) != null) {
    const tbt = tbtSeriesMedian(rich.tbtRunSpread) ?? rich.tbtSeedValue ?? rich.longTasks.totalBlockingTime;
    const ratio = tbt / 200;
    const stab = tbtSeriesMedian(rich.tbtRunSpread) != null ? rich.tbtRunSpread : null;
    const stabNote = stab
      ? (lang === 'cs'
        ? ` · medián z ${stab.runs} srovnatelných běhů (rozsah ${fmtMs(stab.min)}–${fmtMs(stab.max)}); ${stab.attemptedRuns} pokusů. Rozpad dlouhých úloh níže patří jednomu výchozímu běhu, ne mediánu.`
        : ` · median of ${stab.runs} comparable runs (range ${fmtMs(stab.min)}–${fmtMs(stab.max)}); ${stab.attemptedRuns} attempts. The long-task breakdown below belongs to one seed run, not the median.`)
      : (rich.tbtRunSpread ? (lang === 'cs'
        ? ' · jednotlivý výchozí běh; opakovaná měření jsou neúplná / neověřená'
        : ' · single seed run; repeated measurements are incomplete / unverified') : '');
    const psiSeed = rich.tbtRunSpread?.attempts?.find(a =>
      a.conditions?.networkProfile === 'pagespeed-lighthouse' && Number.isFinite(a.value));
    const sourceComparison = rich.tbtRunSpread?.conditions?.networkProfile === 'controlled-lighthouse' && psiSeed
      ? (lang === 'cs'
        ? ` · Výchozí PSI vzorek ${Math.round(psiSeed.value)} ms je jiný profil; nejde o doložené zhoršení webu.`
        : ` · The seed PSI sample of ${Math.round(psiSeed.value)} ms uses a different profile; this is not evidence of a site regression.`)
      : '';
    // Green field LCP/TTFB do not erase a high TBT: they measure different
    // mechanisms. Keep an isolated Lighthouse signal non-causal, but require
    // repetition instead of dismissing it as harmless headroom.
    const tbtIsLabReserve = fieldLoadStatus === 'good' && tbt > 200;
    cards.push({
      label: 'Total Blocking Time · ' + (rich.tbtRunSpread?.conditions?.networkProfile === 'controlled-lighthouse'
        ? (lang === 'cs' ? 'lokální Lighthouse' : 'local Lighthouse') : 'Lighthouse / PSI'),
      value: fmtMs(tbt),
      target: (lang === 'cs'
        ? 'součet částí dlouhých úloh nad 50 ms v okně Lighthouse od FCP do TTI (ne doba nefunkčních kliknutí) · '
        : 'sum of long-task time above 50 ms in the Lighthouse FCP–TTI window (not a duration of unresponsive clicks) · ')
        + (ratio > 1
        ? (lang === 'cs' ? `Cíl: pod 200 ms · ${ratio.toFixed(1)}× nad limitem` : `Target: under 200 ms · ${ratio.toFixed(1)}× over`)
        : (lang === 'cs' ? 'Cíl: pod 200 ms · v limitu' : 'Target: under 200 ms · within budget'))
        + stabNote + sourceComparison
        + (tbtIsLabReserve
          ? (lang === 'cs' ? ' · výrazný samostatný lab signál při načítání; zelené LCP/TTFB jej nevyvracejí. Ověřte opakováním za srovnatelných podmínek; sám o sobě nedokazuje příčinu field INP' : ' · substantial isolated lab load signal; green LCP/TTFB do not invalidate it. Repeat under comparable conditions; by itself it does not establish the cause of field INP')
          : ''),
      severity: tbtIsLabReserve ? 'warning' : (tbt > 600 ? 'critical' : tbt > 200 ? 'warning' : 'good')
    });
  } else if (rich.performanceBudget?.totalActualBytes > 0) {
    const total = rich.performanceBudget.totalActualBytes;
    const budget = rich.performanceBudget.totalBudgetBytes;
    const ratio = budget > 0 ? total / budget : 0;
    cards.push({
      label: lang === 'cs' ? 'Velikost stránky' : 'Page weight',
      value: fmtKb(total),
      target: budget > 0
        ? (lang === 'cs' ? `Rozpočet: ${fmtKb(budget)} · ${ratio.toFixed(1)}× nad limitem` : `Budget: ${fmtKb(budget)} · ${ratio.toFixed(1)}× over`)
        : '',
      severity: ratio > 1.5 ? 'critical' : ratio > 1 ? 'warning' : 'good'
    });
  }

  // Card 3: unused JS or third-party time
  if (rich.performanceBudget?.unusedJsBytes > 50 * 1024) {
    const bytes = rich.performanceBudget.unusedJsBytes;
    cards.push({
      label: lang === 'cs' ? 'Nevyužitý JavaScript' : 'Unused JavaScript',
      value: fmtKb(bytes),
      target: lang === 'cs' ? 'Horní odhad (Lighthouse, studený load)' : 'Upper bound (Lighthouse, cold load)',
      severity: bytes > 500 * 1024 ? 'critical' : 'warning'
    });
  } else if (rich.resourceTiming?.thirdParty?.totalBytes > 0) {
    const tp = rich.resourceTiming.thirdParty;
    cards.push({
      label: lang === 'cs' ? 'Third-party' : 'Third-party',
      value: fmtKb(tp.totalBytes),
      target: lang === 'cs' ? `${tp.count} požadavků · ${fmtMs(tp.totalTimeMs) || '—'} síťového času` : `${tp.count} requests · ${fmtMs(tp.totalTimeMs) || '—'} network time`,
      severity: tp.totalBytes > 1024 * 1024 ? 'warning' : 'good'
    });
  }

  return cards;
};

// =========================================================================
// SAVINGS RENDERERS — kept from previous version (unchanged contract)
// =========================================================================
// True when an individual ms estimate excluded from the headline (e.g. the
// unstable unused-JS simulation) is LARGER than the headline max — the
// report must explain that mismatch or the header looks wrong.
const hasExcludedLargerMs = (summary, recs) => {
  if (!Array.isArray(recs)) return false;
  const headlineMax = summary?.maxPotentialSavingsMs ?? recs
    .filter(r => r.estimatedSavings?.unit === 'ms' && r.estimatedSavings?.headlineEligible !== false)
    .reduce((m, r) => Math.max(m, r.estimatedSavings.value || 0), 0);
  return recs.some(r => r.estimatedSavings?.unit === 'ms'
    && r.estimatedSavings?.headlineEligible === false
    && (r.estimatedSavings.value || 0) > headlineMax);
};

const excludedMsNote = (lang) => lang === 'cs'
  ? 'Do souhrnu „až ~X ms" v hlavičce se nepočítají nestabilní simulované odhady (typicky odstranění nevyužitého JS) — jednotlivá položka proto může ukazovat vyšší číslo než hlavička.'
  : 'The "up to ~X ms" header excludes unstable simulated estimates (typically unused-JS removal) — an individual item can therefore show a larger number than the header.';

const buildSavingsTotals = (sl, summary, recs) => {
  // Estimates overlap and are NOT additive — show the single largest
  // estimate ("up to ~X ms") instead of a misleading sum.
  const recMax = (unit) => {
    if (!Array.isArray(recs)) return 0;
    return recs
      .filter(r => r.estimatedSavings?.unit === unit && r.estimatedSavings?.headlineEligible !== false)
      .reduce((m, r) => Math.max(m, r.estimatedSavings.value || 0), 0);
  };
  const maxMs = summary?.maxPotentialSavingsMs ?? recMax('ms');
  const maxKb = Math.round((summary?.maxPotentialSavingsBytes ?? recMax('bytes')) / 1024);
  const parts = [];
  if (maxMs > 0) parts.push(`~${Math.round(maxMs)} ms`);
  if (maxKb > 0) parts.push(`~${maxKb} KB`);
  // The aggregate is a measured opportunity/model estimate for this load
  // scenario, never a guaranteed directly-removable amount.
  return parts.length ? `${sl.total} ${parts.join(' · ')} (${sl.opportunityNote})` : '';
};

const renderSavingsMockup = (recs, summary, lang, metricLower, containerEvidence) => {
  if (!recs || !recs.length) return '';
  const sl = getSavingsLabels(lang);
  const totals = buildSavingsTotals(sl, summary, recs);
  const impactLabelOf = (k) => sl[`impact${k.charAt(0).toUpperCase()}${k.slice(1)}`] || k;
  const effortLabelOf = (k) => sl[`effort${k.charAt(0).toUpperCase()}${k.slice(1)}`] || k;
  const badgeStyles = {
    green: 'background:rgba(34,197,94,0.12);color:#86efac;border:1px solid rgba(34,197,94,0.3);',
    yellow: 'background:rgba(234,179,8,0.12);color:#fde68a;border:1px solid rgba(234,179,8,0.3);',
    orange: 'background:rgba(249,115,22,0.12);color:#fdba74;border:1px solid rgba(249,115,22,0.3);'
  };
  const impactStyles = {
    high: 'background:rgba(239,68,68,0.12);color:#fca5a5;',
    medium: 'background:rgba(234,179,8,0.12);color:#fde68a;',
    low: 'background:rgba(59,130,246,0.12);color:#93c5fd;'
  };
  const ownerWord = lang === 'cs' ? 'vlastník' : 'owner';
  const items = recs.map(rec => {
    const range = formatSavingsRange(rec.estimatedSavings);
    const bk = savingsBadgeKey(rec);
    const bColor = rec.confidenceBadge?.color || 'yellow';
    const owner = ownerForRecommendation(rec, lang, { hasGtmContainer: containerEvidence });
    return `
        <div style="background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:14px 18px;display:grid;grid-template-columns:1fr auto;gap:14px;align-items:flex-start;">
          <div style="min-width:0">
            <div style="font-size:14px;font-weight:600;color:#f8fafc;margin-bottom:8px;">${escapeHtml(rec.title || rec.id || '')}</div>
            <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;">
              <span style="font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.05em;text-transform:uppercase;padding:3px 8px;border-radius:4px;${badgeStyles[bColor] || badgeStyles.yellow}">${escapeHtml(sl[bk])}</span>
              <span style="font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.05em;text-transform:uppercase;padding:3px 8px;border-radius:4px;${impactStyles[rec.impact] || impactStyles.low}">${escapeHtml(impactLabelOf(rec.impact || 'low'))}</span>
              <span style="font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.05em;text-transform:uppercase;color:#94a3b8;">${escapeHtml(sl.effort)}: ${escapeHtml(effortLabelOf(rec.effort || 'medium'))}</span>
              ${owner ? `<span style="font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.05em;text-transform:uppercase;padding:3px 8px;border-radius:4px;border:1px solid ${owner.color}55;color:${owner.color};">${escapeHtml(ownerWord)}: ${escapeHtml(owner.label)}</span>` : ''}
            </div>
            ${owner ? `<div style="font-size:11px;color:#94a3b8;line-height:1.5;margin-top:8px;">${escapeHtml(owner.procedure)}</div>` : ''}
          </div>
          <div style="text-align:right;flex-shrink:0;min-width:96px;">
            <div style="font-family:'JetBrains Mono',monospace;font-size:18px;font-weight:700;color:#4ade80;">${escapeHtml(formatSavingsValue(rec.estimatedSavings))}</div>
            ${range ? `<div style="font-family:'JetBrains Mono',monospace;font-size:10px;color:#64748b;margin-top:2px;">${escapeHtml(sl.range)} ${escapeHtml(range)}</div>` : ''}
          </div>
        </div>`;
  }).join('');

  return `
  <div class="glass panel" style="margin-bottom:24px;border-color:rgba(34,197,94,0.25);">
    <div class="panel-header">
      <div class="panel-title" style="color:#86efac;">${escapeHtml(sl.title)}${totals ? ` <span style="font-size:11px;color:#94a3b8;font-weight:400;font-family:'JetBrains Mono',monospace;">· ${escapeHtml(totals)}</span>` : ''}</div>
      <div class="panel-subtitle">${recs.length} ${escapeHtml(sl.countSuffix)}</div>
    </div>
    ${totals ? `<div style="font-size:11px;color:#94a3b8;margin:-6px 0 12px;">${escapeHtml(sl.nonAdditive || '')}${hasExcludedLargerMs(summary, recs) ? ' ' + escapeHtml(excludedMsNote(lang)) : ''}</div>` : ''}
    ${metricLower === 'inp' ? `<div style="font-size:11px;color:#94a3b8;margin:0 0 12px;">${lang === 'cs'
      ? 'Tyto úspory se projeví na rychlosti načtení stránky, ne na odezvě (INP) — jsou to dvě různé vrstvy problému.'
      : 'These savings improve page load speed, not responsiveness (INP) — two different layers of the problem.'}</div>` : ''}
    <div style="display:flex;flex-direction:column;gap:10px;">
      ${items}
    </div>
    <div style="margin-top:16px;padding:14px 16px;background:rgba(15,23,42,0.6);border-left:3px solid #22c55e;border-radius:8px;">
      <div style="font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#86efac;margin-bottom:6px;font-weight:600;">${escapeHtml(sl.howComputed)}</div>
      <div style="font-size:12px;color:#cbd5e1;line-height:1.55;">${escapeHtml(sl.methodology)}</div>
    </div>
  </div>`;
};

const renderSavingsAudit = (recs, summary, lang, metricLower, containerEvidence) => {
  if (!recs || !recs.length) return '';
  const sl = getSavingsLabels(lang);
  const totals = buildSavingsTotals(sl, summary, recs);
  const impactLabelOf = (k) => sl[`impact${k.charAt(0).toUpperCase()}${k.slice(1)}`] || k;
  const effortLabelOf = (k) => sl[`effort${k.charAt(0).toUpperCase()}${k.slice(1)}`] || k;
  const ownerWord = lang === 'cs' ? 'vlastník' : 'owner';
  const items = recs.map(rec => {
    const range = formatSavingsRange(rec.estimatedSavings);
    const bk = savingsBadgeKey(rec);
    const owner = ownerForRecommendation(rec, lang, { hasGtmContainer: containerEvidence });
    return `
      <div style="display:grid;grid-template-columns:1fr auto;gap:24px;padding:20px 0;border-bottom:1px solid #c9c3b6;align-items:flex-start;">
        <div>
          <div style="font-family:var(--serif);font-size:20px;font-weight:600;color:var(--ink);margin-bottom:8px;line-height:1.25;">${escapeHtml(rec.title || rec.id || '')}</div>
          <div style="display:flex;flex-wrap:wrap;gap:8px;font-family:var(--mono);font-size:10px;text-transform:uppercase;letter-spacing:0.1em;">
            <span style="padding:3px 8px;border:1px solid var(--ink);border-radius:2px;color:var(--ink);">${escapeHtml(sl[bk])}</span>
            <span style="padding:3px 8px;border:1px solid var(--accent);border-radius:2px;color:var(--accent);">${escapeHtml(impactLabelOf(rec.impact || 'low'))}</span>
            <span style="padding:3px 8px;border:1px solid #888;border-radius:2px;color:#555;">${escapeHtml(sl.effort)}: ${escapeHtml(effortLabelOf(rec.effort || 'medium'))}</span>
            ${owner ? `<span style="padding:3px 8px;border:1px solid var(--ink);border-radius:2px;color:var(--ink);background:var(--paper-2);">${escapeHtml(ownerWord)}: ${escapeHtml(owner.label)}</span>` : ''}
          </div>
          ${owner ? `<div style="font-family:var(--serif);font-size:13px;color:#555;line-height:1.5;margin-top:8px;">${escapeHtml(owner.procedure)}</div>` : ''}
        </div>
        <div style="text-align:right;white-space:nowrap;">
          <div style="font-family:var(--serif);font-size:26px;font-weight:600;color:var(--ok);">${escapeHtml(formatSavingsValue(rec.estimatedSavings))}</div>
          ${range ? `<div style="font-family:var(--mono);font-size:11px;color:#888;margin-top:4px;">${escapeHtml(sl.range)} ${escapeHtml(range)}</div>` : ''}
        </div>
      </div>`;
  }).join('');

  return `
  <section>
    <div class="section-label" style="color:var(--ok);">${escapeHtml(sl.title)}${totals ? ` &nbsp;·&nbsp; ${escapeHtml(totals)}` : ''}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Kolik' : 'How much'} <em>${lang === 'cs' ? 'lze ušetřit' : 'you can save'}</em>.</h2>
    <p>${lang === 'cs'
      ? 'Položky níže ukazují odhad zlepšení v ms (rychlost) nebo KB (objem dat) společně s mírou důvěry v odhad. Odhady se překrývají a nejsou aditivní — jednotlivé úspory nelze prostě sčítat.'
      : 'The items below show estimated improvement in ms (speed) or KB (data volume) along with confidence in the estimate. Estimates overlap and are not additive — individual savings cannot simply be summed.'}${hasExcludedLargerMs(summary, recs) ? ' ' + excludedMsNote(lang) : ''}</p>
    ${metricLower === 'inp' ? `<p style="font-family:var(--mono);font-size:11px;color:#666;">${lang === 'cs'
      ? 'Tyto úspory se projeví na rychlosti načtení stránky, ne na odezvě (INP) — jsou to dvě různé vrstvy problému.'
      : 'These savings improve page load speed, not responsiveness (INP) — two different layers of the problem.'}</p>` : ''}
    <div style="border-top:2px solid var(--ink);margin-top:20px;">
      ${items}
    </div>
    <div style="margin-top:24px;padding:18px 20px;background:var(--paper-2);border-left:3px solid var(--ok);">
      <div style="font-family:var(--mono);font-size:10px;letter-spacing:0.15em;text-transform:uppercase;color:var(--ok);font-weight:700;margin-bottom:8px;">${escapeHtml(sl.howComputed)}</div>
      <div style="font-family:var(--serif);font-size:14px;line-height:1.55;color:var(--ink);">${escapeHtml(sl.methodology)}</div>
    </div>
  </section>`;
};

// =========================================================================
// CLS FINDINGS — concrete per-element layer at INP parity
// =========================================================================
// Turns deepCLSResults (layout-shift entries collected over the WHOLE page
// lifetime, incl. scroll phases, with per-shift element sources) into
// concrete findings: "element X shifts the layout by Y" + a cause-specific
// fix + an owner. Same depth the INP report has for interactions.
const CLS_CAUSE_LABELS = {
  cs: {
    'ad-injection': 'Vložená reklama',
    'consent-banner': 'Cookie lišta (CMP)',
    'unsized-image': 'Obrázek bez rozměrů',
    'image-load': 'Načtení obrázku',
    'iframe-embed': 'Iframe embed',
    'video-embed': 'Video embed',
    'font-swap': 'Výměna fontu',
    'dynamic-content': 'Dynamicky vložený obsah',
    animation: 'Animace',
    unknown: 'Neurčená příčina'
  },
  en: {
    'ad-injection': 'Injected ad',
    'consent-banner': 'Cookie banner (CMP)',
    'unsized-image': 'Unsized image',
    'image-load': 'Image load',
    'iframe-embed': 'Iframe embed',
    'video-embed': 'Video embed',
    'font-swap': 'Font swap',
    'dynamic-content': 'Dynamically injected content',
    animation: 'Animation',
    unknown: 'Unattributed cause'
  }
};
const clsCauseLabel = (cause, lang) =>
  (CLS_CAUSE_LABELS[lang] || CLS_CAUSE_LABELS.cs)[cause]
  || (CLS_CAUSE_LABELS[lang] || CLS_CAUSE_LABELS.cs).unknown;

const CLS_CAUSE_ADVICE = {
  cs: {
    'ad-injection': 'Rezervovat místo pro reklamní slot (min-height dle nejčastější kreativy) — slot nesmí odsouvat obsah až po dotažení reklamy.',
    'consent-banner': 'Cookie lišta nesmí odsouvat obsah: vykreslit jako overlay (position: fixed), ne v toku dokumentu.',
    'unsized-image': 'Doplnit width/height atributy nebo CSS aspect-ratio, aby měl obrázek rezervované místo ještě před stažením.',
    'image-load': 'Doplnit width/height atributy nebo CSS aspect-ratio, aby měl obrázek rezervované místo ještě před stažením.',
    'iframe-embed': 'Obalit iframe kontejnerem s pevným aspect-ratio (např. 16/9), aby embed nedotlačil obsah po načtení.',
    'video-embed': 'Nastavit videu rozměry (width/height nebo aspect-ratio) a poster, aby přehrávač neposunul obsah.',
    'font-swap': 'Preload klíčových fontů + font-display: swap se size-adjust/fallback metrikami, aby výměna fontu neměnila výšku textu.',
    'dynamic-content': 'Rezervovat místo pro dynamicky vkládaný obsah (min-height / skeleton) a nikdy nevkládat obsah nad již vykreslený text.',
    animation: 'Animovat pouze transform/opacity — nikdy vlastnosti měnící layout (top, height, margin).',
    unknown: 'Prověřit element v DevTools (Performance → Layout Shift regions) a rezervovat mu místo.'
  },
  en: {
    'ad-injection': 'Reserve space for the ad slot (min-height per the most common creative) — the slot must not push content once the ad arrives.',
    'consent-banner': 'The cookie banner must not push content: render it as an overlay (position: fixed), not in document flow.',
    'unsized-image': 'Add width/height attributes or CSS aspect-ratio so the image reserves its space before it downloads.',
    'image-load': 'Add width/height attributes or CSS aspect-ratio so the image reserves its space before it downloads.',
    'iframe-embed': 'Wrap the iframe in a fixed aspect-ratio container (e.g. 16/9) so the embed cannot push content after load.',
    'video-embed': 'Give the video dimensions (width/height or aspect-ratio) and a poster so the player cannot shift content.',
    'font-swap': 'Preload key fonts + font-display: swap with size-adjust/fallback metrics so the swap does not change text height.',
    'dynamic-content': 'Reserve space for dynamically injected content (min-height / skeleton) and never insert content above already-rendered text.',
    animation: 'Animate only transform/opacity — never layout-affecting properties (top, height, margin).',
    unknown: 'Inspect the element in DevTools (Performance → Layout Shift regions) and reserve its space.'
  }
};
const clsCauseAdvice = (cause, lang) =>
  (CLS_CAUSE_ADVICE[lang] || CLS_CAUSE_ADVICE.cs)[cause]
  || (CLS_CAUSE_ADVICE[lang] || CLS_CAUSE_ADVICE.cs).unknown;

const fmtShift = (v) => (Number(v) || 0).toFixed(3);

// Field (CrUX p75) CLS available to this report: explicit fieldValue when the
// audited metric IS CLS, otherwise the device-priority ranking's CLS row.
const fieldClsValue = (r) => {
  if (r.metricLower === 'cls' && r.fieldValue != null && !isNaN(r.fieldValue)) return Number(r.fieldValue);
  const it = (r.fieldMatrix || []).find(i => i.metric === 'cls' && i.p75 != null);
  return it ? Number(it.p75) : null;
};

// Field-first CLS framing (same principle as INP): field p75 is what real
// users experience and must lead; the lab run keeps the page alive through
// scroll and late ad injections, so it is an UPPER estimate — never the
// headline number when field data exists.
const clsFieldFirstLine = (r) => {
  const cs = r.lang === 'cs';
  const lab = r.deepCLSResults?.cls;
  const field = fieldClsValue(r);
  const fmtLoc = (v) => fmtShift(v).replace('.', cs ? ',' : '.');
  if (field != null) {
    return cs
      ? `CLS u reálných uživatelů (field p75): ${fmtLoc(field)} (práh dobré hodnoty 0,10).${lab != null ? ` Laboratorní běh naměřil ${fmtLoc(lab)} — horní odhad: lab zachytí i posuny (např. reklamu vloženou desítky sekund po načtení), které reální uživatelé nemusí zažít.` : ''}`
      : `Real-user CLS (field p75): ${fmtLoc(field)} (good threshold 0.10).${lab != null ? ` The lab run measured ${fmtLoc(lab)} — an upper estimate: the lab also captures shifts (e.g. an ad injected tens of seconds after load) that real users may never experience.` : ''}`;
  }
  return cs
    ? `Změřený CLS ${fmtLoc(lab)} (práh dobré hodnoty 0,10) — laboratorní hodnota, field data nejsou k dispozici.`
    : `Measured CLS ${fmtLoc(lab)} (good threshold 0.10) — a lab value, field data unavailable.`;
};

// Container flagging for payloads stored BEFORE the server started merging
// at the source (per-shift, see server clsDiagnostics): a page container
// (body-wrapper) hit by someone else's insertion must never surface as its
// own "reserve space" task. Without shift-level data on the client we cannot
// prove WHICH culprit pushed it, so legacy containers are only FLAGGED as
// affected (victim, culprit unknown) — never causally reassigned. Detection
// is deliberately narrow (explicit wrapper identity / BODY / HTML only) so a
// legit inserted <section> or small ".card-wrapper" is not swallowed.
const CLS_SPECIFIC_CAUSES = new Set([
  'ad-injection', 'iframe-embed', 'video-embed', 'unsized-image',
  'image-load', 'consent-banner', 'font-swap', 'animation'
]);
const CLS_WRAPPER_NAME_RE = /(^|[\s#._-])(wrapper|wrap|container|layout|root|app)([\s#._-]|$)/i;
const isContainerClsElement = (el) => {
  if (el.isContainer) return true;
  if (CLS_SPECIFIC_CAUSES.has(el.cause)) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'BODY' || tag === 'HTML') return true;
  return CLS_WRAPPER_NAME_RE.test(`${el.id || ''} ${el.className || ''}`)
    || CLS_WRAPPER_NAME_RE.test(el.selector || '');
};
export const mergeClsContainerElements = (els) => {
  // Refine generic causes FIRST — a thumbnail wrapper that grew because of
  // its image must become 'unsized-image' before the container heuristic
  // runs, or its "wrapper" name + generic cause gets it swallowed as a
  // pushed container and the report says "culprit not determined".
  const refined = els.map(el => {
    const cause = refineClsCause(el.cause, `${el.selector || ''} ${el.friendlyName || ''}`, el);
    return cause === el.cause ? el : { ...el, cause };
  });
  // Already attributed server-side (new payloads carry the markers) — trust it.
  if (refined.some(el => el.pushedContainers || el.isContainer)) return refined;
  return refined.map(el => (isContainerClsElement(el) ? { ...el, isContainer: true } : el));
};

// Refine a generic CLS cause ('dynamic-content'/'unknown') by what the
// selector says the element IS. A thumbnail wrapper shifting because of the
// image inside it must read "unsized image", not "injected content" — the
// generic label sends the developer hunting for ads/embeds instead of adding
// aspect-ratio to an image. Specific server-detected causes are never touched.
const refineClsCause = (cause, selectorText, el) => {
  if (cause && cause !== 'dynamic-content' && cause !== 'unknown') return cause;
  const s = String(selectorText || '');
  // Ad identity in the class/id is affirmative evidence on its own.
  if (/(^|[^a-z])ads?([^a-z]|$)|adsbygoogle|doubleclick|ad[-_]?slot|leaderboard|skyscraper|billboard/i.test(s)) return 'ad-injection';
  // Image/embed relabeling needs causal evidence, not just a name: the
  // element's own height must have changed (it grew because of its content).
  // A same-size element that merely moved is a pushed victim — a "hero"
  // wrapper displaced by an inserted banner must NOT become "unsized image".
  const selfResized = el?.currentRect && el?.previousRect
    && Math.abs((el.currentRect.height || 0) - (el.previousRect.height || 0)) > 1;
  if (!selfResized) return cause || 'unknown';
  if (/thumb|image|img[-_]|[-_]img|photo|picture|figure|hero/i.test(s)) return 'unsized-image';
  if (/iframe|embed|youtube|player/i.test(s)) return 'iframe-embed';
  return cause || 'unknown';
};

const buildClsFindings = (r) => {
  const els = r.deepCLSResults?.elements;
  if (!Array.isArray(els) || !els.length) return [];
  const lang = r.lang;
  const cs = lang === 'cs';
  // Denominator for the share breakdown: the page's measured total CLS.
  // share = element shift / page CLS — the DevTools-comparable ratio that
  // proves the #1 culprit dominates and the rest are marginal.
  const clsTotal = Number(r.deepCLSResults?.cls);
  const shareOf = (shift) => Number.isFinite(clsTotal) && clsTotal > 0
    ? Math.min(100, Math.round((Number(shift) / clsTotal) * 100))
    : null;
  return mergeClsContainerElements([...els])
    .filter(el => (el.totalShift || 0) > 0.001)
    .sort((a, b) => (b.totalShift || 0) - (a.totalShift || 0))
    .slice(0, 6)
    .map(el => {
      const cause = el.cause || 'unknown'; // already refined in mergeClsContainerElements
      return {
      name: el.friendlyName || el.tagName || 'element',
      selector: el.selector || '',
      // Human identity for body text: friendly name when the server gave
      // one, else the selector without :nth-of-type() noise. The EXACT
      // selector stays in the handover tasks for developers. Never empty or
      // a bare ambiguous tag — fall back through class/tag/raw selector.
      displayName: (() => {
        if (el.friendlyName) return el.friendlyName;
        const stripped = String(el.selector || '').replace(/:nth-of-type\(\d+\)/g, '').trim();
        if (/[.#]/.test(stripped)) return stripped; // still carries class/id identity
        const firstClass = String(el.className || '').trim().split(/\s+/)[0];
        if (firstClass) return `${el.tagName || 'div'}.${firstClass}`;
        return el.selector || el.tagName || 'element';
      })(),
      shift: el.totalShift || 0,
      share: shareOf(el.totalShift || 0),
      // Exclusive share: portion from shifts where this element was the only
      // recorded specific source (see server attribution). null on payloads
      // predating exclusiveShift — dominance claims then stay suppressed.
      exclusiveShare: Number.isFinite(Number(el.exclusiveShift)) ? shareOf(Number(el.exclusiveShift)) : null,
      // Apportioned payloads: totalShift is this source's OWN portion of
      // each shift (impact-weighted), so `share` is real and roughly
      // additive; legacy payloads inherited full entry values (overlapping).
      apportioned: r.deepCLSResults?.attributionModel === 'apportioned',
      shiftCount: el.shiftCount || 1,
      cause,
      causeLabel: el.isContainer
        ? (cs ? 'Zasažený kontejner' : 'Affected container')
        : clsCauseLabel(cause, lang),
      // A container is the VICTIM of an insertion, not the culprit — the fix
      // is never "reserve space for the container".
      advice: el.isContainer
        ? (cs
          ? 'Kontejner je zasažený prvek, ne viník — dohledat, co se do něj vkládá (DevTools → Performance → Layout Shift regions), a rezervovat místo tomu viníkovi. Nerezervovat místo kontejneru.'
          : 'The container is the affected element, not the culprit — find what is being inserted into it (DevTools → Performance → Layout Shift regions) and reserve space for THAT. Do not reserve space for the container.')
        : clsCauseAdvice(cause, lang),
      owner: ownerForClsCause(cause, lang),
      timing: el.timing != null ? Math.round(el.timing) : null,
      isContainer: !!el.isContainer,
      pushedContainers: Array.isArray(el.pushedContainers) && el.pushedContainers.length ? el.pushedContainers : null
      };
    });
};

// One-line dominance verdict over the share breakdown: when the #1 culprit
// carries the clear majority of the page's CLS, say so explicitly — the
// client should fix the dominant source, not spend energy on marginal ones.
const clsDominanceLine = (findings, cs) => {
  const culprits = findings.filter(f => !f.isContainer && f.share != null);
  if (!culprits.length) return null;
  const top = culprits[0];
  const second = culprits[1] || null;
  // The "one fix removes most" claim needs PROVEN attribution. Apportioned
  // payloads: each row's share is that source's own impact-weighted portion,
  // so a >=50 % share IS dominance. Legacy payloads inherited the full entry
  // value on every source (rows can be the same physical shift), so only
  // shifts where the top element was the sole source count toward dominance.
  const dominant = top.apportioned
    ? (Number.isFinite(top.share) && top.share >= 50 && (!second || top.share >= 2 * second.share))
    : (Number.isFinite(top.exclusiveShare) && top.exclusiveShare >= 50
      && (!second || top.share >= 2 * second.share));
  if (!dominant) return null;
  // Distinguish culprits from affected containers in the "the rest is
  // marginal" claim: a pushed container can carry a large share (it is the
  // same physical shift attributed to the victim), and lumping it in with
  // "other sources" makes the 2 % claim look contradicted by a 22 % row.
  const container = findings.filter(f => f.isContainer && f.share != null)
    .sort((a, b) => b.share - a.share)[0] || null;
  const containerNote = container
    ? (cs
      ? ` Položka ${container.displayName || container.selector} (${container.share} %) je zasažený kontejner bez doloženého vlastního viníka — tentýž posun připsaný zasaženému prvku, ne další samostatný zdroj.`
      : ` The ${container.displayName || container.selector} row (${container.share} %) is an affected container with no proven culprit of its own — the same shift attributed to the pushed element, not another independent source.`)
    : '';
  const rest = second
    ? (cs ? ` Druhý největší potvrzený viník je vedle něj marginální (${second.share} %).${containerNote}` : ` The second largest confirmed culprit is marginal next to it (${second.share} %).${containerNote}`)
    : containerNote;
  const exclNote = !top.apportioned && Number.isFinite(top.exclusiveShare)
    ? (cs ? ` (${top.exclusiveShare} % výhradně sám)` : ` (${top.exclusiveShare} % exclusively on its own)`)
    : '';
  return cs
    ? `Viník č. 1 (${top.displayName || top.selector || top.name}) tvoří ${top.share} % celkového CLS stránky${exclNote} — oprava tohoto jednoho prvku odstraní většinu posunů.${rest}`
    : `Culprit #1 (${top.displayName || top.selector || top.name}) accounts for ${top.share} % of the page's total CLS${exclNote} — fixing this single element removes most of the shifting.${rest}`;
};

// Humanized CLS phase note: says WHEN the shifts happened and what that
// means for the fix, instead of three bare numbers behind jargon.
const clsPhaseNote = (phases, cs) => {
  if (!phases) return '';
  const init = Number(phases.initial) || 0;
  const scroll = Number(phases.scroll) || 0;
  const post = Number(phases.postScroll) || 0;
  const nums = cs
    ? `při načítání ${fmtShift(init)}, během scrollování ${fmtShift(scroll)}, po scrollu ${fmtShift(post)}`
    : `during load ${fmtShift(init)}, while scrolling ${fmtShift(scroll)}, after scroll ${fmtShift(post)}`;
  const max = Math.max(init, scroll, post);
  let takeaway = '';
  if (max > 0) {
    if (max === init) {
      takeaway = cs
        ? ' → Převážná část posunů vzniká při načítání stránky — oprava míří do načítání, ne do pozdějšího chování stránky.'
        : ' → Most of the shifting happens during page load — the fix targets loading, not later page behavior.';
    } else if (max === scroll) {
      takeaway = cs
        ? ' → Obsah se posouvá během scrollování (typicky obrázky či obsah doskakující při scrollu bez rezervovaného místa) — oprava míří tam.'
        : ' → Content shifts while scrolling (typically images or content popping in on scroll without reserved space) — that is where the fix goes.';
    } else {
      takeaway = cs
        ? ' → Posuny přicházejí až po scrollu (pozdě doskakující obsah, typicky reklama) — oprava míří na pozdě vkládaný obsah.'
        : ' → Shifts arrive only after scrolling (late-injected content, typically ads) — the fix targets late-inserted content.';
    }
  }
  return (cs
    ? `Měřilo se po celou dobu života stránky včetně scrollování. Kdy k posunům došlo: ${nums}.`
    : `Measured over the whole page lifetime including scrolling. When the shifts happened: ${nums}.`) + takeaway;
};

// Caveat for the share rows. Apportioned payloads: each source got its own
// portion of every shift (impact-weighted), matching what DevTools shows per
// element. Legacy payloads: every source inherited the full entry value, so
// percentages overlap.
const clsShareCaveat = (cs, apportioned = false) => apportioned
  ? (cs
    ? 'Podíl každého zdroje odpovídá jeho vlastní naměřené části posunů (vážené velikostí a vzdáleností posunu) — hodnoty se nepřekrývají a zhruba se sčítají na celkové CLS.'
    : 'Each source\'s share is its own measured portion of the shifts (weighted by moved area and distance) — values do not overlap and roughly add up to the total CLS.')
  : (cs
    ? 'Jeden posun může mít víc zaznamenaných zdrojů (každý dostane plnou hodnotu posunu) — procenta se proto mohou překrývat a nemusí se sčítat na 100 %.'
    : 'One shift can have several recorded sources (each credited with the full shift value) — percentages can therefore overlap and may not add up to 100 %.');

// Dual-state CLS comparison: banner visible (what a user sees on arrival)
// vs after consent. Returns { text, isFinding } — isFinding=true when the
// banner itself causes a meaningful part of the shifting (then the report
// flags it with a fix + owner), false = neutral statement, null = no data.
const clsBannerCompareNote = (r, cs) => {
  // CMP geo-targets away from the measuring locality (gdprApplies:false /
  // display disabled): no banner ever rendered, so a "with banner vs after
  // consent" comparison would compare two identical banner-less runs.
  // The whole CMP section is suppressed; methodology carries a one-line note.
  if (r.deepCLSResults?.cmpState === 'disabled') return null;
  const bc = r.deepCLSResults?.bannerCompare;
  if (!bc || !Number.isFinite(Number(bc.withBannerCls))) return null;
  const withB = Number(bc.withBannerCls);
  const afterC = Number(bc.afterConsentCls) || 0;
  const bannerShift = Number(bc.bannerShift) || 0;
  const base = cs
    ? `CLS s cookie bannerem ${fmtShift(withB)} (co uživatel vidí při příchodu), po odkliknutí ${fmtShift(afterC)}.`
    : `CLS with the cookie banner ${fmtShift(withB)} (what a user sees on arrival), after dismissing it ${fmtShift(afterC)}.`;
  // Meaningful banner contribution: at least 0.02 absolute AND at least
  // half of the banner-state CLS (iprima: 0.0287 of 0.03). Below that it is
  // a statement, not an alarm.
  const significant = bannerShift >= 0.02 && withB > 0 && bannerShift / withB >= 0.5;
  if (!significant) {
    // "The banner did not contribute" may only be claimed when the two
    // total-CLS states actually agree. bannerShift measures only the shift
    // credited directly to the banner element — when the totals differ
    // markedly (e.g. 1.714 vs 0.316), something else changed between the
    // states and the data does not say what; state the facts, no causal claim.
    const statesAgree = Math.abs(withB - afterC) < 0.05;
    if (statesAgree) {
      return {
        isFinding: false,
        text: base + (cs
          ? ' V tomto běhu cookie banner k posunům layoutu znatelně nepřispěl.'
          : ' In this run the cookie banner did not contribute noticeably to layout shifting.')
      };
    }
    return {
      isFinding: false,
      text: base + (cs
        ? ` Posun připsaný přímo elementu lišty je ${fmtShift(bannerShift)}. Z čeho plyne rozdíl mezi oběma stavy, z tohoto měření neplyne.`
        : ` The shift credited directly to the banner element is ${fmtShift(bannerShift)}. What causes the difference between the two states cannot be determined from this measurement.`)
    };
  }
  const src = (bc.bannerSources || [])[0];
  const srcName = src ? (src.friendlyName || src.selector) : null;
  return {
    isFinding: true,
    text: base + (cs
      ? ` Cookie banner je zdroj posunu layoutu (${fmtShift(bannerShift)}${srcName ? `, ${srcName}` : ''}) — reální uživatelé banner vidí a toto skákání zažijí (field CLS ho obsahuje). Řešení: zobrazit banner jako overlay mimo tok stránky (position: fixed), aby netlačil obsah. Vlastník: privacy / správce CMP.`
      : ` The cookie banner is a layout-shift source (${fmtShift(bannerShift)}${srcName ? `, ${srcName}` : ''}) — real users see the banner and experience this shifting (field CLS includes it). Fix: render the banner as an overlay outside the page flow (position: fixed) so it does not push content. Owner: privacy / CMP owner.`)
  };
};

// Note appended to a culprit finding whose shift also propagated into a page
// container — one physical shift, attributed twice.
const clsContainerNote = (f, cs) => f.pushedContainers
  ? (cs
    ? ` Posun se propsal i do kontejneru ${f.pushedContainers.map(c => `${c.selector} (${fmtShift(c.totalShift)})`).join(', ')} — nejspíš tentýž fyzický posun připsaný dvakrát; úkol patří tomuto viníkovi, ne kontejneru.`
    : ` The shift also propagated into the container ${f.pushedContainers.map(c => `${c.selector} (${fmtShift(c.totalShift)})`).join(', ')} — most likely the same physical shift attributed twice; the task belongs to this culprit, not the container.`)
  : '';

// Whether a metric's deep section belongs in this report: single-metric
// reports stay strictly scoped to the audited metric (a stale deep payload
// from panel state must not contradict the headline); the unified CWV report
// presence-gates — every deep layer it carries renders.
const deepSectionAllowed = (r, metric) => r.unified || r.metricLower === metric;

// Loose URL identity for display decisions (protocol/trailing-slash/case).
const normUrlLoose = (u) => String(u || '').replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();

// Editorial-paper section for the audit export.
const renderClsFindingsAudit = (r) => {
  if (!deepSectionAllowed(r, 'cls')) return '';
  const findings = buildClsFindings(r);
  const lang = r.lang;
  const cs = lang === 'cs';
  const phaseNote = clsPhaseNote(r.deepCLSResults?.phases, cs);
  const bannerNote = clsBannerCompareNote(r, cs);
  // The banner comparison is evidence in its own right — a clean
  // post-consent run (no element findings) with a shifting banner must
  // still render the dual-state section.
  if (!findings.length && !bannerNote) return '';
  const rows = findings.map((f, i) => `
    <div style="margin-top:14px;padding:14px 18px;background:var(--paper-2);border-left:3px solid ${f.owner?.color || 'var(--ink)'};">
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;flex-wrap:wrap;">
        <div style="font-family:var(--mono);font-size:12px;font-weight:700;color:var(--ink);">
          ${String(i + 1).padStart(2, '0')} · ${escapeHtml(f.causeLabel)}
        </div>
        <div style="font-family:var(--mono);font-size:12px;color:var(--ink);">
          ${cs ? 'posun' : 'shift'} ${fmtShift(f.shift)}${f.share != null ? ` (${f.share} % ${cs ? 'celkového CLS' : 'of total CLS'})` : ''}${f.shiftCount > 1 ? ` (${f.shiftCount}×)` : ''}
          ${f.owner ? ` · <span style="padding:1px 6px;border:1px solid var(--ink);border-radius:2px;">${cs ? 'vlastník' : 'owner'}: ${escapeHtml(f.owner.label)}</span>` : ''}
        </div>
      </div>
      <div style="font-family:var(--mono);font-size:11px;color:#666;margin-top:6px;word-break:break-all;">${escapeHtml(f.displayName || f.selector || f.name)}</div>
      <div style="font-family:var(--serif);font-size:13px;line-height:1.55;color:var(--ink);margin-top:8px;">
        ${cs
          ? `Element posouvá layout celkem o ${fmtShift(f.shift)}${f.timing != null ? ` (první posun ~${fmtNum(f.timing)} ms po načtení)` : ''}. ${escapeHtml(f.advice)}`
          : `The element shifts the layout by ${fmtShift(f.shift)} in total${f.timing != null ? ` (first shift ~${fmtNum(f.timing)} ms after load)` : ''}. ${escapeHtml(f.advice)}`}${escapeHtml(clsContainerNote(f, cs))}
      </div>
    </div>`).join('');
  return `
  <section>
    <div class="section-label">${cs ? 'Diagnostika CLS' : 'CLS diagnostics'}</div>
    <h2 class="section-title">${cs ? 'Co přesně ' : 'What exactly '}<em>${cs ? 'posouvá layout' : 'shifts the layout'}</em>.</h2>
    <p class="lead">${escapeHtml(clsFieldFirstLine(r))} ${cs
      ? 'Každý nález níže je konkrétní element zachycený při posunu (layout-shift sources) — s příčinou, vlastníkem a opravou.'
      : 'Every finding below is a concrete element captured while shifting (layout-shift sources) — with a cause, an owner and a fix.'}</p>
    ${(() => { const dl = clsDominanceLine(findings, cs); return dl ? `<div class="callout" style="margin-top:12px;"><strong>${cs ? 'Poměr viníků' : 'Culprit share'}</strong> ${escapeHtml(dl)}</div>` : ''; })()}
    ${findings.some(f => f.share != null) ? `<div style="font-family:var(--mono);font-size:10px;color:#666;margin-top:10px;">${escapeHtml(clsShareCaveat(cs, findings[0]?.apportioned))}</div>` : ''}
    ${rows}
    ${bannerNote ? `<div class="callout" style="margin-top:16px;${bannerNote.isFinding ? 'border-left:3px solid var(--accent);' : ''}"><strong>${cs ? 'Cookie banner a CLS' : 'Cookie banner and CLS'}</strong> ${escapeHtml(bannerNote.text)}</div>` : ''}
    ${phaseNote ? `<div class="callout" style="margin-top:16px;"><strong>${cs ? 'Kdy k posunům došlo' : 'When the shifts happened'}</strong> ${escapeHtml(phaseNote)}</div>` : ''}
  </section>`;
};

// Glassmorphic panel for the dashboard-style export. Same scoping rule as
// the audit variant.
const renderClsFindingsMockup = (r) => {
  if (!deepSectionAllowed(r, 'cls')) return '';
  const findings = buildClsFindings(r);
  const cs = r.lang === 'cs';
  // Same rule as the audit template: banner comparison renders even when
  // the consented run left no ordinary findings.
  if (!findings.length && !clsBannerCompareNote(r, cs)) return '';
  return `
  <div class="glass panel" style="margin-bottom:24px;">
    <div class="panel-header">
      <div class="panel-title">${cs ? 'Co přesně posouvá layout' : 'What exactly shifts the layout'}</div>
      <div class="panel-subtitle">${(() => {
        const field = fieldClsValue(r);
        const lab = fmtShift(r.deepCLSResults?.cls);
        return field != null
          ? (cs ? `CLS field p75 ${fmtShift(field)} · lab ${lab} (horní odhad)` : `CLS field p75 ${fmtShift(field)} · lab ${lab} (upper estimate)`)
          : `CLS ${lab}`;
      })()}</div>
    </div>
    ${(() => { const dl = clsDominanceLine(findings, cs); return dl ? `<div style="margin-bottom:12px;padding:12px 14px;border:1px solid rgba(56,189,248,0.25);border-radius:10px;font-size:12px;color:#e2e8f0;"><strong>${cs ? 'Poměr viníků' : 'Culprit share'}</strong> ${escapeHtml(dl)}</div>` : ''; })()}
    ${findings.some(f => f.share != null) ? `<div style="font-size:10px;color:#64748b;margin-bottom:10px;">${escapeHtml(clsShareCaveat(cs, findings[0]?.apportioned))}</div>` : ''}
    ${findings.map((f, i) => `
    <div style="background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:12px 16px;margin-top:${i ? 10 : 0}px;">
      <div style="display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;">
        <div style="font-size:13px;font-weight:600;color:#f8fafc;">${String(i + 1).padStart(2, '0')} · ${escapeHtml(f.causeLabel)}</div>
        <div style="font-family:'JetBrains Mono',monospace;font-size:11px;color:#e2e8f0;">
          ${cs ? 'posun' : 'shift'} ${fmtShift(f.shift)}${f.share != null ? ` (${f.share} % ${cs ? 'celkového CLS' : 'of total CLS'})` : ''}${f.shiftCount > 1 ? ` (${f.shiftCount}×)` : ''}
          ${f.owner ? ` <span style="margin-left:6px;padding:2px 7px;border-radius:4px;border:1px solid ${f.owner.color}55;color:${f.owner.color};text-transform:uppercase;font-size:10px;letter-spacing:0.05em;">${cs ? 'vlastník' : 'owner'}: ${escapeHtml(f.owner.label)}</span>` : ''}
        </div>
      </div>
      <div style="font-family:'JetBrains Mono',monospace;font-size:10px;color:#64748b;margin-top:5px;word-break:break-all;">${escapeHtml(f.displayName || f.selector || f.name)}</div>
      <div style="font-size:12px;color:#94a3b8;line-height:1.55;margin-top:7px;">${escapeHtml(f.advice)}${escapeHtml(clsContainerNote(f, cs))}</div>
    </div>`).join('')}
    ${(() => { const bn = clsBannerCompareNote(r, cs); return bn ? `<div style="margin-top:14px;padding:12px 14px;border:1px solid ${bn.isFinding ? 'rgba(239,68,68,0.45)' : 'rgba(56,189,248,0.25)'};border-radius:10px;font-size:12px;color:#e2e8f0;"><strong>${cs ? 'Cookie banner a CLS' : 'Cookie banner and CLS'}</strong> ${escapeHtml(bn.text)}</div>` : ''; })()}
    ${(() => { const pn = clsPhaseNote(r.deepCLSResults?.phases, cs); return pn ? `<div style="margin-top:14px;padding:12px 14px;border:1px solid rgba(56,189,248,0.25);border-radius:10px;font-size:12px;color:#e2e8f0;"><strong>${cs ? 'Kdy k posunům došlo' : 'When the shifts happened'}</strong> ${escapeHtml(pn)}</div>` : ''; })()}
  </div>`;
};

// Carried-INP breakdown section for unified reports where INP is NOT the
// primary metric. The primary-INP story lives in the scenario copy; this
// section makes sure a measured INP deep run never disappears from a
// CLS/LCP-primary unified report. Field-first: real-user p75 leads, the lab
// interaction is one specific run.
// Compact summary of a carried (non-primary) metric's multi-page sweep —
// one honest line: pages measured, average, worst page. Metric-matched only.
// Full stats — SAME shape and semantics as the primary metric's multiPage
// block (distribution, median, range, worst page, confidence). A carried
// metric's site-wide sweep must not be demoted to a one-line average while
// the primary metric gets the full table.
const carriedMpSummary = (r, metricUpper) => {
  const mp = r.multiPageByMetric?.[metricUpper];
  const raw = mp?.results?.length ? mp.results : (mp?.pages?.length ? mp.pages : null);
  if (!raw) return null;
  const valOf = (p) => {
    const v = p?.value ?? p?.inp ?? p?.lcp ?? p?.cls;
    return (v == null || isNaN(v)) ? null : Number(v);
  };
  const valid = raw.filter(p => !p.error && valOf(p) != null);
  if (!valid.length) return null;
  const metricLower = metricUpper.toLowerCase();
  const avg = valid.reduce((s, p) => s + valOf(p), 0) / valid.length;
  const sortedVals = valid.map(valOf).sort((a, b) => a - b);
  const mid = Math.floor(sortedVals.length / 2);
  const median = sortedVals.length % 2 ? sortedVals[mid] : (sortedVals[mid - 1] + sortedVals[mid]) / 2;
  const worst = valid.reduce((w, p) => valOf(p) > valOf(w) ? p : w, valid[0]);
  const best = valid.reduce((b, p) => valOf(p) < valOf(b) ? p : b, valid[0]);
  const statuses = valid.map(p => statusOf(metricLower, valOf(p)));
  return {
    pages: valid.length,
    avg,
    median,
    goodCount: statuses.filter(s => s === 'good').length,
    warnCount: statuses.filter(s => s === 'warning').length,
    poorCount: statuses.filter(s => s === 'critical').length,
    bestValue: valOf(best),
    worstUrl: worst.url,
    worstValue: valOf(worst),
    worstConfidence: Number(worst.validRuns) >= 3 ? 'median-confirmed'
      : Number(worst.runs) > 1 ? 'repeated-incomplete'
      : Number(worst.runs) === 1 ? 'single-run' : null,
    worstRuns: sweepRunCounts(worst).calls,
    worstValidRuns: sweepRunCounts(worst).valid,
    worstInvalidRuns: sweepRunCounts(worst).invalid,
    worstSkippedRuns: sweepRunCounts(worst).skipped,
    worstInternalRetries: sweepRunCounts(worst).retries,
    worstHasLedger: sweepRunCounts(worst).ledger,
    repeatedPages: valid.filter(p => Number(p.runs) > 1).length
  };
};

const incompleteSweepNote = (mp, cs) => {
  const attempts = Number(mp.worstRuns);
  const valid = Number(mp.worstValidRuns);
  if (!Number.isFinite(attempts) || !Number.isFinite(valid) || attempts <= 1) return '';
  const invalid = mp.worstInvalidRuns != null && Number.isFinite(Number(mp.worstInvalidRuns))
    ? Number(mp.worstInvalidRuns) : attempts - valid;
  const otherPages = Number(mp.repeatedPages) > 1
    ? (cs ? 'I další stránky mají opakované běhy.' : 'Other pages also have repeated runs.')
    : (cs ? 'Počet opakování dalších stránek ověřujte jednotlivě.' : 'Check repeat counts for other pages individually.');
  const skipped = Number(mp.worstSkippedRuns) || 0;
  const retries = Number(mp.worstInternalRetries) || 0;
  const counts = mp.worstHasLedger
    ? (cs
      ? `${attempts} spuštěné běhy (${valid} validní, ${invalid} nevalidní)${skipped ? `; ${skipped} plánovaný pokus nebyl spuštěn (časový limit)` : ''}${retries ? `; ${retries} běh obsahoval vnitřní opakování, které není dalším sweep během` : ''}`
      : `${attempts} launched runs (${valid} valid, ${invalid} invalid)${skipped ? `; ${skipped} planned attempt was not launched (time budget)` : ''}${retries ? `; ${retries} run included an internal retry, not another sweep run` : ''}`)
    : (cs
      ? `${attempts} pokusů, ${valid} validní${invalid > 0 ? `, ${invalid} nevalidní` : ''}`
      : `${attempts} attempts, ${valid} valid${invalid > 0 ? `, ${invalid} invalid` : ''}`);
  return cs
    ? `Nejhorší stránka: ${counts}. Méně než 3 validní běhy — výsledek je orientační, nikoli potvrzený medián. ${otherPages}`
    : `Worst page: ${counts}. Fewer than 3 valid runs — indicative, not a confirmed median. ${otherPages}`;
};

// Shared full "site-wide" paragraph for a carried metric — mirrors the
// primary multiPageBlock wording so both metrics read at the same level.
const carriedMpLines = (r, metricUpper, deepValue) => {
  const mp = carriedMpSummary(r, metricUpper);
  if (!mp) return null;
  const cs = r.lang === 'cs';
  const fmt = (v) => formatMetricValue(metricUpper.toLowerCase(), v) || '—';
  const lines = [];
  lines.push(cs
    ? `${metricUpper} napříč webem — měřeno na ${mp.pages} stránkách. Rozložení: ${mp.goodCount}× dobré · ${mp.warnCount}× k zlepšení · ${mp.poorCount}× špatné. Medián ${fmt(mp.median)} (průměr ${fmt(mp.avg)}), rozsah ${fmt(mp.bestValue)} – ${fmt(mp.worstValue)}. Nejhorší stránka: ${mp.worstUrl || '—'}.`
    : `${metricUpper} site-wide — measured on ${mp.pages} pages. Distribution: ${mp.goodCount}× good · ${mp.warnCount}× needs improvement · ${mp.poorCount}× poor. Median ${fmt(mp.median)} (average ${fmt(mp.avg)}), range ${fmt(mp.bestValue)} – ${fmt(mp.worstValue)}. Worst page: ${mp.worstUrl || '—'}.`);
  // Same-URL variance honesty note, same rule as the primary block.
  const normU = (u) => String(u || '').replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();
  // Carried (non-primary) deep layers always ran on the entry URL — the
  // primary metric's deep may have been promoted elsewhere, so the note must
  // name the page instead of pointing at "the deep test above".
  if (deepValue != null && mp.worstUrl && normU(mp.worstUrl) === normU(r.url)) {
    lines.push(cs
      ? `Pozn.: hloubkový test ${metricUpper} běžel na téže URL jako tato nejhorší stránka — ${mp.worstUrl} (${fmt(deepValue)}). Rozdíl mezi hodnotami je rozptyl dvou nezávislých běhů téže stránky.`
      : `Note: the ${metricUpper} deep test ran on the same URL as this worst page — ${mp.worstUrl} (${fmt(deepValue)}). The difference between the values is run-to-run variance of the same page.`);
  }
  if (mp.worstConfidence === 'median-confirmed') {
    lines.push(cs
      ? `Nejhorší stránka byla přeměřena ${mp.worstRuns || 5}× opakovanými mělkými (sweep) běhy — hodnota je medián z ${mp.worstValidRuns || mp.worstRuns || 5} validních běhů, nikoli verdikt o každém z nich. Případný hloubkový diagnostický běh je samostatná vrstva měření.`
      : `The worst page was re-measured ${mp.worstRuns || 5}× with repeated shallow (sweep) runs — the value is the median of ${mp.worstValidRuns || mp.worstRuns || 5} valid runs, not a verdict on each one. Any deep diagnostic run is a separate measurement layer.`);
  } else if (mp.worstConfidence === 'single-run') {
    lines.push(cs
      ? 'Nejhorší stránka má jeden běh — hodnotu berte orientačně. Počet opakování ostatních stránek se může lišit.'
      : 'The worst page has one run — treat its value as indicative. Other pages may have different run counts.');
  } else if (mp.worstConfidence === 'repeated-incomplete') {
    lines.push(incompleteSweepNote(mp, cs));
  }
  return lines;
};

// Per-device deep runs comparison (complex audit measures every
// above-threshold metric × device pair sequentially).
const DEVICE_LABELS = { phone: { cs: 'Mobil', en: 'Mobile' }, tablet: { cs: 'Tablet', en: 'Tablet' }, desktop: { cs: 'Desktop', en: 'Desktop' } };
const fmtMetricValue = (metric, v) => v == null ? '—'
  : metric === 'CLS' ? Number(v).toFixed(3) : `${fmtNum(Math.round(Number(v)))} ms`;

// Per-device culprit comparison: for every metric with ≥2 successful device
// deep runs carrying full results, decide whether the devices share ONE
// culprit (one fix covers both) or differ (each device needs its own task) —
// and explain WHY the worse device is worse by naming its culprit.
const deviceCulpritGroups = (r) => {
  const runs = (r.deviceRuns || []).filter(d => d && d.success && d.result);
  if (!runs.length) return [];
  const cs = r.lang === 'cs';
  const byMetric = new Map();
  for (const d of runs) {
    const m = String(d.metric).toUpperCase();
    if (!['CLS', 'INP', 'LCP', 'TTFB'].includes(m)) continue;
    if (!byMetric.has(m)) byMetric.set(m, []);
    byMetric.get(m).push(d);
  }
  const groups = [];
  for (const [m, list] of byMetric) {
    if (list.length < 2) continue;
    const entries = list.map(d => ({
      device: d.device,
      deviceLabel: DEVICE_LABELS[d.strategy === 'mobile' ? 'phone' : d.strategy]?.[cs ? 'cs' : 'en'] || d.device,
      primary: !!d.primary,
      share: Number.isFinite(Number(d.deviceShare)) ? Number(d.deviceShare) : null,
      value: deepValFor(m, d.result),
      culprit: culpritTextFor(m, d.result, cs),
      culpritKey: culpritKeyFor(m, d.result)
    })).filter(e => e.value != null);
    if (entries.length < 2) continue;
    entries.sort((a, b) => b.value - a.value); // worst first
    // No verdict when even the worst lab run landed in the good band — the
    // lab did not reproduce the field problem, so comparing lab culprits
    // across devices would be a conclusion drawn from noise.
    if (statusOf(m.toLowerCase(), entries[0].value) === 'good') continue;
    const keyed = entries.filter(e => e.culpritKey);
    const agree = keyed.length >= 2 && keyed.every(e => e.culpritKey === keyed[0].culpritKey);
    groups.push({ m, cs, entries, agree: keyed.length >= 2 ? agree : null, worst: entries[0] });
  }
  return groups;
};

// One-line cross-device verdict per metric group.
const deviceConsensusLine = (g) => {
  const devList = g.entries.map(e => e.deviceLabel.toLowerCase()).join(cz(g, ' a ', ' and '));
  function cz(gr, a, b) { return gr.cs ? a : b; }
  const fmt = (v) => formatMetricValue(g.m.toLowerCase(), v) || '—';
  if (g.agree === true) {
    return g.cs
      ? `${g.m}: shodný kandidát na ${devList} (${g.entries.map(e => `${e.deviceLabel} ${fmt(e.value)}`).join(' · ')}). Rozdíl na ${g.worst.deviceLabel.toLowerCase()} může souviset s CPU/sítí, ale příčinu ani účinek jedné opravy tato čísla neprokazují.`
      : `${g.m}: matching candidate on ${devList} (${g.entries.map(e => `${e.deviceLabel} ${fmt(e.value)}`).join(' · ')}). The gap on ${g.worst.deviceLabel.toLowerCase()} may reflect CPU/network differences, but these numbers prove neither the cause nor the effect of one fix.`;
  }
  if (g.agree === false) {
    const lines = g.entries.filter(e => e.culprit).map(e => `${e.deviceLabel}: ${e.culprit}`);
    return g.cs
      ? `${g.m}: viníci se mezi zařízeními liší — ${lines.join(' · ')}. Každé zařízení potřebuje vlastní opravu.`
      : `${g.m}: culprits differ between devices — ${lines.join(' · ')}. Each device needs its own fix.`;
  }
  return null;
};

// Two lab values for the same metric+device can legitimately appear in one
// report (header narrative vs this table) — they come from different sources.
// Every lab value needs its source stated, and the dominant-phase text must
// say which value it belongs to, or the report contradicts itself.
const deviceRunsProvenanceNote = (r) => {
  const cs = r.lang === 'cs';
  const norm = (u) => String(u || '').replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase();
  const promoted = r.deepAnalyzedUrl && norm(r.deepAnalyzedUrl) !== norm(r.url);
  const spread = r.metricLower === 'inp' ? r.deepINPResults?.labRunSpread
    : r.metricLower === 'lcp' ? r.deepLCPResults?.labRunSpread
      : r.metricLower === 'ttfb' ? r.deepTTFBResults?.labRunSpread
        : r.deepCLSResults?.labRunSpread;
  const headerBasis = labSeriesIsVerified(spread)
    ? (cs
      ? `ověřený medián z ${spread.runs} validních běhů`
      : `verified median from ${spread.runs} valid runs`)
    : spread?.runs >= 2
      ? (cs
        ? `medián z ${spread.runs} validních srovnatelných běhů; jistota diagnostiky omezená`
        : `median from ${spread.runs} valid comparable runs; limited diagnostic confidence`)
      : (cs ? 'jeden orientační běh' : 'one indicative run');
  if (cs) {
    return promoted
      ? `Zdroj hodnot: tabulka níže = jeden hloubkový běh tohoto auditu na vstupní stránce (${r.url}) pro každé zařízení. Hodnota a rozpad fází v hlavičce reportu pochází z vybrané hloubkově měřené stránky (${r.deepAnalyzedUrl}) — dominantní fáze uvedená v hlavičce se vztahuje k ní, ne k řádkům této tabulky. Fáze u jednotlivých zařízení níže patří běhům na vstupní stránce.`
      : `Zdroj hodnot: tabulka níže = jeden hloubkový běh tohoto auditu na vstupní stránce (${r.url}) pro každé zařízení. Hodnota v hlavičce reportu je ${headerBasis} téže stránky — proto se může od jednotlivého běhu v tabulce lišit. Zdroj konkrétního rozpadu fází označuje vrstva jednotlivých běhů; fáze u zařízení níže patří běhům této tabulky.`;
  }
  return promoted
    ? `Value sources: the table below = one deep run of this audit on the entry page (${r.url}) per device. The value and phase breakdown in the report header come from the selected deep-measured page (${r.deepAnalyzedUrl}) — the dominant phase stated in the header refers to that page, not to the rows of this table. Phases next to individual devices below belong to the entry-page runs.`
    : `Value sources: the table below = one deep run of this audit on the entry page (${r.url}) per device. The report header uses the ${headerBasis} for the same page, so it can differ from a single table row. The individual-run layer identifies the exact phase-breakdown source; per-device phases below belong to this table's runs.`;
};

const renderDeviceRunsRows = (r) => {
  const runs = (r.deviceRuns || []).filter(d => ['CLS', 'INP', 'LCP', 'TTFB'].includes(String(d.metric).toUpperCase()));
  if (runs.length < 2) return null;
  const cs = r.lang === 'cs';
  return runs.map(d => {
    const m = String(d.metric).toUpperCase();
    const dev = DEVICE_LABELS[d.device]?.[cs ? 'cs' : 'en'] || d.device;
    const invalidInp = m === 'INP' && (!d.success || Number(d.value) <= 0
      || d.noValidInteraction === true || d.interactionMeasured === false
      || d.result?.noValidInteraction === true || d.result?.interactionMeasured === false);
    return {
      metric: m,
      device: dev,
      lab: invalidInp ? (cs ? 'nezměřeno' : 'not measured') : (d.success ? fmtMetricValue(m, d.value) : (cs ? 'selhalo' : 'failed')),
      field: d.fieldP75 != null ? fmtMetricValue(m, d.fieldP75) : '—',
      failed: !d.success || invalidInp
    };
  });
};

const renderDeviceRunsAudit = (r) => {
  const rows = renderDeviceRunsRows(r);
  if (!rows) return '';
  const cs = r.lang === 'cs';
  return `
  <section>
    <div class="section-label">${cs ? 'Srovnání zařízení' : 'Device comparison'}</div>
    <h2 class="section-title">${cs ? 'Hloubkové běhy ' : 'Deep runs '}<em>${cs ? 'podle zařízení' : 'per device'}</em>.</h2>
    <p class="lead">${cs
      ? 'Každá metrika nad prahem se měřila samostatným laboratorním během pro každé zasažené zařízení — jeden běh za druhým. Stav souhlasu je nutné ověřit zvlášť pro každý běh.'
      : 'Each above-threshold metric was measured with its own lab run per affected device — sequentially. Consent status must be checked separately for each run.'}</p>
    ${deviceRunsProvenanceNote(r) ? `<div class="callout" style="margin-top:10px;">${escapeHtml(deviceRunsProvenanceNote(r))}</div>` : ''}
    <table style="width:100%;border-collapse:collapse;margin-top:12px;font-size:13px;">
      <thead><tr>
        <th style="text-align:left;border-bottom:1px solid #ccc;padding:8px 10px;font-family:var(--mono);font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#666;">${cs ? 'Metrika' : 'Metric'}</th>
        <th style="text-align:left;border-bottom:1px solid #ccc;padding:8px 10px;font-family:var(--mono);font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#666;">${cs ? 'Zařízení' : 'Device'}</th>
        <th style="text-align:right;border-bottom:1px solid #ccc;padding:8px 10px;font-family:var(--mono);font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#666;">${cs ? 'Lab · vstupní stránka' : 'Lab · entry page'}</th>
        <th style="text-align:right;border-bottom:1px solid #ccc;padding:8px 10px;font-family:var(--mono);font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#666;">field p75</th>
      </tr></thead>
      <tbody>
        ${rows.map(row => `<tr>
          <td style="padding:8px 10px;border-bottom:1px solid #eee;font-weight:600;">${escapeHtml(row.metric)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #eee;">${escapeHtml(row.device)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;font-family:var(--mono);${row.failed ? 'color:#b91c1c;' : ''}">${escapeHtml(row.lab)}</td>
          <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;font-family:var(--mono);color:#666;">${escapeHtml(row.field)}</td>
        </tr>`).join('')}
      </tbody>
    </table>
    ${deviceRunsMismatchLines(r).map(line => `
    <div class="callout" style="margin-top:14px;">${escapeHtml(line)}</div>`).join('')}
    ${deviceCulpritGroups(r).map(g => {
      const line = deviceConsensusLine(g);
      return line ? `
    <div class="callout" style="margin-top:14px;">
      <strong>${cs ? 'Viník podle zařízení' : 'Culprit per device'}</strong>
      ${escapeHtml(line)}
      ${g.agree === false ? '' : g.entries.filter(e => e.culprit).map(e => `<div style="margin-top:6px;font-size:12px;">${escapeHtml(`${e.deviceLabel}: ${e.culprit}`)}</div>`).join('')}
    </div>` : '';
    }).join('')}
  </section>`;
};

const renderDeviceRunsMockup = (r) => {
  const rows = renderDeviceRunsRows(r);
  if (!rows) return '';
  const cs = r.lang === 'cs';
  return `
  <div class="glass panel" style="margin-bottom:24px;">
    <div class="panel-header">
      <div class="panel-title">${cs ? 'Srovnání zařízení (hloubkové běhy)' : 'Device comparison (deep runs)'}</div>
      <div class="panel-subtitle">${cs ? 'jeden laboratorní běh na metriku a zařízení' : 'one lab run per metric and device'}</div>
    </div>
    <div style="margin-bottom:12px;padding:12px 14px;border:1px solid rgba(100,116,139,0.25);border-radius:10px;font-size:12px;color:#94a3b8;">${escapeHtml(deviceRunsProvenanceNote(r))}</div>
    <div style="display:flex;flex-direction:column;gap:8px;">
      ${rows.map(row => `
      <div style="display:flex;align-items:center;gap:12px;background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:10px 14px;">
        <span style="font-weight:600;color:#f8fafc;min-width:44px;">${escapeHtml(row.metric)}</span>
        <span style="color:#94a3b8;min-width:70px;">${escapeHtml(row.device)}</span>
        <span style="font-family:'JetBrains Mono',monospace;color:${row.failed ? '#f87171' : '#f8fafc'};margin-left:auto;">${escapeHtml(row.lab)}</span>
        <span style="font-family:'JetBrains Mono',monospace;color:#64748b;font-size:11px;">field ${escapeHtml(row.field)}</span>
      </div>`).join('')}
    </div>
    ${deviceRunsMismatchLines(r).map(line => `
    <div style="margin-top:12px;padding:12px 14px;border:1px solid rgba(251,191,36,0.3);border-radius:10px;font-size:12px;color:#cbd5e1;">${escapeHtml(line)}</div>`).join('')}
    ${deviceCulpritGroups(r).map(g => {
      const line = deviceConsensusLine(g);
      return line ? `
    <div style="margin-top:12px;padding:12px 14px;border:1px solid rgba(56,189,248,0.25);border-radius:10px;font-size:12px;color:#e2e8f0;">
      <strong>${cs ? 'Viník podle zařízení' : 'Culprit per device'}</strong>
      <div style="margin-top:4px;color:#cbd5e1;">${escapeHtml(line)}</div>
      ${g.agree === false ? '' : g.entries.filter(e => e.culprit).map(e => `<div style="margin-top:4px;color:#94a3b8;font-size:11px;">${escapeHtml(`${e.deviceLabel}: ${e.culprit}`)}</div>`).join('')}
    </div>` : '';
    }).join('')}
  </div>`;
};

// INP interaction target arrives as an object ({tagName,id,className}) —
// render it as a CSS-like selector, never String(object) ("[object Object]").
const targetSelectorText = (t) => {
  if (!t) return null;
  if (typeof t === 'string') return t.startsWith('[object') ? null : t;
  const tag = String(t.tagName || '').toLowerCase();
  const id = t.id ? `#${t.id}` : '';
  // className must be a plain string — SVG targets (SVGAnimatedString) or a
  // pre-serialized object would yield garbage like "path.[object". A partial
  // selector is worse than none: return null and let the caller say
  // "nelze určit" instead.
  const clsStr = typeof t.className === 'string' && !t.className.startsWith('[object') ? t.className.trim() : '';
  const cls = !id && clsStr ? `.${clsStr.split(/\s+/)[0]}` : '';
  const sel = `${tag}${id}${cls}`;
  return sel || null;
};

// "Měřený prvek" line under the INP breakdown. Two rules:
// 1. When the lab run did NOT reproduce the field problem (field poor, lab
//    green), the measured element has no diagnostic value — omit the line.
// 2. When a selector cannot be built, say "nelze určit" — never a partial
//    or garbled string.
const inpTargetLine = (c, cs) => {
  const t = metricThresholds.inp;
  const seriesReproduced = (c.spread?.values || []).some(value => Number(value) > t.good);
  const notReproduced = !seriesReproduced
    && c.fieldP75 != null && Number(c.fieldP75) > t.good && Number(c.inp) <= t.good;
  if (notReproduced) return null;
  return targetSelectorText(c.target) || (cs ? 'nelze určit' : 'cannot be determined');
};

// When real users see the metric above threshold but the lab run lands in
// the green band, the report must EXPLAIN the gap instead of staying silent —
// the no-verdict guard is honest, but the reader needs to know why field 486
// has no lab culprit. Applies to every metric.
export const labFieldMismatchLine = (mUp, fieldVal, labVal, cs, spread = null) => {
  const t = metricThresholds[String(mUp).toLowerCase()];
  if (!t || fieldVal == null || labVal == null) return null;
  if (!(Number(fieldVal) > t.good && Number(labVal) <= t.good)) return null;
  const fmt = (v) => formatMetricValue(String(mUp).toLowerCase(), v) || '—';
  const validValues = (spread?.values || []).map(Number).filter(Number.isFinite);
  const overThreshold = validValues.filter(value => value > t.good).length;
  if (String(mUp).toUpperCase() === 'INP' && overThreshold > 0) {
    return cs
      ? `INP u reálných uživatelů je nad prahem (field ${fmt(fieldVal)}). Medián laboratorní série je ${fmt(labVal)}, ale problém se reprodukoval přerušovaně: ${overThreshold} z ${validValues.length} validních běhů překročil${overThreshold === 1 ? '' : 'y'} práh.${inpOutlierNote(spread, 'cs')} Série nepotvrzuje konkrétní příčinu.`
      : `Real-user INP is over threshold (field ${fmt(fieldVal)}). The lab-series median is ${fmt(labVal)}, but the problem reproduced intermittently: ${overThreshold} of ${validValues.length} valid runs exceeded the threshold.${inpOutlierNote(spread, 'en')} The series does not confirm a specific cause.`;
  }
  // Důvod nereprodukce je vázaný na mechanismus metriky (metricCopy) —
  // "reklama, interakce, zařízení" u TTFB byl šestý výskyt téže chyby.
  // Chybí-li důvod v mapě, věta konstatuje jen fakt neshody (pojistka:
  // prázdno místo cizího mechanismu).
  const reason = metricCopy(mUp, 'notReproducedReason', cs ? 'cs' : 'en');
  const reasonPart = reason ? ` — ${reason}` : '';
  return cs
    ? `${mUp} u reálných uživatelů je vysoký (field ${fmt(fieldVal)}), ale laboratorní test ho nereprodukuje (${fmt(labVal)})${reasonPart}. Rozhodující je field; lab tu konkrétní příčinu neurčí.`
    : `${mUp} for real users is high (field ${fmt(fieldVal)}), but the lab test does not reproduce it (${fmt(labVal)})${reasonPart}. The field value decides; the lab cannot pinpoint the specific cause here.`;
};

// Per-metric mismatch lines derived from the device-run table: only when the
// metric's EVERY successful lab run is green while its field p75 is above
// threshold somewhere (worst field value shown, worst lab value shown).
const deviceRunsMismatchLines = (r) => {
  const cs = r.lang === 'cs';
  const byMetric = new Map();
  for (const d of (r.deviceRuns || [])) {
    if (!d || !d.success || d.value == null) continue;
    const m = String(d.metric).toUpperCase();
    if (m === 'INP' && (Number(d.value) <= 0
      || d.noValidInteraction === true || d.interactionMeasured === false
      || d.result?.noValidInteraction === true || d.result?.interactionMeasured === false)) continue;
    if (!byMetric.has(m)) byMetric.set(m, []);
    byMetric.get(m).push(d);
  }
  const lines = [];
  for (const [m, list] of byMetric) {
    const t = metricThresholds[m.toLowerCase()];
    if (!t) continue;
    for (const d of list) {
      const recordedDevice = d.result?.labRunSpread?.conditions?.device || d.result?.device;
      const normalize = value => String(value || '').toLowerCase().replace(/^phone$/, 'mobile');
      if (!d.device || (recordedDevice && normalize(recordedDevice) !== normalize(d.device))) continue;
      const spread = d.result?.labRunSpread
        || (m === 'INP' ? r.deepINPResults?.labRunSpread : null);
      const line = labFieldMismatchLine(m, d.fieldP75, d.value, cs, spread);
      if (line) lines.push(`${deviceLabel(d.device, r.lang)}: ${line}`);
    }
  }
  return lines;
};

const renderInpFindingsAudit = (r) => {
  const c = getCarriedInp(r);
  if (!c) return '';
  const cs = r.lang === 'cs';
  const labNote = c.spread?.runs >= 2
    ? (labSeriesIsVerified(c.spread)
      ? (cs ? `medián ${c.spread.runs} běhů` : `median of ${c.spread.runs} runs`)
      : (cs ? `medián ${c.spread.runs} srovnatelných běhů; jistota diagnostiky omezená` : `median of ${c.spread.runs} comparable runs; limited diagnostic confidence`))
    : (cs ? 'jeden běh, orientační' : 'single run, indicative');
  const lead = c.fieldP75 != null
    ? (cs
      ? `INP · reální uživatelé: ${fmtNum(c.fieldP75)} ms (field p75) · laboratorní interakce ${fmtNum(c.inp)} ms (${labNote}).`
      : `INP · real users: ${fmtNum(c.fieldP75)} ms (field p75) · lab interaction ${fmtNum(c.inp)} ms (${labNote}).`)
    : (cs
      ? `INP · laboratorní interakce: ${fmtNum(c.inp)} ms (${labNote}).`
      : `INP · lab interaction: ${fmtNum(c.inp)} ms (${labNote}).`);
  const bd = c.breakdown;
  const basisNote = bd.basis === 'median'
    ? (cs ? `Rozpad níže je z běhu nejblíže mediánu (z ${bd.runs} uložených běhů), ne z nejhoršího běhu.` : `The breakdown below is from the run closest to the median (of ${bd.runs} stored runs), not the worst run.`)
    : (cs ? 'Rozpad níže je z jedné konkrétní zachycené interakce (nejhorší v běhu), ne typická hodnota.' : 'The breakdown below is one specific captured interaction (the worst in the run), not a typical value.');
  const bdRow = `
    <div style="display:flex;gap:16px;flex-wrap:wrap;margin-top:12px;">
      ${[
        [cs ? 'Zpoždění vstupu' : 'Input delay', bd.inputDelay],
        [cs ? 'Zpracování' : 'Processing', bd.processingTime],
        [cs ? 'Vykreslení' : 'Presentation', bd.presentationDelay]
      ].map(([label, ms]) => `
      <div style="flex:1;min-width:140px;border:1px solid #ddd;padding:12px 14px;">
        <div style="font-family:var(--mono);font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:#666;">${escapeHtml(label)}</div>
        <div style="font-family:var(--serif);font-size:20px;font-weight:600;color:var(--ink);margin-top:4px;">${fmtNum(ms)} ms</div>
      </div>`).join('')}
    </div>
    ${inpTargetLine(c, cs) ? `<div style="font-family:var(--mono);font-size:11px;color:#666;margin-top:8px;word-break:break-all;">${cs ? 'Měřený prvek' : 'Measured element'}: ${escapeHtml(inpTargetLine(c, cs))}</div>` : ''}
    ${labFieldMismatchLine('INP', c.fieldP75, c.inp, cs, c.spread) ? `<div class="callout" style="margin-top:12px;">${escapeHtml(labFieldMismatchLine('INP', c.fieldP75, c.inp, cs, c.spread))}</div>` : ''}`;
  const spreadNote = c.spread && c.spread.runs >= 2 && c.spread.min !== c.spread.max
    ? (labSeriesIsVerified(c.spread)
      ? (cs
        ? `Rozptyl ${c.spread.runs} uložených běhů: ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, medián ~${fmtNum(c.spread.median)} ms.`
        : `Spread across ${c.spread.runs} stored runs: ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, median ~${fmtNum(c.spread.median)} ms.`)
      : (cs
        ? `Medián srovnatelných běhů ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; jistota diagnostiky zůstává omezená.`
        : `Comparable-run median ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; diagnostic confidence remains limited.`))
    : '';
  return `
  <section>
    <div class="section-label">${cs ? 'Diagnostika INP' : 'INP diagnostics'}</div>
    <h2 class="section-title">${cs ? 'Odezva na interakci ' : 'Interaction response '}<em>${cs ? 'změřená ve stejném běhu' : 'measured in the same run'}</em>.</h2>
    <p class="lead">${escapeHtml(lead)} ${cs
      ? 'Rozpad ukazuje, kde se čas ztrácí — čekání na volné vlákno (vstup), běh skriptů (zpracování), nebo vykreslení výsledku.'
      : 'The breakdown shows where the time goes — waiting for a free main thread (input), script execution (processing), or rendering the result (presentation).'} ${escapeHtml(basisNote)}</p>
    ${bdRow}
    ${spreadNote ? `<div class="callout" style="margin-top:16px;"><strong>${cs ? 'Stabilita měření' : 'Measurement stability'}</strong> ${escapeHtml(spreadNote)}</div>` : ''}
    ${(() => {
      const lines = carriedMpLines(r, 'INP', c.inp);
      if (!lines) return '';
      return `<div class="callout" style="margin-top:12px;"><strong>${cs ? 'Napříč webem' : 'Site-wide'}</strong> ${lines.map(escapeHtml).join('<br><br>')}</div>`;
    })()}
  </section>`;
};

const renderInpFindingsMockup = (r) => {
  const c = getCarriedInp(r);
  if (!c) return '';
  const cs = r.lang === 'cs';
  const bd = c.breakdown;
  const basisTag = bd.basis === 'median'
    ? (cs ? `medián ${bd.runs} běhů` : `median of ${bd.runs} runs`)
    : (cs ? 'jeden běh' : 'single run');
  return `
  <div class="glass panel" style="margin-bottom:24px;">
    <div class="panel-header">
      <div class="panel-title">${cs ? 'Odezva na interakci (INP)' : 'Interaction response (INP)'}</div>
      <div class="panel-subtitle">${c.fieldP75 != null
        ? `field p75 ${fmtNum(c.fieldP75)} ms · lab ${fmtNum(c.inp)} ms (${basisTag})`
        : `lab ${fmtNum(c.inp)} ms (${basisTag})`}</div>
    </div>
    <div style="display:flex;gap:10px;flex-wrap:wrap;">
      ${[
        [cs ? 'Zpoždění vstupu' : 'Input delay', bd.inputDelay],
        [cs ? 'Zpracování' : 'Processing', bd.processingTime],
        [cs ? 'Vykreslení' : 'Presentation', bd.presentationDelay]
      ].map(([label, ms]) => `
      <div style="flex:1;min-width:120px;background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:12px 14px;">
        <div style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#64748b;">${escapeHtml(label)}</div>
        <div style="font-family:'JetBrains Mono',monospace;font-size:18px;font-weight:600;color:#f8fafc;margin-top:4px;">${fmtNum(ms)} ms</div>
      </div>`).join('')}
    </div>
    ${inpTargetLine(c, cs) ? `<div style="font-family:'JetBrains Mono',monospace;font-size:10px;color:#64748b;margin-top:8px;word-break:break-all;">${cs ? 'Měřený prvek' : 'Measured element'}: ${escapeHtml(inpTargetLine(c, cs))}</div>` : ''}
    ${labFieldMismatchLine('INP', c.fieldP75, c.inp, cs, c.spread) ? `<div style="margin-top:12px;padding:12px 14px;border:1px solid rgba(251,191,36,0.3);border-radius:10px;font-size:12px;color:#e2e8f0;">${escapeHtml(labFieldMismatchLine('INP', c.fieldP75, c.inp, cs, c.spread))}</div>` : ''}
    ${(() => {
      const lines = carriedMpLines(r, 'INP', c.inp);
      if (!lines) return '';
      return `<div style="margin-top:12px;background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:12px 14px;font-size:12px;color:#cbd5e1;"><strong style="color:#f8fafc;">${cs ? 'Napříč webem' : 'Site-wide'}</strong><br>${lines.map(escapeHtml).join('<br><br>')}</div>`;
    })()}
  </div>`;
};

const renderInpCandidateCardsMockup = (r) => {
  if (!deepSectionAllowed(r, 'inp')) return '';
  const causes = Array.isArray(r.deepINPResults?.causes)
    ? r.deepINPResults.causes.filter(Boolean).map(normalizeInpDiagnosticCause)
    : [];
  if (!causes.length) return '';
  const lang = r.lang;
  return `
  <div class="glass panel" style="margin-bottom:24px;">
    <div class="panel-header">
      <div class="panel-title">${lang === 'cs' ? 'Pozorovaní kandidáti a postup ověření' : 'Observed candidates & verification steps'}</div>
      <div class="panel-subtitle">${escapeHtml(severityLabels.candidate[lang])}</div>
    </div>
    <div style="display:flex;flex-direction:column;gap:10px;">
      ${causes.map(cause => {
        const label = inpCauseContent.labels[cause.cause]?.[lang] || cause.cause;
        const detail = cause.cause === 'long-tasks' && !longTaskCountDiscrepancy(r.deepINPResults, lang)
          ? longTaskScopeDetail(cause, lang) : '';
        return `<div style="padding:12px 14px;background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.2);border-radius:10px;">
          <div style="display:flex;gap:8px;align-items:center;">
            <span class="tag" style="background:rgba(100,116,139,0.25);color:#cbd5e1;">${escapeHtml(severityLabels.candidate[lang])}</span>
            <strong style="color:#f8fafc;">${escapeHtml(label)}</strong>
          </div>
          <p style="font-size:12px;color:#cbd5e1;margin:8px 0 0;">${escapeHtml(inpCauseDescription(cause, lang))}</p>
          ${cause.cause === 'long-tasks' && longTaskCountDiscrepancy(r.deepINPResults, lang)
            ? `<p style="font-size:11px;color:#fbbf24;margin:6px 0 0;">${escapeHtml(longTaskCountDiscrepancy(r.deepINPResults, lang))}</p>`
            : detail ? `<p style="font-size:11px;color:#94a3b8;margin:6px 0 0;">${escapeHtml(detail)}</p>` : ''}
          <p style="font-size:11px;color:#94a3b8;margin:6px 0 0;">${escapeHtml(inpCauseRecommendationsFor(cause, lang)[0])}</p>
        </div>`;
      }).join('')}
    </div>
  </div>`;
};

// Carried / primary LCP deep run — Google's standard three-phase breakdown
// (TTFB → resource load → render delay) + the LCP element itself, field-first.
const LCP_CAUSE_COPY = {
  'slow-server': { cs: 'Pomalá odpověď serveru (TTFB)', en: 'Slow server response (TTFB)' },
  'redirect-chain': { cs: 'Řetěz přesměrování', en: 'Redirect chain' },
  'render-blocking-css': { cs: 'CSS blokující vykreslení', en: 'Render-blocking CSS' },
  'render-blocking-js': { cs: 'Synchronní skripty blokující vykreslení', en: 'Render-blocking scripts' },
  'third-party-blocking': { cs: 'Blokující zdroje třetích stran', en: 'Third-party blocking resources' },
  'lazy-above-fold': { cs: 'LCP obrázek má loading="lazy"', en: 'LCP image has loading="lazy"' },
  'large-image': { cs: 'Velký LCP obrázek', en: 'Large LCP image' },
  'slow-resource': { cs: 'Pomalé stažení LCP zdroje', en: 'Slow LCP resource load' },
  'webfont-blocking': { cs: 'Webfonty bez font-display: swap', en: 'Web fonts without font-display: swap' },
  'render-delay': { cs: 'Prodleva mezi načtením a vykreslením', en: 'Delay between load and render' }
};

const getCarriedLcp = (r) => {
  if (!deepSectionAllowed(r, 'lcp')) return null;
  const deep = r.deepLCPResults;
  if (!deep || !Number.isFinite(Number(deep.lcp)) || Number(deep.lcp) <= 0) return null;
  const spread = deep.labRunSpread || null;
  const useMedian = !!(spread && labSeriesIsVerified(spread) && spread.median != null);
  const phSrc = (useMedian && spread.phases && spread.phases.runs >= 5) ? spread.phases : (deep.phases || {});
  const phaseBasis = (useMedian && spread.phases && spread.phases.runs >= 5) ? 'median' : 'single';
  // v != null guard first — Number(null) === 0 would turn an undetermined
  // phase into a false-known zero.
  const num = (v) => (v != null && Number.isFinite(Number(v)) && Number(v) >= 0) ? Math.round(Number(v)) : null;
  const phases = {
    ttfb: num(phSrc.ttfb),
    resourceLoad: num(phSrc.resourceLoad),
    renderDelay: num(phSrc.renderDelay)
  };
  // Dominant phase → owner. Ties go to the later phase (closer to the fix).
  let dominant = null;
  const entries = [['ttfb', phases.ttfb], ['resourceLoad', phases.resourceLoad], ['renderDelay', phases.renderDelay]].filter(([, v]) => v != null);
  // A dominant phase can only be claimed when ALL phases are known — with an
  // undetermined phase the largest known one may not be the real bottleneck.
  if (entries.length === 3) dominant = entries.reduce((a, b) => (b[1] >= a[1] ? b : a))[0];
  const field = (r.fieldMatrix || []).find(i => i.metric === 'lcp' && i.device === 'phone')
    || (r.fieldMatrix || []).find(i => i.metric === 'lcp');
  return {
    lcp: Math.round(labSeriesDisplayValue(spread, deep.lcp)),
    basis: spread?.runs >= 2 ? 'median' : 'single',
    runs: spread?.runs || 1,
    spread,
    phases,
    phaseBasis,
    phaseRuns: phaseBasis === 'median' ? spread.phases.runs : 1,
    dominant,
    element: deep.lcpElement || null,
    causes: Array.isArray(deep.causes) ? deep.causes : [],
    fieldP75: field?.p75 != null ? Math.round(Number(field.p75)) : null,
    fieldStatus: field?.status || null
  };
};

const lcpPhaseRows = (c, cs) => [
  [cs ? 'Odpověď serveru (TTFB)' : 'Server response (TTFB)', c.phases.ttfb, 'ttfb'],
  [cs ? 'Stažení LCP zdroje' : 'LCP resource load', c.phases.resourceLoad, 'resourceLoad'],
  [cs ? 'Prodleva vykreslení' : 'Render delay', c.phases.renderDelay, 'renderDelay']
];

const lcpOwnerLine = (c, cs) => {
  if (!c.dominant) return '';
  const map = {
    ttfb: cs
      ? 'Největší podíl má odpověď serveru — vlastníkem je infrastruktura/backend (cache, CDN, databáze).'
      : 'The server response dominates — owned by infrastructure/backend (caching, CDN, database).',
    resourceLoad: cs
      ? 'Největší podíl má stažení LCP zdroje — vlastníkem je frontend (formát, velikost a priorita obrázku; u reklamních pozic ad ops).'
      : 'The LCP resource load dominates — owned by frontend (image format, size, priority; ad ops for ad slots).',
    renderDelay: cs
      ? 'Největší podíl má prodleva vykreslení — vlastníkem je frontend (blokující CSS/JS, práce na hlavním vlákně před vykreslením).'
      : 'The render delay dominates — owned by frontend (blocking CSS/JS, main-thread work before paint).'
  };
  return map[c.dominant] || '';
};

const lcpElementLine = (c, cs) => {
  const el = c.element;
  if (!el) return '';
  const kind = el.isImage ? (cs ? 'obrázek' : 'image') : (cs ? 'textový blok' : 'text block');
  const bits = [
    el.selector ? `\`${el.selector}\`` : el.tagName,
    kind,
    el.size ? `${fmtNum(Math.round(el.size / 1024))} kB` : null,
    el.hasLazyLoading ? (cs
      ? 'loading="lazy" ⚠ (lazy loading odkládá načtení — správně u obrázků níže na stránce, ale chyba u hlavního obrázku nahoře, který se má načíst hned)'
      : 'loading="lazy" ⚠ (lazy loading defers the load — right for images further down, but a mistake on the main image up top, which should load immediately)') : null
  ].filter(Boolean).join(' · ');
  return cs ? `LCP element: ${bits}` : `LCP element: ${bits}`;
};

const lcpCauseLines = (c, cs) => c.causes.slice(0, 4).map(cause => {
  const label = LCP_CAUSE_COPY[cause.cause]?.[cs ? 'cs' : 'en'] || cause.cause;
  const isMs = ['slow-server', 'redirect-chain', 'slow-resource', 'render-delay'].includes(cause.cause);
  const val = isMs ? ` — ${fmtNum(Math.round(cause.value))} ms`
    : cause.cause === 'large-image' ? ` — ${fmtNum(Math.round(cause.value / 1024))} kB` : '';
  return { label, val, severity: cause.severity };
});

const renderLcpFindingsAudit = (r) => {
  const c = getCarriedLcp(r);
  if (!c) return '';
  const cs = r.lang === 'cs';
  const labTag = c.basis === 'median'
    ? (cs ? `medián ${c.runs} běhů` : `median of ${c.runs} runs`)
    : c.basis === 'average'
      ? (cs ? `orientační průměr ${c.runs} běhů` : `indicative average of ${c.runs} runs`)
      : (cs ? 'jeden běh, orientační' : 'single run, indicative');
  const lead = c.fieldP75 != null
    ? (cs
      ? `LCP · reální uživatelé: ${fmtNum(c.fieldP75)} ms (field p75) · laboratorní běh ${fmtNum(c.lcp)} ms (${labTag}). Field hodnota z CrUX je rozhodující — laboratorní běh říká, kterou fází se LCP zdržel.`
      : `LCP · real users: ${fmtNum(c.fieldP75)} ms (field p75) · lab run ${fmtNum(c.lcp)} ms (${labTag}). The CrUX field value is authoritative — the lab run tells you which phase delayed the LCP.`)
    : (cs
      ? `LCP · laboratorní běh: ${fmtNum(c.lcp)} ms (${labTag}).`
      : `LCP · lab run: ${fmtNum(c.lcp)} ms (${labTag}).`);
  const basisNote = c.phaseBasis === 'median'
    ? (cs ? `Celkové LCP výše vychází z ${c.phaseRuns} uložených běhů; každá fáze níže je počítaná samostatně. Shodu podmínek včetně skutečného souhlasu ověřte v evidenci série. Nejde o rozpad jednoho reálného běhu — součet fází proto nemusí odpovídat celkovému LCP.` : `The total LCP above uses ${c.phaseRuns} stored runs; each phase below is computed separately. Check the series evidence for comparable conditions including effective consent. This is not a breakdown of a single real run, so phases need not sum to the total.`)
    : (cs ? 'Rozpad fází je z jednoho laboratorního běhu — hodnoty berte orientačně.' : 'The phase breakdown is one lab run — treat values as indicative.');
  // Percentages only from a COMPLETE decomposition — with an undetermined
  // phase, a share computed from the known ones fabricates a complete claim.
  const total = [c.phases.ttfb, c.phases.resourceLoad, c.phases.renderDelay].every(v => v != null)
    ? c.phases.ttfb + c.phases.resourceLoad + c.phases.renderDelay
    : 0;
  const phaseCells = lcpPhaseRows(c, cs).map(([label, ms, key]) => `
      <div style="flex:1;min-width:150px;border:1px solid ${key === c.dominant ? 'var(--ink)' : '#ddd'};padding:12px 14px;${key === c.dominant ? 'background:var(--paper-2);' : ''}">
        <div style="font-family:var(--mono);font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:#666;">${escapeHtml(label)}${key === c.dominant ? (cs ? ' · největší podíl' : ' · dominant') : ''}</div>
        <div style="font-family:var(--serif);font-size:20px;font-weight:600;color:var(--ink);margin-top:4px;">${ms != null ? `${fmtNum(ms)} ms` : '—'}${ms != null && total > 0 ? ` <span style="font-size:12px;color:#666;">(${Math.round(ms / total * 100)} %)</span>` : ''}</div>
      </div>`).join('');
  const elLine = lcpElementLine(c, cs);
  const ownerLine = lcpOwnerLine(c, cs);
  const causes = lcpCauseLines(c, cs);
  const causesRows = causes.length ? causes.map((x, i) => `
    <div style="margin-top:10px;padding:12px 16px;background:var(--paper-2);border-left:3px solid ${x.severity === 'critical' ? '#b91c1c' : x.severity === 'high' ? '#c2410c' : 'var(--ink)'};">
      <div style="font-family:var(--mono);font-size:12px;font-weight:700;color:var(--ink);">${String(i + 1).padStart(2, '0')} · ${escapeHtml(x.label)}${escapeHtml(x.val)}</div>
    </div>`).join('') : '';
  const spreadNote = c.spread && c.spread.runs >= 2 && c.spread.min !== c.spread.max
    ? (labSeriesIsVerified(c.spread)
      ? (cs
        ? `Rozptyl ${c.spread.runs} uložených běhů (shodu podmínek ověřte v evidenci série): ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, medián ${fmtNum(c.spread.median)} ms. Laboratorní hodnota výše je medián, ne jeden konkrétní běh.`
        : `Spread across ${c.spread.runs} stored runs (check the series evidence for comparable conditions): ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, median ${fmtNum(c.spread.median)} ms. The lab value above is the median, not one specific run.`)
      : (cs
        ? `Medián ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; jistota diagnostiky omezená.`
        : `Median ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; limited diagnostic confidence.`))
    : '';
  return `
  <section>
    <div class="section-label">${cs ? 'Diagnostika LCP' : 'LCP diagnostics'}</div>
    <h2 class="section-title">${cs ? 'Kterou fází se ' : 'Which phase delayed '}<em>${cs ? 'LCP zdržel' : 'the LCP'}</em>.</h2>
    <p class="lead">${escapeHtml(lead)} ${escapeHtml(basisNote)}</p>
    ${elLine ? `<div style="font-family:var(--mono);font-size:12px;color:var(--ink);margin-top:10px;">${escapeHtml(elLine)}</div>` : ''}
    <div style="display:flex;gap:12px;flex-wrap:wrap;margin-top:12px;">${phaseCells}</div>
    ${ownerLine ? `<div class="callout" style="margin-top:14px;"><strong>${cs ? 'Kdo to vlastní' : 'Who owns it'}</strong> ${escapeHtml(ownerLine)}</div>` : ''}
    ${causesRows ? `<h3 style="font-family:var(--mono);font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#666;margin-top:20px;">${cs ? 'Zjištěné příčiny' : 'Detected causes'}</h3>${causesRows}` : ''}
    ${spreadNote ? `<div class="callout" style="margin-top:16px;"><strong>${cs ? 'Stabilita měření LCP' : 'LCP measurement stability'}</strong> ${escapeHtml(spreadNote)}</div>` : ''}
    ${(() => {
      const lines = carriedMpLines(r, 'LCP', c.lcp);
      if (!lines) return '';
      return `<div class="callout" style="margin-top:12px;"><strong>${cs ? 'Napříč webem' : 'Site-wide'}</strong> ${lines.map(escapeHtml).join('<br><br>')}</div>`;
    })()}
  </section>`;
};

const renderLcpFindingsMockup = (r) => {
  const c = getCarriedLcp(r);
  if (!c) return '';
  const cs = r.lang === 'cs';
  const labTag = c.basis === 'median'
    ? (cs ? `medián ${c.runs} běhů` : `median of ${c.runs} runs`)
    : c.basis === 'average'
      ? (cs ? `orientační průměr ${c.runs} běhů` : `indicative average of ${c.runs} runs`)
      : (cs ? 'jeden běh, orientační' : 'single run, indicative');
  // Percentages only from a COMPLETE decomposition — with an undetermined
  // phase, a share computed from the known ones fabricates a complete claim
  // (e.g. TTFB "100 %" while the real bottleneck is the unknown phase).
  const total = [c.phases.ttfb, c.phases.resourceLoad, c.phases.renderDelay].every(v => v != null)
    ? c.phases.ttfb + c.phases.resourceLoad + c.phases.renderDelay
    : 0;
  const elLine = lcpElementLine(c, cs);
  const ownerLine = lcpOwnerLine(c, cs);
  const causes = lcpCauseLines(c, cs);
  return `
  <div class="glass panel" style="margin-bottom:24px;">
    <div class="panel-header">
      <div class="panel-title">${cs ? 'Největší vykreslení (LCP)' : 'Largest paint (LCP)'}</div>
      <div class="panel-subtitle">${c.fieldP75 != null
        ? `field p75 ${fmtNum(c.fieldP75)} ms · lab ${fmtNum(c.lcp)} ms (${labTag})`
        : `lab ${fmtNum(c.lcp)} ms (${labTag})`}</div>
    </div>
    ${elLine ? `<div style="font-family:'JetBrains Mono',monospace;font-size:11px;color:#cbd5e1;margin-bottom:10px;">${escapeHtml(elLine)}</div>` : ''}
    <div style="display:flex;gap:10px;flex-wrap:wrap;">
      ${lcpPhaseRows(c, cs).map(([label, ms, key]) => `
      <div style="flex:1;min-width:140px;background:rgba(15,23,42,0.5);border:1px solid ${key === c.dominant ? 'rgba(248,250,252,0.4)' : 'rgba(100,116,139,0.12)'};border-radius:10px;padding:12px 14px;">
        <div style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#64748b;">${escapeHtml(label)}</div>
        <div style="font-family:'JetBrains Mono',monospace;font-size:18px;font-weight:600;color:#f8fafc;margin-top:4px;">${ms != null ? `${fmtNum(ms)} ms` : '—'}${ms != null && total > 0 ? ` <span style="font-size:11px;color:#64748b;">${Math.round(ms / total * 100)} %</span>` : ''}</div>
      </div>`).join('')}
    </div>
    ${c.phaseBasis === 'median' ? `<div style="margin-top:10px;font-size:11px;color:#94a3b8;">${escapeHtml(cs
      ? `Celkové LCP je medián celkové hodnoty napříč ${c.phaseRuns} běhy; každá fáze je medián dané fáze samostatně napříč týmiž běhy — nejde o rozpad jednoho běhu a součet fází nemusí sedět na celkové LCP.`
      : `The total LCP is the median of the total value across ${c.phaseRuns} runs; each phase is that phase's median computed independently across the same runs — not one run's decomposition, so phases need not sum to the total.`)}</div>` : ''}
    ${ownerLine ? `<div style="margin-top:12px;background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:10px 14px;font-size:12px;color:#cbd5e1;">${escapeHtml(ownerLine)}</div>` : ''}
    ${causes.length ? `<div style="margin-top:12px;display:flex;flex-direction:column;gap:8px;">
      ${causes.map((x, i) => `<div style="background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:10px 14px;font-size:12px;color:#e2e8f0;"><span style="font-family:'JetBrains Mono',monospace;color:#64748b;margin-right:8px;">${String(i + 1).padStart(2, '0')}</span>${escapeHtml(x.label)}${escapeHtml(x.val)}</div>`).join('')}
    </div>` : ''}
    ${c.spread && c.spread.runs >= 2 && c.spread.min !== c.spread.max ? `<div style="margin-top:12px;font-size:11px;color:#94a3b8;">${escapeHtml(
      labSeriesIsVerified(c.spread)
        ? (cs
          ? `Rozptyl ${c.spread.runs} běhů: ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, medián ${fmtNum(c.spread.median)} ms — lab hodnota je medián.`
          : `Spread across ${c.spread.runs} runs: ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, median ${fmtNum(c.spread.median)} ms — the lab value is the median.`)
        : (cs
          ? `Medián ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; jistota diagnostiky omezená.`
          : `Median ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; limited diagnostic confidence.`)
    )}</div>` : ''}
    ${(() => {
      const lines = carriedMpLines(r, 'LCP', c.lcp);
      if (!lines) return '';
      return `<div style="margin-top:12px;background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:12px 14px;font-size:12px;color:#cbd5e1;"><strong style="color:#f8fafc;">${cs ? 'Napříč webem' : 'Site-wide'}</strong><br>${lines.map(escapeHtml).join('<br><br>')}</div>`;
    })()}
  </div>`;
};

// Carried / primary TTFB deep run — same field-first + median-across-runs
// pattern as CLS/INP. TTFB is measured on the first navigation of a fresh
// browser context, so the DNS/TCP/TLS phases here are REAL Navigation Timing
// values of the lab run (unlike Lighthouse network-requests, which lacks
// them) — but they still describe the lab network path, not the field one.
const TTFB_CAUSE_COPY = {
  'redirect-chain': { cs: 'Řetěz přesměrování', en: 'Redirect chain' },
  'slow-dns': { cs: 'Pomalé DNS', en: 'Slow DNS' },
  'slow-tcp': { cs: 'Pomalé TCP spojení', en: 'Slow TCP connection' },
  'slow-tls': { cs: 'Pomalý TLS handshake', en: 'Slow TLS handshake' },
  'slow-backend': { cs: 'Pomalé zpracování na serveru', en: 'Slow server processing' },
  'slow-request-wait': { cs: 'Dlouhé čekání na odpověď (backend nerozlišen)', en: 'Long request wait (backend not isolated)' },
  'http1': { cs: 'HTTP/1.1 místo HTTP/2+', en: 'HTTP/1.1 instead of HTTP/2+' },
  'no-cache': { cs: 'Chybí cache hlavičky', en: 'Missing cache headers' },
  'no-keepalive': { cs: 'Vypnuté keep-alive', en: 'Keep-alive disabled' },
  'large-document': { cs: 'Velký HTML dokument', en: 'Large HTML document' }
};

const getCarriedTtfb = (r) => {
  if (!deepSectionAllowed(r, 'ttfb')) return null;
  const deep = r.deepTTFBResults;
  if (!deep || deep.ttfb == null || !Number.isFinite(Number(deep.ttfb))) return null;
  const spread = deep.labRunSpread || null;
  const useMedian = !!(spread && labSeriesIsVerified(spread) && spread.median != null);
  // Phase breakdown: median across stored runs when available, else this run.
  const phSrc = (useMedian && spread.phases && spread.phases.runs >= 5) ? spread.phases : (deep.phases || {});
  const phaseBasis = (useMedian && spread.phases && spread.phases.runs >= 5) ? 'median' : 'single';
  const field = (r.fieldMatrix || []).find(i => i.metric === 'ttfb' && i.device === 'phone')
    || (r.fieldMatrix || []).find(i => i.metric === 'ttfb');
  const num = (v) => (v != null && Number.isFinite(Number(v)) && Number(v) >= 0) ? Math.round(Number(v)) : null;
  return {
    ttfb: Math.round(labSeriesDisplayValue(spread, deep.ttfb)),
    basis: spread?.runs >= 2 ? 'median' : 'single',
    runs: spread?.runs || 1,
    spread,
    phases: {
      redirect: num(phSrc.redirect),
      dns: num(phSrc.dns),
      tcp: num(phSrc.tcp),
      tls: num(phSrc.tls),
      request: num(phSrc.request),
      response: num(phSrc.response)
    },
    phaseBasis,
    phaseRuns: phaseBasis === 'median' ? spread.phases.runs : 1,
    protocol: deep.protocol || null,
    transferSize: Number.isFinite(Number(deep.transferSize)) ? Number(deep.transferSize) : null,
    causes: Array.isArray(deep.causes) ? deep.causes : [],
    fieldP75: field?.p75 != null ? Math.round(Number(field.p75)) : null,
    fieldStatus: field?.status || null
  };
};

const ttfbPhaseRows = (c, cs) => [
  [cs ? 'Přesměrování' : 'Redirects', c.phases.redirect],
  ['DNS', c.phases.dns],
  ['TCP', c.phases.tcp],
  ['TLS', c.phases.tls],
  [cs ? 'Čekání na server' : 'Server wait', c.phases.request],
  [cs ? 'Stažení dokumentu' : 'Document download', c.phases.response]
];

const ttfbLeadLine = (r, c) => {
  const cs = r.lang === 'cs';
  const labTag = c.basis === 'median'
    ? (cs ? `medián ${c.runs} běhů` : `median of ${c.runs} runs`)
    : c.basis === 'average'
      ? (cs ? `orientační průměr ${c.runs} běhů` : `indicative average of ${c.runs} runs`)
      : (cs ? 'jeden běh, orientační' : 'single run, indicative');
  return c.fieldP75 != null
    ? (cs
      ? `TTFB · reální uživatelé: ${fmtNum(c.fieldP75)} ms (field p75) · laboratorní odpověď serveru ${fmtNum(c.ttfb)} ms (${labTag}). Field hodnota z CrUX je rozhodující — laboratorní běh vysvětluje, z čeho se čekání skládá.`
      : `TTFB · real users: ${fmtNum(c.fieldP75)} ms (field p75) · lab server response ${fmtNum(c.ttfb)} ms (${labTag}). The CrUX field value is authoritative — the lab run explains what the wait consists of.`)
    : (cs
      ? `TTFB · laboratorní odpověď serveru: ${fmtNum(c.ttfb)} ms (${labTag}).`
      : `TTFB · lab server response: ${fmtNum(c.ttfb)} ms (${labTag}).`);
};

const ttfbCauseLines = (c, cs) => c.causes.slice(0, 4).map(cause => {
  const label = TTFB_CAUSE_COPY[cause.cause]?.[cs ? 'cs' : 'en'] || cause.cause;
  const isMs = ['redirect-chain', 'slow-dns', 'slow-tcp', 'slow-tls', 'slow-backend', 'slow-request-wait'].includes(cause.cause);
  const val = isMs ? ` — ${cause.value != null && Number.isFinite(Number(cause.value)) ? `${fmtNum(cause.value)} ms` : (cs ? 'nedostupné' : 'unavailable')}`
    : cause.cause === 'large-document' ? ` — ${fmtNum(Math.round(cause.value / 1024))} kB` : '';
  return { label, val, severity: cause.severity };
});

const renderTtfbFindingsAudit = (r) => {
  const c = getCarriedTtfb(r);
  if (!c) return '';
  const cs = r.lang === 'cs';
  const phaseCells = ttfbPhaseRows(c, cs).map(([label, ms]) => `
      <div style="flex:1;min-width:110px;border:1px solid #ddd;padding:12px 14px;">
        <div style="font-family:var(--mono);font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:#666;">${escapeHtml(label)}</div>
        <div style="font-family:var(--serif);font-size:20px;font-weight:600;color:var(--ink);margin-top:4px;">${ms != null ? `${fmtNum(ms)} ms` : '—'}</div>
      </div>`).join('');
  const basisNote = c.phaseBasis === 'median'
    ? (cs ? `Rozpad níže je medián přes ${c.phaseRuns} uložených běhů (shodu podmínek ověřte v evidenci série), ne jeden konkrétní běh.` : `The breakdown below is the median across ${c.phaseRuns} stored runs (check the series evidence for comparable conditions), not one specific run.`)
    : (cs ? 'Rozpad níže je z jednoho laboratorního běhu — hodnoty berte orientačně; síťové fáze se běh od běhu liší.' : 'The breakdown below is one lab run — treat values as indicative; network phases vary run to run.');
  const causes = ttfbCauseLines(c, cs);
  const causesRows = causes.length ? causes.map((x, i) => `
    <div style="margin-top:10px;padding:12px 16px;background:var(--paper-2);border-left:3px solid ${x.severity === 'critical' ? '#b91c1c' : x.severity === 'high' ? '#c2410c' : 'var(--ink)'};">
      <div style="font-family:var(--mono);font-size:12px;font-weight:700;color:var(--ink);">${String(i + 1).padStart(2, '0')} · ${escapeHtml(x.label)}${escapeHtml(x.val)}</div>
    </div>`).join('') : '';
  const metaBits = [
    c.protocol ? `${cs ? 'protokol' : 'protocol'} ${escapeHtml(String(c.protocol))}` : null,
    c.transferSize != null ? `${cs ? 'dokument' : 'document'} ${fmtNum(Math.round(c.transferSize / 1024))} kB` : null
  ].filter(Boolean).join(' · ');
  const spreadNote = c.spread && c.spread.runs >= 2 && c.spread.min !== c.spread.max
    ? (labSeriesIsVerified(c.spread)
      ? (cs
        ? `Rozptyl ${c.spread.runs} uložených běhů (shodu podmínek ověřte v evidenci série): ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, medián ${fmtNum(c.spread.median)} ms. Laboratorní hodnota výše je medián, ne jeden konkrétní běh.`
        : `Spread across ${c.spread.runs} stored runs (check the series evidence for comparable conditions): ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, median ${fmtNum(c.spread.median)} ms. The lab value above is the median, not one specific run.`)
      : (cs
        ? `Medián ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; jistota diagnostiky omezená.`
        : `Median ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; limited diagnostic confidence.`))
    : '';
  return `
  <section>
    <div class="section-label">${cs ? 'Diagnostika TTFB' : 'TTFB diagnostics'}</div>
    <h2 class="section-title">${cs ? 'Z čeho se skládá ' : 'What makes up '}<em>${cs ? 'čekání na server' : 'the server wait'}</em>.</h2>
    <p class="lead">${escapeHtml(ttfbLeadLine(r, c))} ${cs
      ? 'Fáze níže jsou reálné hodnoty Navigation Timing z laboratorního běhu (čerstvý prohlížeč, bez cache) — DNS, TCP a TLS jsou tu změřené, ne dopočítané. TTFB se měří na první navigaci, ještě před jakoukoli interakcí s cookie lištou — stav souhlasu uvedený jinde v reportu se této sekce netýká.'
      : 'The phases below are real Navigation Timing values from the lab run (fresh browser, no cache) — DNS, TCP and TLS are measured here, not derived. TTFB is measured on the first navigation, before any cookie-banner interaction — the consent state stated elsewhere in this report does not apply to this section.'} ${escapeHtml(basisNote)}</p>
    <div style="display:flex;gap:12px;flex-wrap:wrap;margin-top:12px;">${phaseCells}</div>
    ${metaBits ? `<div style="font-family:var(--mono);font-size:11px;color:#666;margin-top:8px;">${metaBits}</div>` : ''}
    ${causesRows ? `<h3 style="font-family:var(--mono);font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#666;margin-top:20px;">${cs ? 'Zjištěné příčiny' : 'Detected causes'}</h3>${causesRows}` : ''}
    ${spreadNote ? `<div class="callout" style="margin-top:16px;"><strong>${cs ? 'Stabilita měření TTFB' : 'TTFB measurement stability'}</strong> ${escapeHtml(spreadNote)}</div>` : ''}
    ${(() => {
      const lines = carriedMpLines(r, 'TTFB', c.ttfb);
      if (!lines) return '';
      return `<div class="callout" style="margin-top:12px;"><strong>${cs ? 'Napříč webem' : 'Site-wide'}</strong> ${lines.map(escapeHtml).join('<br><br>')}</div>`;
    })()}
  </section>`;
};

const renderTtfbFindingsMockup = (r) => {
  const c = getCarriedTtfb(r);
  if (!c) return '';
  const cs = r.lang === 'cs';
  const labTag = c.basis === 'median'
    ? (cs ? `medián ${c.runs} běhů` : `median of ${c.runs} runs`)
    : c.basis === 'average'
      ? (cs ? `orientační průměr ${c.runs} běhů` : `indicative average of ${c.runs} runs`)
      : (cs ? 'jeden běh, orientační' : 'single run, indicative');
  const causes = ttfbCauseLines(c, cs);
  return `
  <div class="glass panel" style="margin-bottom:24px;">
    <div class="panel-header">
      <div class="panel-title">${cs ? 'Odpověď serveru (TTFB)' : 'Server response (TTFB)'}</div>
      <div class="panel-subtitle">${c.fieldP75 != null
        ? `field p75 ${fmtNum(c.fieldP75)} ms · lab ${fmtNum(c.ttfb)} ms (${labTag})`
        : `lab ${fmtNum(c.ttfb)} ms (${labTag})`}</div>
    </div>
    <div style="display:flex;gap:10px;flex-wrap:wrap;">
      ${ttfbPhaseRows(c, cs).map(([label, ms]) => `
      <div style="flex:1;min-width:100px;background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:12px 14px;">
        <div style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#64748b;">${escapeHtml(label)}</div>
        <div style="font-family:'JetBrains Mono',monospace;font-size:18px;font-weight:600;color:#f8fafc;margin-top:4px;">${ms != null ? `${fmtNum(ms)} ms` : '—'}</div>
      </div>`).join('')}
    </div>
    ${causes.length ? `<div style="margin-top:12px;display:flex;flex-direction:column;gap:8px;">
      ${causes.map((x, i) => `<div style="background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:10px 14px;font-size:12px;color:#e2e8f0;"><span style="font-family:'JetBrains Mono',monospace;color:#64748b;margin-right:8px;">${String(i + 1).padStart(2, '0')}</span>${escapeHtml(x.label)}${escapeHtml(x.val)}</div>`).join('')}
    </div>` : ''}
    ${c.spread && c.spread.runs >= 2 && c.spread.min !== c.spread.max ? `<div style="margin-top:12px;font-size:11px;color:#94a3b8;">${escapeHtml(
      labSeriesIsVerified(c.spread)
        ? (cs
          ? `Rozptyl ${c.spread.runs} běhů: ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, medián ${fmtNum(c.spread.median)} ms — lab hodnota je medián.`
          : `Spread across ${c.spread.runs} runs: ${fmtNum(c.spread.min)}–${fmtNum(c.spread.max)} ms, median ${fmtNum(c.spread.median)} ms — the lab value is the median.`)
        : (cs
          ? `Medián ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; jistota diagnostiky omezená.`
          : `Median ${fmtNum(labSeriesDisplayValue(c.spread))} ms · n=${c.spread.runs}; limited diagnostic confidence.`)
    )}</div>` : ''}
    <div style="margin-top:10px;font-size:10px;color:#64748b;">${cs
      ? 'TTFB se měří na první navigaci, před interakcí s cookie lištou — stav souhlasu uvedený jinde v reportu se této sekce netýká.'
      : 'TTFB is measured on the first navigation, before any cookie-banner interaction — the consent state stated elsewhere does not apply to this section.'}</div>
    ${(() => {
      const lines = carriedMpLines(r, 'TTFB', c.ttfb);
      if (!lines) return '';
      return `<div style="margin-top:12px;background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:12px 14px;font-size:12px;color:#cbd5e1;"><strong style="color:#f8fafc;">${cs ? 'Napříč webem' : 'Site-wide'}</strong><br>${lines.map(escapeHtml).join('<br><br>')}</div>`;
    })()}
  </div>`;
};

// =========================================================================
// SWEEP→DEEP SECTION — deep diagnostics re-run on the worst sweep pages
// =========================================================================
// The multi-page sweep names the worst pages, but the main deep test runs on
// the entry URL. These runs are the primary metric's deep layer executed
// DIRECTLY on up to 2 worst sweep pages, so the culprit belongs to that page
// (templates differ: homepage, article and listing break for different
// reasons). Two pages, not one: the sweep is one shallow run per page, so a
// single "worst" can be noise — two independent deep runs let the reader
// compare instead of trusting one outlier.
const SWEEP_DEEP_GOOD = Object.freeze({
  cls: metricThresholds.cls.good,
  inp: metricThresholds.inp.good,
  lcp: metricThresholds.lcp.good,
  ttfb: metricThresholds.ttfb.good
});

// Metric-parameterized deep-result readers, shared by the sweep→deep block
// and the per-device culprit comparison (same identity rules everywhere).
const deepValFor = (m, res) => res == null ? null
  : m === 'CLS' ? (res.cls ?? null)
  : m === 'INP' ? evaluateInpEvidence({ deepINPResults: res }).labValue
  : m === 'TTFB' ? (res.ttfb ?? null)
  : (res.lcp ?? null);

const culpritTextFor = (m, res, cs) => {
    if (!res) return null;
    if (m === 'CLS') {
      const els = Array.isArray(res.elements) ? mergeClsContainerElements(res.elements) : [];
      const top = els[0];
      if (!top?.selector) return null;
      const shift = Number(top.totalShift ?? 0);
      return cs
        ? `Hlavní viník na této stránce: ${top.selector}${shift ? ` (posun ${shift.toFixed(3)})` : ''}`
        : `Main culprit on this page: ${top.selector}${shift ? ` (shift ${shift.toFixed(3)})` : ''}`;
    }
    if (m === 'LCP') {
      const el = res.lcpElement || null;
      const ph = res.phases || {};
      const rows = [['ttfb', ph.ttfb], ['resourceLoad', ph.resourceLoad], ['renderDelay', ph.renderDelay]]
        .filter(([, v]) => v != null && Number.isFinite(Number(v)));
      // Dominance only with a complete phase set — an unknown phase could be
      // the real bottleneck.
      const dom = rows.length === 3 ? rows.reduce((w, x) => Number(x[1]) >= Number(w[1]) ? x : w) : null;
      const phLabel = dom ? (cs
        ? (dom[0] === 'ttfb' ? 'odpověď serveru' : dom[0] === 'resourceLoad' ? 'stažení zdroje' : 'zpoždění vykreslení')
        : (dom[0] === 'ttfb' ? 'server response' : dom[0] === 'resourceLoad' ? 'resource load' : 'render delay')) : null;
      const bits = [];
      if (el?.selector) bits.push(cs ? `LCP element: ${el.selector}${el.hasLazyLoading ? ' (loading="lazy" — chyba na LCP elementu)' : ''}` : `LCP element: ${el.selector}${el.hasLazyLoading ? ' (loading="lazy" — a mistake on the LCP element)' : ''}`);
      if (phLabel && dom) bits.push(cs ? `dominuje ${phLabel} (${fmtNum(Math.round(Number(dom[1])))} ms)` : `${phLabel} dominates (${fmtNum(Math.round(Number(dom[1])))} ms)`);
      return bits.length ? bits.join(' · ') : null;
    }
    if (m === 'INP') {
      const wi = res.worstInteraction || null;
      if (!wi) return null;
      const rows = [['inputDelay', wi.inputDelay], ['processingTime', wi.processingTime], ['presentationDelay', wi.presentationDelay]]
        .filter(([, v]) => Number.isFinite(Number(v)));
      const dom = rows.length ? rows.reduce((w, x) => Number(x[1]) >= Number(w[1]) ? x : w) : null;
      const phLabel = dom ? (cs
        ? (dom[0] === 'inputDelay' ? 'blokované hlavní vlákno (input delay)' : dom[0] === 'processingTime' ? 'zpracování handleru' : 'vykreslení po interakci')
        : (dom[0] === 'inputDelay' ? 'blocked main thread (input delay)' : dom[0] === 'processingTime' ? 'handler processing' : 'post-interaction rendering')) : null;
      const tgt = wi.target ? `${String(wi.target.tagName || '').toLowerCase()}${wi.target.id ? `#${wi.target.id}` : ''}` : null;
      const bits = [];
      if (phLabel && dom) bits.push(cs ? `dominuje ${phLabel} (${fmtNum(Math.round(Number(dom[1])))} ms)` : `${phLabel} dominates (${fmtNum(Math.round(Number(dom[1])))} ms)`);
      if (tgt) bits.push(cs ? `interakce na ${tgt}` : `interaction on ${tgt}`);
      return bits.length ? bits.join(' · ') : null;
    }
    if (m === 'TTFB') {
      const ph = res.phases || {};
      const rows = Object.entries(ph).filter(([, v]) => Number.isFinite(Number(v)) && Number(v) > 0);
      if (!rows.length) return null;
      const dom = rows.reduce((w, x) => Number(x[1]) >= Number(w[1]) ? x : w);
      return cs ? `dominuje fáze ${dom[0]} (${fmtNum(Math.round(Number(dom[1])))} ms)` : `${dom[0]} phase dominates (${fmtNum(Math.round(Number(dom[1])))} ms)`;
    }
    return null;
};

// Machine-comparable culprit identity (not the display string) — used to
// decide whether two deep runs (worst pages, or devices) share ONE culprit.
const culpritKeyFor = (m, res) => {
    if (!res) return null;
    if (m === 'CLS') {
      const els = Array.isArray(res.elements) ? mergeClsContainerElements(res.elements) : [];
      return els[0]?.selector || null;
    }
    if (m === 'LCP') return res.lcpElement?.selector || null;
    if (m === 'INP') {
      // Even a matching phase@selector is only an interaction signature.
      // It cannot establish a shared implementation defect.
      return null;
    }
    if (m === 'TTFB') {
      const ph = res.phases || {};
      const rows = Object.entries(ph).filter(([, v]) => Number.isFinite(Number(v)) && Number(v) > 0);
      return rows.length ? rows.reduce((w, x) => Number(x[1]) >= Number(w[1]) ? x : w)[0] : null;
    }
    return null;
};

const sweepDeepEntries = (r) => {
  const runs = Array.isArray(r.sweepDeepRuns) ? r.sweepDeepRuns : [];
  if (!runs.length) return null;
  const cs = r.lang === 'cs';
  const requestedMetric = String(r.metricLower || r.metric || runs[0].metric || '').toUpperCase();
  const selectedRuns = runs.filter(run => String(run?.metric || '').toUpperCase() === requestedMetric);
  if (!selectedRuns.length) return null;
  const m = requestedMetric;
  const key = m.toLowerCase();
  const fmt = (v) => formatMetricValue(key, v) || '—';
  const entries = selectedRuns.map(run => {
    const runMetric = String(run.metric || '').toUpperCase();
    const res = run.success ? run.result : null;
    const deepValue = deepValFor(runMetric, res);
    return {
      metric: runMetric.toLowerCase(),
      url: run.url,
      sweepValue: Number.isFinite(Number(run.sweepValue)) ? Number(run.sweepValue) : null,
      deepValue,
      culprit: culpritTextFor(runMetric, res, cs),
      culpritKey: culpritKeyFor(runMetric, res),
      promoted: !!run.promoted,
      failed: !run.success,
      failure: run.failure || null,
      // Deep run landed in the "good" band while the sweep flagged it — the
      // sweep's single shallow run was likely noise for this page.
      sweepLikelyNoise: deepValue != null && deepValue <= SWEEP_DEEP_GOOD[key]
    };
  });
  // Culprit agreement across the worst pages. Agreement across ≥2 pages is a
  // TEMPLATE-level finding (fix the template, not individual pages);
  // disagreement means the sweep's worst pages fail for their own reasons.
  const keyed = entries.filter(e => !e.failed && e.culpritKey);
  const consensus = keyed.length >= 2 ? {
    agree: keyed.every(e => e.culpritKey === keyed[0].culpritKey),
    culpritKey: keyed[0].culpritKey,
    pages: keyed.length
  } : null;
  return { m, cs, fmt, entries, consensus: r.evidenceContract ? null : consensus,
    contractPresent: !!r.evidenceContract };
};

// A worst-page ticket is selected by the shallow sweep, but once the same URL
// has a successful deep run the ticket must lead with the stronger deep value.
// Keep the sweep value alongside it to preserve why the page was selected and
// make both evidence sources explicit.
const worstPageMeasurementEvidence = (r, worst) => {
  const metric = String(r.metricLower || r.metric || '').toLowerCase();
  const cs = r.lang === 'cs';
  const sweepValue = Number.isFinite(Number(worst?.value)) ? Number(worst.value) : null;
  const d = sweepDeepEntries(r);
  const normalizeUrl = (url) => {
    try {
      const parsed = new URL(/^https?:\/\//i.test(String(url || '')) ? String(url) : `https://${url}`);
      const pathname = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/+$/, '');
      return `${parsed.host.toLowerCase()}${pathname}${parsed.search}`;
    } catch {
      const raw = String(url || '').replace(/^https?:\/\//i, '').replace(/#.*$/, '');
      const slash = raw.indexOf('/');
      const host = (slash === -1 ? raw : raw.slice(0, slash)).toLowerCase();
      const rest = slash === -1 ? '' : raw.slice(slash).replace(/\/+$/, '');
      return `${host}${rest}`;
    }
  };
  const deepEntry = d && String(d.m || '').toLowerCase() === metric
    ? d.entries.find(e => !e.failed
      && e.metric === metric
      && e.deepValue != null
      && Number.isFinite(Number(e.deepValue))
      && normalizeUrl(e.url) === normalizeUrl(worst?.url))
    : null;
  if (deepEntry) {
    const sweep = deepEntry.sweepValue ?? sweepValue;
    return {
      source: 'deep',
      value: Number(deepEntry.deepValue),
      text: cs
        ? `hloubkový test: ${formatMetricValue(metric, deepEntry.deepValue) || '—'}${sweep != null ? `; sweep: ${formatMetricValue(metric, sweep) || '—'}` : ''}`
        : `deep test: ${formatMetricValue(metric, deepEntry.deepValue) || '—'}${sweep != null ? `; sweep: ${formatMetricValue(metric, sweep) || '—'}` : ''}`
    };
  }
  return {
    source: 'sweep',
    value: sweepValue,
    text: `${cs ? 'sweep' : 'sweep'}: ${formatMetricValue(metric, sweepValue) || '—'}`
  };
};

const sweepDeepIntro = (d) => {
  if (d.contractPresent) return d.cs
    ? `Sweep vybral měřené stránky pro ${d.m}; následující hloubkové běhy patří jednotlivým URL. Shodný selektor nebo diagnostický kandidát nepotvrzuje společný mechanismus ani šablonu.`
    : `The sweep selected measured pages for ${d.m}; the following deep runs belong to their respective URLs. A matching selector or diagnostic candidate proves neither a common mechanism nor a template.`;
  return d.cs
    ? `Hloubkové běhy ${d.m} patří vybraným URL ze sweepu. Výběr není potvrzeným pořadím nejhorších stránek. Sweep a hloubkové běhy jsou odlišná měření; jejich podmínky a počty opakování je nutné posuzovat samostatně.`
    : `The ${d.m} deep runs belong to selected sweep URLs. Selection is not a verified ranking of the worst pages. Sweep and deep runs are distinct measurements; assess their conditions and repeat counts separately.`;
};

// One-line verdict on culprit agreement across the worst pages — the
// template-vs-page-specific conclusion the client actually acts on.
const sweepDeepConsensusLine = (d) => {
  if (!d.consensus) return null;
  if (d.consensus.agree) {
    return d.cs
      ? `Shoda: všech ${d.consensus.pages} hloubkově měřených nejhorších stránek má stejného viníka (${d.consensus.culpritKey}) — problém je v šabloně, ne v jednotlivých stránkách. Oprava šablony opraví stránky, které ji používají.`
      : `Agreement: all ${d.consensus.pages} deep-measured worst pages share the same culprit (${d.consensus.culpritKey}) — the problem lives in the template, not in individual pages. Fixing the template fixes them all at once.`;
  }
  return d.cs
    ? `Viníci se mezi nejhoršími stránkami liší — problém je stránkově specifický, ne šablonový. Řešte stránky jednotlivě, počínaje tou nejhorší.`
    : `Culprits differ across the worst pages — the problem is page-specific, not template-level. Fix pages individually, starting with the worst one.`;
};

const sweepDeepEntryLines = (d, e) => {
  const lines = [];
  if (e.failed) {
    lines.push(d.cs ? `Navazující hloubkový diagnostický běh na této stránce selhal — k dispozici zůstávají jen mělké sweep běhy (${d.fmt(e.sweepValue)}). Jde o dvě různé vrstvy měření: případný medián z opakovaných sweep běhů tím není zpochybněn, chybí ale hloubkový rozpad příčin.` : `The follow-up deep diagnostic run on this page failed — only the shallow sweep runs remain (${d.fmt(e.sweepValue)}). These are two different measurement layers: a median of repeated sweep runs is not invalidated by this, but the deep cause breakdown is missing.`);
    const reason = e.failure?.reason || e.failure?.reasonCode;
    const descriptions = {
      'inputs-not-received': ['Měření nezachytilo očekávaný vstup na testovaném prvku.', 'The measurement did not capture the expected input on the tested element.'],
      'no-targets': ['Nebyl nalezen bezpečný prvek pro testování.', 'No safe interaction target was found.'],
      'no-event-timing-entry': ['Pro interakci nebyl zachycen časový záznam.', 'No Event Timing entry was captured for the interaction.'],
      'timeout': ['Měření překročilo časový limit.', 'The measurement exceeded its time limit.'],
      'browser-disconnected': ['Během měření se odpojil prohlížeč.', 'The browser disconnected during measurement.'],
      'navigation-error': ['Nepodařilo se načíst měřenou stránku.', 'The measured page could not be loaded.'],
      'missing-attribution': ['Chybí platný rozpad měřené interakce.', 'A valid interaction breakdown is missing.'],
      'budget-exhausted': ['Na další měření nezbyl čas v rozpočtu auditu.', 'The audit had no remaining time for another measurement.']
    };
    if (Object.hasOwn(descriptions, reason)) {
      lines.push(`${d.cs ? 'Důvod' : 'Reason'}: ${descriptions[reason][d.cs ? 0 : 1]} (${reason})`);
    } else {
      lines.push(d.cs ? 'Konkrétní důvod selhání není k dispozici.' : 'The specific failure reason is unavailable.');
    }
    return lines;
  }
  lines.push(d.cs
    ? `Sweep: ${d.fmt(e.sweepValue)} · hloubkový test: ${d.fmt(e.deepValue)}`
    : `Sweep: ${d.fmt(e.sweepValue)} · deep test: ${d.fmt(e.deepValue)}`);
  if (e.culprit && !d.contractPresent) lines.push(e.culprit);
  if (e.sweepLikelyNoise) {
    lines.push(d.cs
      ? 'Hloubkový test vyšel v pásmu „dobré“. Sweep a hloubkové měření jsou samostatné série; rozdíl nepotvrzuje šum ani opravu. Před implementačním úkolem porovnejte podmínky a profil pomalých interakcí.'
      : 'The deep test landed in the "good" band. Sweep and deep measurements are separate series; the difference proves neither noise nor a fix. Compare conditions and slow-interaction profiles before creating an implementation task.');
  }
  return lines;
};

const renderSweepDeepAudit = (r) => {
  const d = sweepDeepEntries(r);
  if (!d) return '';
  return `
  <section class="section">
    <h2 class="section-title">${d.cs ? `Hloubková diagnostika vybraných stránek (${d.m})` : `Deep diagnostics on selected pages (${d.m})`}</h2>
    <p style="font-size:13px;color:#555;max-width:720px;">${escapeHtml(sweepDeepIntro(d))}</p>
    ${d.entries.map(e => `
    <div class="callout" style="margin-top:12px;">
      <strong style="word-break:break-all;">${escapeHtml(e.url)}</strong>${e.promoted ? ` <span style="font-family:var(--mono);font-size:11px;border:1px solid var(--ink);border-radius:2px;padding:1px 6px;">${d.cs ? 'zdroj hlavní diagnostiky' : 'source of main diagnostics'}</span>` : ''}<br>
      ${sweepDeepEntryLines(d, e).map(escapeHtml).join('<br>')}
    </div>`).join('')}
    ${sweepDeepConsensusLine(d) ? `
    <div class="callout" style="margin-top:16px;">
      <strong>${d.cs ? 'Šablona, nebo jednotlivá stránka?' : 'Template, or individual page?'}</strong>
      ${escapeHtml(sweepDeepConsensusLine(d))}
    </div>` : ''}
  </section>`;
};

const renderSweepDeepMockup = (r) => {
  const d = sweepDeepEntries(r);
  if (!d) return '';
  return `
  <div class="glass panel" style="margin-bottom:24px;">
    <div class="panel-header">
      <div class="panel-title">${d.cs ? `Hloubková diagnostika vybraných stránek (${d.m})` : `Deep diagnostics on selected pages (${d.m})`}</div>
    </div>
    <div style="font-size:12px;color:#94a3b8;margin-bottom:12px;">${escapeHtml(sweepDeepIntro(d))}</div>
    ${d.entries.map(e => `
    <div style="background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:12px 14px;margin-top:8px;font-size:12px;color:#e2e8f0;">
      <div style="font-family:'JetBrains Mono',monospace;color:#f8fafc;word-break:break-all;">${escapeHtml(e.url)}${e.promoted ? ` <span style="font-size:10px;border:1px solid rgba(148,163,184,0.5);border-radius:4px;padding:1px 6px;color:#94a3b8;">${d.cs ? 'zdroj hlavní diagnostiky' : 'source of main diagnostics'}</span>` : ''}</div>
      ${sweepDeepEntryLines(d, e).map(l => `<div style="margin-top:4px;color:#cbd5e1;">${escapeHtml(l)}</div>`).join('')}
    </div>`).join('')}
    ${sweepDeepConsensusLine(d) ? `
    <div style="margin-top:12px;padding:12px 14px;border:1px solid rgba(56,189,248,0.25);border-radius:10px;font-size:12px;color:#e2e8f0;">
      <strong>${d.cs ? 'Šablona, nebo jednotlivá stránka?' : 'Template, or individual page?'}</strong>
      <div style="margin-top:4px;color:#cbd5e1;">${escapeHtml(sweepDeepConsensusLine(d))}</div>
    </div>` : ''}
  </div>`;
};

// =========================================================================
// EXECUTIVE SUMMARY ("Shrnutí pro vedení") — 3-4 plain-language lines at the
// very top of the report, BEFORE any technical section. Answers the four
// questions a manager/director cares about: what the problem is in business
// terms (no metric values), who it affects (device + visit share), who fixes
// it (owner role), and when the effect shows (28-day CrUX window). Numbers
// and metrics stay in the technical sections below — this is for decisions,
// not diagnostics. The lead follows the PRIORITY ranking (metric #1), never
// a hard-coded metric.
// =========================================================================
const execProblemLine = (mUp, cs, severity = 'critical') => ({
  CLS: cs
    ? 'Web se při načítání posouvá návštěvníkům pod rukama — může to vést k omylným klikům a působí nespolehlivě. Dobré Core Web Vitals jsou součástí hodnocení uživatelské zkušenosti ve vyhledávání; zlepšení CLS odstraní jednu z výkonnostních slabin.'
    : 'The page shifts under visitors\u2019 hands while loading — it can cause mis-clicks and feels unreliable. Good Core Web Vitals are part of how search evaluates user experience; improving CLS removes one of the performance weak spots.',
  INP: severity === 'warning'
    ? (cs
      ? 'INP u reálných uživatelů překračuje hranici dobrého výsledku a vyžaduje zlepšení. Laboratorní měření určí konkrétní opravu teprve po reprodukci pomalé interakce.'
      : 'Real-user INP exceeds the good threshold and needs improvement. Lab testing can identify a concrete fix only after reproducing a slow interaction.')
    : (cs
      ? 'Web reaguje na kliknutí a dotyky návštěvníků s výrazným zpožděním. Dobré Core Web Vitals jsou součástí hodnocení uživatelské zkušenosti ve vyhledávání; zlepšení INP odstraní jednu z výkonnostních slabin.'
      : 'The site responds to visitors\u2019 clicks and taps with a significant delay. Good Core Web Vitals are part of how search evaluates user experience; improving INP removes one of the performance weak spots.'),
  LCP: cs
    ? 'Hlavní obsah se návštěvníkům zobrazuje pomalu — část lidí odejde dřív, než web vůbec uvidí. Dobré Core Web Vitals jsou součástí hodnocení uživatelské zkušenosti ve vyhledávání; zlepšení LCP odstraní jednu z výkonnostních slabin.'
    : 'The main content appears slowly for visitors — some leave before they even see the page. Good Core Web Vitals are part of how search evaluates user experience; improving LCP removes one of the performance weak spots.',
  TTFB: cs
    ? 'Server odpovídá pomalu — každá stránka startuje se zpožděním, které žádná úprava obsahu nedožene. Dobré Core Web Vitals jsou součástí hodnocení uživatelské zkušenosti ve vyhledávání; rychlejší odpověď serveru odstraní jednu z výkonnostních slabin.'
    : 'The server responds slowly — every page starts late in a way no content change can recover. Good Core Web Vitals are part of how search evaluates user experience; a faster server response removes one of the performance weak spots.',
  FCP: cs
    ? 'Stránka se návštěvníkům začíná vykreslovat pozdě — web působí pomalu už od prvního okamžiku. Dobré Core Web Vitals jsou součástí hodnocení uživatelské zkušenosti ve vyhledávání; zlepšení FCP odstraní jednu z výkonnostních slabin.'
    : 'The page starts rendering late for visitors — the site feels slow from the first moment. Good Core Web Vitals are part of how search evaluates user experience; improving FCP removes one of the performance weak spots.'
}[mUp] || null);

// Single source of the template-vs-page-specific verdict: the culprit
// comparison across deep-measured worst sweep pages (sweepDeepEntries
// consensus). Every place that mentions "šablona" reads THIS verdict —
// the exec summary composed its own template sentence and contradicted
// the consensus section ("page-specific") in the same report.
//   'template'              — ≥2 deep runs, same culprit
//   'page_specific'         — ≥2 deep runs, culprits differ
//   'insufficient_evidence' — fewer than 2 usable deep runs
const templateVerdict = (r) => {
  if (r.evidenceContract) return 'insufficient_evidence';
  if (r.metricUpper === 'INP') return 'insufficient_evidence';
  const sd = sweepDeepEntries(r);
  if (!sd?.consensus) return 'insufficient_evidence';
  return sd.consensus.agree ? 'template' : 'page_specific';
};

const execOwnerPhrase = (key, cs, verdict = 'insufficient_evidence') => ({
  // The template clause is a claim — it needs the template verdict. With
  // page_specific or insufficient_evidence the sentence must not exist.
  frontend: verdict === 'template'
    ? (cs ? 'práce pro frontend vývojáře (úprava šablony webu — opraví stránky, které stejnou šablonu používají)' : 'a job for the frontend developer (a template change — fixes the pages that use the same template)')
    : (cs ? 'práce pro frontend vývojáře' : 'a job for the frontend developer'),
  gtm: cs ? 'práce pro správce GTM (marketingové měřicí kódy)' : 'a job for the GTM admin (marketing tags)',
  tagging: cs ? 'práce pro správce měření / integrací po ověření způsobu nasazení tagů' : 'a job for the measurement / integrations owner after verifying tag deployment',
  adops: cs ? 'práce pro reklamní tým (nastavení reklamních pozic)' : 'a job for the ad ops team (ad slot configuration)',
  infra: cs ? 'práce pro správce hostingu / infrastruktury' : 'a job for the hosting / infrastructure admin',
  privacy: cs ? 'práce pro privacy specialistu se správcem CMP (cookie lišta)' : 'a job for the privacy specialist with the CMP owner (cookie banner)'
}[key] || (cs ? 'práce pro frontend vývojáře' : 'a job for the frontend developer'));

// Top cause (plain language) + owner key for the LEAD metric. Returns
// { cause, ownerKey } with nullable cause when the deep layer has nothing.
const execCauseAndOwner = (r, mUp, cs) => {
  if (mUp === 'CLS') {
    const f = buildClsFindings(r).find(x => !x.isContainer) || buildClsFindings(r)[0];
    if (f) return { cause: (f.causeLabel || '').toLowerCase() || null, ownerKey: f.owner?.key || 'frontend' };
    return { cause: null, ownerKey: 'frontend' };
  }
  if (mUp === 'INP') {
    const finding = deriveCanonicalFinding({ ...r, metric: 'INP', language: cs ? 'cs' : 'en' });
    return { cause: finding?.cause || null, ownerKey: 'frontend' };
  }
  if (mUp === 'TTFB') {
    return { cause: cs ? 'pomalá odpověď serveru (hosting, cache)' : 'slow server response (hosting, caching)', ownerKey: 'infra' };
  }
  // LCP / FCP: top deep-LCP recommendation, owner via the recommendation map.
  const rec = r.deepLCPResults?.recommendations?.[0];
  if (rec) {
    const owner = ownerForRecommendation(rec, r.lang, { hasGtmContainer: r.hasGtmContainer })?.key || 'frontend';
    const cause = (rec.title || rec.cause || '').toLowerCase() || null;
    return { cause, ownerKey: owner };
  }
  return { cause: null, ownerKey: 'frontend' };
};

// Returns { heading, lines } or null. lines are plain text (escaped by the
// renderers). Positive (all-green) reports get a short reassurance instead.
const buildExecSummary = (r) => {
  const cs = r.lang === 'cs';
  const heading = cs ? 'Shrnutí pro vedení' : 'Executive summary';
  const pt = r.priorityTop;
  const leadUp = pt ? String(pt.metric).toUpperCase() : r.metricUpper;
  const leadValue = pt?.p75 ?? pt?.value ?? pt?.fieldValue ?? r.fieldValue ?? r.metricValue;
  const leadSeverity = statusOf(String(leadUp).toLowerCase(), Number(leadValue));
  const inpEvidenceMissing = leadUp === 'INP'
    && (r.scenario === 'inp-not-measured' || r.scenario === 'deep-failed-priority');
  const problem = inpEvidenceMissing
    ? (cs
      ? 'Field data ukazují problém s odezvou INP, ale laboratorní test nezachytil platnou interakci. Bez tohoto důkazu nelze tvrdit, jak se zpoždění na testované stránce projevuje ani co je způsobuje.'
      : 'Field data show an INP responsiveness problem, but the lab test captured no valid interaction. Without that evidence, the report cannot claim how the delay manifests on the tested page or what causes it.')
    : execProblemLine(leadUp, cs, leadSeverity);
  // No actionable priority item + audited metric in the green band = nothing
  // to escalate to management. r.status alone is not enough (scenario logic
  // can leave it neutral), so check the metric value against its threshold.
  const mv = r.fieldValue != null ? r.fieldValue : r.metricValue;
  const noProblem = !pt && (r.status === 'good'
    || (mv != null && !isNaN(mv) && statusOf(r.metricLower, Number(mv)) === 'good'));
  if (noProblem || !problem) {
    return {
      heading,
      lines: [
        cs
          ? 'Web je z pohledu rychlosti a stability, jak je hodnotí Google, v pořádku — návštěvníky nic zásadního nebrzdí a pozice ve vyhledávání to neohrožuje.'
          : 'From the speed and stability standpoint Google grades, the site is fine — nothing significant slows visitors down or threatens search rankings.',
        cs
          ? 'Není potřeba žádná okamžitá akce; doporučujeme jen pravidelné kontrolní měření, aby případné zhoršení nezůstalo bez povšimnutí.'
          : 'No immediate action is needed; we only recommend periodic re-measurement so any regression does not go unnoticed.',
        cs
          ? 'Google hodnotí klouzavé 28denní okno — případná změna k horšímu by se v jeho datech projevila postupně, je tedy čas ji zachytit kontrolním měřením včas.'
          : 'Google grades a rolling 28-day window — any change for the worse would show in its data gradually, leaving time to catch it with a follow-up measurement.'
      ]
    };
  }
  const lines = [problem];
  // Who it affects — device + visit share when the priority ranking has it.
  const dev = pt ? deviceLabel(pt.device, r.lang) : r.device;
  if (dev) {
    // Integer percent — locale-neutral (fmtNum is hard-coded to cs-CZ and
    // would leak Czech formatting into English reports).
    const share = pt?.deviceShare != null ? `${Math.round(Number(pt.deviceShare))} %` : null;
    lines.push(cs
      ? `Problém je doložen na zařízení ${dev}.${share ? ` Toto zařízení tvoří přibližně ${share} všech návštěv; nejde o odhad podílu zasažených návštěvníků.` : ''}`
      : `The issue is evidenced on ${dev}.${share ? ` This device accounts for roughly ${share} of all visits; this is not an estimate of the share of affected visitors.` : ''}`);
  }
  // Cause + owner. When the priority metric's deep test failed, say so
  // honestly instead of attributing a cause the report does not have.
  if (r.scenario === 'deep-failed-priority' || (r.failedDeepMetrics || []).includes(leadUp)) {
    lines.push(cs
      ? 'Hloubkový test hlavního problému se nezdařil. Opakované měření může dodat platná data, ale nelze předem slíbit, že určí konkrétní příčinu nebo odpovědného řešitele.'
      : 'The deep test of the primary problem did not complete. A repeat measurement may provide valid data, but cannot promise in advance that it will identify a specific cause or owner.');
  } else if (leadUp === 'INP') {
    const finding = deriveCanonicalFinding({ ...r, metric: 'INP', language: r.lang });
    lines.push(canonicalFindingSentences(finding).join(' '));
  } else {
    // Evidence-strength parity with the headline: when the INP dominance
    // profile is VARIABLE across stored runs (scenario inp-variable-profile),
    // the summary must NOT name a single "main cause" — it leads both
    // profiles and the profiling next step, same as the technical story.
    const bdSum = leadUp === 'INP' ? getLabBreakdown(r) : null;
    const dSum = bdSum?.basis === 'median' ? bdSum.dominance : null;
    if (leadUp === 'INP' && dSum?.variable && dSum.secondary) {
      const L = INP_COMPONENT_LABELS[cs ? 'cs' : 'en'];
      const p1 = L[dSum.primary] || dSum.primary;
      const p2 = L[dSum.secondary] || dSum.secondary;
      lines.push(cs
        ? `Měření ukazuje dva střídající se mechanismy: v části interakcí dominuje ${p1}, v jiné části ${p2}. Data nedovolují označit jedinou příčinu — prvním krokem je profilování nejpomalejších interakcí, teprve to rozhodne o konkrétní opravě. Oprava je ${execOwnerPhrase('frontend', cs, templateVerdict(r))}.`
        : `The measurements show two alternating mechanisms: some interactions are dominated by ${p1}, others by ${p2}. The data does not support a single cause — the first step is profiling the slowest interactions; only that decides the concrete fix. The fix is ${execOwnerPhrase('frontend', cs, templateVerdict(r))}.`);
    } else {
      const { cause, ownerKey } = execCauseAndOwner(r, leadUp, cs);
      lines.push(cs
        ? `${cause ? `Hlavní příčina: ${cause}. ` : ''}Oprava je ${execOwnerPhrase(ownerKey, cs, templateVerdict(r))}.`
        : `${cause ? `Main cause: ${cause}. ` : ''}The fix is ${execOwnerPhrase(ownerKey, cs, templateVerdict(r))}.`);
    }
  }
  lines.push(cs
    ? 'Efekt v Googlu se projeví postupně — naplno zhruba 4 týdny po nasazení opravy (Google hodnotí klouzavé 28denní okno).'
    : 'The effect in Google shows gradually — fully about 4 weeks after the fix ships (Google grades a rolling 28-day window).');
  return { heading, lines };
};

// Divider between the management part (above) and the technical depth
// (below) — one clear cut so decision-makers know where their part ends.
const execDividerLabel = (cs) => cs
  ? 'Technická analýza — pro vývojáře a správce webu'
  : 'Technical analysis — for developers and site admins';

const renderExecSummaryMockup = (r) => {
  const s = buildExecSummary(r);
  if (!s) return '';
  return `
  <div class="glass panel" style="margin-bottom:24px;border-color:rgba(148,163,184,0.3);">
    <div class="panel-header"><div class="panel-title">${escapeHtml(s.heading)}</div></div>
    <div style="font-size:14px;line-height:1.7;color:#e2e8f0;">
      ${s.lines.map(l => `<p style="margin:0 0 8px;">${escapeHtml(l)}</p>`).join('')}
    </div>
  </div>

  <div style="display:flex;align-items:center;gap:14px;margin:32px 0 24px;">
    <div style="flex:1;border-top:1px solid rgba(148,163,184,0.35);"></div>
    <div style="font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.15em;text-transform:uppercase;color:#94a3b8;white-space:nowrap;">${escapeHtml(execDividerLabel(r.lang === 'cs'))}</div>
    <div style="flex:1;border-top:1px solid rgba(148,163,184,0.35);"></div>
  </div>`;
};

const renderExecSummaryAudit = (r) => {
  const s = buildExecSummary(r);
  if (!s) return '';
  return `
  <section style="margin-bottom:0;">
    <div class="section-label">${escapeHtml(s.heading)}</div>
    <div style="border-left:3px solid var(--ink);padding:14px 0 14px 24px;font-family:var(--serif);font-size:16px;line-height:1.65;color:var(--ink);">
      ${s.lines.map(l => `<p style="margin:0 0 8px;">${escapeHtml(l)}</p>`).join('')}
    </div>
  </section>

  <div style="display:flex;align-items:center;gap:16px;margin:40px 0 44px;">
    <div style="flex:1;border-top:3px double var(--ink);"></div>
    <div style="font-family:var(--mono);font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:var(--ink);white-space:nowrap;">${escapeHtml(execDividerLabel(r.lang === 'cs'))}</div>
    <div style="flex:1;border-top:3px double var(--ink);"></div>
  </div>`;
};

// =========================================================================
// HANDOVER SECTION ("Zadání k předání") — copy-paste brief per owner
// =========================================================================
// Turns the findings into assignable work: one block per owner with concrete
// tasks (from savings recommendations + metric-specific findings) and a
// "done when" acceptance criterion. This is the part a client forwards to
// their supplier verbatim, so every task carries its measured evidence.
const renderHandoverAudit = (r) => {
  const lang = r.lang;
  const recs = Array.isArray(r.savingsRecommendations) ? r.savingsRecommendations : [];
  const cs = lang === 'cs';
  const groups = new Map(); // key -> { owner, tasks: [] }
  // Each task is a COMPLETE ticket a product manager can paste into Jira/
  // Trello/e-mail without rewriting: title (one imperative sentence),
  // priority, owner, description (what's wrong and why, from the finding),
  // fix (the concrete step) and a measurable "done when".
  const addTask = (ownerObj, task) => {
    if (!ownerObj || !task || !task.title) return;
    // A sweep candidate is a profiling ticket, never a second "fix worst
    // page" ticket beside the investigation of the same INP URL.
    if (task.topic === 'inp' && task.intent === 'profile-inp') {
      const existing = groups.get(ownerObj.key)?.tasks.find(t =>
        t.topic === 'inp' && t.intent === 'profile-inp'
        && normUrlLoose(t.intentUrl || r.url) === normUrlLoose(task.intentUrl || r.url));
      if (existing) {
        if (task.desc && !existing.desc?.includes(task.desc)) existing.desc += ` ${task.desc}`;
        return;
      }
    }
    if (task.topic === 'inp' && !evaluateInpEvidence(r).confirmedCause) {
      task = {
        ...task,
        evidenceLevel: 'candidate',
        priority: 'medium',
        title: `${cs ? 'Prověřit' : 'Verify'}: ${task.title}`,
        fix: cs
          ? 'Nejprve profilovat konkrétní pomalou interakci za zaznamenaných podmínek. Fáze Event Timing ani cíl kliknutí nepotvrzují viníka. Opravu zvolit až podle profilu a ověřit opakovaným A/B měřením.'
          : 'First profile the specific slow interaction under the recorded conditions. An Event Timing phase or click target does not confirm a culprit. Select a fix from that profile and verify with repeated A/B measurements.',
        done: cs
          ? 'Uložený profil identifikuje konkrétní příčinu; srovnatelné opakované běhy ověří přínos a funkčnost případné opravy.'
          : 'A saved profile identifies the specific cause; comparable repeated runs verify the benefit and functionality of any proposed fix.'
      };
    }
    if (!groups.has(ownerObj.key)) groups.set(ownerObj.key, { owner: ownerObj, tasks: [] });
    const g = groups.get(ownerObj.key);
    // Diagnostic tickets can be phrased differently while asking for the
    // same work. Prefer an explicit intent + URL + metric identity; retain the
    // older exact-content fallback for unrelated implementation tickets.
    const key = (t) => t.intent
      ? `${t.topic || ''}||${t.intent}||${normUrlLoose(t.intentUrl || r.url)}`
      : `${t.title}||${t.desc || ''}`;
    if (!g.tasks.some(t => key(t) === key(task))) g.tasks.push({ priority: 'medium', ...task });
  };
  // Evidence level of a savings recommendation — the claim strength must
  // match the evidence strength. Server sends rec.evidenceLevel; older
  // stored payloads are inferred from the estimate's source/unit:
  // PSI bytes = observed, PSI ms = supported (simulation), coefficient-based
  // percent/absolute estimates = candidate (must be verified, never phrased
  // as a proven fix with a guaranteed saving).
  const recEvidence = (rec) => {
    if (rec.evidenceLevel) return rec.evidenceLevel;
    const src = rec.estimatedSavings?.source;
    if (src === 'psi' || src === 'psi_direct') {
      return rec.estimatedSavings?.unit === 'bytes' ? 'observed' : 'supported';
    }
    return 'candidate';
  };
  for (const rec of recs) {
    const owner = ownerForRecommendation(rec, lang, { hasGtmContainer: r.hasGtmContainer });
    if (!owner) continue;
    const saving = formatSavingsValue(rec.estimatedSavings);
    const savingTxt = saving && saving !== '—' ? saving : null;
    const evidence = recEvidence(rec);
    const candidate = evidence === 'candidate';
    const unusedJs = rec.id === 'remove-unused-javascript'
      || /unused javascript|nepoužívaného javascriptu/i.test(String(rec.title || ''));
    const loadingReserve = ['inp', 'cls'].includes(r.metricLower)
      && r.fieldValue != null && statusOf(r.metricLower, Number(r.fieldValue)) !== 'good';
    const basePriority = rec.impact === 'high' ? 'high' : rec.impact === 'medium' ? 'medium' : 'low';
    addTask(owner, {
      // Candidate optimizations are "verify" work, not proven fixes — the
      // title must say so (e.g. server-side GTM: the move itself does not
      // guarantee removing browser-side work; depends on tags/architecture).
      title: unusedJs
        ? (cs ? 'Prověřit: Omezit skutečně nepotřebný JavaScript' : 'Verify: Reduce JavaScript confirmed unnecessary')
        : candidate
        ? (cs ? `Prověřit: ${rec.title || rec.id}` : `Verify: ${rec.title || rec.id}`)
        : (rec.title || rec.id),
      priority: loadingReserve ? 'low' : basePriority,
      // Savings recommendations are LOADING optimizations — they must not
      // become the main task of a responsiveness (INP) report without a
      // proven link to a concrete slow interaction.
      topic: 'loading',
      desc: candidate
        ? (cs
          ? `Kandidátní optimalizace: ${rec.title || rec.id}.${savingTxt ? ` Potenciální úspora až ${savingTxt} — jde o modelový odhad, ne měření tohoto webu; skutečný přínos závisí na konkrétní implementaci a je nutné ho ověřit.` : ''}`
          : `Candidate optimization: ${rec.title || rec.id}.${savingTxt ? ` Potential saving up to ${savingTxt} — a model-based estimate, not a measurement of this site; the real benefit depends on the implementation and must be verified.` : ''}`)
        : (cs
          ? `Měření našlo na stránce příležitost: ${rec.title || rec.id}.${savingTxt ? ` Příležitost dle PSI ${savingTxt}, ne zaručená úspora po odstranění; výsledek ověřte po změně.` : ''}`
          : `The measurement found an opportunity on the page: ${rec.title || rec.id}.${savingTxt ? ` PSI opportunity ${savingTxt}, not a guaranteed saving from removal; verify after the change.` : ''}`),
      fix: unusedJs
        ? (cs
          ? 'Nejprve určit konkrétní moduly a scénáře označené jako nepoužité, ověřit jejich závislosti a použití mimo studené načtení. Odstranit nebo odložit jen potvrzeně nepotřebný kód na testovacím nasazení a před produkcí provést regresní test.'
          : 'First identify the specific modules and scenarios marked unused, then review their dependencies and use beyond the cold-load trace. Remove or defer only code confirmed unnecessary in a test deployment, and run regression tests before production.')
        : candidate
        ? (cs
          ? 'Nejprve ověřit přínos A/B laboratorním měřením (testovací nasazení / staging), teprve potom nasadit plošně.'
          : 'Verify the benefit with an A/B lab measurement (test deploy / staging) first, only then roll out.')
        : (cs
          ? `Provést úpravu dle titulku a nasadit.`
          : `Apply the change in the title and deploy.`),
      done: unusedJs
        ? (cs
          ? `Kontrolní měření prokáže redukci oproti výchozímu stavu${savingTxt ? ` (orientační příležitost ${savingTxt})` : ''}; regresní test potvrdí funkčnost všech dotčených scénářů.`
          : `A follow-up measurement shows a reduction against baseline${savingTxt ? ` (indicative opportunity ${savingTxt})` : ''}; regression testing confirms all affected scenarios still work.`)
        : candidate
        ? (cs
          ? 'Kontrolní A/B lab měření prokáže měřitelné zlepšení oproti stavu před úpravou.'
          : 'A follow-up A/B lab measurement shows a measurable improvement over the pre-change state.')
        : (cs
          ? `Kontrolní lab běh po nasazení prokáže měřitelnou redukci oproti stavu před úpravou${savingTxt ? ` (referenční odhad ${savingTxt})` : ''} bez funkční regrese.`
          : `A follow-up lab run after deploy shows a measurable reduction versus the pre-change baseline${savingTxt ? ` (reference estimate ${savingTxt})` : ''} with no functional regression.`)
    });
  }
  // Metric-specific tasks derived from deep-run findings. In the unified CWV
  // report every carried deep run contributes its tasks; a single-metric
  // report stays scoped to its audited metric.
  // Acceptance criterion for a page flagged warn/poor. "Out of the red band"
  // is WRONG for a page already in the orange band (e.g. INP 472 ms is
  // needs-improvement, not poor >500 ms) — such a ticket is "done" before
  // any work starts. The valid target is the metric's GOOD threshold, plus
  // a no-regression clause for the red band.
  const doneBandCriterion = (metricLower) => {
    const t = metricThresholds[metricLower];
    const goodTxt = t ? formatMetricValue(metricLower, t.good) : null;
    return cs
      ? `Medián opakovaných kontrolních měření této stránky je ${goodTxt ? `nejvýše ${goodTxt} (dobré pásmo)` : 'v dobrém pásmu'} a žádný validní běh není v červeném pásmu.`
      : `The median of repeated follow-up runs of this page is ${goodTxt ? `at or below ${goodTxt} (good band)` : 'in the good band'} and no valid run is in the red band.`;
  };
  if (deepSectionAllowed(r, 'inp') && r.deepINPResults) {
    const pd = r.deepINPResults?.worstInteraction?.presentationDelay ?? r.deepINPResults?.attribution?.presentationDelay;
    if (pd > 100 && ['reproduced', 'intermittent'].includes(evaluateInpEvidence(r).reproduction)) {
      addTask(ownerForRecommendation({ owner: 'frontend' }, lang), {
        title: cs ? 'Profilovat pomalou interakci INP' : 'Profile the slow INP interaction',
        priority: 'medium',
        topic: 'inp',
        intent: 'profile-inp',
        intentUrl: r.deepAnalyzedUrl || r.url,
        desc: cs
          ? `Jedna zaznamenaná interakce obsahuje presentation delay ${Math.round(pd)} ms. Jde o kandidátní stopu fáze Event Timing, ne o prokázanou příčinu ani hotový opravný zásah.`
          : `One recorded interaction contains ${Math.round(pd)} ms of presentation delay. This is an Event Timing phase candidate, not a proven cause or a ready-made repair.`,
        fix: cs
          ? 'Profilovat pomalé interakce (DevTools Performance) a podle profilu batchovat DOM zápisy; CSS contain a zmenšení DOM nasadit na subtree, který profil označí jako drahý — bez profilu jde o kandidátní kroky.'
          : 'Profile the slow interactions (DevTools Performance) and batch DOM writes per the profile; apply CSS contain and DOM reduction to the subtree the profile marks as expensive — without a profile these are candidate steps.',
        done: cs
          ? 'Uložený profil identifikuje konkrétní práci v interakčním okně a srovnatelné opakované běhy potvrdí, zda se problém reprodukuje.'
          : 'A saved profile identifies the concrete work in the interaction window and comparable repeated runs confirm whether the problem reproduces.'
      });
    }
    // The site-wide worst page belongs to the PRIMARY metric's multiPage
    // measurement — only an INP-primary report may turn it into an INP
    // ticket, otherwise a CLS worst page would spawn a duplicate "start with
    // the worst template" ticket here besides the CLS one.
    const worst = r.metricLower === 'inp' && r.multiPage?.matchesMetric && r.multiPage.worst?.url ? r.multiPage.worst : null;
    if (worst && (r.multiPage.warnCount > 0 || r.multiPage.poorCount > 0)) {
      // "Šablona" is a claim owned by the deep-run culprit consensus
      // (templateVerdict) — without the template verdict this ticket talks
      // about the worst PAGE, never about a template.
      const isTemplate = templateVerdict(r) === 'template';
      const evidence = worstPageMeasurementEvidence(r, worst);
      addTask(ownerForRecommendation({ owner: 'frontend' }, lang), {
        title: cs
          ? `Začít u nejhorší ${isTemplate ? 'šablony' : 'stránky'}: ${shortenUrl(worst.url, 70)}`
          : `Start with the worst ${isTemplate ? 'template' : 'page'}: ${shortenUrl(worst.url, 70)}`,
        priority: 'medium',
        topic: 'inp',
        intent: 'profile-inp',
        intentUrl: worst.url,
        desc: cs
          ? `Nejhorší stránka webu podle sweepu: ${shortenUrl(worst.url, 70)} (${evidence.text}).`
          : `The site's worst page according to the sweep: ${shortenUrl(worst.url, 70)} (${evidence.text}).`,
        fix: cs
          ? 'Profilovat interakce na této URL za srovnatelných podmínek a porovnat sweep s hloubkovým během. Konkrétní opravu navrhnout teprve po identifikaci mechanismu.'
          : 'Profile interactions on this URL under comparable conditions and compare sweep with the deep run. Propose a specific fix only after identifying a mechanism.',
        done: cs
          ? 'Zaznamenaný profil objasní rozdíl mezi běhy; případný zásah se ověří opakovaným A/B měřením.'
          : 'A recorded profile explains the run-to-run difference; any subsequent change is verified with repeated A/B measurements.'
      });
    }
  }
  // Sweep→deep consensus is computed up front: when the template-level
  // culprit is the SAME element as the #1 CLS culprit, its evidence is folded
  // into that one ticket instead of spawning duplicate tickets for one fix.
  const sd = sweepDeepEntries(r);
  const stripNth = (s) => String(s || '').replace(/:nth-of-type\(\d+\)/g, '');
  let clsConsensusMerged = false;
  let clsWorstMerged = false;
  // CLS: every measured shifting element becomes an assignable task with its
  // measured evidence ("element X shifts the layout by Y") and a cause owner.
  if (deepSectionAllowed(r, 'cls')) {
    const worstClsPage = r.metricLower === 'cls' && r.multiPage?.matchesMetric && r.multiPage.worst?.url ? r.multiPage.worst : null;
    const culprits = buildClsFindings(r).slice(0, 5).filter(f => !f.isContainer);
    culprits.forEach((f, idx) => {
      // Template evidence rides on the #1 culprit ticket when the sweep→deep
      // consensus points at the very same element — three tickets about one
      // element and one fix collapse into one ticket with all the evidence.
      let templateEvidence = '';
      if (idx === 0 && sd?.consensus?.agree
        && String(sd.m || '').toUpperCase() === 'CLS'
        && stripNth(sd.consensus.culpritKey) === stripNth(f.selector)) {
        const urls = sd.entries.filter(e => !e.failed && e.culpritKey).map(e => `${shortenUrl(e.url, 60)} (${sd.fmt(e.deepValue)})`);
        templateEvidence = cs
          ? ` Šablonový důkaz: hloubkové měření našlo stejného viníka (${sd.consensus.culpritKey}) na ${sd.consensus.pages} nejhorších stránkách — ${urls.join(', ')}. Opravit šablonu, ne jednotlivé stránky — oprava se propíše na stránky, které ji používají.`
          : ` Template evidence: the deep run found the same culprit (${sd.consensus.culpritKey}) on the ${sd.consensus.pages} worst pages — ${urls.join(', ')}. Fix the template, not individual pages — the fix carries to the pages using it.`;
        clsConsensusMerged = true;
        if (worstClsPage && (r.multiPage.warnCount > 0 || r.multiPage.poorCount > 0)) {
          const evidence = worstPageMeasurementEvidence(r, worstClsPage);
          templateEvidence += cs
            ? ` Začít u nejhorší šablony: ${shortenUrl(worstClsPage.url, 70)} (${evidence.text}).`
            : ` Start with the worst template: ${shortenUrl(worstClsPage.url, 70)} (${evidence.text}).`;
          clsWorstMerged = true;
        }
      }
      // A pushed page container is a victim, not a culprit — it must NEVER
      // become a standalone "reserve space for <container>" task. Its
      // evidence rides along on the culprit's task instead.
      const containerNote = f.pushedContainers
        ? (cs
          ? ` Tentýž posun se propsal i do kontejneru ${f.pushedContainers.map(c => shortenUrl(c.selector, 40)).join(', ')} (jeden fyzický posun připsaný dvakrát).`
          : ` The same shift also propagated into the container ${f.pushedContainers.map(c => shortenUrl(c.selector, 40)).join(', ')} (one physical shift attributed twice).`)
        : '';
      const shareTxt = f.share != null ? `${f.share} %` : null;
      // The #1 culprit is THE main task of the whole handover: it carries
      // its measured share as the reason it leads.
      // The share-based reason ("81 % of shifts — removes most of the CLS")
      // is only a valid claim when the culprit really dominates; a marginal
      // top culprit just leads as the largest measured shift.
      const leadReason = idx === 0
        ? (f.share != null && f.share >= 50
          ? (cs ? `${shareTxt} naměřených posunů — oprava odstraní většinu CLS` : `${shareTxt} of measured shifts — fixing it removes most of the CLS`)
          : (cs ? 'největší naměřený posun na stránce' : 'the largest measured shift on the page'))
        : null;
      addTask(f.owner, {
        title: `${f.causeLabel}: ${shortenUrl(f.selector || f.name, 60)}`,
        priority: idx === 0 ? 'high' : (f.share != null && f.share >= 15 ? 'medium' : 'low'),
        topic: 'cls',
        leadReason,
        desc: cs
          ? `Prvek ${f.displayName} (${f.causeLabel.toLowerCase()}) posouvá layout o ${fmtShift(f.shift)}${f.shiftCount > 1 ? ` (${f.shiftCount}×)` : ''}${shareTxt ? ` — ${shareTxt} celkového CLS stránky` : ''}. Obsah pod ním uskakuje uživatelům pod rukama.${containerNote}${templateEvidence}`
          : `Element ${f.displayName} (${f.causeLabel.toLowerCase()}) shifts the layout by ${fmtShift(f.shift)}${f.shiftCount > 1 ? ` (${f.shiftCount}×)` : ''}${shareTxt ? ` — ${shareTxt} of the page's total CLS` : ''}. Content below it jumps under the user's hands.${containerNote}${templateEvidence}`,
        fix: f.advice,
        done: cs
          ? `Prvek ${shortenUrl(f.selector || f.name, 50)} se v kontrolním měření již neposouvá (přínos ~${fmtShift(f.shift)} CLS).`
          : `Element ${shortenUrl(f.selector || f.name, 50)} no longer shifts in the follow-up run (worth ~${fmtShift(f.shift)} CLS).`
      });
    });
    const worstCls = clsWorstMerged ? null : worstClsPage;
    if (worstCls && (r.multiPage.warnCount > 0 || r.multiPage.poorCount > 0)) {
      // Same rule as the INP worst-page ticket: "šablona" only with the
      // template verdict from the deep-run culprit consensus.
      const isTemplate = templateVerdict(r) === 'template';
      const evidence = worstPageMeasurementEvidence(r, worstCls);
      addTask(ownerForRecommendation({ owner: 'frontend' }, lang), {
        title: cs
          ? `Začít u nejhorší ${isTemplate ? 'šablony' : 'stránky'}: ${shortenUrl(worstCls.url, 70)}`
          : `Start with the worst ${isTemplate ? 'template' : 'page'}: ${shortenUrl(worstCls.url, 70)}`,
        priority: 'medium',
        topic: 'cls',
        desc: cs
          ? `Nejhorší stránka webu podle sweepu: ${shortenUrl(worstCls.url, 70)} (${evidence.text}).`
          : `The site's worst page according to the sweep: ${shortenUrl(worstCls.url, 70)} (${evidence.text}).`,
        fix: cs
          ? (isTemplate
            ? 'Opravit nejprve tuto šablonu — zlepšení se propíše na všechny stránky stejného typu.'
            : 'Opravit nejprve tuto stránku — je nejhorší naměřenou stránkou webu; šablonový efekt nebyl hloubkovým měřením potvrzen.')
          : (isTemplate
            ? 'Fix this template first — the improvement carries to every page of the same type.'
            : 'Fix this page first — it is the worst measured page of the site; no template-level effect was confirmed by the deep runs.'),
        done: doneBandCriterion(r.metricLower)
      });
    }
  }
  // Ad-ops finding: ad creatives (images from ad-server domains) that take
  // seconds to download are an ad-server/compression problem, not a frontend
  // one — hand them to ad ops with the measured evidence.
  const slowCreatives = (r.diagnostics?.resourceTiming?.slowestResources || [])
    .filter(res => {
      const url = res?.url || '';
      const dur = res?.duration ?? res?.durationMs ?? 0;
      if (dur < 1500) return false;
      if (!/\.(jpe?g|png|gif|webp|avif)(\?|$)/i.test(url) && res?.initiatorType !== 'img') return false;
      return classifyVendor(url)?.category === 'ads';
    })
    .sort((a, b) => ((b.duration ?? b.durationMs) || 0) - ((a.duration ?? a.durationMs) || 0))
    .slice(0, 3);
  if (slowCreatives.length) {
    const worstMs = Math.round((slowCreatives[0].duration ?? slowCreatives[0].durationMs) || 0);
    const hosts = [...new Set(slowCreatives.map(res => { try { return new URL(res.url).hostname; } catch (_) { return null; } }).filter(Boolean))];
    addTask(ownerForCategory('ads', lang), {
      title: cs
        ? `Zmenšit reklamní kreativy z ${hosts.join(', ')}`
        : `Shrink ad creatives from ${hosts.join(', ')}`,
      priority: 'medium',
      desc: cs
        ? `Reklamní kreativy z ${hosts.join(', ')} se stahují až ${(worstMs / 1000).toFixed(1).replace('.', ',')} s (${slowCreatives.length}×) — zdržují vykreslení a mohou posouvat obsah. Jde o problém ad serveru, ne webu.`
        : `Ad creatives from ${hosts.join(', ')} take up to ${(worstMs / 1000).toFixed(1)} s to download (${slowCreatives.length}×) — they delay rendering and can shift content. This is an ad-server problem, not a site problem.`,
      fix: cs
        ? 'Zkontrolovat velikost a kompresi kreativ na ad serveru (WebP/AVIF, správné rozměry).'
        : 'Check creative size/compression on the ad server (WebP/AVIF, correct dimensions).',
      done: cs
        ? 'Kreativy se v kontrolním měření stahují pod 1,5 s.'
        : 'Creatives download under 1.5 s in the follow-up run.'
    });
  }
  // Sweep→deep culprit agreement: when the worst pages share one culprit,
  // the actionable task is "fix the template", with the measured evidence
  // from every page attached — stronger than any single-page finding.
  if (sd?.consensus && !clsConsensusMerged) {
    const fe = ownerForRecommendation({ owner: 'frontend' }, lang);
    if (sd.consensus.agree) {
      const urls = sd.entries.filter(e => !e.failed && e.culpritKey).map(e => `${shortenUrl(e.url, 60)} (${sd.fmt(e.deepValue)})`);
      addTask(fe, {
        title: cs
          ? `Opravit šablonu — stejný viník (${sd.consensus.culpritKey}) na ${sd.consensus.pages} nejhorších stránkách`
          : `Fix the template — the same culprit (${sd.consensus.culpritKey}) on the ${sd.consensus.pages} worst pages`,
        priority: 'high',
        topic: String(sd.m || r.metricLower).toLowerCase(),
        leadReason: cs
          ? `šablonový problém potvrzený na ${sd.consensus.pages} stránkách — jedna oprava zlepší celý web`
          : `a template problem confirmed on ${sd.consensus.pages} pages — one fix improves the whole site`,
        desc: cs
          ? `Šablonový problém ${sd.m}: hloubkové měření našlo stejného viníka (${sd.consensus.culpritKey}) na ${sd.consensus.pages} nejhorších stránkách — ${urls.join(', ')}.`
          : `Template-level ${sd.m} problem: the deep run found the same culprit (${sd.consensus.culpritKey}) on the ${sd.consensus.pages} worst pages — ${urls.join(', ')}.`,
        fix: cs
          ? 'Opravit šablonu, ne jednotlivé stránky — oprava se propíše všude.'
          : 'Fix the template, not individual pages — the fix carries everywhere.',
        done: (() => {
          const t = metricThresholds[String(sd.m || r.metricLower).toLowerCase()];
          const goodTxt = t ? formatMetricValue(String(sd.m || r.metricLower).toLowerCase(), t.good) : null;
          return cs
            ? `Všechny uvedené stránky mají v opakovaném kontrolním měření medián ${goodTxt ? `nejvýše ${goodTxt} (dobré pásmo)` : 'v dobrém pásmu'} a žádný validní běh v červeném pásmu.`
            : `All listed pages have a follow-up median ${goodTxt ? `at or below ${goodTxt} (good band)` : 'in the good band'} and no valid run in the red band.`;
        })()
      });
    } else {
      const worstE = sd.entries.filter(e => !e.failed && e.deepValue != null)
        .sort((a, b) => b.deepValue - a.deepValue)[0];
      if (worstE?.culprit) {
        addTask(fe, {
          title: cs
            ? `Opravit nejhorší stránku: ${shortenUrl(worstE.url, 60)}`
            : `Fix the worst page: ${shortenUrl(worstE.url, 60)}`,
          priority: 'medium',
          topic: String(sd.m || r.metricLower).toLowerCase(),
          desc: cs
            ? `Nejhorší stránka ${shortenUrl(worstE.url, 60)} (${sd.fmt(worstE.deepValue)}): ${worstE.culprit}. Viníci se mezi nejhoršími stránkami liší, šablonová oprava nepomůže.`
            : `Worst page ${shortenUrl(worstE.url, 60)} (${sd.fmt(worstE.deepValue)}): ${worstE.culprit}. Culprits differ across the worst pages, a template fix will not help.`,
          fix: cs
            ? 'Řešit stránky jednotlivě, začít uvedeným viníkem na této stránce.'
            : 'Fix pages individually, starting with the listed culprit on this page.',
          done: doneBandCriterion(String(sd.m || r.metricLower).toLowerCase())
        });
      }
    }
  }
  // Priority floor for red minority devices: share drives the ORDER (the
  // primary device's tasks come from the main findings above), but a POOR
  // metric on a minority device must stay visible in the handover — someone
  // is getting a seriously broken page even if they are 4 % of visits.
  for (const d of (r.deviceRuns || [])) {
    if (d.primary || !d.success || !d.result) continue;
    const mLow = String(d.metric || '').toLowerCase();
    // Field-first: the floor is for metrics REAL users experience as poor.
    // A critical lab value with a green field p75 is diagnostic evidence,
    // not a red task — one synthetic run must not manufacture a 🔴.
    if (d.fieldP75 == null || statusOf(mLow, Number(d.fieldP75)) !== 'critical') continue;
    const mUp = mLow.toUpperCase();
    const devLabel = DEVICE_LABELS[d.strategy === 'mobile' ? 'phone' : d.strategy]?.[lang === 'cs' ? 'cs' : 'en'] || d.device;
    // Same reproduction condition as the device table (labFieldMismatchLine):
    // field above threshold + lab in the good band = the lab run did NOT
    // reproduce the problem, so this ticket must not name a culprit or a
    // dominant phase derived from that run — it becomes a diagnostic brief.
    const thrD = metricThresholds[mLow];
    const reproduced = !(thrD && Number(d.fieldP75) > thrD.good && d.value != null && Number(d.value) <= thrD.good);
    const culprit = reproduced ? culpritTextFor(mUp, d.result, lang === 'cs') : null;
    // deviceShare arrives as a percentage from the server (e.g. 4.0 = 4 %).
    const shareTxt = Number.isFinite(Number(d.deviceShare)) ? `${fmtNum(Number(d.deviceShare), 0)} %` : null;
    const fe = ownerForRecommendation({ owner: 'frontend' }, lang);
    addTask(fe, {
      title: cs
        ? `Opravit ${mUp} na zařízení ${devLabel}`
        : `Fix ${mUp} on ${devLabel}`,
      priority: 'medium',
      topic: mLow,
      desc: reproduced
        ? (cs
          ? `${mUp} na zařízení ${devLabel}: lab ${formatMetricValue(mLow, d.value) || '—'}, field ${d.fieldP75 != null ? formatMetricValue(mLow, d.fieldP75) : '—'} (červené pásmo u reálných uživatelů)${culprit ? ` — ${culprit}` : ''}. ${shareTxt ? `Menší podíl návštěv (${shareTxt}), ale vážně špatné.` : 'Menšinové zařízení, ale vážně špatné.'}`
          : `${mUp} on ${devLabel}: lab ${formatMetricValue(mLow, d.value) || '—'}, field ${d.fieldP75 != null ? formatMetricValue(mLow, d.fieldP75) : '—'} (red band for real users)${culprit ? ` — ${culprit}` : ''}. ${shareTxt ? `Smaller visit share (${shareTxt}) but seriously broken.` : 'Minority device but seriously broken.'}`)
        : (cs
          ? `${mUp} na zařízení ${devLabel}: field ${formatMetricValue(mLow, d.fieldP75) || '—'} (červené pásmo u reálných uživatelů)${shareTxt ? `, podíl návštěv ${shareTxt}` : ''}. Aktuální laboratorní běh problém nereprodukoval (lab ${formatMetricValue(mLow, d.value) || '—'}) — příčinu tedy neurčuje.`
          : `${mUp} on ${devLabel}: field ${formatMetricValue(mLow, d.fieldP75) || '—'} (red band for real users)${shareTxt ? `, visit share ${shareTxt}` : ''}. The current lab run did not reproduce the problem (lab ${formatMetricValue(mLow, d.value) || '—'}) — it therefore does not determine the cause.`),
      fix: reproduced
        ? (cs
          ? 'Opravit po prioritách hlavního zařízení — nevypouštět ze zadání.'
          : "Fix after the primary device's priorities — do not drop from the brief.")
        : (cs
          ? `Nejprve problém reprodukovat a profilovat${metricCopy(mLow, 'reproduceConditions', 'cs') ? ` ${metricCopy(mLow, 'reproduceConditions', 'cs')}` : ''} — teprve potvrzený profil určí příčinu a opravu.`
          : `First reproduce and profile the problem${metricCopy(mLow, 'reproduceConditions', 'en') ? ` ${metricCopy(mLow, 'reproduceConditions', 'en')}` : ''} — only a confirmed profile determines the cause and the fix.`),
      done: cs
        ? `Field p75 ${mUp} na zařízení ${devLabel} je v příštím 28denním okně mimo červené pásmo.`
        : `Field p75 ${mUp} on ${devLabel} is out of the red band in the next 28-day window.`
    });
  }
  // The metric the audit carries in its title must produce at least one
  // ticket. When no confirmed cause exists for it (no task with its topic),
  // the honest output is a DIAGNOSTIC ticket with a concrete measurement
  // protocol — not a generic "reproduce and profile", and never a loading
  // ticket dressed up as the answer.
  {
    const titleMetric = String(r.metricLower || '').toLowerCase();
    const hasMetricTask = [...groups.values()].some(g => g.tasks.some(t => t.topic === titleMetric));
    const fieldBad = r.fieldValue != null && statusOf(titleMetric, Number(r.fieldValue)) !== 'good';
    if (titleMetric && !hasMetricTask && fieldBad && ['inp', 'cls', 'lcp', 'ttfb'].includes(titleMetric)) {
      const mUp = titleMetric.toUpperCase();
      const fe = ownerForRecommendation({ owner: 'frontend' }, lang);
      // Evidence-aware lead-in: "lab runs did not reproduce" may only be
      // claimed when a lab value exists AND sits in the good band. A bad lab
      // value without a ticket means reproduced-without-attribution; a
      // missing deep result means no lab evidence at all.
      const labValD = titleMetric === 'inp' ? (r.deepINPResults?.inp ?? r.deepINPResults?.worstInteraction?.duration)
        : titleMetric === 'cls' ? r.deepCLSResults?.cls
        : titleMetric === 'lcp' ? r.deepLCPResults?.lcp
        : null;
      const labState = labValD == null ? 'missing' : (statusOf(titleMetric, Number(labValD)) === 'good' ? 'notReproduced' : 'unattributed');
      const lead = cs
        ? `Field ${mUp} ${formatMetricValue(titleMetric, r.fieldValue) || '—'} je nad prahem, ale v tomto auditu neexistuje potvrzená příčina — ${labState === 'notReproduced'
          ? 'laboratorní běh problém nereprodukoval'
          : labState === 'unattributed'
            ? `laboratorní běh problém zachytil (${formatMetricValue(titleMetric, Number(labValD)) || '—'}), ale bez určeného prvku či fáze`
            : 'hloubkové laboratorní měření této metriky chybí'}. Zadání je proto diagnostické: implementační ticket vzniká až po reprodukci s určenou příčinou.`
        : `Field ${mUp} ${formatMetricValue(titleMetric, r.fieldValue) || '—'} is over threshold, but this audit has no confirmed cause — ${labState === 'notReproduced'
          ? 'the lab run did not reproduce the problem'
          : labState === 'unattributed'
            ? `the lab run captured the problem (${formatMetricValue(titleMetric, Number(labValD)) || '—'}) but without an identified element or phase`
            : 'this audit has no deep lab measurement of this metric'}. This brief is therefore diagnostic: an implementation ticket follows only after reproduction with an identified cause.`;
      const proto = titleMetric === 'inp'
        ? (cs
          ? {
            desc: lead,
            fix: metricCopy('inp', 'diagnosticProtocol', 'cs'),
            done: `Existuje záznam interakce nad prahem (${mUp} > 200 ms) s identifikovaným cílem a dominantní fází — nebo je doloženo, že se za testovaných podmínek problém neprojevuje.`
          }
          : {
            desc: lead,
            fix: metricCopy('inp', 'diagnosticProtocol', 'en'),
            done: `A recorded interaction over threshold (${mUp} > 200 ms) exists with an identified target and dominant phase — or it is documented that the problem does not manifest under the tested conditions.`
          })
        : (cs
          ? {
            desc: lead,
            // Protokol reprodukce je vázaný na mechanismus metriky
            // (metricCopy) — throttling/reklama patří k INP/CLS/LCP,
            // TTFB se reprodukuje na serverové straně. Chybí-li protokol
            // v mapě, ticket žádné podmínky nevymýšlí (pojistka).
            fix: metricCopy(titleMetric, 'diagnosticProtocol', 'cs')
              || `Zaznamenat u každého běhu hodnotu ${mUp} a konkrétní prvek či fázi, která ji nese. Implementační ticket vzniká až po reprodukci.`,
            done: `Existuje běh s ${mUp} nad prahem a identifikovaným prvkem či fází — nebo je doloženo, že se za testovaných podmínek problém neprojevuje.`
          }
          : {
            desc: lead,
            fix: metricCopy(titleMetric, 'diagnosticProtocol', 'en')
              || `Record each run\u2019s ${mUp} value and the concrete element or phase carrying it. An implementation ticket follows only after reproduction.`,
            done: `A run with ${mUp} over threshold and an identified element or phase exists — or it is documented that the problem does not manifest under the tested conditions.`
          });
      addTask(fe, {
        title: cs ? `Diagnostika ${mUp}: reprodukovat problém z field dat` : `${mUp} diagnostics: reproduce the field-data problem`,
        priority: 'high',
        topic: titleMetric,
        desc: proto.desc,
        fix: proto.fix,
        done: proto.done
      });
    }
  }
  if (!groups.size) return '';
  // Priority order inside each owner group and across groups: the group
  // holding the single most important task (the "main task") always leads.
  const rank = { high: 0, medium: 1, low: 2 };
  for (const g of groups.values()) g.tasks.sort((a, b) => (rank[a.priority] ?? 1) - (rank[b.priority] ?? 1));
  const sortedGroups = [...groups.values()].sort((a, b) => (rank[a.tasks[0]?.priority] ?? 1) - (rank[b.tasks[0]?.priority] ?? 1));
  const allTasks = sortedGroups.flatMap(g => g.tasks);
  // Responsiveness/loading separation: when the priority ranking says the
  // #1 problem is a specific metric (e.g. mobile INP 390 ms), the MAIN task
  // must address that metric. A loading optimization (unused JS, images,
  // GTM…) must never lead an INP report without a proven link to a concrete
  // slow interaction — it stays in the brief as a separate P1/P2 item.
  const prioMetric = String((r.priorityTop?.metric) || r.metricLower || '').toLowerCase();
  // Loading metrics (LCP/FCP/TTFB) ARE legitimately led by loading
  // optimizations; responsiveness (INP) and stability (CLS) are not. When
  // the priority metric has NO matching task at all, there is no honest
  // main task — highlighting a loading ticket as THE answer to an INP
  // report would overstate the evidence, so no MAIN TASK badge is shown.
  const topicsForMetric = (m) => (m === 'inp' ? ['inp'] : m === 'cls' ? ['cls'] : [m, 'loading']);
  const prioPool = allTasks.filter(t => topicsForMetric(prioMetric).includes(t.topic));
  const pool = prioPool;
  const bestRank = pool.length ? Math.min(...pool.map(t => rank[t.priority] ?? 1)) : null;
  // Main task: the top-priority task that carries a lead reason (e.g. "81 %
  // of shifts — fixing it removes most of the CLS"); otherwise the first
  // top-priority task.
  const mainTask = bestRank === null ? null
    : (pool.find(t => (rank[t.priority] ?? 1) === bestRank && t.leadReason)
      || pool.find(t => (rank[t.priority] ?? 1) === bestRank));
  if (mainTask) {
    const mi = sortedGroups.findIndex(g => g.tasks.includes(mainTask));
    if (mi > 0) sortedGroups.unshift(sortedGroups.splice(mi, 1)[0]);
    const mg = sortedGroups[0];
    mg.tasks.splice(mg.tasks.indexOf(mainTask), 1);
    mg.tasks.unshift(mainTask);
  }
  const prioLabel = (p) => cs
    ? ({ high: '🔴 Vysoká', medium: '🟡 Střední', low: '🟢 Nízká' }[p] || '🟡 Střední')
    : ({ high: '🔴 High', medium: '🟡 Medium', low: '🟢 Low' }[p] || '🟡 Medium');
  const L = cs
    ? { priority: 'Priorita', owner: 'Vlastník', desc: 'Popis', fix: 'Řešení', done: 'Hotovo, když', main: 'HLAVNÍ ÚKOL' }
    : { priority: 'Priority', owner: 'Owner', desc: 'Description', fix: 'Fix', done: 'Done when', main: 'MAIN TASK' };
  const row = (label, text) => text ? `
        <div style="display:flex;gap:8px;margin-top:6px;font-family:var(--serif);font-size:13px;line-height:1.55;color:var(--ink);">
          <span style="flex:0 0 92px;font-family:var(--mono);font-size:10px;letter-spacing:0.08em;text-transform:uppercase;font-weight:700;color:#666;padding-top:2px;">${escapeHtml(label)}</span>
          <span style="flex:1;">${escapeHtml(text)}</span>
        </div>` : '';
  const renderTicket = (t, owner) => {
    const isMain = t === mainTask;
    return `
      <div style="margin-top:12px;padding:14px 16px;background:var(--paper);border:1px solid ${isMain ? 'var(--ink)' : 'rgba(0,0,0,0.15)'};${isMain ? 'border-width:2px;' : ''}">
        ${isMain ? `<div style="font-family:var(--mono);font-size:10px;letter-spacing:0.12em;font-weight:700;color:#fff;background:var(--ink);display:inline-block;padding:3px 8px;margin-bottom:8px;">${escapeHtml(L.main)}${t.leadReason ? ` — ${escapeHtml(t.leadReason)}` : ''}</div>` : ''}
        <div style="font-family:var(--serif);font-size:15px;font-weight:700;line-height:1.4;color:var(--ink);">${escapeHtml(t.title)}</div>
        <div style="font-family:var(--mono);font-size:11px;color:var(--ink);margin-top:6px;">${escapeHtml(L.priority)}: ${escapeHtml(prioLabel(t.priority))} &nbsp;·&nbsp; ${escapeHtml(L.owner)}: ${escapeHtml(owner.label)}</div>
        ${row(L.desc, t.desc)}
        ${row(L.fix, t.fix)}
        ${row(L.done, t.done)}
      </div>`;
  };
  const thr = metricThresholds[r.metricLower];
  const doneWhen = cs
    ? `Ověření: u každého úkolu platí jeho vlastní „Hotovo, když“. Optimalizace načítání ověřte metrikou načítání v opakovaném lab testu; samy o sobě nepotvrzují zlepšení ${r.metricUpper}. Pro ${r.metricUpper} samostatně sledujte field p75 v CrUX ${thr ? `v dobrém pásmu (nejvýše ${formatMetricValue(r.metric, thr.good)})` : 'v dobrém pásmu'}; 28denní okno změnu plně ukáže až přibližně po 4 týdnech.`
    : `Verification: each ticket has its own "Done when" criterion. Verify load optimizations using load metrics in repeated lab tests; they do not by themselves establish an improvement in ${r.metricUpper}. Separately track ${r.metricUpper} field p75 in CrUX ${thr ? `in the good band (at or below ${formatMetricValue(r.metric, thr.good)})` : 'in the good band'}; the 28-day window fully reflects a change after roughly 4 weeks.`;
  // DevTools EXPERT MODE — optional last level, never the main path. The
  // report leads with its own diagnosis; this block gives a user who wants
  // manual verification a concrete recipe built from THIS report's numbers
  // ("look for a ~174 ms task"), instead of a generic "profile it".
  // Universal principle, per-metric content: each metric is verified in a
  // different panel, hunting a different number/culprit (INP: Performance
  // long task; LCP: LCP element phases; CLS: Layout Shift regions; TTFB:
  // Network document timing). Only metrics that are PROBLEMATIC in this
  // report get a guide — never all four by default.
  const devtoolsBlock = (() => {
    const isBad = (m, v) => v != null && Number.isFinite(Number(v)) && statusOf(m, Number(v)) !== 'good';
    const guides = []; // { metric, intro (html), steps [plain], note (plain) }

    // ---- INP: Performance panel, hunt the ~X ms main-thread task ----
    (() => {
      const wi = r.deepINPResults?.worstInteraction;
      if (!wi) return;
      const inMs = Math.round(wi.inputDelay || 0);
      const prMs = Math.round(wi.processingTime || 0);
      const psMs = Math.round(wi.presentationDelay || 0);
      if (inMs + prMs + psMs <= 0) return;
      const totMs = Math.round(wi.duration || (inMs + prMs + psMs));
      // Gate on the report's deep INP value — the worst interaction is only
      // the evidence shown inside the recipe, not the problem verdict.
      if (!isBad('inp', r.deepINPResults?.inp)) return;
      const iName = wi.name || 'click';
      const tgt = wi.target?.tagName
        ? `${String(wi.target.tagName).toLowerCase()}${wi.target.id ? `#${wi.target.id}` : ''}`
        : null;
      const dominant = prMs >= psMs && prMs >= inMs ? 'processing' : (psMs >= inMs ? 'presentation' : 'input');
      const domMs = dominant === 'processing' ? prMs : dominant === 'presentation' ? psMs : inMs;
      const d = getLabBreakdown(r)?.dominance;
      const variable = !!(d?.variable && d.secondary);
      const hunt = cs
        ? (dominant === 'processing'
          ? `V Main threadu hledejte dlouhou úlohu přibližně ${fmtNum(domMs)} ms — nehledejte všechno, hledejte tuto úlohu. Rozbalte ji (Event → Call Tree / Function Call) a podívejte se, který skript a funkce ji tvoří.`
          : dominant === 'presentation'
            ? `Fáze presentation delay trvala přibližně ${fmtNum(domMs)} ms. Samotná délka fáze neurčuje příčinu a nevylučuje JavaScript. V profilu ověřte práci mezi koncem handleru a následujícím vykreslením, včetně skriptů, layoutu a paint; konkrétní zásah zvolte až podle zachyceného profilu.`
            : `Přibližně ${fmtNum(domMs)} ms uplyne PŘED spuštěním handleru (input delay). Hledejte úlohu, která v Main threadu běžela v okamžiku interakce — to je viník, ne kód navázaný na kliknutí.`)
        : (dominant === 'processing'
          ? `In the Main thread, look for a long task of roughly ${fmtNum(domMs)} ms — don't look at everything, look for this task. Expand it (Event → Call Tree / Function Call) and see which script and function make it up.`
          : dominant === 'presentation'
            ? `Presentation delay lasted roughly ${fmtNum(domMs)} ms. Phase duration alone does not establish a cause or rule out JavaScript. Inspect the profile between the end of the handler and the next paint, including scripts, layout and paint; choose a repair only from captured evidence.`
            : `Roughly ${fmtNum(domMs)} ms passes BEFORE the handler starts (input delay). Look for the task that was running on the Main thread at the moment of interaction — that is the culprit, not the code attached to the click.`);
      guides.push({
        metric: 'inp',
        intro: cs
          ? `<b>INP</b> — nejproblematičtější naměřená interakce: <b>${escapeHtml(iName)}</b>${tgt ? ` (${escapeHtml(tgt)})` : ''} — ${fmtNum(totMs)} ms (vstup ${fmtNum(inMs)} ms, zpracování ${fmtNum(prMs)} ms, vykreslení ${fmtNum(psMs)} ms).`
          : `<b>INP</b> — worst measured interaction: <b>${escapeHtml(iName)}</b>${tgt ? ` (${escapeHtml(tgt)})` : ''} — ${fmtNum(totMs)} ms (input ${fmtNum(inMs)} ms, processing ${fmtNum(prMs)} ms, presentation ${fmtNum(psMs)} ms).`,
        steps: cs
          ? ['Otevřete Chrome DevTools (F12) → záložka Performance.', 'Spusťte Record.', `Proveďte stejnou interakci: ${iName}${tgt ? ` na prvku ${tgt}` : ''}.`, 'Zastavte nahrávání a najděte blok interakce.', hunt]
          : ['Open Chrome DevTools (F12) → Performance tab.', 'Start recording.', `Perform the same interaction: ${iName}${tgt ? ` on element ${tgt}` : ''}.`, 'Stop the recording and find the interaction block.', hunt],
        note: variable
          ? (cs
            ? 'Měření zachytilo i druhý profil: pokud místo dlouhé úlohy uvidíte výrazný čas v Rendering / Layout / Paint (nebo naopak), odpovídá to druhému diagnostickému profilu z tohoto reportu — oba jsou reálné, opakujte nahrávku pro různé interakce.'
            : 'The measurements also captured a second profile: if you see significant Rendering / Layout / Paint time instead of a long task (or vice versa), it matches the second diagnostic profile in this report — both are real; repeat the recording for different interactions.')
          : null
      });
    })();

    // ---- LCP: Performance panel, LCP element + phase breakdown ----
    (() => {
      const deep = r.deepLCPResults;
      if (!deep || !Number.isFinite(Number(deep.lcp)) || !isBad('lcp', deep.lcp)) return;
      const el = deep.lcpElement || null;
      const sel = el ? (el.selector || String(el.tagName || '').toLowerCase() || null) : null;
      const ph = deep.phases || {};
      // All three phases must be known before naming a dominant one — a null
      // (undetermined) phase may be the real bottleneck.
      const allKnown = [ph.ttfb, ph.resourceLoad, ph.renderDelay].every(v => v != null && Number.isFinite(Number(v)));
      const t = Math.round(ph.ttfb || 0), rl = Math.round(ph.resourceLoad || 0), rd = Math.round(ph.renderDelay || 0);
      const hasPhases = allKnown && (t + rl + rd > 0);
      const dominant = hasPhases ? (rd >= rl && rd >= t ? 'render' : (rl >= t ? 'load' : 'ttfb')) : null;
      const hunt = !dominant
        ? (cs ? 'Porovnejte fáze mezi sebou — dominantní fáze říká, kde se čas ztrácí.' : 'Compare the phases — the dominant phase tells you where the time is lost.')
        : dominant === 'render'
          ? (cs
            ? `Když dominuje render delay (v tomto reportu ~${fmtNum(rd)} ms), problém je ve vykreslování, ne ve stahování — hledejte, co blokuje vykreslení už staženého obsahu (skripty, styly, klientský rendering).`
            : `When render delay dominates (in this report ~${fmtNum(rd)} ms), the problem is rendering, not downloading — look for what blocks rendering of already-downloaded content (scripts, styles, client-side rendering).`)
          : dominant === 'load'
            ? (cs
              ? `Když dominuje načítání zdroje (~${fmtNum(rl)} ms v tomto reportu), problém je stahování LCP prvku — zkontrolujte velikost, prioritu (fetchpriority), lazy-loading a kdy prohlížeč zdroj objevil.`
              : `When resource load dominates (~${fmtNum(rl)} ms in this report), the problem is downloading the LCP element — check its size, priority (fetchpriority), lazy-loading and when the browser discovered it.`)
            : (cs
              ? `Když dominuje TTFB (~${fmtNum(t)} ms v tomto reportu), čas se ztrácí ještě na serveru — pokračujte postupem pro TTFB (Network panel).`
              : `When TTFB dominates (~${fmtNum(t)} ms in this report), the time is lost on the server — continue with the TTFB recipe (Network panel).`);
      guides.push({
        metric: 'lcp',
        intro: cs
          ? `<b>LCP</b> — ${formatMetricValue('lcp', deep.lcp)}${sel ? `, LCP prvek: <b>${escapeHtml(sel)}</b>` : ''}${hasPhases ? ` (TTFB ${fmtNum(t)} ms · načtení zdroje ${fmtNum(rl)} ms · render delay ${fmtNum(rd)} ms)` : ''}.`
          : `<b>LCP</b> — ${formatMetricValue('lcp', deep.lcp)}${sel ? `, LCP element: <b>${escapeHtml(sel)}</b>` : ''}${hasPhases ? ` (TTFB ${fmtNum(t)} ms · resource load ${fmtNum(rl)} ms · render delay ${fmtNum(rd)} ms)` : ''}.`,
        steps: cs
          ? ['Otevřete Chrome DevTools (F12) → záložka Performance.', 'Klikněte na „Record and reload" (⟳) a nechte stránku načíst.', `V liště Timings najděte značku LCP a klikněte na ni — DevTools zvýrazní LCP prvek${sel ? ` (má odpovídat ${sel})` : ''} a ukáže rozpad na fáze.`, hunt]
          : ['Open Chrome DevTools (F12) → Performance tab.', 'Click "Record and reload" (⟳) and let the page load.', `In the Timings track find the LCP marker and click it — DevTools highlights the LCP element${sel ? ` (it should match ${sel})` : ''} and shows the phase breakdown.`, hunt],
        note: null
      });
    })();

    // ---- CLS: Layout Shift regions + the shifting element ----
    (() => {
      const deep = r.deepCLSResults;
      if (!deep || !Number.isFinite(Number(deep.cls)) || !isBad('cls', deep.cls)) return;
      const els = Array.isArray(deep.elements) ? deep.elements : [];
      // Containers are pushed victims, never culprits (culprit-not-victim
      // guardrail) — when only containers exist, name no culprit at all.
      const worst = els.filter(e => !e.isContainer)
        .slice().sort((a, b) => (Number(b.totalShift) || 0) - (Number(a.totalShift) || 0))[0] || null;
      const wName = worst ? (worst.friendlyName || worst.selector || worst.tagName || null) : null;
      const wShift = worst && Number.isFinite(Number(worst.totalShift)) && Number(worst.totalShift) > 0
        ? Number(worst.totalShift).toFixed(3) : null;
      guides.push({
        metric: 'cls',
        intro: cs
          ? `<b>CLS</b> — ${formatMetricValue('cls', deep.cls)}${wName ? `, největší posun působí: <b>${escapeHtml(wName)}</b>${wShift ? ` (~${wShift})` : ''}` : ''}.`
          : `<b>CLS</b> — ${formatMetricValue('cls', deep.cls)}${wName ? `, biggest shift caused by: <b>${escapeHtml(wName)}</b>${wShift ? ` (~${wShift})` : ''}` : ''}.`,
        steps: cs
          ? ['Otevřete Chrome DevTools (F12) → Ctrl/Cmd+Shift+P → napište „Show Rendering" a zapněte „Layout Shift Regions".', 'Znovu načtěte stránku — oblasti, které se posouvají, se modře zvýrazní.', 'V záložce Performance nahrajte načtení stránky a v liště Experience najděte záznamy Layout Shift.', wShift ? `Hledejte posun přibližně ${wShift}${wName ? ` — podle tohoto reportu ho způsobuje ${wName}` : ''}; v detailu záznamu („Moved from/to") uvidíte, který prvek layout posouvá.` : 'V detailu záznamu („Moved from/to") uvidíte, který prvek layout posouvá.']
          : ['Open Chrome DevTools (F12) → Ctrl/Cmd+Shift+P → type "Show Rendering" and enable "Layout Shift Regions".', 'Reload the page — shifting areas get highlighted in blue.', 'In the Performance tab record a page load and find the Layout Shift entries in the Experience track.', wShift ? `Look for a shift of roughly ${wShift}${wName ? ` — per this report it is caused by ${wName}` : ''}; the entry detail ("Moved from/to") shows which element shifts the layout.` : 'The entry detail ("Moved from/to") shows which element shifts the layout.'],
        note: null
      });
    })();

    // ---- TTFB: Network panel, document request timing ----
    (() => {
      const deep = r.deepTTFBResults;
      if (!deep || !Number.isFinite(Number(deep.ttfb)) || !isBad('ttfb', deep.ttfb)) return;
      const ms = Math.round(deep.ttfb);
      const ph = deep.phases || {};
      const labels = cs
        ? { redirect: 'přesměrování', dns: 'DNS', tcp: 'TCP', tls: 'TLS', request: 'zpracování na serveru', response: 'přenos odpovědi' }
        : { redirect: 'redirect', dns: 'DNS', tcp: 'TCP', tls: 'TLS', request: 'server processing', response: 'response transfer' };
      const top = Object.entries(ph).filter(([k, v]) => labels[k] && Number(v) > 0)
        .sort((a, b) => b[1] - a[1])[0] || null;
      guides.push({
        metric: 'ttfb',
        intro: cs
          ? `<b>TTFB</b> — ${fmtNum(ms)} ms${top ? `, největší podíl: ${labels[top[0]]} (~${fmtNum(Math.round(top[1]))} ms)` : ''}.`
          : `<b>TTFB</b> — ${fmtNum(ms)} ms${top ? `, largest share: ${labels[top[0]]} (~${fmtNum(Math.round(top[1]))} ms)` : ''}.`,
        steps: (() => {
          // "Waiting for server response" in DevTools maps to phases.request
          // (responseStart − requestStart) — NOT the whole TTFB, which also
          // contains redirect/DNS/TCP/TLS. Pointing the user at the wrong
          // number would overstate the evidence.
          const reqMs = Number.isFinite(Number(ph.request)) && Number(ph.request) > 0 ? Math.round(ph.request) : null;
          const waitCs = reqMs != null
            ? `„Waiting for server response" by mělo být přibližně ${fmtNum(reqMs)} ms — to je čas, který stráví server generováním odpovědi. Zbytek TTFB (celkem ${fmtNum(ms)} ms) tvoří fáze před požadavkem: přesměrování, DNS, TCP a TLS — zkontrolujte je ve stejném Timing rozpadu.`
            : `Porovnejte fáze v Timing rozpadu — celkové TTFB z tohoto reportu je ${fmtNum(ms)} ms a skládá se z přesměrování, DNS, TCP, TLS, čekání na server a přenosu odpovědi.`;
          const waitEn = reqMs != null
            ? `"Waiting for server response" should be roughly ${fmtNum(reqMs)} ms — that is time the server spends generating the response. The rest of the TTFB (total ${fmtNum(ms)} ms) comes from the phases before the request: redirect, DNS, TCP and TLS — check them in the same Timing breakdown.`
            : `Compare the phases in the Timing breakdown — the total TTFB from this report is ${fmtNum(ms)} ms and consists of redirect, DNS, TCP, TLS, waiting for the server and response transfer.`;
          return cs
            ? ['Otevřete Chrome DevTools (F12) → záložka Network.', 'Znovu načtěte stránku a klikněte na první request (HTML dokument).', 'Otevřete záložku Timing.', waitCs]
            : ['Open Chrome DevTools (F12) → Network tab.', 'Reload the page and click the first request (the HTML document).', 'Open the Timing tab.', waitEn];
        })(),
        note: null
      });
    })();

    if (!guides.length) return '';
    // Primary metric's guide first — it is the report's main story.
    guides.sort((a, b) => (a.metric === r.metricLower ? -1 : 0) - (b.metric === r.metricLower ? -1 : 0));
    return `
    <div style="margin-top:16px;padding:14px 20px;border:1px dashed rgba(0,0,0,0.35);font-family:var(--serif);font-size:13px;line-height:1.6;color:var(--ink);">
      <div style="font-family:var(--mono);font-size:10px;letter-spacing:0.12em;text-transform:uppercase;font-weight:700;color:#666;margin-bottom:6px;">${cs ? 'Ověření v DevTools — volitelný expertní krok' : 'DevTools verification — optional expert step'}</div>
      <div style="font-size:12px;color:#666;">${cs
        ? 'Report vede vlastní diagnózu výše; tyto postupy slouží jen k ručnímu ověření. Návod je jen pro metriky, které jsou v tomto reportu problémové.'
        : 'The report leads its own diagnosis above; these recipes are only for manual verification. Guides are shown only for the metrics that are problematic in this report.'}</div>
      ${guides.map(g => `
      <div style="margin-top:10px;">
        <div>${g.intro}</div>
        <ol style="margin:6px 0 0;padding-left:20px;">${g.steps.map(s => `<li style="margin-top:4px;">${escapeHtml(s)}</li>`).join('')}</ol>
        ${g.note ? `<div style="margin-top:6px;font-size:12px;color:#666;">${escapeHtml(g.note)}</div>` : ''}
      </div>`).join('')}
    </div>`;
  })();
  // Framed as an ACTION deliverable — every ticket is complete (title,
  // priority, owner, description, fix, done-when) so a product manager can
  // copy it into Jira/Trello/e-mail without rewriting anything.
  return `
  <section style="margin-top:56px;border:2px solid var(--ink);background:var(--paper-2);padding:28px 32px 32px;">
    <div class="section-label">${lang === 'cs' ? 'Zadání k předání' : 'Handover brief'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Komu ' : 'Who '}<em>${lang === 'cs' ? 'co zadat' : 'gets what'}</em>.</h2>
    <p>${lang === 'cs'
      ? 'Hotová zadání seřazená podle priority — každý úkol lze zkopírovat do Jiry, Trella nebo e-mailu tak, jak je. Odhady úspor se překrývají a nesčítají se.'
      : 'Ready-made tickets sorted by priority — each task can be copied into Jira, Trello or an e-mail as-is. Savings estimates overlap and are not additive.'}</p>
    <p style="font-size:12px;color:#666;">${lang === 'cs'
      ? 'Doporučení vycházejí z měření v daném okamžiku. Před nasazením je posuďte v kontextu vašeho kódu, po nasazení ověřte funkčnost — implementaci a její dopady posuzuje vývojář, který zná závislosti webu.'
      : 'Recommendations reflect measurements taken at a point in time. Review them against your codebase before deploying, and verify functionality after deployment — implementation and its side effects are for a developer who knows the site\'s dependencies to assess.'}</p>
    ${sortedGroups.map(({ owner, tasks }) => `
    <div style="margin-top:18px;padding:16px 20px;background:var(--paper-2);border-left:3px solid ${owner.color};">
      <div style="font-family:var(--mono);font-size:11px;letter-spacing:0.12em;text-transform:uppercase;font-weight:700;color:var(--ink);">${escapeHtml(owner.label)}</div>
      ${tasks.map(t => renderTicket(t, owner)).join('')}
      <div style="font-size:12px;color:#666;margin-top:10px;">${escapeHtml(owner.procedure)}</div>
    </div>`).join('')}
    <div style="margin-top:16px;padding:14px 20px;border:1px solid var(--ink);font-family:var(--serif);font-size:13px;line-height:1.55;color:var(--ink);">
      ${escapeHtml(doneWhen)}
    </div>
    ${devtoolsBlock}
  </section>`;
};

// =========================================================================
// FOLLOW-UP MEASUREMENT SECTION ("Kontrolní měření")
// =========================================================================
const renderFollowUpMockup = (lang, baseDate) => {
  const fl = followUpLabels(lang);
  const { auditDate, fieldDate } = buildFollowUpSchedule(baseDate);
  return `
  <div class="glass panel" style="margin-bottom:24px;border-color:rgba(59,130,246,0.25);">
    <div class="panel-header">
      <div class="panel-title" style="color:#93c5fd;">${escapeHtml(fl.title)}</div>
      <div class="panel-subtitle">${escapeHtml(fl.auditDateLabel)}: ${escapeHtml(formatFollowUpDate(auditDate, lang))}</div>
    </div>
    <div style="display:flex;flex-direction:column;gap:10px;">
      <div style="background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:14px 18px;">
        <div style="font-size:13px;font-weight:600;color:#f8fafc;margin-bottom:6px;">1. ${escapeHtml(fl.labTitle)}</div>
        <div style="font-size:12px;color:#94a3b8;line-height:1.55;">${escapeHtml(fl.labText)}</div>
      </div>
      <div style="background:rgba(15,23,42,0.5);border:1px solid rgba(100,116,139,0.12);border-radius:10px;padding:14px 18px;">
        <div style="font-size:13px;font-weight:600;color:#f8fafc;margin-bottom:6px;">2. ${escapeHtml(fl.fieldTitle)} — <span style="color:#93c5fd;">${escapeHtml(formatFollowUpDate(fieldDate, lang))}</span></div>
        <div style="font-size:12px;color:#94a3b8;line-height:1.55;">${escapeHtml(fl.fieldTextBefore)} <strong style="color:#e2e8f0;">${escapeHtml(formatFollowUpDate(fieldDate, lang))}</strong> ${escapeHtml(fl.fieldTextAfter)}</div>
      </div>
    </div>
    <div style="margin-top:14px;padding:12px 16px;background:rgba(15,23,42,0.6);border-left:3px solid #3b82f6;border-radius:8px;">
      <div style="font-size:12px;color:#cbd5e1;line-height:1.55;">${escapeHtml(fl.windowExplain)}</div>
    </div>
  </div>`;
};

// Two SEPARATE verdicts — responsiveness (INP) vs loading (LCP/TTFB/FCP) —
// so a good loading result never masks a responsiveness problem or vice
// versa. Each group's severity is the worst of its cards, escalated by
// confirmed poor pages site-wide for the matching metric.
// Executive summary: CrUX field matrix (metric × device). Real-user data is
// the most authoritative layer in the report — the reader sees in seconds
// which metric/device combinations actually have a problem.
const renderFieldOverviewAudit = (r) => {
  if (!r.fieldMatrix || !r.fieldMatrix.length) return '';
  const lang = r.lang;
  const metricsOrder = ['lcp', 'inp', 'cls', 'fcp', 'ttfb'];
  const devices = [
    ['phone', lang === 'cs' ? 'Telefon' : 'Phone'],
    ['desktop', 'Desktop'],
    ['tablet', 'Tablet']
  ];
  const presentMetrics = metricsOrder.filter(m => r.fieldMatrix.some(i => i.metric === m));
  if (!presentMetrics.length) return '';
  const get = (m, d) => r.fieldMatrix.find(i => i.metric === m && i.device === d);
  const badge = (s) => s === 'good' ? '✅' : (s === 'needs-improvement' || s === 'warning') ? '🟡' : '🔴';
  const fmt = (i) => {
    if (!i || i.p75 == null) return '—';
    if (i.metric === 'cls') return Number(i.p75).toFixed(3).replace('.', lang === 'cs' ? ',' : '.');
    return i.p75 >= 1000 ? `${(i.p75 / 1000).toFixed(2).replace('.', lang === 'cs' ? ',' : '.')} s` : `${Math.round(i.p75)} ms`;
  };
  const period = r.devicePriority?.collectionPeriod;
  const periodStr = period?.firstDate && period?.lastDate
    ? `${period.firstDate.day}. ${period.firstDate.month}. – ${period.lastDate.day}. ${period.lastDate.month}. ${period.lastDate.year}`
    : null;
  const cell = 'padding:8px 12px;border-bottom:1px solid #e5e0d8;font-family:var(--mono);font-size:12px;';
  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Field přehled (CrUX)' : 'Field overview (CrUX)'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Co měří ' : 'What '}<em>${lang === 'cs' ? 'reální uživatelé' : 'real users measure'}</em>.</h2>
    <p>${lang === 'cs'
      ? `Data Chrome UX Reportu — p75 skutečných návštěv za 28 dní${periodStr ? ` (${periodStr})` : ''}. Toto je vrstva, kterou hodnotí Google; další části popisují laboratorní pozorování, jejich omezení a kroky k ověření možných příčin.`
      : `Chrome UX Report data — p75 of real visits over 28 days${periodStr ? ` (${periodStr})` : ''}. This is the layer Google evaluates; the following sections describe lab observations, their limits, and steps to verify possible causes.`}</p>
    <table style="width:100%;border-collapse:collapse;margin-top:8px;">
      <thead><tr>
        <th style="${cell}text-align:left;color:#666;text-transform:uppercase;letter-spacing:0.1em;font-size:10px;">${lang === 'cs' ? 'Metrika' : 'Metric'}</th>
        ${devices.map(([, label]) => `<th style="${cell}text-align:right;color:#666;text-transform:uppercase;letter-spacing:0.1em;font-size:10px;">${label}</th>`).join('')}
      </tr></thead>
      <tbody>
        ${presentMetrics.map(m => `<tr>
          <td style="${cell}font-weight:600;">${m.toUpperCase()}</td>
          ${devices.map(([d]) => { const i = get(m, d); return `<td style="${cell}text-align:right;">${i ? `${fmt(i)} ${badge(i.status)}` : '—'}</td>`; }).join('')}
        </tr>`).join('')}
      </tbody>
    </table>
    <p style="font-size:11px;color:#64748b;margin-top:8px;">✅ ${lang === 'cs' ? 'dobré' : 'good'} · 🟡 ${lang === 'cs' ? 'vyžaduje zlepšení' : 'needs improvement'} · 🔴 ${lang === 'cs' ? 'špatné' : 'poor'}</p>
  </section>`;
};

// Carried INP deep run in a unified report where INP is NOT the primary
// metric. Verdict cards are built for the primary metric only, so without
// this the report would claim "interaction not measured" while a measured
// INP number sits in the payload (the exact bug: complex run measured both
// CLS and INP, but the CLS-primary report dropped the INP half).
const getCarriedInp = (r) => {
  if (!r.unified || r.metricLower === 'inp') return null;
  const deep = r.deepINPResults;
  if (!hasValidInpMeasurement(deep)) return null;
  // Same validity predicate as the primary-INP scenario (detectScenario's
  // 'inp-not-measured' rule): no valid captured interaction with a positive
  // breakdown total = not measured, in the carried path too. A partial deep
  // payload must not become "measured" just because the report is unified.
  const wi = deep.worstInteraction || null;
  const bdTotal = (wi?.inputDelay || 0) + (wi?.processingTime || 0) + (wi?.presentationDelay || 0);
  if (deep.attributionValid === false || !wi || bdTotal <= 0) return null;
  const labDevice = deep.labRunSpread?.conditions?.device || deep.labRunSpread?.device || deep.device;
  const normalizedDevice = labDevice === 'mobile' ? 'phone' : labDevice;
  const field = normalizedDevice
    ? (r.fieldMatrix || []).find(i => i.metric === 'inp' && i.device === normalizedDevice)
    : null;
  const spread = deep.labRunSpread || null;
  // Median component breakdown across stored runs when available — the same
  // basis the primary-INP copy uses; one specific run must be labeled as such.
  const comps = spread?.components && labSeriesIsVerified(spread) && spread.components.runs >= 5 ? spread.components : null;
  return {
    // Median across stored runs when a spread exists — the carried-INP lead
    // number must match the basis of the breakdown shown under it.
    inp: Math.round(labSeriesDisplayValue(spread, deep.inp)),
    breakdown: comps
      ? { inputDelay: comps.inputDelay, processingTime: comps.processingTime, presentationDelay: comps.presentationDelay, basis: 'median', runs: comps.runs }
      : { inputDelay: Math.round(wi.inputDelay || 0), processingTime: Math.round(wi.processingTime || 0), presentationDelay: Math.round(wi.presentationDelay || 0), basis: 'single', runs: 1 },
    target: wi.target || null,
    spread,
    fieldP75: field?.p75 != null ? Math.round(Number(field.p75)) : null,
    fieldStatus: field?.status || null
  };
};

const inpSeverity = (ms) => ms > 500 ? 'critical' : ms > 200 ? 'warning' : 'good';

const renderDualVerdictAudit = (r) => {
  const lang = r.lang;
  const cards = r.verdictCards || [];
  // TBT ("Total Blocking Time") is a Lighthouse LOAD-phase metric — it must
  // never contaminate the responsiveness verdict next to a measured INP,
  // otherwise the two verdicts contradict each other (INP 32 ms vs "poor").
  const isResp = (c) => /INP/i.test(c.label || '');
  const isLoad = (c) => /LCP|TTFB|FCP|CLS|weight|váha|Blocking/i.test(c.label || '');
  const respCards = cards.filter(isResp);
  const loadCards = cards.filter(isLoad);
  // A unified report carries the INP deep run even when INP is not the
  // primary metric — the responsiveness verdict must consume it instead of
  // claiming "not measured" next to a measured number (field p75 first, the
  // single lab interaction as supporting detail).
  const carriedInp = getCarriedInp(r);
  if (!respCards.length && carriedInp) {
    if (carriedInp.fieldP75 != null) {
      respCards.push({
        label: lang === 'cs' ? 'INP · reální uživatelé' : 'INP · real users',
        value: `${fmtNum(carriedInp.fieldP75)} ms`,
        severity: inpSeverity(carriedInp.fieldP75)
      });
    }
    respCards.push({
      label: lang === 'cs' ? 'INP · hloubkový test' : 'INP · deep test',
      value: `${fmtNum(carriedInp.inp)} ms`,
      severity: inpSeverity(carriedInp.inp)
    });
  }
  // Fallback: an INP-metric export has no LCP verdict card, but a deep LCP
  // measurement may still exist — surface it so loading never reads
  // "not measured" while an LCP number appears elsewhere in the report.
  if (!loadCards.some(c => /LCP/i.test(c.label || '')) && Number.isFinite(r.deepLCPResults?.lcp)) {
    const lcpMs = r.deepLCPResults.lcp;
    loadCards.push({
      label: lang === 'cs' ? 'LCP · hloubkový test' : 'LCP · deep test',
      value: lcpMs >= 1000 ? `${(lcpMs / 1000).toFixed(2)} s` : `${Math.round(lcpMs)} ms`,
      severity: lcpMs > 4000 ? 'critical' : lcpMs > 2500 ? 'warning' : 'good'
    });
  }
  const worstSev = (arr) => arr.reduce((w, c) =>
    c.severity === 'critical' ? 'critical' : (c.severity === 'warning' && w !== 'critical' ? 'warning' : w), 'good');
  // ----- Verdicts are FIELD-ONLY -----
  // The verdict of each box must be derivable solely from the field bands
  // shown in it. Lab numbers (deep runs, single interactions, sweeps) stay
  // listed as context but never move the verdict — two sites with the same
  // field profile must get the same verdict regardless of their lab medians.
  const isLabCard = (c) => /laborator|lab test|lab run|hloubkov|deep test|deep run|\(lab\)/i.test(c.label || '');
  const bandSev = (st) => st === 'poor' ? 'critical' : st === 'needs-improvement' ? 'warning' : st === 'good' ? 'good' : null;
  const devName = (d) => lang === 'cs'
    ? (d === 'phone' ? 'mobil' : d === 'desktop' ? 'desktop' : d === 'tablet' ? 'tablet' : d)
    : (d === 'phone' ? 'mobile' : d);
  // Responsiveness verdict: worst field INP band among the devices in the
  // report's field matrix; the device it comes from is named.
  const fieldInpItems = (r.fieldMatrix || []).filter(i => i.metric === 'inp' && i.p75 != null);
  let respSev = null;
  let respFieldNote = '';
  if (fieldInpItems.length) {
    const worstItem = fieldInpItems.reduce((w, i) => {
      const rank = (s) => s === 'poor' ? 2 : s === 'needs-improvement' ? 1 : 0;
      return rank(i.status) > rank(w.status) ? i : w;
    });
    respSev = bandSev(worstItem.status) ?? inpSeverity(Number(worstItem.p75));
    respFieldNote = `<span style="font-size:12px;color:#777;">${lang === 'cs'
      ? `Verdikt vychází z field dat (reální uživatelé): nejhorší pásmo mezi zobrazenými zařízeními má ${devName(worstItem.device)} (INP ${fmtNum(Math.round(Number(worstItem.p75)))} ms). Lab hodnoty níže jsou kontext, do verdiktu nevstupují.`
      : `Verdict follows field data (real users): the worst band among the displayed devices is on ${devName(worstItem.device)} (INP ${fmtNum(Math.round(Number(worstItem.p75)))} ms). Lab values below are context and do not enter the verdict.`}</span>`;
  } else {
    const respField = respCards.filter(c => !isLabCard(c));
    respSev = respField.length ? worstSev(respField) : null;
  }
  // Loading verdict is FIELD-FIRST: when CrUX says real users load fine
  // (LCP/TTFB/FCP green), lab TBT / JS budget must not flip the verdict to
  // "poor" — they are lab proxies, not user-facing load metrics. Lab numbers
  // stay listed, explicitly framed as lab load / headroom.
  const loadFieldCards = loadCards.filter(c => !isLabCard(c));
  let loadSev = loadFieldCards.length ? worstSev(loadFieldCards) : null;
  let loadFieldNote = '';
  if (r.fieldLoadStatus) {
    loadSev = r.fieldLoadStatus;
    const f = (m) => (r.fieldMatrix || []).find(i => i.metric === m && i.device === 'phone') || (r.fieldMatrix || []).find(i => i.metric === m);
    const fmtF = (i) => !i ? null : (i.metric === 'cls' ? String(i.p75) : (i.p75 >= 1000 ? `${(i.p75 / 1000).toFixed(2)} s` : `${Math.round(i.p75)} ms`));
    const lcpF = f('lcp'), ttfbF = f('ttfb');
    const fieldBits = [
      lcpF ? `LCP (field): <b>${fmtF(lcpF)}</b>${lcpF.status === 'good' ? ' <span style="color:var(--ok, #15803d);">✓</span>' : ''}` : null,
      ttfbF ? `TTFB (field): <b>${fmtF(ttfbF)}</b>${ttfbF.status === 'good' ? ' <span style="color:var(--ok, #15803d);">✓</span>' : ''}` : null
    ].filter(Boolean);
    loadFieldNote = fieldBits.length
      ? `${fieldBits.join(' · ')}${r.fieldLoadStatus === 'good'
        ? `<br><span style="font-size:12px;color:#777;">${lang === 'cs'
          ? 'Verdikt vychází z field dat (reální uživatelé). Lab čísla níže jsou oddělené signály zátěže při načítání: zelené LCP/TTFB je nevyvracejí. Výraznou hodnotu ověřte opakováním za srovnatelných podmínek; sama nedokazuje příčinu field INP.'
          : 'Verdict follows field data (real users). The lab numbers below are separate load-time signals: green LCP/TTFB do not invalidate them. Repeat a substantial value under comparable conditions; it does not by itself establish the cause of field INP.'}</span>` : ''}`
      : '';
  }
  if (respSev === null && loadSev === null) return '';
  const sevLabel = (s) => s === null
    ? (lang === 'cs' ? 'neměřeno v tomto reportu' : 'not measured in this report')
    : statusLabel(s, lang);
  const sevColor = (s) => s === 'critical' ? 'var(--bad, #b91c1c)' : s === 'warning' ? '#b45309' : s === 'good' ? 'var(--ok, #15803d)' : '#888';
  const box = (title, sev, detail) => `
      <div style="flex:1;min-width:260px;border:2px solid ${sevColor(sev)};padding:18px 20px;">
        <div style="font-family:var(--mono);font-size:10px;letter-spacing:0.15em;text-transform:uppercase;color:#666;margin-bottom:6px;">${escapeHtml(title)}</div>
        <div style="font-family:var(--serif);font-size:22px;font-weight:600;color:${sevColor(sev)};">${escapeHtml(sevLabel(sev))}</div>
        ${detail ? `<div style="font-family:var(--serif);font-size:13px;color:#555;line-height:1.5;margin-top:6px;">${detail}</div>` : ''}
      </div>`;
  // Each metric inside a verdict box carries its OWN status — a green LCP
  // listed inside a "Loading: Poor" box must say so, or the reader assumes
  // every listed number is bad (the verdict is driven by the worst card only).
  const listVals = (arr) => arr.map(c => `${escapeHtml(c.label)}: <b>${escapeHtml(c.value)}</b>${c.severity === 'good' ? ` <span style="color:var(--ok, #15803d);">✓ ${lang === 'cs' ? 'v pořádku' : 'OK'}</span>` : ''}`).join(' · ');
  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Dva verdikty' : 'Two verdicts'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Odezva a načítání ' : 'Responsiveness and loading '}<em>${lang === 'cs' ? 'zvlášť' : 'separately'}</em>.</h2>
    <p>${lang === 'cs'
      ? 'Odezva na interakci a rychlost načtení jsou dvě různé vrstvy — dobrý výsledek jedné nesmí zakrýt problém druhé.'
      : 'Interaction responsiveness and load speed are two different layers — a good result in one must not mask a problem in the other.'}</p>
    <div style="display:flex;gap:16px;flex-wrap:wrap;">
      ${box(lang === 'cs' ? 'Odezva (INP)' : 'Responsiveness (INP)', respSev,
        [respCards.length ? listVals(respCards) : (lang === 'cs' ? 'Interakce nebyla v tomto běhu měřena.' : 'No interaction was measured in this run.'), respFieldNote].filter(Boolean).join('<br>'))}
      ${box(loadCards.some(c => /CLS/i.test(c.label || ''))
        ? (lang === 'cs' ? 'Načítání a stabilita (LCP / TTFB / CLS)' : 'Loading & stability (LCP / TTFB / CLS)')
        : (lang === 'cs' ? 'Načítání (LCP / TTFB)' : 'Loading (LCP / TTFB)'), loadSev,
        [loadFieldNote, loadCards.length ? listVals(loadCards.map(c => (r.fieldLoadStatus && /Blocking|weight|váha/i.test(c.label || '')) ? { ...c, label: `${c.label} (lab)` } : c)) : (loadFieldNote ? '' : (lang === 'cs' ? 'Metriky načítání nejsou v tomto reportu k dispozici.' : 'Loading metrics are not available in this report.'))].filter(Boolean).join('<br>'))}
    </div>
  </section>`;
};

// Methodology & acknowledged data gaps — every number's provenance in one
// place: lab runs are UNTHROTTLED (any "connection" figure is the headless
// browser's own navigator.connection report, not a simulated profile),
// which numbers are single-run, and which parts of the measurement failed.
const knownDataGaps = (r) => {
  const lang = r.lang;
  const gaps = [];
  const primaryFailed = (r.failedDeepMetrics || []).includes(r.metricUpper);
  if (primaryFailed) gaps.push(lang === 'cs'
    ? `Hloubkový test hlavní metriky ${r.metricUpper} selhal. Report proto nemá platnou laboratorní hodnotu ani rozpad příčiny hlavního problému.`
    : `The deep test for the primary metric ${r.metricUpper} failed. The report therefore has no valid lab value or cause breakdown for the primary problem.`);
  if (r.deepINPResults?.inpUpperBound) gaps.push(lang === 'cs'
    ? 'INP je horní odhad — všechny interakce proběhly pod 16 ms (pod prahem měřicího API).'
    : 'INP is an upper bound — every interaction stayed under 16 ms (below the measurement API threshold).');
  if (r.deepINPResults?.attribution?.attributionFailed || r.deepINPResults?.attributionValid === false) gaps.push(lang === 'cs'
    ? 'Atribuce blokujícího času v hloubkovém běhu selhala — rozdělení první/třetí strany není v reportu uvedeno.'
    : 'Blocking-time attribution failed in the deep run — the first-/third-party split is not shown.');
  if (r.multiPage && r.multiPage.worstConfidence === 'single-run') gaps.push(lang === 'cs'
    ? 'Nejhorší stránka z měření napříč webem má méně než 3 validní běhy — hodnota je orientační.'
    : 'The worst site-wide page has fewer than 3 valid runs — its value is indicative.');
  if (r.multiPage?.worstConfidence === 'repeated-incomplete') gaps.push(incompleteSweepNote(r.multiPage, lang === 'cs'));
  if (r.multiPage && !r.multiPage.worstConfidence) gaps.push(lang === 'cs'
    ? 'Počet opakování patří ke konkrétní uložené sérii. Sweep a hloubkové běhy jsou oddělené vrstvy; chybějící počet opakování nelze odvodit.'
    : 'Repeat counts belong to each recorded series. Sweep and deep runs are separate layers; missing repeat counts cannot be inferred.');
  if (r.multiPage && Number(r.multiPage.unmeasuredPages) > 0) {
    const attempted = Number(r.multiPage.attemptedPages || (r.multiPage.validPages + r.multiPage.unmeasuredPages));
    gaps.push(lang === 'cs'
      ? `Sweep hlavní metriky platně změřil ${Number(r.multiPage.validPages || 0)} z ${attempted} stránek; ${Number(r.multiPage.unmeasuredPages)} nezměřených stránek není zahrnuto do statistik.`
      : `The primary-metric sweep validly measured ${Number(r.multiPage.validPages || 0)} of ${attempted} pages; ${Number(r.multiPage.unmeasuredPages)} unmeasured pages are excluded from the statistics.`);
  }
  // A unified audit whose primary metric got NO matching sweep must say so —
  // the report otherwise silently claims site-wide knowledge it doesn't have.
  if (r.unified && (!r.multiPage || !r.multiPage.matchesMetric)) gaps.push(lang === 'cs'
    ? `Měření hlavní metriky (${r.metricUpper}) napříč webem v tomto auditu neproběhlo nebo selhalo — report nedokáže říct, zda se problém týká celého webu, nebo jen vstupní stránky.`
    : `The site-wide sweep for the primary metric (${r.metricUpper}) did not run or failed in this audit — the report cannot say whether the problem is site-wide or specific to the entry page.`);
  if (r.inpCompareResults?.summary?.some?.(s => !s.success)) gaps.push(lang === 'cs'
    ? 'Část konfiguračních běhů (zařízení × souhlas) selhala a je z interpretace vyloučena.'
    : 'Some configuration runs (device × consent) failed and are excluded from interpretation.');
  if (r.sweepDeepSkipReason && !r.sweepDeepRuns?.length) {
    const sweepDeepSkipMessages = {
      no_deep_endpoint: lang === 'cs'
        ? `Hloubkový test nejhorší stránky ze sweepu byl přeskočen — pro metriku ${r.metricUpper} hloubkový test neexistuje.`
        : `The deep test of the worst sweep page was skipped — no deep test is available for ${r.metricUpper}.`,
      no_sweep_data: lang === 'cs'
        ? 'Hloubkový test nejhorší stránky ze sweepu byl přeskočen — vícestránkový sweep nevrátil žádné stránky.'
        : 'The deep test of the worst sweep page was skipped — the multi-page sweep returned no pages.',
      no_candidates_above_threshold: lang === 'cs'
        ? `Hloubkový test nejhorší stránky ze sweepu byl přeskočen — žádná stránka sweepu nepřekročila práh ${r.metricUpper}.`
        : `The deep test of the worst sweep page was skipped — no sweep page exceeded the ${r.metricUpper} threshold.`,
      entry_is_worst: lang === 'cs'
        ? 'Hloubkový test nejhorší stránky ze sweepu byl přeskočen — nejhorší stránka ze sweepu je vstupní stránka, která již byla hloubkově testována výše.'
        : 'The deep test of the worst sweep page was skipped — the worst sweep page is the entry page, which was already deep-tested above.',
      budget_exhausted: lang === 'cs'
        ? 'Hloubkový test nejhorší stránky ze sweepu byl přeskočen — vypršel časový rozpočet auditu.'
        : 'The deep test of the worst sweep page was skipped — the audit time budget was exhausted.',
      deep_not_allowed: lang === 'cs'
        ? 'Hloubkový test nejhorší stránky ze sweepu nebyl proveden — hloubkové laboratorní testy jsou součástí tarifů Premium a Enterprise.'
        : 'The deep test of the worst sweep page was not run — deep lab tests are part of Premium and Enterprise plans.',
    };
    const msg = sweepDeepSkipMessages[r.sweepDeepSkipReason];
    if (msg) gaps.push(msg);
  }
  // A missing field cell is an absence in this report, not evidence that an
  // upstream service never has data. Only list devices known to have a
  // non-zero visit share, and never invent a reason for the missing value.
  const fractions = r.devicePriority?.cruxFormFactorFractions || {};
  const availability = r.devicePriority?.deviceAvailability || {};
  const expectedDevices = ['phone', 'desktop', 'tablet']
    .filter(device => Number(fractions[device]) > 0 || availability[device]?.available === false);
  const fieldMetrics = [...new Set((r.fieldMatrix || []).map(item => item.metric).filter(Boolean))];
  const missingByDevice = expectedDevices.map(device => ({
    device,
    reason: availability[device]?.reason || null,
    metrics: fieldMetrics.filter(metric => !(r.fieldMatrix || [])
      .some(item => item.device === device && item.metric === metric && Number.isFinite(Number(item.p75))))
  })).filter(entry => entry.metrics.length);
  for (const entry of missingByDevice) {
    const reason = entry.reason === 'no_field_data'
      ? (lang === 'cs'
        ? 'Dotaz pro tento běh nevrátil dostatek device-specific CrUX dat; nejde o tvrzení, že data nemohou existovat jindy.'
        : 'The query returned insufficient device-specific CrUX data for this run; this does not claim that data can never be available.')
      : entry.reason === 'query_failed'
        ? (lang === 'cs'
          ? 'Device-specific CrUX dotaz selhal; nepřítomnost field dat proto není potvrzena.'
          : 'The device-specific CrUX query failed, so absence of field data is not established.')
        : (lang === 'cs'
          ? 'Důvod z dostupných dat nelze určit.'
          : 'The available data does not establish why.');
    gaps.push(lang === 'cs'
      ? `Field p75 pro zařízení ${deviceLabel(entry.device, lang)} v tomto běhu chybí u metrik ${entry.metrics.map(m => m.toUpperCase()).join(', ')}. ${reason}`
      : `Field p75 for ${deviceLabel(entry.device, lang)} is missing in this run for ${entry.metrics.map(m => m.toUpperCase()).join(', ')}. ${reason}`);
  }
  return gaps;
};

const renderKnownGaps = (r, { dark = false } = {}) => {
  const gaps = knownDataGaps(r);
  if (!gaps.length) return '';
  const title = r.lang === 'cs' ? 'Známé mezery v datech' : 'Acknowledged data gaps';
  if (dark) return `
    <div class="glass panel" style="margin-top:16px;border-color:rgba(245,158,11,.45);">
      <div class="panel-header"><h2>${escapeHtml(title)}</h2></div>
      <div style="font-size:13px;line-height:1.65;color:#cbd5e1;">${gaps.map(g => `• ${escapeHtml(g)}`).join('<br>')}</div>
    </div>`;
  return `
    <div class="callout">
      <strong>${escapeHtml(title)}</strong>
      ${gaps.map(g => `• ${escapeHtml(g)}`).join('<br>')}
    </div>`;
};

const renderMethodologyAudit = (r) => {
  const lang = r.lang;
  const gaps = knownDataGaps(r);
  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Metodika' : 'Methodology'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Jak čísla ' : 'How the numbers '}<em>${lang === 'cs' ? 'vznikla' : 'were produced'}</em>.</h2>
    <p>${lang === 'cs'
      ? 'Laboratorní běhy probíhají v headless prohlížeči <b>bez síťového ani CPU throttlingu</b> — měří stránku tak, jak ji server a prohlížeč reálně zvládnou. Pokud report uvádí rychlost připojení, jde o hodnotu, kterou o sobě hlásí sám prohlížeč (navigator.connection), ne o nastavený simulační profil. Lighthouse hodnoty (TBT, dlouhé úlohy při načtení) naopak vycházejí ze simulovaného načtení dle metodiky Lighthouse. Terénní data (CrUX) jsou 28denní klouzavý sběr od reálných uživatelů Chrome.'
      : 'Lab runs execute in a headless browser <b>with no network or CPU throttling</b> — they measure the page as the server and browser actually handle it. Where the report quotes a connection speed, it is the browser\'s own self-reported value (navigator.connection), not a configured simulation profile. Lighthouse values (TBT, load-time long tasks) come from Lighthouse\'s simulated load instead. Field data (CrUX) is a 28-day rolling collection from real Chrome users.'}</p>
    ${r.deepCLSResults?.cmpState === 'disabled' ? `<p>${(() => {
      const reason = r.deepCLSResults?.cmpStateReason === 'displayStatus:disabled'
        ? (lang === 'cs' ? 'CMP webu má zobrazení lišty pro měřicí lokalitu vypnuté (displayStatus: disabled)' : 'the site\'s CMP has banner display disabled for the measuring locality (displayStatus: disabled)')
        : (lang === 'cs' ? 'CMP webu hlásí gdprApplies: false pro měřicí lokalitu' : 'the site\'s CMP reports gdprApplies: false for the measuring locality');
      return lang === 'cs'
        ? `Stav souhlasu se v tomto měření neuplatnil (<b>${reason}</b>) — cookie lišta se nezobrazila a srovnání stavů „s lištou / po souhlasu" proto není k dispozici.`
        : `Consent state did not apply in this measurement (<b>${reason}</b>) — the cookie banner never rendered, so the "with banner / after consent" comparison is not available.`;
    })()}</p>` : ''}
    ${r.deepCLSResults?.cmpState ? `<p style="font-family:var(--mono);font-size:11px;color:#666;">cmp_state: ${escapeHtml(r.deepCLSResults.cmpState === 'nedetekovano' ? (lang === 'cs' ? 'nedetekováno' : 'not detected') : r.deepCLSResults.cmpState)}</p>` : ''}
    <p>${r.deepCLSResults?.cmpState === 'disabled' ? '' : r.cmpMode ? (lang === 'cs'
      ? `Stav souhlasu v reprezentativním běhu: <b>${formatConsentState(r.consentEvidence, lang, { prefix: false })}</b>. Ani zaznamenané přijetí samo o sobě nedokazuje, že byly globálně aktivní všechny marketingové skripty. U dalších laboratorních čísel (opakování, jiná zařízení a sweep) stav souhlasu bez jejich vlastních podmínek neznáme.`
      : `Consent state of the representative run: <b>${formatConsentState(r.consentEvidence, lang, { prefix: false })}</b>. A recorded accept action alone does not prove that every marketing script was globally active. Consent for other lab numbers (repeats, other devices and sweeps) is unknown without their own recorded conditions.`) : ''}</p>
    ${gaps.length ? renderKnownGaps(r) : ''}
    <div style="margin-top:20px;padding:16px 20px;background:var(--paper-2);border-left:3px solid var(--ink);">
      <strong style="font-family:var(--mono);font-size:11px;letter-spacing:0.12em;text-transform:uppercase;">${lang === 'cs' ? 'Diagnóza vs. implementace' : 'Diagnosis vs. implementation'}</strong>
      <div style="font-family:var(--serif);font-size:13px;line-height:1.6;color:#444;margin-top:8px;">${lang === 'cs'
        ? 'Audit ukazuje naměřené signály a možné příčiny k ověření; bez navazujícího důkazu nejde o potvrzené viníky ani hotové změny připravené k nasazení. Každé doporučení je nutné posoudit v kontextu konkrétní implementace a závislostí webu. Za správné provedení, otestování a ověření funkčnosti po nasazení odpovídá implementační tým. Doporučujeme nasazovat změny samostatně a před dalším krokem ověřit lab testem přínos i funkčnost webu.'
        : 'The audit shows measured signals and possible causes to verify; without further evidence these are not confirmed culprits or finished changes ready to deploy. Each recommendation must be assessed in the context of the specific implementation and the site\'s dependencies. Correct execution, testing and post-deployment verification of functionality are the responsibility of the implementing team. We recommend deploying changes one at a time and confirming both the improvement (lab re-test) and site functionality before proceeding.'}</div>
    </div>
  </section>`;
};

const renderFollowUpAudit = (lang, baseDate) => {
  const fl = followUpLabels(lang);
  const { auditDate, fieldDate } = buildFollowUpSchedule(baseDate);
  return `
  <section>
    <div class="section-label" style="color:var(--accent);">${escapeHtml(fl.title)}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Kdy' : 'When to'} <em>${lang === 'cs' ? 'měřit znovu' : 're-measure'}</em>.</h2>
    <p style="font-family:var(--mono);font-size:11px;color:#666;">${escapeHtml(fl.auditDateLabel)}: ${escapeHtml(formatFollowUpDate(auditDate, lang))}</p>
    <div style="border-top:2px solid var(--ink);margin-top:16px;">
      <div style="display:grid;grid-template-columns:1fr auto;gap:24px;padding:18px 0;border-bottom:1px solid #c9c3b6;align-items:flex-start;">
        <div>
          <div style="font-family:var(--serif);font-size:18px;font-weight:600;color:var(--ink);margin-bottom:6px;">1. ${escapeHtml(fl.labTitle)}</div>
          <div style="font-family:var(--serif);font-size:13px;color:#555;line-height:1.55;">${escapeHtml(fl.labText)}</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr auto;gap:24px;padding:18px 0;border-bottom:1px solid #c9c3b6;align-items:flex-start;">
        <div>
          <div style="font-family:var(--serif);font-size:18px;font-weight:600;color:var(--ink);margin-bottom:6px;">2. ${escapeHtml(fl.fieldTitle)}</div>
          <div style="font-family:var(--serif);font-size:13px;color:#555;line-height:1.55;">${escapeHtml(fl.fieldTextBefore)} <strong>${escapeHtml(formatFollowUpDate(fieldDate, lang))}</strong> ${escapeHtml(fl.fieldTextAfter)}</div>
        </div>
        <div style="text-align:right;white-space:nowrap;">
          <div style="font-family:var(--mono);font-size:11px;color:#888;">${escapeHtml(fl.fieldDateLabel)}</div>
          <div style="font-family:var(--serif);font-size:20px;font-weight:600;color:var(--accent);">${escapeHtml(formatFollowUpDate(fieldDate, lang))}</div>
        </div>
      </div>
    </div>
    <div style="margin-top:20px;padding:16px 20px;background:var(--paper-2);border-left:3px solid var(--accent);">
      <div style="font-family:var(--serif);font-size:14px;line-height:1.55;color:var(--ink);">${escapeHtml(fl.windowExplain)}</div>
    </div>
  </section>`;
};

// =========================================================================
// SECTION RENDERERS — DASHBOARD (glass dark style)
// =========================================================================
const renderCulpritsDashboard = (r) => {
  if (!r.culprits.length) return '';
  const lang = r.lang;
  const maxMs = r.culprits.reduce((m, c) => Math.max(m, c.ms || 0), 0) || 1;
  const totalBlocking = r.culprits.reduce((s, c) => s + (c.blockingMs ?? c.ms ?? 0), 0);

  return `
  <div class="glass panel" style="margin-bottom:16px">
    <div class="panel-header">
      <div class="panel-title">${escapeHtml(r.culpritKindLabel)}</div>
      <div class="panel-subtitle">${r.culprits.length} ${lang === 'cs' ? 'položek' : 'items'}</div>
    </div>
    <div class="culprit-list">
      ${r.culprits.map(c => {
        const sub = escapeHtml(c.subtitle || '');
        const urlText = c.url ? escapeHtml(shortenUrl(c.url, 70)) : '';
        const secondary = sub && urlText
          ? `${sub} <span style="color:#475569">·</span> <span style="opacity:0.85">${urlText}</span>`
          : (urlText || sub);
        return `
        <div class="culprit">
          <span class="rank">${escapeHtml(c.rank)}</span>
          <div class="info">
            <div class="source">${escapeHtml(c.title)}${c.count ? ` <span style="color:#64748b;font-weight:400">· ${c.count}× ${lang === 'cs' ? 'úloh' : 'tasks'}</span>` : ''}</div>
            <div class="url">${secondary}</div>
          </div>
          <div class="ms">${c.ms != null ? fmtNum(c.ms) : '—'}<span class="unit"> ${escapeHtml(c.unit || 'ms')}</span>${c.blockingMs != null ? `<div style="font-size:10px;color:#f59e0b;font-weight:600;">+${fmtNum(c.blockingMs)} ms ${lang === 'cs' ? 'blokuje' : 'block'}</div>` : ''}</div>
          <div class="bar"><div class="fill" style="width:${Math.min(100, ((c.ms || 0) / maxMs) * 100)}%"></div></div>
        </div>`;
      }).join('')}
    </div>
    ${totalBlocking > 0 && r.culpritKind === 'vendor' ? `
    <div class="culprit-total">
      <span class="lbl">${lang === 'cs' ? 'Součet blokujícího času' : 'Total blocking time'}</span>
      <span class="val">${fmtNum(totalBlocking)} ms</span>
    </div>` : ''}
  </div>`;
};

const renderLongTasksDashboard = (r) => {
  const lt = r.rich?.longTasks;
  if (!lt?.items?.length) return '';
  const lang = r.lang;
  const items = lt.items.slice(0, 10);
  const maxDur = items.reduce((m, t) => Math.max(m, t.duration || 0), 0) || 1;
  return `
  <div class="glass panel" style="margin-bottom:16px">
    <div class="panel-header">
      <div class="panel-title">${lang === 'cs' ? 'Dlouhé úlohy (>50 ms) — detail' : 'Long tasks (>50 ms) — detail'}</div>
      <div class="panel-subtitle">${lt.count} ${lang === 'cs' ? 'úloh (celé načtení, Lighthouse)' : 'tasks (full page load, Lighthouse)'} · ${lang === 'cs' ? 'Samostatná metrika TBT' : 'Separate TBT metric'}: ${fmtNum(lt.totalBlockingTime)} ms</div>
    </div>
    <p>${lang === 'cs' ? 'TBT započítává jen přesahy v okně FCP–TTI, nikoli součet všech níže vypsaných přesahů.' : 'TBT includes only excesses within FCP–TTI, not the sum of all listed excesses.'}</p>
    <div class="culprit-list">
      ${items.map((t) => `
        <div class="culprit" style="grid-template-columns: 1fr auto 100px;">
          <div class="info">
            <div class="source">${escapeHtml(t.vendor || t.name || (lang === 'cs' ? 'Neurčeno' : 'Unattributed'))}</div>
            <div class="url">${escapeHtml(t.url ? shortenUrl(t.url, 70) : '')}</div>
          </div>
          <div class="ms">${fmtNum(t.duration)}<span class="unit"> ms</span><div style="font-size:10px;color:#f59e0b;font-weight:600;">+${fmtNum(t.blockingTime)} ms ${lang === 'cs' ? 'blokuje' : 'block'}</div></div>
          <div class="bar"><div class="fill" style="width:${Math.min(100, ((t.duration || 0) / maxDur) * 100)}%"></div></div>
        </div>`).join('')}
    </div>
  </div>`;
};

const renderInsightsDashboard = (r) => {
  const lang = r.lang;
  const insights = [];

  // CMP warning
  if (r.rich.cmpWarning) {
    insights.push({
      eyebrow: lang === 'cs' ? 'Pozor' : 'Note',
      title: lang === 'cs' ? 'Měřeno bez CMP' : 'Measured without CMP',
      body: lang === 'cs'
        ? 'Reálný uživatel uvidí ještě další tracking po kliknutí na „Přijmout vše".'
        : 'A real user will see more tracking after clicking "Accept all".',
      highlight: true
    });
  }

  // Server timing insight
  if (r.rich.serverTiming) {
    const st = r.rich.serverTiming;
    insights.push({
      eyebrow: lang === 'cs' ? 'Server' : 'Server',
      title: st.isFast
        ? (lang === 'cs' ? `Server odpovídá za ${st.total} ms` : `Server responds in ${st.total} ms`)
        : (lang === 'cs' ? `TTFB ${st.ttfb} ms` : `TTFB ${st.ttfb} ms`),
      body: st.isFast
        ? (lang === 'cs' ? 'Backend není úzkým hrdlem — můžete optimalizovat klientskou stranu.' : 'Backend is not the bottleneck — optimize the client side.')
        : (lang === 'cs' ? 'Server potřebuje příliš dlouho. Zkontrolujte cache a databázové dotazy.' : 'Server takes too long. Check cache and database queries.')
    });
  }

  // Third-party insight
  if (r.rich.resourceTiming?.thirdParty?.count > 5) {
    const tp = r.rich.resourceTiming.thirdParty;
    insights.push({
      eyebrow: lang === 'cs' ? '3rd party' : '3rd party',
      title: `${tp.count} ${lang === 'cs' ? 'externích požadavků' : 'external requests'}`,
      body: lang === 'cs'
        ? `${fmtKb(tp.totalBytes)} přenesených dat, ${fmtMs(tp.totalTimeMs) || '—'} síťového času (přenos, ne blokování CPU).`
        : `${fmtKb(tp.totalBytes)} of data transferred, ${fmtMs(tp.totalTimeMs) || '—'} of network time (transfer, not CPU blocking).`
    });
  }

  // CLS run-to-run stability (median basis) — mockup counterpart of the
  // audit style's "Stabilita měření CLS" callout.
  const clsSp = r.metricLower === 'cls' ? r.deepCLSResults?.labRunSpread : null;
  if (clsSp && clsSp.runs >= 2 && clsSp.min !== clsSp.max) {
    insights.push({
      eyebrow: lang === 'cs' ? 'Stabilita' : 'Stability',
      title: labSeriesIsVerified(clsSp)
        ? (lang === 'cs' ? `CLS medián přes ${clsSp.runs} běhů` : `CLS median across ${clsSp.runs} runs`)
        : (lang === 'cs' ? `CLS orientační série · n=${clsSp.runs}` : `CLS indicative series · n=${clsSp.runs}`),
      body: labSeriesIsVerified(clsSp)
        ? (lang === 'cs'
          ? `Rozptyl ${formatMetricValue('cls', clsSp.min) || '—'}–${formatMetricValue('cls', clsSp.max) || '—'} — lab hodnota je medián, viníci řazeni podle četnosti napříč běhy.`
          : `Spread ${formatMetricValue('cls', clsSp.min) || '—'}–${formatMetricValue('cls', clsSp.max) || '—'} — the lab value is the median; culprits ranked by cross-run frequency.`)
        : (lang === 'cs'
          ? `Průměr ~${formatMetricValue('cls', labSeriesDisplayValue(clsSp)) || '—'}; pro ověřený výsledek je potřeba 5 validních běhů.`
          : `Average ~${formatMetricValue('cls', labSeriesDisplayValue(clsSp)) || '—'}; 5 valid runs are required for a verified result.`)
    });
  }

  // Multi-page
  if (r.multiPage) {
    insights.push({
      eyebrow: lang === 'cs' ? 'Napříč webem' : 'Site-wide',
      title: lang === 'cs' ? `Měřeno na ${r.multiPage.pages} stránkách` : `Measured on ${r.multiPage.pages} pages`,
      body: (() => {
        const base = lang === 'cs'
          ? `Nejhorší: ${formatMetricValue(r.metric, r.multiPage.worst?.value) || '—'} · průměr: ${formatMetricValue(r.metric, r.multiPage.avg) || '—'}`
          : `Worst: ${formatMetricValue(r.metric, r.multiPage.worst?.value) || '—'} · avg: ${formatMetricValue(r.metric, r.multiPage.avg) || '—'}`;
        // Same confidence honesty as the audit template: say whether the
        // worst page's value is a multi-run median or one indicative run.
        if (r.multiPage.worstConfidence === 'median-confirmed') {
          return base + (lang === 'cs'
            ? ` — nejhorší stránka přeměřena ${r.multiPage.worstRuns || 5}× opakovanými mělkými (sweep) běhy, hodnota je medián z ${r.multiPage.worstValidRuns || r.multiPage.worstRuns || 5} validních běhů.`
            : ` — worst page re-measured ${r.multiPage.worstRuns || 5}× with repeated shallow (sweep) runs, the value is the median of ${r.multiPage.worstValidRuns || r.multiPage.worstRuns || 5} valid runs.`);
        }
        if (r.multiPage.worstConfidence === 'single-run') {
          return base + (lang === 'cs'
            ? ' — nejhorší stránka změřena jen jednou, berte orientačně.'
            : ' — worst page measured once, treat as indicative.');
        }
        return base;
      })()
    });
  }

  // Scenario insight
  if (r.copy?.pullquote) {
    insights.push({
      eyebrow: lang === 'cs' ? 'Souhrn' : 'Summary',
      title: r.copy.pullquote,
      body: '',
      highlight: true
    });
  }

  if (!insights.length) return '';

  return insights.map(ins => `
    <div class="insight ${ins.highlight ? 'highlight' : ''}">
      <div class="eyebrow">${escapeHtml(ins.eyebrow)}</div>
      <h4>${escapeHtml(ins.title)}</h4>
      ${ins.body ? `<p>${escapeHtml(ins.body)}</p>` : ''}
    </div>`).join('');
};

const renderServerTimingDashboard = (r) => {
  const st = r.rich.serverTiming;
  if (!st) return '';
  const lang = r.lang;
  const phases = [
    { key: 'DNS', val: st.dns, color: '#f59e0b' },
    { key: 'TCP', val: st.tcp, color: '#10b981' },
    { key: 'TLS', val: st.tls, color: '#6366f1' },
    { key: 'TTFB', val: st.ttfb, color: '#22d3ee' },
    { key: 'DL', val: st.download, color: '#a855f7' }
  ].filter(p => p.val > 0);

  return `
  <div class="glass panel" style="margin-bottom:16px">
    <div class="panel-header">
      <div class="panel-title">${lang === 'cs' ? 'Server Timing' : 'Server Timing'} <span style="font-family:'JetBrains Mono',monospace;font-size:10px;color:#64748b;margin-left:8px">W3C</span></div>
      <div class="panel-subtitle">${lang === 'cs' ? 'Celkem' : 'Total'} ${st.total} ms ${st.isFast ? `<span style="color:#22c55e;margin-left:6px">${lang === 'cs' ? '✓ rychlé' : '✓ fast'}</span>` : ''}</div>
    </div>
    <div class="timing-grid">
      ${phases.map(p => `
        <div class="timing-item">
          <div class="timing-key" style="color:${p.color}">${p.key}</div>
          <div class="timing-val">${p.val} ms</div>
        </div>`).join('')}
    </div>
    ${st.phasesAvailable === false ? `<div style="font-size:10px;color:#64748b;margin-top:8px">${lang === 'cs'
      ? 'Fáze DNS / TCP / TLS nejsou z Lighthouse dostupné — hodnotí se doba odpovědi serveru (TTFB, jen serverová část; field TTFB z CrUX bývá vyšší).'
      : 'DNS / TCP / TLS phases are not available from Lighthouse — server response time (TTFB, server portion only; field TTFB from CrUX tends to be higher) is evaluated.'}</div>` : ''}
  </div>`;
};

const renderRenderBlockingDashboard = (r) => {
  const rb = r.rich.renderBlocking;
  if (!rb || !rb.count) return '';
  const lang = r.lang;
  return `
  <div class="glass panel" style="margin-bottom:16px">
    <div class="panel-header">
      <div class="panel-title">${lang === 'cs' ? 'Render-blocking zdroje' : 'Render-blocking resources'}</div>
      <div class="panel-subtitle">${rb.count} ${lang === 'cs' ? 'zdrojů' : 'resources'} · ${fmtMs(rb.totalBlockingMs) || '—'}</div>
    </div>
    <div class="rb-list">
      ${rb.items.map(it => `
        <div class="rb-row">
          <span class="rb-type">${escapeHtml(String(it.type).toUpperCase())}</span>
          <span class="rb-name">${escapeHtml(it.name)}</span>
          <span class="rb-bytes">${fmtKb(it.bytes) || '—'}</span>
          <span class="rb-ms">+${it.wastedMs} ms</span>
        </div>`).join('')}
    </div>
  </div>`;
};

const renderMainThreadDashboard = (r) => {
  const mtw = r.rich.mainThreadWork;
  if (!mtw || !mtw.categories.length) return '';
  const lang = r.lang;
  return `
  <div class="glass panel" style="margin-bottom:16px">
    <div class="panel-header">
      <div class="panel-title">${lang === 'cs' ? 'Práce hlavního vlákna' : 'Main Thread Work'}</div>
      <div class="panel-subtitle">${(mtw.totalMs / 1000).toFixed(2)} s</div>
    </div>
    <div class="mt-bar">
      ${mtw.categories.map(c => `<div class="mt-segment" style="width:${c.percentage}%;background:${c.color}" title="${escapeHtml(c.label)}: ${c.percentage}%"></div>`).join('')}
    </div>
    <div class="mt-legend">
      ${mtw.categories.slice(0, 6).map(c => `
        <div class="mt-legend-item">
          <span class="mt-dot" style="background:${c.color}"></span>
          <span class="mt-legend-label">${escapeHtml(c.label)}</span>
          <span class="mt-legend-pct">${c.percentage}%</span>
        </div>`).join('')}
    </div>
    ${mtw.topScripts.length ? `
    <div class="mt-scripts">
      <div class="mt-scripts-label">${lang === 'cs' ? 'Nejnáročnější skripty' : 'Most expensive scripts'}</div>
      ${mtw.topScripts.map(s => `
        <div class="mt-script-row">
          <span class="mt-script-name">${escapeHtml(s.name)}</span>
          <span class="mt-script-ms">${s.ms} ms</span>
        </div>`).join('')}
    </div>` : ''}
  </div>`;
};

const renderResourceTimingDashboard = (r) => {
  const rt = r.rich.resourceTiming;
  if (!rt || !rt.byType.length) return '';
  const lang = r.lang;
  const typeIcons = { script: '⚡', font: '🔤', image: '🖼', stylesheet: '🎨', document: '📄', other: '📦', media: '🎬' };
  return `
  <div class="glass panel" style="margin-bottom:16px">
    <div class="panel-header">
      <div class="panel-title">${lang === 'cs' ? 'Zdroje stránky' : 'Page Resources'}</div>
      <div class="panel-subtitle">${rt.totalRequests} ${lang === 'cs' ? 'požadavků' : 'requests'} · ${fmtKb(rt.totalBytes) || '—'}</div>
    </div>
    <div class="resource-grid">
      ${rt.byType.slice(0, 8).map(t => `
        <div class="resource-card">
          <div class="resource-icon">${typeIcons[String(t.type).toLowerCase()] || '📦'}</div>
          <div class="resource-info">
            <div class="resource-count">${t.count}</div>
            <div class="resource-type">${escapeHtml(String(t.type))}</div>
            <div class="resource-bytes">${fmtKb(t.bytes) || '—'}</div>
          </div>
        </div>`).join('')}
    </div>
    ${rt.thirdParty.count > 0 ? `
    <div class="third-party-card">
      <div class="tp-label">${lang === 'cs' ? '3rd-party dopad' : '3rd-party impact'}</div>
      <div class="tp-stats">
        <div><span class="tp-num">${rt.thirdParty.count}</span><span class="tp-lbl">${lang === 'cs' ? 'požadavků' : 'requests'}</span></div>
        <div><span class="tp-num">${fmtKb(rt.thirdParty.totalBytes) || '—'}</span><span class="tp-lbl">${lang === 'cs' ? 'dat' : 'data'}</span></div>
        <div><span class="tp-num">${fmtMs(rt.thirdParty.totalTimeMs) || '—'}</span><span class="tp-lbl">${lang === 'cs' ? 'čas' : 'time'}</span></div>
      </div>
    </div>` : ''}
  </div>`;
};

const renderFontLoadingDashboard = (r) => {
  const fl = r.rich.fontLoading;
  if (!fl || !fl.totalFonts) return '';
  const lang = r.lang;
  return `
  <div class="glass panel" style="margin-bottom:16px">
    <div class="panel-header">
      <div class="panel-title">${lang === 'cs' ? 'Načítání fontů' : 'Font Loading'}</div>
      <div class="panel-subtitle">${fl.totalFonts} ${lang === 'cs' ? 'fontů' : 'fonts'}</div>
    </div>
    <div class="font-grid">
      <div class="font-stat">
        <div class="font-num">${fl.totalFonts}</div>
        <div class="font-lbl">${lang === 'cs' ? 'celkem' : 'total'}</div>
      </div>
      <div class="font-stat ${fl.withoutSwapCount > 0 ? 'bad' : 'good'}">
        <div class="font-num">${fl.withoutSwapCount}</div>
        <div class="font-lbl">${lang === 'cs' ? 'bez swap' : 'no swap'}</div>
      </div>
      <div class="font-stat ${fl.foitRisk ? 'bad' : 'good'}">
        <div class="font-num">${fl.foitRisk ? '✗' : '✓'}</div>
        <div class="font-lbl">FOIT</div>
      </div>
      <div class="font-stat ${fl.foutRisk ? 'warn' : 'good'}">
        <div class="font-num">${fl.foutRisk ? '✗' : '✓'}</div>
        <div class="font-lbl">FOUT</div>
      </div>
    </div>
  </div>`;
};

const renderBudgetDashboard = (r) => {
  const pb = r.rich.performanceBudget;
  if (!pb || !pb.items.length) return '';
  const lang = r.lang;
  return `
  <div class="glass panel" style="margin-bottom:16px">
    <div class="panel-header">
      <div class="panel-title">Performance Budget</div>
      <div class="panel-subtitle">${pb.overBudgetCount} / ${pb.items.length} ${lang === 'cs' ? 'nad limitem' : 'over budget'}</div>
    </div>
    ${pb.items.map(b => {
      const pct = Math.min(150, b.percentage || 0);
      const fillColor = pct > 100 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#22c55e';
      return `
      <div class="budget-row">
        <div class="budget-cat">${escapeHtml(b.label)}</div>
        <div class="budget-bar-wrap">
          <div class="budget-bar"><div class="budget-fill" style="width:${Math.min(100, pct)}%;background:${fillColor}"></div></div>
        </div>
        <div class="budget-vals">
          <b style="color:${b.overBudget ? '#ef4444' : '#22c55e'}">${fmtKb(b.actual) || '—'}</b> / ${fmtKb(b.budget) || '—'}
          <span class="budget-pct" style="color:${b.overBudget ? '#ef4444' : '#64748b'}">${b.percentage}%</span>
        </div>
      </div>`;
    }).join('')}
    ${pb.unusedJsBytes > 0 || pb.unusedCssBytes > 0 ? `
    <div class="unused-row">
      ${pb.unusedJsBytes > 0 ? `<div class="unused-stat"><b>${fmtKb(pb.unusedJsBytes)}</b> ${lang === 'cs' ? 'nepoužitý JS' : 'unused JS'}</div>` : ''}
      ${pb.unusedCssBytes > 0 ? `<div class="unused-stat"><b>${fmtKb(pb.unusedCssBytes)}</b> ${lang === 'cs' ? 'nepoužité CSS' : 'unused CSS'}</div>` : ''}
    </div>` : ''}
  </div>`;
};

// =========================================================================
// DASHBOARD STYLE — Dark glassmorphic
// =========================================================================
export const generateMockupHTML = (data) => {
  const r = buildReportData(data);
  const lang = r.lang;
  const diagnosticEvidenceHtml = renderEvidenceHTML(data, lang, 'dark');
  const contractHtml = renderAuditEvidenceContractHTML(data.evidenceContract, lang, 'dark');
  const inpScenariosHtml = renderInpScenarioScopesHTML(data, lang, 'dark');
  const labEvidenceHtml = renderLabRunEvidenceAudit(r, { theme: 'dark' });
  const lcpReconciliationHtml = renderLcpFieldReconciliation(r, { theme: 'dark' });

  const verdictCardsHtml = r.verdictCards.map(c => `
    <div class="glass metric-card ${c.severity}">
      <div class="label">${escapeHtml(c.label)}</div>
      <div class="value">${escapeHtml(c.value)}</div>
      <div class="compare">${escapeHtml(c.target)}</div>
    </div>`).join('');

  const cmpBlock = r.rich.cmpWarning ? `
    <div class="cmp-warning">
      <div class="icon">!</div>
      <div class="content">
        <h4>${lang === 'cs' ? 'Měřeno bez CMP — reálný dopad bude vyšší' : 'Measured without CMP — real impact will be higher'}</h4>
        <p>${lang === 'cs'
          ? 'Analýza proběhla před souhlasem s cookies. Po kliknutí na „Přijmout vše" se probudí další tracery (analytika, remarketing), které v testu nejsou. V praxi bude dopad na uživatele vyšší.'
          : 'Analysis ran before cookie consent. After "Accept all" more trackers wake up that are not in this test. Real-world impact will be higher.'}</p>
      </div>
    </div>` : '';

  const tasksList = (r.tasks || []).slice(0, 6).map((t, i) => `
        <div class="rec">
          <div class="rec-num">${String(i + 1).padStart(2, '0')}</div>
          <div class="rec-body">
            <h3>${escapeHtml(t.title || t.task || 'Doporučení')}</h3>
            ${t.description ? `<p class="desc">${escapeHtml(t.description)}</p>` : ''}
            <div class="tags">
              ${t.priority === 'critical' || t.priority === 'high'
                ? '<span class="tag impact-high">' + (lang === 'cs' ? 'Dopad vysoký' : 'Impact high') + '</span>'
                : t.priority === 'medium'
                ? '<span class="tag impact-med">' + (lang === 'cs' ? 'Dopad střední' : 'Impact medium') + '</span>'
                : ''}
              ${t.estimatedTime ? `<span class="tag" style="background:rgba(100,116,139,0.12);color:#cbd5e1;border:1px solid rgba(100,116,139,0.25)">${escapeHtml(t.estimatedTime)}</span>` : ''}
            </div>
          </div>
        </div>`).join('');

  return embedAuditEvidenceDigest(annotateFirstJargon(`<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="UTF-8">
<title>ControlVitals — ${escapeHtml(r.metricUpper)} · ${escapeHtml(r.domain)}</title>
<meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { background: #020617; color: #e2e8f0; font-family: 'Inter', system-ui, sans-serif; line-height: 1.5; -webkit-font-smoothing: antialiased; min-height: 100vh; position: relative; overflow-x: hidden; }
body::before { content: ''; position: fixed; inset: 0; background: radial-gradient(circle at 15% 20%, rgba(6,182,212,0.08) 0%, transparent 50%), radial-gradient(circle at 85% 70%, rgba(139,92,246,0.06) 0%, transparent 50%); pointer-events: none; z-index: 0; }
.app { position: relative; z-index: 1; max-width: 1280px; margin: 0 auto; padding: 32px 24px; }
.topbar { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; background: rgba(15,23,42,0.6); backdrop-filter: blur(12px); border: 1px solid rgba(6,182,212,0.12); border-radius: 12px; margin-bottom: 24px; }
.logo { display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 15px; }
.logo-mark { width: 28px; height: 28px; border-radius: 6px; background: linear-gradient(135deg,#06b6d4,#8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 800; color: white; }
.breadcrumb { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #94a3b8; }
.breadcrumb .current { color: #22d3ee; font-family: 'JetBrains Mono', monospace; }
.breadcrumb .sep { color: #475569; }
.audit-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 32px; margin-bottom: 32px; padding: 8px 8px 0; }
.audit-title h1 { font-size: 28px; font-weight: 600; letter-spacing: -0.02em; margin-bottom: 6px; color: #f8fafc; }
.audit-title h1 .domain { background: linear-gradient(135deg,#06b6d4,#8b5cf6); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; font-weight: 700; }
.audit-title .meta { display: flex; flex-wrap: wrap; gap: 16px; font-size: 12px; color: #64748b; font-family: 'JetBrains Mono', monospace; }
.audit-title .meta .dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: #10b981; margin-right: 6px; vertical-align: middle; box-shadow: 0 0 8px rgba(16,185,129,0.6); }
.glass { background: rgba(2,6,23,0.65); backdrop-filter: blur(24px); border: 1px solid rgba(6,182,212,0.15); border-top: 1px solid rgba(6,182,212,0.25); box-shadow: 0 8px 32px rgba(0,0,0,0.6), 0 0 80px -20px rgba(6,182,212,0.12); border-radius: 16px; }
.verdict-grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 16px; margin-bottom: 24px; }
.verdict-main { padding: 28px 32px; position: relative; overflow: hidden; }
.verdict-main::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg,#ef4444,#f59e0b); }
.verdict-label { display: flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase; color: #ef4444; font-weight: 600; margin-bottom: 12px; font-family: 'JetBrains Mono', monospace; }
.verdict-label::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: #ef4444; box-shadow: 0 0 12px rgba(239,68,68,0.8); }
.verdict-main .headline { font-size: 22px; font-weight: 500; line-height: 1.3; letter-spacing: -0.01em; margin-bottom: 8px; color: #f8fafc; }
.verdict-main .headline strong { color: #22d3ee; font-weight: 700; }
.verdict-main .sub { font-size: 14px; color: #94a3b8; max-width: 560px; }
.metric-card { padding: 22px 24px; position: relative; overflow: hidden; }
.metric-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; }
.metric-card.critical::before { background: #ef4444; }
.metric-card.warning::before { background: #f59e0b; }
.metric-card.good::before { background: #10b981; }
.metric-card .label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.12em; font-family: 'JetBrains Mono', monospace; margin-bottom: 12px; }
.metric-card .value { font-size: 32px; font-weight: 700; letter-spacing: -0.02em; line-height: 1; margin-bottom: 6px; font-family: 'JetBrains Mono', monospace; }
.metric-card.critical .value { color: #ef4444; }
.metric-card.warning .value { color: #f59e0b; }
.metric-card.good .value { color: #10b981; }
.metric-card .compare { font-size: 12px; color: #64748b; font-family: 'JetBrains Mono', monospace; line-height: 1.4; }
.cmp-warning { padding: 16px 24px; background: rgba(245,158,11,0.05); border: 1px solid rgba(245,158,11,0.25); border-radius: 12px; margin-bottom: 20px; display: flex; align-items: flex-start; gap: 14px; }
.cmp-warning .icon { width: 36px; height: 36px; border-radius: 8px; background: rgba(245,158,11,0.15); display: flex; align-items: center; justify-content: center; color: #f59e0b; flex-shrink: 0; font-size: 18px; font-weight: 700; }
.cmp-warning .content h4 { font-size: 12px; font-weight: 600; color: #fbbf24; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em; font-family: 'JetBrains Mono', monospace; }
.cmp-warning .content p { font-size: 13px; color: #cbd5e1; line-height: 1.55; }
.scenario-card { padding: 24px 28px; margin-bottom: 20px; }
.scenario-card .scenario-eyebrow { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #22d3ee; margin-bottom: 10px; }
.scenario-card h2 { font-size: 22px; font-weight: 600; line-height: 1.3; color: #f8fafc; margin-bottom: 12px; letter-spacing: -0.01em; }
.scenario-card h2 strong { color: #22d3ee; font-weight: 700; }
.scenario-card p { font-size: 14px; color: #cbd5e1; line-height: 1.65; margin-bottom: 12px; }
.scenario-card p:last-child { margin-bottom: 0; }
.content-grid { display: grid; grid-template-columns: 1fr 340px; gap: 16px; margin-bottom: 16px; }
@media (max-width: 1000px) { .content-grid { grid-template-columns: 1fr; } .verdict-grid { grid-template-columns: 1fr; } }
.panel { padding: 22px 26px; }
.panel-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid rgba(100,116,139,0.15); flex-wrap: wrap; gap: 8px; }
.panel-title { font-size: 14px; font-weight: 600; color: #f8fafc; }
.panel-subtitle { font-size: 11px; color: #64748b; font-family: 'JetBrains Mono', monospace; text-transform: uppercase; letter-spacing: 0.08em; }
.culprit-list { display: flex; flex-direction: column; gap: 10px; }
.culprit { display: grid; grid-template-columns: 28px 1fr auto 100px; gap: 12px; padding: 12px 14px; background: rgba(15,23,42,0.5); border: 1px solid rgba(100,116,139,0.12); border-radius: 10px; align-items: center; }
.culprit .rank { font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 700; color: #ef4444; }
.culprit .info { min-width: 0; }
.culprit .source { font-size: 13px; color: #e2e8f0; font-weight: 500; margin-bottom: 3px; }
.culprit .url { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.culprit .ms { font-family: 'JetBrains Mono', monospace; font-size: 16px; font-weight: 700; color: #ef4444; text-align: right; white-space: nowrap; }
.culprit .ms .unit { font-size: 10px; color: #64748b; font-weight: 500; margin-left: 2px; }
.culprit .bar { height: 4px; background: rgba(100,116,139,0.2); border-radius: 2px; overflow: hidden; }
.culprit .bar .fill { height: 100%; background: linear-gradient(90deg,#ef4444,#f59e0b); border-radius: 2px; }
.culprit-total { margin-top: 14px; padding: 12px 14px; background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); border-radius: 10px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; }
.culprit-total .lbl { color: #fca5a5; font-weight: 500; }
.culprit-total .val { font-family: 'JetBrains Mono', monospace; color: #ef4444; font-weight: 700; font-size: 15px; }
.insight { padding: 18px 20px; background: rgba(15,23,42,0.5); border: 1px solid rgba(100,116,139,0.12); border-radius: 12px; margin-bottom: 12px; }
.insight.highlight { background: rgba(6,182,212,0.06); border-color: rgba(6,182,212,0.25); }
.insight .eyebrow { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #22d3ee; font-weight: 600; font-family: 'JetBrains Mono', monospace; margin-bottom: 8px; }
.insight h4 { font-size: 14px; font-weight: 600; color: #f8fafc; margin-bottom: 6px; line-height: 1.35; }
.insight p { font-size: 12px; color: #94a3b8; line-height: 1.55; }
.timing-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(72px, 1fr)); gap: 10px; }
.timing-item { background: rgba(15,23,42,0.5); border-radius: 8px; padding: 10px 12px; text-align: center; }
.timing-key { font-size: 10px; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.1em; margin-bottom: 4px; font-weight: 700; }
.timing-val { font-size: 16px; font-weight: 700; color: #f8fafc; font-family: 'JetBrains Mono', monospace; }
.mt-bar { height: 14px; border-radius: 7px; overflow: hidden; display: flex; background: #0f172a; margin-bottom: 14px; }
.mt-segment { height: 100%; }
.mt-legend { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 14px; }
.mt-legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; }
.mt-dot { width: 8px; height: 8px; border-radius: 50%; }
.mt-legend-label { color: #94a3b8; }
.mt-legend-pct { color: #f8fafc; font-weight: 600; font-family: 'JetBrains Mono', monospace; }
.mt-scripts { padding-top: 12px; border-top: 1px solid rgba(100,116,139,0.15); }
.mt-scripts-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: #64748b; margin-bottom: 8px; font-family: 'JetBrains Mono', monospace; }
.mt-script-row { display: flex; justify-content: space-between; padding: 6px 10px; background: rgba(15,23,42,0.5); border-radius: 6px; margin-bottom: 5px; }
.mt-script-name { font-size: 11px; color: #94a3b8; font-family: 'JetBrains Mono', monospace; overflow: hidden; text-overflow: ellipsis; max-width: 70%; white-space: nowrap; }
.mt-script-ms { font-size: 11px; color: #f59e0b; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
.resource-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-bottom: 14px; }
.resource-card { display: flex; align-items: center; gap: 10px; padding: 12px; background: rgba(15,23,42,0.5); border-radius: 8px; }
.resource-icon { font-size: 20px; }
.resource-info { font-family: 'JetBrains Mono', monospace; }
.resource-count { font-size: 18px; font-weight: 700; color: #f8fafc; line-height: 1; }
.resource-type { font-size: 10px; color: #64748b; text-transform: capitalize; margin: 2px 0; }
.resource-bytes { font-size: 11px; color: #94a3b8; }
.third-party-card { padding: 14px 16px; background: linear-gradient(135deg, rgba(249,115,22,0.08), rgba(234,88,12,0.08)); border: 1px solid rgba(249,115,22,0.25); border-radius: 10px; }
.tp-label { font-size: 11px; color: #fb923c; font-weight: 600; margin-bottom: 10px; font-family: 'JetBrains Mono', monospace; text-transform: uppercase; letter-spacing: 0.1em; }
.tp-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.tp-stats > div { text-align: center; }
.tp-num { display: block; font-size: 17px; font-weight: 700; color: #fb923c; font-family: 'JetBrains Mono', monospace; }
.tp-lbl { display: block; font-size: 10px; color: #94a3b8; margin-top: 2px; }
.font-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.font-stat { padding: 14px; background: rgba(15,23,42,0.5); border-radius: 8px; text-align: center; }
.font-stat.bad { background: rgba(239,68,68,0.1); }
.font-stat.warn { background: rgba(245,158,11,0.1); }
.font-stat.good { background: rgba(34,197,94,0.08); }
.font-num { font-size: 22px; font-weight: 700; font-family: 'JetBrains Mono', monospace; color: #f8fafc; }
.font-stat.bad .font-num { color: #ef4444; }
.font-stat.warn .font-num { color: #f59e0b; }
.font-stat.good .font-num { color: #22c55e; }
.font-lbl { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; font-family: 'JetBrains Mono', monospace; margin-top: 4px; }
.budget-row { display: grid; grid-template-columns: 110px 1fr 200px; gap: 14px; padding: 10px 0; align-items: center; border-bottom: 1px solid rgba(100,116,139,0.1); }
.budget-row:last-child { border-bottom: none; }
.budget-cat { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; font-family: 'JetBrains Mono', monospace; font-weight: 500; }
.budget-bar-wrap { width: 100%; }
.budget-bar { height: 6px; background: rgba(100,116,139,0.15); border-radius: 3px; overflow: hidden; }
.budget-fill { height: 100%; border-radius: 3px; }
.budget-vals { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #94a3b8; text-align: right; }
.budget-pct { margin-left: 8px; font-weight: 700; }
.unused-row { display: flex; gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(100,116,139,0.15); }
.unused-stat { flex: 1; padding: 10px 12px; background: rgba(245,158,11,0.08); border-radius: 8px; font-size: 11px; color: #94a3b8; font-family: 'JetBrains Mono', monospace; }
.unused-stat b { color: #f59e0b; font-size: 13px; }
.rb-list { display: flex; flex-direction: column; gap: 6px; }
.rb-row { display: grid; grid-template-columns: 60px 1fr 80px 80px; gap: 10px; padding: 10px 12px; background: rgba(15,23,42,0.5); border-radius: 8px; align-items: center; }
.rb-type { font-family: 'JetBrains Mono', monospace; font-size: 9px; font-weight: 700; padding: 3px 6px; background: rgba(239,68,68,0.12); color: #fca5a5; border-radius: 3px; text-align: center; letter-spacing: 0.05em; }
.rb-name { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #cbd5e1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rb-bytes { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #94a3b8; text-align: right; }
.rb-ms { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #ef4444; font-weight: 700; text-align: right; }
.recs-section { padding: 24px 28px; margin-bottom: 16px; }
.recs-section h2 { font-size: 18px; font-weight: 600; color: #f8fafc; letter-spacing: -0.01em; margin-bottom: 18px; }
.rec-list { display: flex; flex-direction: column; gap: 10px; }
.rec { display: grid; grid-template-columns: 44px 1fr; gap: 16px; padding: 18px 20px; background: rgba(15,23,42,0.5); border: 1px solid rgba(100,116,139,0.12); border-radius: 12px; align-items: start; }
.rec-num { width: 40px; height: 40px; border-radius: 10px; background: rgba(6,182,212,0.1); border: 1px solid rgba(6,182,212,0.3); display: flex; align-items: center; justify-content: center; font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: #22d3ee; }
.rec-body h3 { font-size: 14px; font-weight: 600; color: #f8fafc; margin-bottom: 6px; line-height: 1.3; }
.rec-body .desc { font-size: 13px; color: #94a3b8; line-height: 1.55; margin-bottom: 10px; }
.rec-body .tags { display: flex; gap: 6px; flex-wrap: wrap; }
.tag { padding: 3px 8px; border-radius: 4px; font-size: 10px; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.05em; font-weight: 600; text-transform: uppercase; }
.tag.impact-high { background: rgba(239,68,68,0.12); color: #fca5a5; border: 1px solid rgba(239,68,68,0.25); }
.tag.impact-med { background: rgba(245,158,11,0.12); color: #fbbf24; border: 1px solid rgba(245,158,11,0.25); }
.footer-note { text-align: center; font-size: 11px; color: #475569; font-family: 'JetBrains Mono', monospace; margin-top: 28px; padding: 16px; }
@media (max-width: 700px) { .culprit { grid-template-columns: 24px 1fr auto; } .culprit .bar { grid-column: 1 / -1; } .budget-row { grid-template-columns: 1fr; gap: 6px; } .budget-vals { text-align: left; } .font-grid { grid-template-columns: repeat(2, 1fr); } }
</style>
</head>
<body>
<div class="app">
  ${renderDiagnosticAuditContext(data, lang)}
  <div class="topbar">
    <div class="logo"><div class="logo-mark">C</div><span>ControlVitals</span></div>
    <div class="breadcrumb">
      <span>${r.unified ? (r.lang === 'cs' ? 'Komplexní CWV audit' : 'Complete CWV audit') : 'Audit'}</span><span class="sep">/</span>
      <span>${r.unified ? (r.lang === 'cs' ? `Core Web Vitals · hlavní problém: ${escapeHtml(metricHumanPhrase(r.metricUpper, true))}` : `Core Web Vitals · main problem: ${escapeHtml(metricHumanPhrase(r.metricUpper, false))}`) : escapeHtml(metricHumanPhrase(r.metricUpper, r.lang === 'cs'))}</span><span class="sep">/</span>
      <span class="current">${escapeHtml(r.domain)}</span>
    </div>
    <div class="breadcrumb" style="margin-left:auto;">${[
      r.device ? (r.lang === 'cs' ? `hlavní zařízení: ${r.device}` : `primary device: ${r.device}`) : null,
      r.deepCLSResults?.cmpState === 'disabled'
        ? (r.lang === 'cs' ? 'stav souhlasu se neuplatnil' : 'consent state not applicable')
        : (r.cmpMode ? formatConsentState(r.consentEvidence, r.lang, { prefix: false }) : null),
      r.monthYear
    ].filter(Boolean).map(escapeHtml).join(' &nbsp;·&nbsp; ')}</div>
  </div>

  <div class="audit-header">
    <div class="audit-title">
      <h1>${lang === 'cs' ? 'Hloubková analýza' : 'Deep analysis'} <span class="domain">${escapeHtml(r.domain)}</span></h1>
      <div class="meta">
        <span><span class="dot"></span>${r.scenario === 'deep-failed-priority'
          ? (lang === 'cs' ? 'NEKOMPLETNÍ — analýza selhala' : 'INCOMPLETE — analysis failed')
          : (lang === 'cs' ? 'Analýza dokončena' : 'Analysis complete')}</span>
        <span>${escapeHtml(r.dateNow)}</span>
        ${r.device ? `<span>${escapeHtml(r.device)}</span>` : ''}
      </div>
    </div>
  </div>

  ${renderPartialMeasurementNotice(r, { theme: 'dark' })}${renderMeasurementProvenanceNotice(r, { theme: 'dark' })}${renderInpScenarioFailureNotice(data, lang, 'dark')}

  ${renderExecSummaryMockup(r)}

  ${r.scenario === 'deep-failed-priority' ? `
  <div class="cmp-warning" style="border-color:rgba(229,72,77,0.6);">
    <div class="icon">!</div>
    <div class="content">
      <h4>${escapeHtml(r.deepFailurePresentation?.title || (lang === 'cs'
        ? `NEKOMPLETNÍ REPORT — hloubková analýza ${r.metricUpper} selhala`
        : `INCOMPLETE REPORT — ${r.metricUpper} deep analysis failed`))}</h4>
      <p><strong>${escapeHtml(r.deepFailurePresentation?.labels?.cause || (lang === 'cs' ? 'Pravděpodobná příčina' : 'Likely cause'))}:</strong>
        ${escapeHtml(r.deepFailurePresentation?.cause || '')}</p>
      <p><strong>${escapeHtml(r.deepFailurePresentation?.labels?.available || (lang === 'cs' ? 'Co je stále k dispozici' : 'Data still available'))}:</strong>
        ${escapeHtml(r.deepFailurePresentation?.available || '')}</p>
      <p><strong>${escapeHtml(r.deepFailurePresentation?.labels?.next || (lang === 'cs' ? 'Další krok' : 'Next step'))}:</strong>
        ${escapeHtml(r.deepFailurePresentation?.action || '')}</p>
    </div>
  </div>` : ''}

  <div class="verdict-grid">
    <div class="glass verdict-main">
      <div class="verdict-label">${lang === 'cs' ? 'Verdikt' : 'Verdict'}</div>
      <div class="headline">${escapeHtml(r.copy.subhead)}</div>
      <div class="sub">${escapeHtml(r.url || '')}</div>
      ${(r.deepAnalyzedUrl && normUrlLoose(r.deepAnalyzedUrl) !== normUrlLoose(r.url)) ? `<div class="sub" style="margin-top:4px;">${lang === 'cs'
        ? `Hlavní hloubková diagnostika ${escapeHtml(r.metricUpper)} běžela na nejhorší stránce ze sweepu: ${escapeHtml(r.deepAnalyzedUrl)}${r.entryDeep?.value != null ? ` (vstupní stránka ${formatMetricValue(r.metric, r.entryDeep.value) || '—'} — reference)` : ''}`
        : `The main ${escapeHtml(r.metricUpper)} deep diagnostics ran on the sweep's worst page: ${escapeHtml(r.deepAnalyzedUrl)}${r.entryDeep?.value != null ? ` (entry page ${formatMetricValue(r.metric, r.entryDeep.value) || '—'} — reference)` : ''}`}</div>` : ''}
    </div>
    ${verdictCardsHtml || `<div class="glass metric-card ${r.status}"><div class="label">${escapeHtml(r.metricUpper)}</div><div class="value">${escapeHtml(r.formattedMetricValue)}</div><div class="compare">${escapeHtml(r.statusLabel)}</div></div>`}
  </div>

  ${cmpBlock}${lcpReconciliationHtml ? `\n\n  ${lcpReconciliationHtml}` : ''}${labEvidenceHtml ? `\n\n  ${labEvidenceHtml}` : ''}

  <div class="glass scenario-card">
    <div class="scenario-eyebrow">${lang === 'cs' ? 'Hlavní zjištění' : 'Main finding'}</div>
    ${r.aiNarrative
      ? `<h2>${escapeHtml(r.aiNarrative.headline)}</h2>
         ${r.aiNarrative.paragraphs.map(p => `<p>${escapeHtml(p)}</p>`).join('')}`
      : `<h2>${r.copy.headline.map((part, i) => i === r.copy.headlineEm ? `<strong>${escapeHtml(part)}</strong>` : escapeHtml(part)).join(' ')}</h2>
         ${r.copy.body.map(p => `<p>${escapeHtml(p)}</p>`).join('')}`}
  </div>

  <div class="content-grid">
    <div>
      ${renderCulpritsDashboard(r)}
      ${renderClsFindingsMockup(r)}
      ${renderInpFindingsMockup(r)}${renderInpCandidateCardsMockup(r)}
      ${deepSectionAllowed(r, 'inp') ? renderInteractionDiagnosticsHtml(r.deepINPResults?.interactionDiagnostics, lang, 'dark') : ''}
      ${renderLcpFindingsMockup(r)}
      ${renderTtfbFindingsMockup(r)}
      ${renderSweepDeepMockup(r)}
      ${renderDeviceRunsMockup(r)}
      ${renderLongTasksDashboard(r)}
      ${renderServerTimingDashboard(r)}
      ${renderRenderBlockingDashboard(r)}
      ${renderMainThreadDashboard(r)}
      ${renderResourceTimingDashboard(r)}
      ${renderFontLoadingDashboard(r)}
      ${renderBudgetDashboard(r)}
    </div>
    <div>
      ${renderInsightsDashboard(r)}
    </div>
  </div>

  ${tasksList ? `
  <div class="glass recs-section">
    <h2>${lang === 'cs' ? 'Doporučení — seřazeno podle dopadu' : 'Recommendations — by impact'}</h2>
    <div class="rec-list">
      ${tasksList}
    </div>
  </div>` : ''}

  ${renderSavingsMockup(r.savingsRecommendations, r.savingsSummary, lang, r.metricLower, r.hasGtmContainer)}

  ${renderFollowUpMockup(lang, r.auditDate)}

  ${renderKnownGaps(r, { dark: true })}${inpScenariosHtml}${diagnosticEvidenceHtml ? `\n\n  ${diagnosticEvidenceHtml}` : ''}${contractHtml}

  <div class="glass panel" style="margin-top:16px">
    <div class="panel-header">
      <h2>${lang === 'cs' ? 'Diagnóza vs. implementace' : 'Diagnosis vs. implementation'}</h2>
    </div>
    <p style="font-size:13px;line-height:1.6;color:#94a3b8;margin:0;">${lang === 'cs'
      ? 'Audit ukazuje naměřené signály a možné příčiny k ověření; bez navazujícího důkazu nejde o potvrzené viníky ani hotové změny připravené k nasazení. Každé doporučení je nutné posoudit v kontextu konkrétní implementace a závislostí webu. Za správné provedení, otestování a ověření funkčnosti po nasazení odpovídá implementační tým. Doporučujeme nasazovat změny samostatně a před dalším krokem ověřit lab testem přínos i funkčnost webu.'
      : 'The audit shows measured signals and possible causes to verify; without further evidence these are not confirmed culprits or finished changes ready to deploy. Each recommendation must be assessed in the context of the specific implementation and the site\'s dependencies. Correct execution, testing and post-deployment verification of functionality are the responsibility of the implementing team. We recommend deploying changes one at a time and confirming both the improvement (lab re-test) and site functionality before proceeding.'}</p>
  </div>

  <div class="footer-note">
    ControlVitals — ${escapeHtml(r.metricUpper)} ${lang === 'cs' ? 'analýza' : 'analysis'} · ${escapeHtml(r.domain)} · ${escapeHtml(r.dateNow)}
  </div>
</div>
</body>
</html>`, lang), data.evidenceContract);
};

// =========================================================================
// SECTION RENDERERS — AUDIT (editorial paper style)
// =========================================================================
const renderCulpritsAudit = (r) => {
  if (!r.culprits.length) return '';
  return `
    <div class="culprits">
      ${r.culprits.map(c => `
        <div class="culprit">
          <span class="rank">${escapeHtml(c.rank)}</span>
          <span class="name">${escapeHtml(c.title)}<small>${escapeHtml(c.subtitle || '')}${c.url ? (c.subtitle ? ' · ' : '') + escapeHtml(shortenUrl(c.url, 80)) : ''}${c.count ? ' · ' + c.count + 'x' : ''}</small></span>
          <span class="ms">${c.ms != null ? fmtNum(c.ms) : '—'}<small> ${escapeHtml(c.unit || 'ms')}</small>${c.blockingMs != null ? `<small style="display:block;color:#b45309;">+${fmtNum(c.blockingMs)} ms ${r.lang === 'cs' ? 'blokuje' : 'block'}</small>` : ''}</span>
        </div>`).join('')}
    </div>`;
};

const renderLongTasksAudit = (r) => {
  const lt = r.rich?.longTasks;
  if (!lt?.items?.length) return '';
  const lang = r.lang;
  const items = lt.items.slice(0, 10);
  const hasInteractionWindow = hasValidInpMeasurement(r.deepINPResults)
    && Array.isArray(r.deepINPResults?.longTasksInWindow);
  const discrepancy = longTaskCountDiscrepancy(r.deepINPResults, lang);
  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Dlouhé úlohy' : 'Long tasks'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Co přesně ' : 'What exactly '}<em>${lang === 'cs' ? 'blokuje hlavní vlákno' : 'blocks the main thread'}</em>.</h2>${discrepancy ? `
    <p>${escapeHtml(discrepancy)}</p>` : ''}
    <p>${lang === 'cs'
      ? `Celkem ${lt.count} úloh nad 50 ms během načítání stránky (Lighthouse). Samostatná metrika TBT: ${fmtNum(lt.totalBlockingTime)} ms. TBT započítává pouze přesahy v okně Lighthouse mezi FCP a TTI; není součtem níže vypsaných přesahů za celý záznam ani dobou nefunkčních kliknutí.${hasInteractionWindow ? ' Hloubkový test interakce níže používá jiné měření; počty nelze přímo porovnat.' : ' Platné interakční okno v tomto reportu chybí; tyto úlohy proto nelze spojovat s konkrétní interakcí ani vydávat za příčinu field INP.'}`
      : `${lt.count} tasks over 50 ms during page load (Lighthouse). Separate TBT metric: ${fmtNum(lt.totalBlockingTime)} ms. TBT includes only excess time within Lighthouse's FCP–TTI window; it is not the sum of the listed excesses across the recording or time when clicks stopped working.${hasInteractionWindow ? ' The deep interaction test below uses a different measurement; counts cannot be compared directly.' : ' This report has no valid interaction window; these tasks therefore cannot be linked to a specific interaction or presented as the cause of field INP.'}`}</p>
    ${r.rich?.tbtRunSpread ? `<p>${lang === 'cs'
      ? 'Tento rozpad a podíly vlastníků patří jednomu výchozímu běhu Lighthouse, nikoli mediánu TBT ze srovnatelných běhů.'
      : 'This breakdown and owner shares belong to one seed Lighthouse run, not the TBT median across comparable runs.'}</p>` : ''}
    ${!r.rich?.tbtRunSpread && r.rich?.labStability?.longTaskCount && r.rich.labStability.longTaskCount.min !== r.rich.labStability.longTaskCount.max ? `
    <p style="font-family:var(--mono);font-size:11px;color:#888;">${lang === 'cs'
      ? `Stabilita: medián z ${r.rich.labStability.runs} běhů · počet úloh ${r.rich.labStability.longTaskCount.min}–${r.rich.labStability.longTaskCount.max} · TBT ${fmtNum(r.rich.labStability.tbt?.min)}–${fmtNum(r.rich.labStability.tbt?.max)} ms.`
      : `Stability: median of ${r.rich.labStability.runs} runs · task count ${r.rich.labStability.longTaskCount.min}–${r.rich.labStability.longTaskCount.max} · TBT ${fmtNum(r.rich.labStability.tbt?.min)}–${fmtNum(r.rich.labStability.tbt?.max)} ms.`}</p>` : ''}
    <div class="culprits">
      ${items.map((t, i) => `
        <div class="culprit">
          <span class="rank">${String(i + 1).padStart(2, '0')}</span>
          <span class="name">${escapeHtml(t.vendor || t.name || (lang === 'cs' ? 'Neurčeno' : 'Unattributed'))}<small>${escapeHtml(t.url ? shortenUrl(t.url, 80) : '')}</small></span>
          <span class="ms">${fmtNum(t.duration)}<small> ms</small><small style="display:block;color:#b45309;">+${fmtNum(t.blockingTime)} ms ${lang === 'cs' ? 'blokuje' : 'block'}</small></span>
        </div>`).join('')}
    </div>
  </section>`;
};

// INP attribution + 4-run comparison — rendered in the audit's paper
// aesthetic when the deep INP run / comparison data are available.
const renderInpCompareAudit = (r) => {
  const lang = r.lang;
  let html = deepSectionAllowed(r, 'inp')
    ? renderInteractionDiagnosticsHtml(r.deepINPResults?.interactionDiagnostics, lang, 'paper')
    : '';

  const attr = r.deepINPResults?.attribution;
  const attrInvalid = attr && (attr.attributionFailed || r.deepINPResults?.attributionValid === false);
  if (attrInvalid) {
    const reason = r.deepINPResults?.navigatedTo
      ? (lang === 'cs' ? `měřená interakce navigovala na jinou stránku (${escapeHtml(r.deepINPResults.navigatedTo)})` : `the measured interaction navigated to another page (${escapeHtml(r.deepINPResults.navigatedTo)})`)
      : r.deepINPResults?.attributionInvalidReason === 'dom-sanity'
      ? (lang === 'cs' ? 'měření zachytilo jiný dokument (podezřele malý DOM)' : 'the run captured a different document (suspiciously small DOM)')
      : (lang === 'cs' ? `příliš velká část blokujícího času (${Math.round((attr.undeterminedShare || 0) * 100)} %) nejde přiřadit konkrétnímu skriptu` : `too much blocking time (${Math.round((attr.undeterminedShare || 0) * 100)}%) cannot be attributed to a specific script`);
    html += `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Atribuce' : 'Attribution'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Atribuce ' : 'Attribution '}<em>${lang === 'cs' ? 'selhala' : 'failed'}</em>.</h2>
    <p>${lang === 'cs'
      ? `Rozdělení blokujícího času mezi první a třetí strany v tomto běhu nezobrazujeme — ${reason}. Interpretace by byla zavádějící; spusťte analýzu znovu.`
      : `The first-/third-party split of blocking time is not shown for this run — ${reason}. Interpreting it would be misleading; re-run the analysis.`}</p>
  </section>`;
  } else if (attr?.scriptBlockingMs > 0) {
    // Current bounded attribution may aggregate multiple captured interaction
    // windows and has no production linkage contract to the representative
    // interaction/source attempt. Keep every bounded payload diagnostic-only.
    const tpPct = Math.min(100, Math.round((attr.thirdPartyShare || 0) * 100));
    const deepDev = r.deepINPResults?.device;
    const deviceNote = lang === 'cs'
      ? ` Měřeno na emulovaném zařízení: ${deepDev === 'desktop' ? 'desktop' : deepDev === 'tablet' ? 'tablet' : 'mobil'}.`
      : ` Measured on an emulated device: ${deepDev === 'desktop' ? 'desktop' : deepDev === 'tablet' ? 'tablet' : 'mobile'}.`;
    html += `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Atribuce' : 'Attribution'}</div>
    <h2 class="section-title">${attr.windowBounded === false
      ? (lang === 'cs' ? 'Skripty během <em>celého běhu</em>.' : 'Scripts across the <em>whole run</em>.')
      : (lang === 'cs' ? 'Diagnostické <em>okno skriptů</em>.' : 'Diagnostic <em>script window</em>.')}</h2>
    <p>${attr.windowBounded === false
      ? (lang === 'cs'
        ? `Nepodařilo se zachytit interakci, čísla proto pokrývají <b>celý běh včetně načtení stránky</b>. Z celkových ${fmtNum(attr.scriptBlockingMs)} ms blokujícího času skriptů připadá ${fmtNum(attr.thirdPartyBlockingMs)} ms (${tpPct} %) na skripty třetích stran.`
        : `No interaction was captured, so the numbers cover <b>the whole run including page load</b>. Of ${fmtNum(attr.scriptBlockingMs)} ms of total script blocking time, ${fmtNum(attr.thirdPartyBlockingMs)} ms (${tpPct}%) comes from third-party scripts.`)
      : (lang === 'cs'
          ? `V diagnostických oknech bylo zaznamenáno ${fmtNum(attr.scriptBlockingMs)} ms práce skriptů. Data nejsou jednoznačně propojena s reprezentativní interakcí ani se zdrojovým pokusem rozpadu fází; podíl třetích stran a konkrétní skripty proto nelze vydávat za příčinu této interakce.`
          : `${fmtNum(attr.scriptBlockingMs)} ms of script work was recorded in diagnostic windows. The data is not explicitly linked to the representative interaction or the source attempt of the phase breakdown, so the third-party share and individual scripts cannot be presented as causes of that interaction.`)}
      ${(() => {
        if (attr.windowBounded !== false) return '';
        // The attribution numbers above come from the DEEP run — so the
        // blockers named here must come from the same deep run, never from
        // the Lighthouse table (a different measurement with different tasks
        // and totals). Claiming they "lead the same ranking" was false.
        const deepTasks = (r.deepINPResults?.longTasks || [])
          .slice().sort((a, b) => (b.duration || 0) - (a.duration || 0)).slice(0, 3);
        if (!deepTasks.length) return '';
        const nameOf = (lt) => {
          const src = lt.scripts?.[0]?.sourceURL;
          if (src) {
            try { const u = new URL(src); return `${u.hostname}${u.pathname.split('/').pop() ? '/' + u.pathname.split('/').pop() : ''}`; } catch { return src.slice(0, 60); }
          }
          return lt.vendor || lt.name || 'unknown';
        };
        const list = deepTasks.map(t => `<code>${escapeHtml(nameOf(t))}</code> (${fmtNum(Math.round(t.duration || 0))} ms)`).join(', ');
        // Header-bidding stack detection: two separate bidding wrappers on
        // one page (e.g. r2b2 + PubMatic pwt.js) is a consolidation
        // candidate worth naming explicitly in the client audit.
        const allDeep = r.deepINPResults?.longTasks || [];
        const biddingVendors = new Set();
        allDeep.forEach(t => {
          const s = `${t.scripts?.[0]?.sourceURL || ''} ${t.vendor || ''} ${t.name || ''}`.toLowerCase();
          if (/r2b2/.test(s)) biddingVendors.add('r2b2');
          if (/pubmatic|pwt\.js/.test(s)) biddingVendors.add('PubMatic (pwt.js)');
          if (/prebid/.test(s)) biddingVendors.add('Prebid');
          if (/amazon-adsystem|apstag/.test(s)) biddingVendors.add('Amazon TAM');
        });
        const biddingNote = biddingVendors.size >= 2
          ? (lang === 'cs'
            ? ` Stránka nese ${biddingVendors.size === 2 ? 'dvě' : String(biddingVendors.size)} header-bidding vrstvy (${[...biddingVendors].join(', ')}) — kandidát na konsolidaci.`
            : ` The page carries ${biddingVendors.size} header-bidding layers (${[...biddingVendors].join(', ')}) — a consolidation candidate.`)
          : '';
        return (lang === 'cs'
          ? ` Nejdelší úlohy tohoto hloubkového běhu: ${list} — jde o totéž měření jako čísla výše. Žebříček dlouhých úloh v jiné části reportu vychází z Lighthouse (celé načtení) a jeho pořadí se proto může lišit.`
          : ` Longest tasks of this deep run: ${list} — the same measurement as the numbers above. The long-tasks ranking elsewhere in the report comes from Lighthouse (full page load), so its order may differ.`) + biddingNote;
      })()}${deviceNote}</p>
    ${(attr.outOfScopeMs > 0 || attr.undeterminedMs > 0) ? `<p style="font-size: 0.85em; opacity: 0.7;">${lang === 'cs'
      ? `Mimo měřenou stránku (iframe apod.): ${fmtNum(attr.outOfScopeMs || 0)} ms · nepřiřazeno: ${fmtNum(attr.undeterminedMs || 0)} ms — do procent výše se nepočítá.`
      : `Outside the measured page (iframes etc.): ${fmtNum(attr.outOfScopeMs || 0)} ms · unattributed: ${fmtNum(attr.undeterminedMs || 0)} ms — excluded from the percentages above.`}</p>` : ''}
  </section>`;
  }

  const summary = r.inpCompareResults?.summary;
  if (Array.isArray(summary) && summary.length) {
    const cmpLabels = {
      A: { cs: 'Mobil + přijmout cookies', en: 'Mobile + accept cookies' },
      B: { cs: 'Mobil + odmítnout cookies', en: 'Mobile + reject cookies' },
      C: { cs: 'Desktop + odmítnout cookies', en: 'Desktop + reject cookies' },
      D: { cs: 'Mobil + banner ponechán', en: 'Mobile + banner untouched' }
    };
    const isValidRun = (s) => (s.valid != null ? !!s.valid : (!!s.success && (s.cmpMode === 'none' || !!s.cmpClicked)));
    const allValid = summary.every(isValidRun);
    html += `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Porovnání' : 'Comparison'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Čtyři konfigurace, ' : 'Four configurations, '}<em>${lang === 'cs' ? 'jedna stránka' : 'one page'}</em>.</h2>
    <p>${allValid
      ? (lang === 'cs'
        ? 'Rozdíl mezi konfiguracemi ukazuje vliv souhlasu i zařízení dohromady — nelze z něj izolovat jen marketingové skripty. Každá konfigurace je jeden běh (ne medián), rozptyl mezi běhy je velký a výsledek může vyjít oběma směry — jde proto o orientační porovnání, ne kontrolovaný experiment.'
        : 'The differences between configurations reflect consent and device combined — marketing scripts alone cannot be isolated from them. Each configuration is a single run (not a median), run-to-run variance is large and results can go either way — so this is an indicative comparison, not a controlled experiment.')
      : (lang === 'cs'
        ? 'Část běhů není validní (cookie lišta nebyla nalezena, nebo běh selhal) — konfigurace proto nelze mezi sebou porovnávat. Nevalidní běhy jsou z interpretace vyloučeny.'
        : 'Some runs are invalid (the cookie banner was not found, or the run failed) — the configurations therefore cannot be compared. Invalid runs are excluded from interpretation.')}</p>
    <div class="culprits">
      ${summary.map(s => {
        const valid = isValidRun(s);
        return `
        <div class="culprit"${valid ? '' : ' style="opacity:0.65;"'}>
          <span class="rank">${s.key}</span>
          <span class="name">${escapeHtml(cmpLabels[s.key]?.[lang] || `${s.device} / ${s.cmpMode}`)}<small>${!s.success
            ? escapeHtml(s.error || (lang === 'cs' ? 'běh selhal' : 'run failed'))
            : !valid
              ? (lang === 'cs' ? 'běh nevalidní — CMP nenalezeno' : 'invalid run — CMP not found')
              : `${s.longTasksCount} ${lang === 'cs' ? 'dlouhých úloh' : 'long tasks'}${s.thirdPartyShare != null ? ` · ${Math.round(s.thirdPartyShare * 100)} % ${lang === 'cs' ? '3. strany' : '3rd-party'}` : ''}`}</small></span>
          <span class="ms">${valid ? `${fmtNum(Math.round(s.inp || 0))}<small> ms</small>` : '—'}</span>
        </div>`;
      }).join('')}
    </div>
  </section>`;
  }

  return html;
};

// "Causes & Development Recommendations" — same content as the diagnostics
// export (deep-run causes with severity, fix list and bad/good code samples),
// rendered in the audit's paper aesthetic.
const renderCausesAudit = (r) => {
  const lang = r.lang;
  // Unified CWV report shows cause cards for every carried deep run; a
  // single-metric report stays scoped to its audited metric. The audited
  // metric's causes come first.
  const sets = [];
  const inpCauses = Array.isArray(r.deepINPResults?.causes)
    ? r.deepINPResults.causes.filter(Boolean).map(normalizeInpDiagnosticCause)
    : [];
  const lcpCauses = Array.isArray(r.deepLCPResults?.causes) ? r.deepLCPResults.causes.filter(Boolean) : [];
  if (inpCauses.length && deepSectionAllowed(r, 'inp')) sets.push({ metric: 'inp', causes: inpCauses, content: inpCauseContent });
  if (lcpCauses.length && deepSectionAllowed(r, 'lcp')) sets.push({ metric: 'lcp', causes: lcpCauses, content: lcpCauseContent });
  sets.sort((a, b) => (a.metric === r.metricLower ? -1 : 0) - (b.metric === r.metricLower ? -1 : 0));
  if (!sets.length) return '';
  const causes = sets.flatMap(s => s.causes.map(c => ({ ...c, __content: s.content, __metric: s.metric })));

  const sevClass = (s) => s === 'critical' || s === 'high' ? 'sev-high' : (s === 'medium' ? 'sev-med' : 'sev-low');
  const sevText = (s) => severityLabels[s]?.[lang] || severityLabels.low[lang];

  // Below-threshold guard: a measured value barely over its recommended
  // limit (e.g. DOM 1 516 vs 1 500 = +1 %) is information, not a finding —
  // it must not carry a high/critical badge. Overshoot under 10 % renders
  // as an info row without severity.
  const RISK_THRESHOLDS = { 'excessive-dom': 1500 };
  const isBelowThresholdInfo = (cause) => {
    const thr = RISK_THRESHOLDS[cause.cause];
    return thr != null && Number.isFinite(Number(cause.value)) && Number(cause.value) <= thr * 1.1;
  };
  // Order risk factors by measured magnitude (severity band first, larger
  // measured value first within a band); info-only rows go last.
  const sevRank = { critical: 0, high: 1, medium: 2, low: 3 };
  causes.sort((a, b) => {
    const ai = isBelowThresholdInfo(a) ? 1 : 0, bi = isBelowThresholdInfo(b) ? 1 : 0;
    if (ai !== bi) return ai - bi;
    const sr = (sevRank[a.severity] ?? 2) - (sevRank[b.severity] ?? 2);
    if (sr !== 0) return sr;
    return (Number(b.value) || 0) - (Number(a.value) || 0);
  });

  const cards = causes.map(cause => {
    if (isBelowThresholdInfo(cause)) {
      const content2 = cause.__content;
      const label2 = content2.labels[cause.cause]?.[lang] || cause.cause;
      const thr = RISK_THRESHOLDS[cause.cause];
      const overPct = Math.round((Number(cause.value) / thr - 1) * 100);
      return `
      <div class="cause sev-low">
        <div class="cause-head">
          <span class="tag sev-low">${lang === 'cs' ? 'Informace' : 'Info'}</span>
          <span class="cause-title">${escapeHtml(label2)}</span>
        </div>
        <p>${escapeHtml(lang === 'cs'
          ? `Naměřeno ${fmtNum(Number(cause.value))} proti doporučeným ${fmtNum(thr)} (překročení o ${overPct} %) — informativní údaj bez závažnosti, na hranici doporučené meze.`
          : `Measured ${fmtNum(Number(cause.value))} against the recommended ${fmtNum(thr)} (${overPct} % over) — informational, no severity; right at the recommended limit.`)}</p>
      </div>`;
    }
    const content = cause.__content;
    const key = cause.cause;
    const label = content.labels[key]?.[lang] || key || (lang === 'cs' ? 'Neznámá příčina' : 'Unknown cause');
    const inpDiagnostic = cause.__metric === 'inp';
    const desc = inpDiagnostic
      ? inpCauseDescription(cause, lang)
      : (content.descriptions[key]?.[lang] || '');
    const recs = inpDiagnostic
      ? inpCauseRecommendationsFor(cause, lang)
      : (content.recommendations[key]?.[lang] || []);
    const ex = content.examples[key];
    // Multi-page cap: when the site-wide crawl shows zero "poor" pages for
    // this metric, per-cause severity must not stay "critical" while the
    // headline says the metric is fine — same rule as the headline cap.
    const rawSev = inpDiagnostic ? inpDiagnosticSeverity(cause) : (cause.severity || 'medium');
    // Severity caps derive from PRIMARY-metric context (r.status, the
    // metric-selected multi-page crawl). In a unified report they must not
    // leak onto another metric's cause cards — a good CLS verdict says
    // nothing about INP causes.
    const primaryContext = cause.__metric === r.metricLower;
    // Same gate as the headline: a "critical" cause badge must not contradict
    // a good overall verdict for the metric ("rezervy, ne kritický problém").
    const verdictGood = primaryContext && r.status === 'good';
    // Zero "poor" pages site-wide must also cap the badge — otherwise the
    // report says "partial headroom, not a critical problem" a few sections
    // above a "high" badge for the same finding.
    const zeroPoorSiteWide = !!(primaryContext && r.multiPage && r.multiPage.matchesMetric
      && !r.multiPage.unavailable && r.multiPage.validPages > 0
      && r.multiPage.unmeasuredPages === 0 && r.multiPage.poorCount === 0);
    // "No poor pages" is a weak excuse when half the site sits in the
    // needs-improvement band — in that case a critical finding only softens
    // to "high", never to "medium".
    const halfSiteBorderline = !!(primaryContext && r.multiPage && r.multiPage.matchesMetric
      && r.multiPage.pages > 0 && (r.multiPage.warnCount || 0) >= r.multiPage.pages / 2);
    const capped = ((primaryContext && r.multiPageCapApplied) || verdictGood || zeroPoorSiteWide) && (rawSev === 'critical' || rawSev === 'high');
    const sev = !capped ? rawSev : (halfSiteBorderline ? 'high' : 'medium');
    const capNote = halfSiteBorderline
      ? (lang === 'cs' ? 'bez „špatných" stránek, ale většina webu v pásmu „k zlepšení" — ponecháno jako vysoká' : 'no "poor" pages, but most of the site sits in "needs improvement" — kept high')
      : (r.multiPageCapApplied || zeroPoorSiteWide)
        ? (lang === 'cs' ? 'zmírněno podle laboratorního sweepu — žádná stránka webu v pásmu „špatné"' : 'softened by the lab sweep — no page of the site in the "poor" band')
        : (lang === 'cs' ? 'zmírněno — celkový verdikt metriky je dobrý' : 'softened — overall metric verdict is good');

    const resources = Array.isArray(cause.resources) ? cause.resources.slice(0, 5) : [];
    const fonts = Array.isArray(cause.fonts) ? cause.fonts.slice(0, 5) : [];

    return `
      <div class="cause ${sevClass(sev)}">
        <div class="cause-head">
          <span class="tag ${sevClass(sev)}">${escapeHtml(sevText(sev))}</span>
          <span class="cause-title">${escapeHtml(label)}</span>
          ${capped ? `<span style="font-family:var(--mono);font-size:10px;color:#888;text-transform:none;letter-spacing:0;">${capNote}</span>` : ''}
        </div>
        ${desc ? `<p>${escapeHtml(desc)}</p>` : ''}
        ${key === 'long-tasks' && longTaskCountDiscrepancy(r.deepINPResults, lang)
          ? `<div class="cause-detail">${escapeHtml(longTaskCountDiscrepancy(r.deepINPResults, lang))}</div>`
          : key === 'long-tasks' && longTaskScopeDetail(cause, lang)
          ? `<div class="cause-detail">${escapeHtml(longTaskScopeDetail(cause, lang))}</div>`
          : (!inpDiagnostic && cause.description && cause.description !== desc ? `<div class="cause-detail">${escapeHtml(translateCauseDescription(cause.description, lang))}</div>` : '')}
        ${resources.length ? `<div class="cause-res">${resources.map(res => {
          const u = typeof res === 'string' ? res : (res?.url || JSON.stringify(res));
          return `<code>${escapeHtml(shortenUrl(u, 90))}</code>`;
        }).join('')}</div>` : ''}
        ${fonts.length ? `<div class="cause-res">${fonts.map(f => `<code>${escapeHtml(f.fontFamily || '')} (font-display: ${escapeHtml(f.fontDisplay || 'auto')})</code>`).join('')}</div>` : ''}
        ${recs.length ? `
        <div class="cause-recs">
          <div class="cause-recs-label">${lang === 'cs' ? 'Doporučení' : 'Fix'}</div>
          <ul>${recs.map(rec => `<li>${escapeHtml(rec)}</li>`).join('')}</ul>
        </div>` : ''}
        ${ex ? `
        <div class="code-pair">
          <div style="grid-column:1/-1;font-family:var(--mono);font-size:10px;color:#666;line-height:1.5;">${escapeHtml(inpDiagnostic ? inpGenericCodeExampleLabel[lang] : genericCodeExampleLabel[lang])}</div>
          <div class="code-block code-bad">
            <div class="code-cap">✗ ${inpDiagnostic ? (lang === 'cs' ? 'Obecný rizikový vzor' : 'Generic risk pattern') : (lang === 'cs' ? 'Problémový kód' : 'Problem code')}</div>
            <pre>${escapeHtml(ex.bad)}</pre>
          </div>
          <div class="code-block code-good">
            <div class="code-cap">✓ ${inpDiagnostic ? (lang === 'cs' ? 'Možná varianta po potvrzení profilem' : 'Possible variant after profile confirmation') : (lang === 'cs' ? 'Opravený kód' : 'Fixed code')}</div>
            <pre>${escapeHtml(ex.good)}</pre>
          </div>
        </div>` : ''}
      </div>`;
  }).join('');

  // Three explicit evidence levels (never present risk factors as causes):
  // 1. Confirmed cause — only when this run reproduced the problem AND names
  //    a concrete element/phase; otherwise say so explicitly.
  // 2. Measured risk factors — the cards; non-causal framing.
  // 3. Next diagnostic step — what to do to turn a risk factor into a cause.
  const cs2 = lang === 'cs';
  // This section deliberately carries risk factors from every available deep
  // layer. Its heading must therefore be metric-neutral; filtering the
  // contents as well would hide measured evidence.
  const sectionTitle = cs2 ? 'Co může výkon zhoršovat' : 'What can degrade performance';
  const unconfirmedMetrics = [];
  const confirmedBlocks = sets.map(s => {
    const mUp = s.metric.toUpperCase();
    if (mUp === 'INP') {
      const finding = deriveCanonicalFinding({ ...r, metric: 'INP', language: lang });
      unconfirmedMetrics.push(mUp);
      return `<p><strong>${cs2 ? 'Důkazy INP (příčina nepotvrzena):' : 'INP evidence (cause unconfirmed):'}</strong> ${escapeHtml(canonicalFindingSentences(finding).slice(0, 2).join(' '))}</p>`;
    }
    const labVal = s.metric === 'inp'
      ? (r.deepINPResults?.inp ?? r.deepINPResults?.worstInteraction?.duration)
      : r.deepLCPResults?.lcp;
    const reproduced = labVal != null && statusOf(s.metric, Number(labVal)) !== 'good';
    const culprit = reproduced && !r.evidenceContract
      ? culpritTextFor(mUp, s.metric === 'inp' ? r.deepINPResults : r.deepLCPResults, cs2)
      : null;
    // Three evidence states, never conflated: (1) reproduced WITH concrete
    // attribution = confirmed cause; (2) reproduced without attribution =
    // say so, do not claim non-reproduction; (3) lab in the good band (or
    // missing) = not reproduced / cannot be determined.
    const text = culprit
      ? culprit
      : reproduced
        ? (cs2
          ? `laboratorní běh problém reprodukoval (${formatMetricValue(s.metric, Number(labVal)) || '—'}), ale konkrétní prvek ani fázi neurčil — příčina zůstává nepotvrzená.`
          : `the lab run reproduced the problem (${formatMetricValue(s.metric, Number(labVal)) || '—'}) but identified no concrete element or phase — the cause remains unconfirmed.`)
        : labVal == null
          ? (cs2
            ? 'nelze určit — hloubkové laboratorní měření této metriky v tomto auditu chybí.'
            : 'cannot be determined — this audit has no deep lab measurement of this metric.')
          : (cs2
            ? 'v tomto běhu nebyla reprodukována — laboratorní měření problém nezachytilo, žádný konkrétní prvek ani fázi proto neurčuje.'
            : 'not reproduced in this run — the lab measurement did not capture the problem, so it names no concrete element or phase.');
    if (!culprit) unconfirmedMetrics.push(mUp);
    return `<p><strong>${r.evidenceContract
      ? (cs2 ? `Důkazy ${mUp} (příčina nepotvrzena):` : `${mUp} evidence (cause unconfirmed):`)
      : (cs2 ? `Potvrzená příčina (${mUp}):` : `Confirmed cause (${mUp}):`)}</strong> ${escapeHtml(text)}</p>`;
  }).join('');
  // The "next diagnostic step" exists to turn a risk factor into a cause —
  // it only makes sense for metrics WITHOUT a confirmed cause. Rendering it
  // when the lab already reproduced and attributed the problem tells the
  // reader to go measure something the report just measured.
  // Podmínky reprodukce jsou pro každou metriku jiné (TTFB = serverová
  // strana, ne throttling a reklama) — skládají se po metrikách z
  // metricCopy, nikdy jedním společným seznamem podmínek.
  const perMetricRepro = unconfirmedMetrics.map(mUp => {
    const conds = metricCopy(mUp, 'reproduceConditions', cs2 ? 'cs' : 'en');
    return cs2
      ? `${mUp}: reprodukovat problém${conds ? ` ${conds}` : ''} a zaznamenat konkrétní prvek či fázi.`
      : `${mUp}: reproduce the problem${conds ? ` ${conds}` : ''} and record the concrete element or phase.`;
  }).join(' ');
  const nextStep = unconfirmedMetrics.length === 0 ? '' : cs2
    ? `${unconfirmedMetrics.length < sets.length ? `Platí pro metriky bez potvrzené příčiny (${unconfirmedMetrics.join(', ')}): ` : ''}${perMetricRepro} Implementační úkol vzniká až z potvrzeného profilu — postup je rozepsán v zadání pro vývoj.`
    : `${unconfirmedMetrics.length < sets.length ? `Applies to metrics without a confirmed cause (${unconfirmedMetrics.join(', ')}): ` : ''}${perMetricRepro} An implementation ticket follows only from a confirmed profile — the procedure is detailed in the development brief.`;
  return `
  <section>
    <div class="section-label">${cs2 ? 'Diagnostika' : 'Diagnostics'}</div>
    <h2 class="section-title">${escapeHtml(sectionTitle)}<em>.</em></h2>
    ${confirmedBlocks}
    <p><strong>${cs2 ? 'Naměřené rizikové faktory:' : 'Measured risk factors:'}</strong> ${cs2
      ? 'položky níže jsou naměřené hodnoty, které mohou zhoršovat načítání nebo odezvu — bez reprodukce z nich kauzalitu k field hodnotě odvodit nelze.'
      : 'the items below are measured values that may degrade loading or responsiveness — without reproduction, no causality to the field value can be derived from them.'}</p>
    ${cards}
    ${nextStep ? `<p><strong>${cs2 ? 'Další diagnostický krok:' : 'Next diagnostic step:'}</strong> ${escapeHtml(nextStep)}</p>` : ''}
  </section>`;
};

const renderServerTimingAudit = (r) => {
  const st = r.rich.serverTiming;
  if (!st) return '';
  const lang = r.lang;
  const phases = [
    { key: 'DNS', val: st.dns },
    { key: 'TCP', val: st.tcp },
    { key: 'TLS', val: st.tls },
    { key: 'TTFB', val: st.ttfb },
    { key: lang === 'cs' ? 'Stáhnutí' : 'Download', val: st.download }
  ].filter(p => p.val > 0);

  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Server Timing' : 'Server Timing'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Jak dlouho ' : 'How long '}<em>${lang === 'cs' ? 'odpovídá server' : 'the server takes'}</em>.</h2>
    <p>${lang === 'cs'
      ? `Celkem ${st.total} ms od zahájení požadavku po stažení dokumentu. ${st.isFast ? 'Backend je v pořádku.' : st.isSlow ? `TTFB nad ${metricThresholds.ttfb.good} ms — server je úzkým hrdlem.` : 'Backend není kritickým problémem, ale dá se zlepšit.'}`
      : `Total ${st.total} ms from request start to document download. ${st.isFast ? 'Backend is fine.' : st.isSlow ? `TTFB above ${metricThresholds.ttfb.good} ms — server is a bottleneck.` : 'Backend is not critical but can be improved.'}`}</p>
    <div class="timing-row">
      ${phases.map(p => `
        <div class="timing-cell">
          <div class="timing-cell-key">${escapeHtml(p.key)}</div>
          <div class="timing-cell-val">${p.val} <small>ms</small></div>
        </div>`).join('')}
    </div>
    ${st.phasesAvailable === false ? `<p style="font-size:11px;color:#64748b;margin-top:8px">${lang === 'cs'
      ? 'Fáze DNS / TCP / TLS nejsou z Lighthouse dostupné — hodnotí se doba odpovědi serveru (TTFB, jen serverová část; field TTFB z CrUX bývá vyšší).'
      : 'DNS / TCP / TLS phases are not available from Lighthouse — server response time (TTFB, server portion only; field TTFB from CrUX tends to be higher) is evaluated.'}</p>` : ''}
  </section>`;
};

const renderMainThreadAudit = (r) => {
  const mtw = r.rich.mainThreadWork;
  if (!mtw || !mtw.categories.length) return '';
  const lang = r.lang;
  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Hlavní vlákno' : 'Main thread'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Co dělá ' : 'What '}<em>${lang === 'cs' ? 'prohlížeč' : 'the browser does'}</em>.</h2>
    <p>${lang === 'cs'
      ? `Při načítání stránky stráví prohlížeč ${(mtw.totalMs / 1000).toFixed(2).replace('.', ',')} s aktivní prací. Rozložení práce ukazuje, kam jde čas.`
      : `During page load the browser spends ${(mtw.totalMs / 1000).toFixed(2)} s actively working. The breakdown shows where the time goes.`}</p>
    <div class="mt-bar-print">
      ${mtw.categories.map(c => `<div class="mt-seg-print" style="width:${c.percentage}%;background:${c.color}"></div>`).join('')}
    </div>
    <div class="mt-legend-print">
      ${mtw.categories.slice(0, 6).map(c => `
        <div class="mt-leg-print">
          <span class="dot" style="background:${c.color}"></span>
          <span>${escapeHtml(c.label)}</span>
          <b>${c.percentage}%</b>
        </div>`).join('')}
    </div>
  </section>`;
};

const renderBudgetAudit = (r) => {
  const pb = r.rich.performanceBudget;
  if (!pb || !pb.items.length) return '';
  const lang = r.lang;
  return `
  <section>
    <div class="section-label">Performance budget</div>
    <h2 class="section-title">${lang === 'cs' ? 'Kde se ' : 'Where you '}<em>${lang === 'cs' ? 'vejdete, kde ne' : 'fit and don\'t'}</em>.</h2>
    <p>${lang === 'cs'
      ? `Performance budget je interní doporučený benchmark ControlVitals — strop, kolik dat by stránka měla maximálně mít, aby se rychle načetla i na průměrném mobilu s 4G. Nejde o oficiální limit Google Core Web Vitals. ${escapeHtml(r.domain)} překračuje tento doporučený rozpočet v ${pb.overBudgetCount} z ${pb.items.length} kategorií.`
      : `The performance budget is ControlVitals' internal recommended benchmark — a cap on how much data a page should have to load fast on an average 4G mobile. It is not an official Google Core Web Vitals limit. ${escapeHtml(r.domain)} exceeds this recommended budget in ${pb.overBudgetCount} of ${pb.items.length} categories.`}${
      // Field load metrics green → over-budget weight is lab headroom, not an
      // acute problem. Same framing as the unused-JS line, so the 295 %
      // images bar doesn't read as an emergency next to a green field LCP.
      r.fieldLoadStatus === 'good' && pb.overBudgetCount > 0
        ? (lang === 'cs'
          ? ' Field metriky načítání (LCP/TTFB) jsou přitom zelené — překročení je rezerva k úklidu, ne akutní problém.'
          : ' Field load metrics (LCP/TTFB) are green, though — the overage is headroom to clean up, not an acute problem.')
        : ''}</p>
    <div class="budget">
      ${pb.items.map(b => {
        const pct = Math.min(150, b.percentage || 0);
        const fillPct = Math.min(100, pct);
        return `
        <div class="budget-row-print">
          <div class="cat">${escapeHtml(b.label)}</div>
          <div class="budget-bar-print">
            <div class="fill" style="width:${fillPct}%;background:${b.overBudget ? 'var(--accent)' : 'var(--ok)'}"></div>
            ${b.budget > 0 ? `<div class="limit" style="left:${Math.min(100, (b.budget / Math.max(b.actual, b.budget)) * 100)}%"></div>` : ''}
          </div>
          <div class="values">
            <b style="color:${b.overBudget ? 'var(--accent)' : 'var(--ok)'}">${fmtKb(b.actual) || '—'}</b> / ${fmtKb(b.budget) || '—'}
            <span style="color:#888">· ${b.percentage}%</span>
          </div>
        </div>`;
      }).join('')}
    </div>
    ${pb.unusedJsBytes > 0 || pb.unusedCssBytes > 0 ? `
    <p style="margin-top:16px;font-family:var(--mono);font-size:12px;color:#666">
      ${lang === 'cs' ? 'Nevyužité' : 'Unused'}:
      ${pb.unusedJsBytes > 0 ? `<b style="color:var(--accent)">${fmtKb(pb.unusedJsBytes)}</b> JS` : ''}
      ${pb.unusedJsBytes > 0 && pb.unusedCssBytes > 0 ? ' · ' : ''}
      ${pb.unusedCssBytes > 0 ? `<b style="color:var(--accent)">${fmtKb(pb.unusedCssBytes)}</b> CSS` : ''}
    </p>` : ''}
  </section>`;
};

const renderResourceTimingAudit = (r) => {
  const rt = r.rich.resourceTiming;
  if (!rt || !rt.byType.length) return '';
  const lang = r.lang;
  const tp = rt.thirdParty || { count: 0, totalBytes: 0, totalTimeMs: 0 };
  // Measurement-context guardrail (see extractResourceTiming): subset
  // phrasing ("z toho X") and share % are allowed ONLY against the
  // compatible network-requests total. Incompatible/unverifiable context →
  // values shown separately, comparison suppressed. Impossible values within
  // the same context ('error') → comparison block suppressed entirely.
  const cmp = rt.tpComparison || 'incompatible';
  const compatible = cmp === 'compatible';
  const nt = rt.networkTotals;
  const sharePct = compatible && nt && nt.bytes > 0
    ? Math.min(100, Math.round((tp.totalBytes / nt.bytes) * 100))
    : null;
  const introCs = compatible && nt
    ? `Prohlížeč stáhl ${nt.count} požadavků o velikosti ${fmtKb(nt.bytes) || '—'} (síťový záznam měřeného běhu). ${tp.count > 0 ? `Požadavky na jiné hostname: ${tp.count} z ${nt.count}${sharePct !== null ? ` (${sharePct} % objemu)` : ''}. Pozor na interpretaci: „jiný hostname" může zahrnovat i vlastní infrastrukturu webu (např. CDN s aplikačními soubory). Bez doloženého vlastnictví domény report nerozlišuje externího dodavatele od vlastní CDN.` : ''}`
    : `Prohlížeč stáhl ${rt.totalRequests} požadavků o velikosti ${fmtKb(rt.totalBytes) || '—'} (souhrn Lighthouse podle typu).${cmp === 'incompatible' && tp.count > 0 ? ` Samostatné měření síťového záznamu napočítalo ${tp.count} požadavků na cizí domény — jde o jinou metodiku počítání, čísla proto nelze přímo porovnávat.` : ''}`;
  const introEn = compatible && nt
    ? `The browser fetched ${nt.count} requests totaling ${fmtKb(nt.bytes) || '—'} (network log of the measured run). ${tp.count > 0 ? `Requests to other hostnames: ${tp.count} of ${nt.count}${sharePct !== null ? ` (${sharePct}% of bytes)` : ''}. Interpretation note: "other hostname" may include the site's own infrastructure (e.g. a CDN serving application bundles). Without documented domain ownership the report does not distinguish an external vendor from the site's own CDN.` : ''}`
    : `The browser fetched ${rt.totalRequests} requests totaling ${fmtKb(rt.totalBytes) || '—'} (Lighthouse per-type summary).${cmp === 'incompatible' && tp.count > 0 ? ` A separate network-log measurement counted ${tp.count} third-party requests — a different counting methodology, so the numbers are not directly comparable.` : ''}`;
  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Zdroje stránky' : 'Page resources'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Z čeho se ' : 'What the '}<em>${lang === 'cs' ? 'stránka skládá' : 'page is made of'}</em>.</h2>
    <p>${lang === 'cs' ? introCs : introEn}</p>
    <div class="rt-table">
      ${rt.byType.slice(0, 8).map(t => {
        // The 'third-party' row comes from Lighthouse Resource Summary — a
        // DIFFERENT classification than the cross-hostname count above. Label
        // it explicitly so the two numbers are never read as one metric.
        const isTpRow = String(t.type).toLowerCase() === 'third-party';
        const label = isTpRow
          ? (lang === 'cs' ? 'THIRD-PARTY (LIGHTHOUSE RESOURCE SUMMARY)' : 'THIRD-PARTY (LIGHTHOUSE RESOURCE SUMMARY)')
          : escapeHtml(String(t.type).toUpperCase());
        return `
        <div class="rt-row-print">
          <span class="rt-type-print">${label}</span>
          <span class="rt-count-print">${t.count} <small>${lang === 'cs' ? 'požadavků' : 'requests'}</small></span>
          <span class="rt-bytes-print">${fmtKb(t.bytes) || '—'}</span>
        </div>`;
      }).join('')}
    </div>
    ${rt.byType.some(t => String(t.type).toLowerCase() === 'third-party') ? `
    <p style="margin-top:8px;font-family:var(--mono);font-size:11px;color:#666">${lang === 'cs'
      ? 'Third-party dle Lighthouse Resource Summary používá jinou klasifikaci než počet požadavků na jiné hostname výše; hodnoty nejsou určeny k přímému porovnání.'
      : 'Third-party per Lighthouse Resource Summary uses a different classification than the other-hostname request count above; the values are not meant for direct comparison.'}</p>` : ''}
    ${tp.count > 0 && cmp !== 'error' ? `
    <div class="rt-third-print">
      <div class="rt-third-label">${lang === 'cs' ? 'Požadavky na jiné hostname (síťový záznam)' : 'Requests to other hostnames (network log)'}</div>
      <div class="rt-third-grid">
        <div><b>${tp.count}</b><small>${lang === 'cs' ? 'požadavků' : 'requests'}</small></div>
        <div><b>${fmtKb(tp.totalBytes) || '—'}</b><small>${lang === 'cs' ? 'dat' : 'data'}</small></div>
        <div><b>${fmtMs(tp.totalTimeMs) || '—'}</b><small>${lang === 'cs' ? 'síťového času (přenos, ne blokování CPU)' : 'network time (transfer, not CPU blocking)'}</small></div>
        ${sharePct !== null ? `<div><b>${sharePct}%</b><small>${lang === 'cs' ? 'objemu' : 'of bytes'}</small></div>` : ''}
      </div>
      ${cmp === 'incompatible' ? `<p style="margin-top:8px;font-family:var(--mono);font-size:11px;color:#666">${lang === 'cs'
        ? 'Měřeno ze síťového záznamu (jiná metodika než souhrn podle typu výše) — hodnoty nelze přímo porovnávat s celkovým počtem požadavků.'
        : 'Measured from the network log (a different methodology than the per-type summary above) — values are not directly comparable with the total request count.'}</p>` : ''}
    </div>` : ''}
    ${tp.count > 0 && cmp === 'error' ? `
    <p style="margin-top:12px;font-family:var(--mono);font-size:11px;color:#a66">${lang === 'cs'
      ? 'Interní kontrola dat: hodnoty třetích stran neprošly validací (podmnožina větší než celek ve stejném měřicím kontextu) — porovnání bylo potlačeno.'
      : 'Internal data check: third-party values failed validation (subset larger than total within the same measurement context) — the comparison was suppressed.'}</p>` : ''}
  </section>`;
};

const renderRenderBlockingAudit = (r) => {
  const rb = r.rich.renderBlocking;
  if (!rb || !rb.count) return '';
  const lang = r.lang;
  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Render blocking' : 'Render blocking'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Co ' : 'What '}<em>${lang === 'cs' ? 'blokuje vykreslení' : 'blocks rendering'}</em>.</h2>
    <p>${lang === 'cs'
      ? `Než prohlížeč začne kreslit první pixely, musí stáhnout a zpracovat ${rb.count} kritických zdrojů. Dohromady přidávají ${fmtMs(rb.totalBlockingMs) || '—'} čekání.`
      : `Before the browser can paint the first pixel, it must download and process ${rb.count} critical resources, adding ${fmtMs(rb.totalBlockingMs) || '—'} of waiting.`}</p>
    <div class="rb-table">
      ${rb.items.map(it => `
        <div class="rb-row-print">
          <span class="rb-type-print">${escapeHtml(String(it.type).toUpperCase())}</span>
          <span class="rb-name-print">${escapeHtml(it.name)}</span>
          <span class="rb-bytes-print">${fmtKb(it.bytes) || '—'}</span>
          <span class="rb-ms-print"><b>+${it.wastedMs}</b> <small>ms</small></span>
        </div>`).join('')}
    </div>
  </section>`;
};

const renderFontLoadingAudit = (r) => {
  const fl = r.rich.fontLoading;
  if (!fl || !fl.totalFonts) return '';
  const lang = r.lang;
  // Render whenever font data exists — Dashboard does the same. The Audit
  // surface should never silently drop a section that the Dashboard shows
  // for the same input, even when there are no FOIT/FOUT/swap issues.
  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Načítání fontů' : 'Font loading'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Fonty ' : 'Fonts '}<em>${lang === 'cs' ? 'a prázdný text' : 'and invisible text'}</em>.</h2>
    <p>${fl.withoutSwapCount > 0
      ? (lang === 'cs'
        ? `Stránka načítá ${fl.totalFonts} vlastních fontů. ${fl.withoutSwapCount} z nich nemá nastaveno font-display: swap, což znamená, že text je do okamžiku načtení fontu neviditelný.`
        : `The page loads ${fl.totalFonts} custom fonts. ${fl.withoutSwapCount} of them have no font-display: swap, meaning text is invisible until the font loads.`)
      : (lang === 'cs'
        ? `Stránka načítá ${fl.totalFonts} vlastních fontů a všechny mají nastaveno font-display: swap — text je viditelný i během načítání fontů. Zde není co opravovat.`
        : `The page loads ${fl.totalFonts} custom fonts and all of them use font-display: swap — text stays visible while fonts load. Nothing to fix here.`)}</p>
  </section>`;
};

// =========================================================================
// AUDIT STYLE — Editorial paper
// =========================================================================
// ---------------------------------------------------------------------------
// "Co řešit první a proč" — device-priority ranking (CrUX × device share)
// priority = device_share × distance_from_threshold × influenceability
// ---------------------------------------------------------------------------
// Which metrics have a deep layer available in the report architecture, and
// whether this particular report actually carries that metric's deep data.
// Adding a new metric's deep layer = add its key here + its section renderers;
// the unified report picks it up without structural changes.
const depthStatusForMetric = (r, metric) => {
  const m = String(metric || '').toLowerCase();
  if ((r.failedDeepMetrics || []).includes(m.toUpperCase())) return 'failed';
  const carried = {
    inp: !!(r.deepINPResults && (r.deepINPResults.inp != null || r.deepINPResults.success)),
    cls: buildClsFindings(r).length > 0,
    lcp: !!(r.deepLCPResults && (r.deepLCPResults.lcp != null || r.deepLCPResults.success)),
    ttfb: !!(r.deepTTFBResults && (r.deepTTFBResults.ttfb != null || r.deepTTFBResults.success))
  };
  if (m in carried) return carried[m] ? 'included' : 'pending';
  // fcp: deep layer not built yet — permanent "pending" slot for now.
  return 'pending';
};

const renderDevicePriorityAudit = (r) => {
  const dp = r.devicePriority;
  if (!dp || !dp.items?.length) return '';
  const lang = r.lang;
  const actionable = dp.items.filter(i => i.score > 0);
  let items = (actionable.length ? actionable : dp.items).slice(0, 6);
  // The ranking must cover loading as well as responsiveness: if every
  // actionable item is INP, append the highest-scoring non-INP item (LCP/CLS/
  // TTFB) so the reader sees where the other metrics stand — even when they
  // are within threshold (score 0 = "in the good band, keep watching").
  if (items.length && items.every(i => i.metric === 'inp')) {
    const bestNonInp = dp.items.filter(i => i.metric !== 'inp')
      .sort((a, b) => b.score - a.score)[0];
    if (bestNonInp) items = [...items.slice(0, 5), bestNonInp];
  }
  const allGood = actionable.length === 0;

  const shareNote = dp.shareSource === 'ga4'
    ? (lang === 'cs'
      ? `Podíl zařízení pochází z Google Analytics 4 (${fmtNum(dp.ga4?.totalSessions) || '—'} návštěv za posledních 28 dní).`
      : `Device share comes from Google Analytics 4 (${fmtNum(dp.ga4?.totalSessions) || '—'} sessions over the last 28 days).`)
    : dp.shareSource === 'crux'
      ? (lang === 'cs'
        ? 'GA4 není připojeno — podíl zařízení vychází z reálných dat Chrome UX Reportu (form-factor fractions).'
        : 'GA4 is not connected — device share is based on real Chrome UX Report data (form-factor fractions).')
      : (lang === 'cs'
        ? 'Podíl zařízení nebyl k dispozici — použito rovnoměrné rozdělení (orientační).'
        : 'Device share was unavailable — an equal split was used (indicative only).');

  const rows = items.map((it, idx) => {
    const metricUp = it.metric.toUpperCase();
    const p75Str = it.metric === 'cls' ? Number(it.p75).toFixed(3) : (fmtMs(it.p75) || '—');
    const thrStr = it.metric === 'cls' ? Number(it.threshold).toFixed(2) : (fmtMs(it.threshold) || '—');
    const inflNote = it.influenceabilityBasis === 'measured-attribution'
      ? (lang === 'cs' ? 'změřená atribuce skriptů' : 'measured script attribution')
      : (lang === 'cs' ? 'odhad' : 'estimate');
    // Depth slot per metric: the unified CWV report says explicitly whether
    // this metric's deep layer is included in this report, or is a prepared
    // slot that fills in once that metric's deep layer exists.
    const depth = r.unified && it.score > 0 ? depthStatusForMetric(r, it.metric) : null;
    const depthChip = depth === 'included'
      ? `<span style="font-family:var(--mono);font-size:10px;color:var(--ok);border:1px solid var(--ok);border-radius:2px;padding:1px 6px;margin-left:8px;">${lang === 'cs' ? 'hloubka v tomto reportu ↓' : 'depth in this report ↓'}</span>`
      : depth === 'failed'
        ? `<span style="font-family:var(--mono);font-size:10px;color:#b91c1c;border:1px solid #b91c1c;border-radius:2px;padding:1px 6px;margin-left:8px;">${lang === 'cs' ? 'hloubkový test selhal' : 'deep test failed'}</span>`
      : depth === 'pending'
        ? `<span style="font-family:var(--mono);font-size:10px;color:#888;border:1px solid #aaa;border-radius:2px;padding:1px 6px;margin-left:8px;">${lang === 'cs' ? 'hloubková vrstva se připravuje — zatím základní čísla' : 'deep layer in preparation — basic numbers for now'}</span>`
        : '';
    return `
      <div class="culprit">
        <div class="rank">${String(idx + 1).padStart(2, '0')}</div>
        <div class="name">${metricUp} · ${escapeHtml(deviceLabel(it.device, lang))}${depthChip}
          <small>${lang === 'cs'
            ? `field p75 (CrUX) ${p75Str} (práh ${thrStr}) · podíl uživatelů ${fmtNum(it.deviceShare, 1)} % · odstup od prahu ${fmtNum(it.distanceFromThreshold, 2)}× · ovlivnitelnost ${fmtNum(it.influenceability * 100)} % (${inflNote})`
            : `field p75 (CrUX) ${p75Str} (threshold ${thrStr}) · user share ${fmtNum(it.deviceShare, 1)} % · distance from threshold ${fmtNum(it.distanceFromThreshold, 2)}× · influenceability ${fmtNum(it.influenceability * 100)} % (${inflNote})`}</small>
        </div>
        <div class="ms">${fmtNum(it.score, 1)} <small>${lang === 'cs' ? 'priorita' : 'priority'}</small></div>
      </div>`;
  }).join('');

  return `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Priorita podle zařízení' : 'Priority by device'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Co řešit ' : 'What to fix '}<em>${lang === 'cs' ? 'první a proč' : 'first and why'}</em>.</h2>
    <p class="lead">${allGood
      ? (lang === 'cs'
        ? 'Podle reálných dat CrUX jsou všechny sledované metriky (LCP, INP, CLS, TTFB) na všech zařízeních v pásmu „dobré". Není co hasit — pořadí níže je jen informativní.'
        : 'According to real CrUX data, all tracked metrics (LCP, INP, CLS, TTFB) are in the "good" band on every device. Nothing is on fire — the list below is informational only.')
      : (lang === 'cs'
        ? 'Pořadí není dojem — je to výpočet: podíl uživatelů na daném zařízení × jak daleko je metrika za prahem „dobré" × jak moc ji máte ve vlastních rukou (first-party kód váží víc než skripty třetích stran).'
        : 'The order is not an opinion — it is a calculation: share of users on that device × how far the metric is past the "good" threshold × how much of it is in your own hands (first-party code weighs more than third-party scripts).')}</p>
    <div class="culprits">
      ${rows}
    </div>
    <div class="callout">
      <strong>${lang === 'cs' ? 'Jak se počítá' : 'How it is calculated'}</strong>
      ${lang === 'cs'
        ? 'priorita = podíl uživatelů × odstup od prahu × ovlivnitelnost. '
        : 'priority = user share × distance from threshold × influenceability. '}
      ${escapeHtml(shareNote)}
      ${dp.cruxFormFactorFractions ? (lang === 'cs'
        ? ` Rozložení návštěv dle CrUX: mobil ${fmtNum(dp.cruxFormFactorFractions.phone, 1)} %, desktop ${fmtNum(dp.cruxFormFactorFractions.desktop, 1)} %, tablet ${fmtNum(dp.cruxFormFactorFractions.tablet, 1)} %.`
        : ` CrUX visit split: mobile ${fmtNum(dp.cruxFormFactorFractions.phone, 1)} %, desktop ${fmtNum(dp.cruxFormFactorFractions.desktop, 1)} %, tablet ${fmtNum(dp.cruxFormFactorFractions.tablet, 1)} %.`) : ''}
    </div>
  </section>`;
};

const renderMeasurementProvenanceNotice = (r, { theme = 'paper' } = {}) => {
  const cs = r.lang === 'cs';
  const dark = theme === 'dark';
  const provenance = normalizeReportMeasurementProvenance(r.measurementProvenance);
  const { collector, verification } = provenance;
  const verified = verification.status === 'verified';
  const place = [
    collector.region !== 'unknown' ? collector.region.toUpperCase() : null,
    collector.country,
    collector.zone
  ].filter(Boolean).join(' / ');
  const identity = `${collector.provider}/${collector.id}`;
  const statusText = verified
    ? (cs
        ? `Poloha collectoru je ověřena${place ? `: ${place}` : ''}.`
        : `The collector location is verified${place ? `: ${place}` : ''}.`)
    : (cs
        ? 'Poloha collectoru je neověřená. Tento běh nelze označit jako měření z EU ani z USA.'
        : 'The collector location is unverified. This run cannot be labelled as an EU or US measurement.');
  const pathText = cs
    ? 'Laboratorní TTFB a LCP proto nemusí odpovídat síťové cestě uživatele v Česku.'
    : 'Lab TTFB and LCP may therefore not represent the network path of a user in Czechia.';
  const authorityText = cs
    ? 'Field p75 z CrUX zůstává autoritativním podkladem pro verdikt.'
    : 'CrUX field p75 remains authoritative for the verdict.';
  const cruxText = cs
    ? 'Geografie CrUX popisuje populaci field dat; není důkazem geografie laboratorního collectoru.'
    : 'CrUX geography describes the field-data population; it is not evidence of the lab collector’s geography.';
  const style = dark
    ? 'class="glass panel" style="margin:0 0 24px;padding:18px 22px;border-color:rgba(245,158,11,.45);background:rgba(120,53,15,.12);"'
    : 'class="callout" style="margin:18px 0 32px;border-color:#a16207;background:rgba(161,98,7,.06);"';

  return `
  <aside ${style}>
    <strong>${cs ? 'PROVENIENCE LABORATORNÍHO MĚŘENÍ' : 'LAB MEASUREMENT PROVENANCE'}</strong>
    <p style="margin:8px 0;line-height:1.55;">${escapeHtml(statusText)} ${escapeHtml(pathText)}</p>
    <p style="margin:8px 0;line-height:1.55;">${escapeHtml(authorityText)} ${escapeHtml(cruxText)}</p>
    <p style="margin:8px 0 0;line-height:1.45;font-family:var(--mono, monospace);font-size:11px;opacity:.75;">Collector: ${escapeHtml(identity)} · ${escapeHtml(verification.status)}</p>
  </aside>`;
};

const renderPartialMeasurementNotice = (r, { theme = 'paper' } = {}) => {
  const partialMetrics = ['INP', 'LCP', 'CLS', 'TTFB'].filter(metric =>
    r[`deep${metric}Results`]?.labRunSpread?.evidenceStatus === 'partial');
  if (!partialMetrics.length) return '';
  const cs = r.lang === 'cs';
  const style = theme === 'dark'
    ? 'class="glass panel" style="margin:0 0 24px;padding:18px 22px;border-color:rgba(245,158,11,.7);background:rgba(120,53,15,.2);"'
    : 'class="callout" style="margin:18px 0 32px;border-color:#a16207;background:rgba(161,98,7,.1);"';
  return `<aside ${style} role="note">
    <strong>⚠ ${cs ? 'ČÁSTEČNÉ MĚŘENÍ' : 'PARTIAL MEASUREMENT'} — ${escapeHtml(partialMetrics.join(', '))}</strong>
    <p>${cs
      ? 'Laboratorní důkazy pro uvedené metriky jsou částečné / neověřené. Hodnoty berte jako orientační a před rozhodnutím měření opakujte za srovnatelných podmínek.'
      : 'Lab evidence for these metrics is partial / unverified. Treat the values as indicative and repeat measurements under comparable conditions before deciding.'}</p>
  </aside>`;
};

const renderLcpFieldReconciliation = (r, { theme = 'paper' } = {}) => {
  if (r.metricLower !== 'lcp' || !r.deepLCPResults) return '';
  const evidence = r.lcpFieldEvidence;
  const spread = r.deepLCPResults?.labRunSpread || null;
  if (!evidence && !spread) return '';
  const cs = r.lang === 'cs';
  const dark = theme === 'dark';
  const analyzedUrl = evidence?.analyzedPage?.requestedUrl || r.deepAnalyzedUrl || r.url;
  const pageField = evidence?.analyzedPage || null;
  const authoritative = evidence?.authoritative || {
    scope: 'origin',
    target: null,
    p75: r.fieldValue
  };
  const pageFieldAvailable = pageField?.available === true && Number.isFinite(Number(pageField.p75));
  const fieldReference = pageFieldAvailable ? Number(pageField.p75) : Number(authoritative.p75);
  const labMedian = spread
    ? labSeriesDisplayValue(spread, r.deepLCPResults?.lcp ?? null)
    : Number(r.deepLCPResults?.lcp);
  const fieldStatus = Number.isFinite(fieldReference) ? statusOf('LCP', fieldReference) : null;
  const labStatus = Number.isFinite(labMedian) ? statusOf('LCP', labMedian) : null;
  const ratio = Number.isFinite(fieldReference) && fieldReference > 0 && Number.isFinite(labMedian)
    ? Math.max(fieldReference, labMedian) / Math.min(fieldReference, labMedian)
    : null;
  const diverges = !!(fieldStatus && labStatus && (fieldStatus !== labStatus || ratio >= 1.5));
  const sourceKeys = Array.isArray(spread?.uniqueLcpSourceKeys) ? spread.uniqueLcpSourceKeys : [];
  const thirdPartyHosts = Array.isArray(spread?.thirdPartyHosts) ? spread.thirdPartyHosts : [];
  const attribution = spread?.variabilityAttribution || 'unresolved';
  const sourceDetail = attribution === 'observed-lcp-source-change'
    ? (cs
      ? `Pozorování: LCP kandidát se mezi běhy měnil (${sourceKeys.slice(0, 3).join(', ')}). To je slučitelné s proměnlivým obsahem nebo stavem načtení, ale samo o sobě nedokazuje příčinu rozptylu.`
      : `Observation: the LCP candidate changed between runs (${sourceKeys.slice(0, 3).join(', ')}). This is consistent with changing content or load state, but does not by itself prove the cause of the spread.`)
    : attribution === 'observed-third-party-set-change'
      ? (cs
        ? `Pozorování: mezi běhy se měnila zachycená sada externích blokujících zdrojů (${thirdPartyHosts.slice(0, 4).join(', ')}). To je slučitelné s proměnlivým third-party načítáním, ale neprokazuje, že způsobilo rozptyl LCP.`
        : `Observation: the captured external blocking-resource set changed between runs (${thirdPartyHosts.slice(0, 4).join(', ')}). This is consistent with varying third-party loading, but does not prove it caused the LCP spread.`)
      : attribution === 'controlled-run-residual' && labSeriesIsVerified(spread)
        ? (cs
          ? 'Zachycený LCP zdroj a evidované měřicí podmínky zůstaly stejné. Data neprokazují změnu obsahu ani third-party sady; zbývající rozptyl proto označujeme jen jako variabilitu mezi kontrolovanými laboratorními běhy, ne jako reprezentativní realitu uživatelů.'
          : 'The captured LCP source and recorded measurement conditions stayed the same. The data does not establish changing content or third parties; the residual spread is therefore labelled controlled lab run-to-run variance, not representative user reality.')
        : (cs
          ? 'Zachycená evidence nestačí k rozlišení proměnlivého obsahu, third-party vlivu a zbytkového laboratorního rozptylu. Report proto žádnou z těchto příčin netvrdí.'
          : 'The captured evidence is insufficient to distinguish changing content, third-party influence, and residual lab variance. The report therefore does not claim any one of them.');
  const rangeText = spread && spread.runs
    ? `${formatMetricValue('lcp', spread.min)}–${formatMetricValue('lcp', spread.max)}, ${cs ? 'medián' : 'median'} ${formatMetricValue('lcp', labMedian)} (${spread.runs} ${cs ? 'validních běhů' : 'valid runs'})`
    : formatMetricValue('lcp', labMedian);
  const phaseSummaryText = spread?.phaseSummary
    ? ['ttfb', 'resourceLoad', 'renderDelay'].map(key => {
        const summary = spread.phaseSummary[key];
        return summary
          ? `${key} ${formatMetricValue('lcp', summary.min)}–${formatMetricValue('lcp', summary.max)} (${cs ? 'medián' : 'median'} ${formatMetricValue('lcp', summary.median)})`
          : `${key} —`;
      }).join(' · ')
    : null;
  const pageFieldText = pageFieldAvailable
    ? (cs
      ? `CrUX má pro tutéž URL page-level LCP p75 ${formatMetricValue('lcp', pageField.p75)} (${escapeHtml(analyzedUrl)}).`
      : `CrUX has page-level LCP p75 ${formatMetricValue('lcp', pageField.p75)} for the same URL (${escapeHtml(analyzedUrl)}).`)
    : pageField?.error
      ? (cs
        ? `Dostupnost page-level CrUX pro tutéž URL se nepodařilo ověřit; nejde o potvrzení, že stránková data neexistují.`
        : `Same-URL page-level CrUX availability could not be verified; this does not establish that page data is absent.`)
      : (cs
        ? `CrUX pro tutéž URL nemá dostatečná page-level data (${escapeHtml(analyzedUrl)}); srovnání proto nesmí tuto origin hodnotu vydávat za data této stránky.`
        : `CrUX has insufficient page-level data for the same URL (${escapeHtml(analyzedUrl)}); the comparison must not present the origin value as data for this page.`);
  const entryPage = evidence?.entryPage || null;
  const entryDiffers = entryPage?.requestedUrl
    && normUrlLoose(entryPage.requestedUrl) !== normUrlLoose(analyzedUrl);
  const entryPageText = entryDiffers
    ? (entryPage.available === true && Number.isFinite(Number(entryPage.p75))
      ? (cs
        ? ` Vstupní URL má vlastní page-level p75 ${formatMetricValue('lcp', entryPage.p75)} (${escapeHtml(entryPage.requestedUrl)}).`
        : ` The entry URL has its own page-level p75 ${formatMetricValue('lcp', entryPage.p75)} (${escapeHtml(entryPage.requestedUrl)}).`)
      : (cs
        ? ` Pro vstupní URL nejsou page-level data potvrzena (${escapeHtml(entryPage.requestedUrl)}).`
        : ` Page-level data is not confirmed for the entry URL (${escapeHtml(entryPage.requestedUrl)}).`))
    : '';
  const authorityText = Number.isFinite(Number(authoritative.p75))
    ? (cs
      ? `Autoritativní verdikt reportu zůstává field p75 ${formatMetricValue('lcp', authoritative.p75)} pro origin ${escapeHtml(authoritative.target || '')} a zařízení ${escapeHtml(evidence?.device || pageField?.formFactor || '')}; laboratorní série verdikt nemění.`
      : `The report verdict remains authoritative field p75 ${formatMetricValue('lcp', authoritative.p75)} for origin ${escapeHtml(authoritative.target || '')} and device ${escapeHtml(evidence?.device || pageField?.formFactor || '')}; the lab series does not change it.`)
    : (cs
      ? 'Laboratorní série sama field verdikt nenahrazuje.'
      : 'The lab series does not replace the field verdict.');
  const divergenceText = diverges
    ? (cs
      ? 'Výrazná lab–field odchylka je očekávatelná: cold laboratorní běh používá pevný profil, jeden testovací bod a prázdnou cache, zatímco field p75 shrnuje skutečné návštěvy, zařízení, sítě a stav cache v okně CrUX.'
      : 'A large lab–field divergence can occur because the cold lab run uses a fixed profile, one test location, and an empty cache, while field p75 aggregates real visits, devices, networks, and cache states over the CrUX window.')
    : (cs
      ? 'Cold laboratorní série a field p75 mají odlišné podmínky a účel, i když nyní neleží v odlišném výkonnostním pásmu.'
      : 'The cold lab series and field p75 have different conditions and purposes even when they currently fall in the same performance band.');
  const style = dark
    ? 'class="glass panel" style="margin:24px 0;padding:24px;border-color:rgba(245,158,11,.35);"'
    : 'class="callout" style="border-color:#a16207;background:rgba(161,98,7,.06);"';
  return `
    <section ${style}>
      <strong>${cs ? 'LCP: FIELD DATA A COLD LAB SÉRIE' : 'LCP: FIELD DATA AND COLD LAB SERIES'}</strong>
      <p style="margin:8px 0;line-height:1.6;"><b>${cs ? 'Cold lab série' : 'Cold lab series'}:</b> ${escapeHtml(rangeText || '—')} · ${escapeHtml(analyzedUrl || '')}</p>
      ${phaseSummaryText ? `<p style="margin:8px 0;line-height:1.6;"><b>${cs ? 'Fáze série' : 'Series phases'}:</b> ${escapeHtml(phaseSummaryText)}</p>` : ''}
      <p style="margin:8px 0;line-height:1.6;">${pageFieldText}${entryPageText} ${authorityText}</p>
      <p style="margin:8px 0;line-height:1.6;">${divergenceText}</p>
      <p style="margin:8px 0 0;line-height:1.6;">${escapeHtml(sourceDetail)}</p>
    </section>`;
};

const renderLabRunEvidenceAudit = (r, { theme = 'paper' } = {}) => {
  const cs = r.lang === 'cs';
  const dark = theme === 'dark';
  const colors = dark
    ? {
        cardBorder: 'rgba(6,182,212,.2)',
        cardBackground: 'rgba(15,23,42,.72)',
        text: '#e2e8f0',
        muted: '#94a3b8',
        rule: '#334155',
        rowRule: '#1e293b',
        point: '#e2e8f0',
        pointBorder: '#020617'
      }
    : {
        cardBorder: '#d8d5ce',
        cardBackground: 'var(--paper-2)',
        text: '#222',
        muted: '#666',
        rule: '#b8b3a9',
        rowRule: '#e3e0da',
        point: '#222',
        pointBorder: '#fff'
      };
  const conditionBitsOf = (input = {}) => {
    const conditions = input || {};
    return [
    conditions.url || null,
    conditions.device || null,
    conditions.cmpMode || null,
    conditions.networkProfile || null,
    conditions.protocol ? `protocol ${conditions.protocol}` : null,
    conditions.captureMethod ? `capture ${conditions.captureMethod}` : null,
    conditions.lighthouseVersion ? `Lighthouse ${conditions.lighthouseVersion}` : null,
    conditions.throttlingMethod ? `throttling ${conditions.throttlingMethod}` : null,
    conditions.throttling ? JSON.stringify(conditions.throttling) : null,
    conditions.screenEmulation ? `screen ${JSON.stringify(conditions.screenEmulation)}` : null,
    (conditions.collectorRegion || conditions.collectorVerification)
      ? `collector ${conditions.collectorRegion || 'unknown'} (${conditions.collectorVerification || 'unverified'})`
      : null
    ].filter(Boolean);
  };
  const formatStartedAt = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime())
      ? parsed.toISOString().replace('T', ' ').slice(0, 19) + 'Z'
      : String(value);
  };
  const resultEntries = [
    ['LCP', r.deepLCPResults, r.deepLCPResults?.lcp],
    ['INP', r.deepINPResults, hasValidInpMeasurement(r.deepINPResults) ? r.deepINPResults.inp : null],
    ['TTFB', r.deepTTFBResults, r.deepTTFBResults?.ttfb],
    ['CLS', r.deepCLSResults, r.deepCLSResults?.cls]
  ];
  const tbt = r.rich?.longTasks?.totalBlockingTime;
  if (r.rich?.tbtRunSpread) {
    resultEntries.push(['TBT', { labRunSpread: r.rich.tbtRunSpread }, tbt]);
  } else if (Number.isFinite(tbt) && tbt >= 0) {
    resultEntries.push(['TBT', {
      labRunSpread: {
        metric: 'TBT',
        runs: 1,
        validRuns: 1,
        attemptedRuns: 1,
        failedRuns: 0,
        displayValue: Math.round(tbt),
        basis: 'single',
        verified: false,
        evidenceStatus: 'partial',
        stability: 'stable',
        seriesId: 'psi-tbt-pagespeed-lab',
        representativeRunId: 'psi-tbt-1',
        representativeValue: Math.round(tbt),
        conditions: { url: r.url, device: r.device, networkProfile: 'pagespeed-lab' },
        conditionsConsistent: true,
        attempts: [{
          id: 'psi-tbt-1',
          seriesId: 'psi-tbt-pagespeed-lab',
          index: 1,
          status: 'ok',
          value: Math.round(tbt),
          outlier: false,
          startedAt: r.auditDate || null,
          conditions: { url: r.url, device: r.device, networkProfile: 'pagespeed-lab' }
        }]
      }
    }, tbt]);
  }

  const entries = resultEntries.map(([metric, result, fallback]) => {
    if (metric === 'INP' && result && !hasValidInpMeasurement(result)) {
      return { metric, result, spread: null, attempts: [], unavailable: true };
    }
    if (!result || (metric !== 'TBT' && (fallback == null || !Number.isFinite(Number(fallback))))) return null;
    const spread = result.labRunSpread || {
      metric,
      runs: 1,
      validRuns: 1,
      attemptedRuns: 1,
      failedRuns: 0,
      displayValue: Number(fallback),
      basis: 'single',
      verified: false,
      evidenceStatus: 'partial',
      seriesId: `${metric.toLowerCase()}-legacy-series`,
      representativeRunId: `${metric.toLowerCase()}-run-1`,
      representativeValue: Number(fallback),
      conditionsConsistent: true,
      attempts: [{
        id: `${metric.toLowerCase()}-run-1`,
        seriesId: `${metric.toLowerCase()}-legacy-series`,
        index: 1,
        status: 'ok',
        value: Number(fallback),
        outlier: false,
        conditions: {}
      }]
    };
    const attempts = Array.isArray(spread.attempts) && spread.attempts.length
      ? spread.attempts
      : (spread.values || []).map((value, index) => ({
        id: `${metric.toLowerCase()}-run-${index + 1}`,
        index: index + 1,
        status: 'ok',
        value,
        outlier: Array.isArray(spread.outlierIds) && spread.outlierIds.includes(`${metric.toLowerCase()}-run-${index + 1}`)
      }));
    return { metric, result, spread, attempts };
  }).filter(Boolean);
  if (!entries.length) return '';

  const main = entries.find(entry => entry.metric.toLowerCase() === r.metricLower);
  const mainPartial = !!(main && (main.unavailable || !labSeriesIsVerified(main.spread)));
  const cards = entries.map(({ metric, spread, attempts, unavailable }) => {
    if (unavailable) {
      return `
      <div data-lab-metric-unavailable="INP" style="border:1px solid ${colors.cardBorder};padding:16px 18px;margin-top:14px;background:${colors.cardBackground};color:${colors.text};${dark ? 'border-radius:12px;' : ''}">
        <div style="font-family:${dark ? "'JetBrains Mono',monospace" : 'var(--mono)'};font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:${colors.muted};">INP · ${cs ? 'nedostupné' : 'unavailable'}</div>
        <div style="font-family:${dark ? "'Inter',sans-serif" : 'var(--serif)'};font-size:24px;font-weight:700;margin-top:4px;">${cs ? 'Nezměřeno' : 'Not measured'}</div>
        <div style="font-size:11px;color:${colors.muted};margin-top:8px;">${cs
          ? 'Laboratorní běh nezachytil platnou interakci. Nula není hodnota INP a tento běh se neporovnává s field p75.'
          : 'The lab run captured no valid interaction. Zero is not an INP value and this run is not compared with field p75.'}</div>
      </div>`;
    }
    const verified = labSeriesIsVerified(spread);
    const threshold = metricThresholds[metric.toLowerCase()]?.good ?? null;
    const numeric = attempts.filter(run => run.status === 'ok' && Number.isFinite(Number(run.value)));
    const axisMax = Math.max(
      Number(spread.max || 0),
      Number(threshold || 0),
      ...numeric.map(run => Number(run.value)),
      1
    ) * 1.1;
    const pos = (value) => Math.max(0, Math.min(100, (Number(value) / axisMax) * 100));
    const statusText = verified
      ? (cs ? 'ověřené časy série' : 'verified series timings')
      : (cs ? 'částečný / neověřený' : 'partial / unverified');
    const badgeText = verified
      ? (spread.stability === 'high'
          ? (cs ? 'vysoký rozptyl' : 'high variance')
          : spread.stability === 'moderate'
            ? (cs ? 'mírný rozptyl' : 'moderate variance')
            : (cs ? 'stabilní' : 'stable'))
      : `${cs ? 'orientační' : 'indicative'} · n=${Number(spread.runs || 0)}`;
    const badgeColor = verified
      ? (spread.stability === 'high' ? '#ef4444' : spread.stability === 'moderate' ? '#f59e0b' : '#22c55e')
      : colors.muted;
    const failed = Number(spread.failedRuns || 0) + Number(spread.partialRuns || 0);
    const conditions = spread.conditions || {};
    const conditionBits = conditionBitsOf({
      url: conditions.url || spread.url || null,
      device: conditions.device || spread.device || null,
      cmpMode: conditions.effectiveCmpMode || null,
      networkProfile: conditions.networkProfile || null,
      protocol: conditions.protocol || null,
      captureMethod: conditions.captureMethod || null
    });
    const mixedAttemptCount = attempts.filter(attempt => attempt?.conditionMismatch === true).length;
    const mixedCount = Math.max(
      Array.isArray(spread.mixedConditionAttemptIds) ? spread.mixedConditionAttemptIds.length : 0,
      mixedAttemptCount,
      Array.isArray(spread.conditionGroups) && spread.conditionGroups.length > 1
        ? spread.conditionGroups.length - 1
        : 0
    );
    const hasBreakdown = attempts.some(run => run?.breakdown && Object.values(run.breakdown).some(value => value != null))
      || !!spread.phaseSummary;
    const sourceText = metric === 'TBT'
      ? (tbtSeriesMedian(spread) != null
        ? (cs
          ? `Medián používá jen srovnatelné ${spread.conditions?.networkProfile === 'controlled-lighthouse' ? 'lokální Lighthouse' : 'PSI'} běhy. Dlouhé úlohy a jejich vlastníci níže pocházejí z jednoho výchozího běhu, nikoli z mediánu. TBT není INP.`
          : `The median uses comparable ${spread.conditions?.networkProfile === 'controlled-lighthouse' ? 'local Lighthouse' : 'PSI'} runs only. Long tasks and their owners below come from one seed run, not the median. TBT is not INP.`)
        : (cs
          ? 'Srovnatelná opakovaná měření TBT nejsou k dispozici. Případné dlouhé úlohy a jejich vlastníci patří pouze jednomu výchozímu běhu Lighthouse. TBT není INP.'
          : 'Comparable repeated TBT measurements are unavailable. Any long tasks and their owners belong only to one seed Lighthouse run. TBT is not INP.'))
        + (cs
          ? ' Pro TBT rozpad fází pro tuto sérii není k dispozici; úlohy při načítání nejsou fázemi interakce.'
          : ' For TBT, no phase breakdown is available for this series; load-time tasks are not interaction phases.')
        + (spread.budgetExhausted ? (cs ? ' Časový limit série vyčerpán.' : ' Series deadline exhausted.') : '')
      : spread.representativeRunId
      ? (hasBreakdown
        ? (cs
          ? `Rozpad fází vychází ze skutečného běhu ${spread.representativeRunId}, který je nejblíže středu série.`
          : `The phase breakdown comes from actual run ${spread.representativeRunId}, closest to the centre of the series.`)
        : (cs
          ? `Zobrazená hodnota vychází z běhu ${spread.representativeRunId}; rozpad fází pro tuto sérii není k dispozici.`
          : `The displayed value comes from run ${spread.representativeRunId}; no phase breakdown is available for this series.`))
      : (hasBreakdown
        ? (cs ? 'Zdroj rozpadu fází není v této sérii identifikovatelný.' : 'The phase-breakdown source cannot be identified in this series.')
        : (cs ? 'Tato série neobsahuje rozpad fází.' : 'This series contains no phase breakdown.'));
    return `
      <div style="border:1px solid ${colors.cardBorder};padding:16px 18px;margin-top:14px;background:${colors.cardBackground};color:${colors.text};${dark ? 'border-radius:12px;' : ''}">
        <div style="display:flex;justify-content:space-between;gap:16px;align-items:flex-start;flex-wrap:wrap;">
          <div>
            <div style="font-family:${dark ? "'JetBrains Mono',monospace" : 'var(--mono)'};font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:${colors.muted};">${escapeHtml(metric)} · ${escapeHtml(statusText)}</div>
            <div style="font-family:${dark ? "'Inter',sans-serif" : 'var(--serif)'};font-size:24px;font-weight:700;margin-top:4px;">${escapeHtml(labSeriesSummaryText(spread, metric, r.lang))}</div>
            <div style="font-family:${dark ? "'JetBrains Mono',monospace" : 'var(--mono)'};font-size:9px;color:${colors.muted};margin-top:3px;">series: ${escapeHtml(spread.seriesId || 'legacy-unknown')}</div>
          </div>
          <div data-lab-series-badge="${verified ? 'verified' : 'indicative'}" style="font-family:${dark ? "'JetBrains Mono',monospace" : 'var(--mono)'};font-size:11px;text-align:right;color:${badgeColor};">
            ${escapeHtml(badgeText)}${verified && spread.spreadPercent != null ? ` · ${escapeHtml(String(spread.spreadPercent))} %` : ''}
            ${failed ? `<br>${cs ? 'nezdařené/částečné pokusy' : 'failed/partial attempts'}: ${failed}` : ''}
          </div>
        </div>
        ${mixedCount ? `<div style="margin-top:12px;padding:8px 10px;border:1px solid #b91c1c;background:rgba(185,28,28,.1);font-size:11px;color:${dark ? '#fca5a5' : '#991b1b'};"><strong>${cs ? 'NESROVNATELNÉ POKUSY VYLOUČENY' : 'INCOMPARABLE ATTEMPTS EXCLUDED'}</strong> · ${mixedCount}. ${cs
          ? 'Jejich hodnoty nejsou součástí rozdělení ani zobrazované hodnoty; série zůstává neověřená.'
          : 'Their values are not part of the distribution or display value; the series remains unverified.'}</div>` : ''}
        ${numeric.length > 1 ? `<div data-lab-distribution="series" style="position:relative;height:34px;margin:16px 0 8px;border-bottom:2px solid ${colors.rule};">
          ${threshold != null ? `<span title="${cs ? 'Cíl' : 'Target'} ${escapeHtml(formatMetricValue(metric, threshold) || '')}" style="position:absolute;left:${pos(threshold)}%;top:2px;height:30px;border-left:2px dashed #166534;"></span>` : ''}
          ${numeric.map(run => `<span title="${escapeHtml(run.id)} · ${escapeHtml(formatMetricValue(metric, run.value) || '—')}" style="position:absolute;left:${pos(run.value)}%;top:${run.outlier ? '1px' : '9px'};width:${run.outlier ? '14px' : '10px'};height:${run.outlier ? '14px' : '10px'};border-radius:50%;transform:translateX(-50%);background:${run.outlier ? '#b91c1c' : run.id === spread.representativeRunId ? '#2563eb' : colors.point};border:${run.outlier ? '2px solid #7f1d1d' : `1px solid ${colors.pointBorder}`};"></span>`).join('')}
        </div>` : ''}
        <div style="font-family:${dark ? "'JetBrains Mono',monospace" : 'var(--mono)'};font-size:10px;color:${colors.muted};">
          ${conditionBits.length ? `${cs ? 'Zaznamenané podmínky' : 'Recorded conditions'}: ${escapeHtml(conditionBits.join(' · '))}. ` : ''}
          ${metric === 'TBT' ? escapeHtml(cs
            ? 'Lighthouse / PSI: laboratorní načítání, nikoli INP. Srovnání používá zaznamenanou verzi, zařízení, URL, throttling a emulaci obrazovky. '
            : 'Lighthouse / PSI: lab page load, not INP. Comparability uses recorded version, device, URL, throttling and screen emulation. ') : !conditions.networkProfile || !conditions.protocol || !conditions.captureMethod || conditions.effectiveCmpMode == null || conditions.cpuThrottling == null
            ? escapeHtml(cs
              ? 'Časy mohou být validní; profil sítě, protokol, CPU throttling, způsob zachycení nebo skutečný stav souhlasu nejsou plně známé. Shodu podmínek nelze potvrdit. '
              : 'Timings may be valid; network profile, protocol, CPU throttling, capture method or effective consent state are not fully known. Comparable conditions cannot be confirmed. ')
            : ''}
          ${escapeHtml(sourceText)}
          ${spread.budgetExhausted && !verified ? ` ${escapeHtml(cs
            ? 'Časový rozpočet auditu skončil před pátým validním během; report pokračuje jako částečný, ne jako chyba.'
            : 'The audit time budget ended before the fifth valid run; the report continues as partial rather than failing.')}` : ''}
        </div>
        <details style="margin-top:12px;">
          <summary style="cursor:pointer;font-family:${dark ? "'JetBrains Mono',monospace" : 'var(--mono)'};font-size:11px;font-weight:700;">${cs ? 'Zobrazit všechny pokusy' : 'Show all attempts'} (${attempts.length})</summary>
          <div style="overflow-x:auto;">
          <table style="width:100%;border-collapse:collapse;margin-top:10px;font-family:${dark ? "'JetBrains Mono',monospace" : 'var(--mono)'};font-size:10px;">
            <thead><tr>
              <th style="text-align:left;border-bottom:1px solid #bbb;padding:6px;">ID</th>
              <th style="text-align:left;border-bottom:1px solid #bbb;padding:6px;">${cs ? 'Čas' : 'Started'}</th>
              <th style="text-align:right;border-bottom:1px solid #bbb;padding:6px;">${cs ? 'Hodnota' : 'Value'}</th>
              <th style="text-align:right;border-bottom:1px solid #bbb;padding:6px;">${cs ? 'Odchylka' : 'Delta'}</th>
              <th style="text-align:left;border-bottom:1px solid #bbb;padding:6px;">${cs ? 'Stav' : 'Status'}</th>
              <th style="text-align:left;border-bottom:1px solid #bbb;padding:6px;">${cs ? 'Podmínky pokusu' : 'Attempt conditions'}</th>
              <th style="text-align:left;border-bottom:1px solid #bbb;padding:6px;">${cs ? 'Rozpad' : 'Breakdown'}</th>
            </tr></thead>
            <tbody>${attempts.map(run => `
              <tr>
                <td style="padding:6px;border-bottom:1px solid ${colors.rowRule};">${escapeHtml(run.id || String(run.index || '—'))}${run.id === spread.representativeRunId ? ` · ${hasBreakdown ? (cs ? 'zdroj rozpadu' : 'breakdown source') : (cs ? 'reprezentativní běh' : 'representative run')}` : ''}</td>
                <td style="padding:6px;border-bottom:1px solid ${colors.rowRule};white-space:nowrap;">${escapeHtml(formatStartedAt(run.startedAt))}</td>
                <td style="padding:6px;text-align:right;border-bottom:1px solid ${colors.rowRule};">${run.value != null ? escapeHtml(formatMetricValue(metric, run.value) || '—') : '—'}</td>
                <td style="padding:6px;text-align:right;border-bottom:1px solid ${colors.rowRule};">${run.deltaPercent != null ? `${run.deltaPercent > 0 ? '+' : ''}${escapeHtml(String(run.deltaPercent))} %` : '—'}</td>
                <td style="padding:6px;border-bottom:1px solid ${colors.rowRule};color:${run.conditionMismatch || run.outlier ? '#ef4444' : colors.text};">${escapeHtml(run.status || 'unknown')}${run.outlier ? ` · outlier (${escapeHtml(spread.outlierMethod || 'IQR')})` : ''}${run.conditionMismatch ? ` · ${cs ? 'vyloučeno: jiné' : 'excluded: different'} ${escapeHtml((run.differingConditions || []).join(', '))}` : ''}${run.error ? ` · ${escapeHtml(run.error)}` : ''}</td>
                <td style="padding:6px;border-bottom:1px solid ${colors.rowRule};min-width:220px;">${escapeHtml(conditionBitsOf(run.conditions).join(' · ') || '—')}${run.exclusionReason ? ` · ${escapeHtml(run.exclusionReason)}` : ''}</td>
                <td style="padding:6px;border-bottom:1px solid ${colors.rowRule};">${[
                  run.breakdown
                    ? Object.entries(run.breakdown).filter(([, value]) => value != null).map(([key, value]) => `${key} ${formatMetricValue(metric === 'CLS' ? 'cls' : 'inp', value) || value}`).join(' · ')
                    : null,
                  run.lcpSource?.key ? `LCP source ${run.lcpSource.key}` : null,
                  run.pageSignals?.resourceCount != null ? `resources ${run.pageSignals.resourceCount}` : null,
                  Array.isArray(run.thirdPartyHosts) && run.thirdPartyHosts.length ? `3P ${run.thirdPartyHosts.join(', ')}` : null
                ].filter(Boolean).map(escapeHtml).join('<br>') || '—'}</td>
              </tr>`).join('')}</tbody>
          </table>
          </div>
        </details>
      </div>`;
  }).join('');

  return `
    <section${dark ? ' class="glass panel" style="margin:24px 0;padding:24px;"' : ''}>
      <div${dark ? ' style="font-family:JetBrains Mono,monospace;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#22d3ee;margin-bottom:8px;"' : ' class="section-label"'}>${cs ? 'Poctivost měření' : 'Measurement evidence'}</div>
      <h2${dark ? ' style="font-size:22px;color:#f8fafc;margin:0 0 8px;"' : ' class="section-title"'}>${cs ? 'Jednotlivé běhy a ' : 'Individual runs and '}<em>${cs ? 'jejich rozptyl' : 'their variance'}</em>.</h2>
      <p${dark ? ' style="color:#94a3b8;font-size:13px;margin:0 0 12px;"' : ' class="lead"'}>${cs
        ? 'Field p75 z reálných uživatelů zůstává oddělený. Do rozdělení vstupují pouze laboratorní pokusy za shodných podmínek; odlišné pokusy se zachovají pro audit, ale z agregace se vyloučí. Outliery za shodných podmínek ve výpočtu zůstávají.'
        : 'Real-user field p75 remains separate. Only lab attempts under matching conditions enter the distribution; differing attempts remain visible for audit but are excluded from aggregation. Outliers under matching conditions remain in the calculation.'}</p>
      ${mainPartial ? `<div${dark ? ' style="padding:12px;border:1px solid #f59e0b;background:rgba(245,158,11,.08);color:#fde68a;margin:12px 0;"' : ' class="callout" style="border-color:#a16207;background:rgba(161,98,7,.08);"'}><strong>${main?.unavailable
        ? (cs ? 'LABORATORNÍ HODNOTA NENÍ K DISPOZICI' : 'LAB VALUE IS UNAVAILABLE')
        : (cs ? 'LABORATORNÍ DŮKAZY JSOU ČÁSTEČNÉ / NEOVĚŘENÉ' : 'LAB EVIDENCE IS PARTIAL / UNVERIFIED')}</strong> ${main?.unavailable
          ? (cs
            ? 'Běh nezachytil platnou interakci; nula se proto nezobrazuje jako INP ani neporovnává s field p75.'
            : 'The run captured no valid interaction; zero is therefore neither shown as INP nor compared with field p75.')
          : (cs
            ? `Hlavní série ${escapeHtml(main.metric)} má ${main.spread?.runs || 0} validních běhů z požadovaných 5. Report se vykreslil, ale nevydává tuto lab hodnotu za stabilní výsledek.`
            : `The primary ${escapeHtml(main.metric)} series has ${main.spread?.runs || 0} valid runs out of the required 5. The report rendered, but does not present this lab value as stable.`)}</div>` : ''}
      ${cards}
    </section>`;
};

export const generateAuditHTML = (data) => {
  const r = buildReportData(data);
  const lang = r.lang;
  const diagnosticEvidenceHtml = renderEvidenceHTML(data, lang, 'light');
  const contractHtml = renderAuditEvidenceContractHTML(data.evidenceContract, lang, 'light');
  const inpScenariosHtml = renderInpScenarioScopesHTML(data, lang, 'light');
  const copy = r.copy;
  const labEvidenceHtml = renderLabRunEvidenceAudit(r);
  const lcpReconciliationHtml = renderLcpFieldReconciliation(r);

  const headlineHtml = copy.headline
    .map((part, i) => i === copy.headlineEm ? `<em>${escapeHtml(part)}</em>` : escapeHtml(part))
    .join(' ');

  const cmpCallout = r.rich.cmpWarning ? `
    <div class="callout">
      <strong>${lang === 'cs' ? 'Pozor: realita bude horší' : 'Note: reality will be worse'}</strong>
      ${lang === 'cs'
        ? 'Měření proběhlo bez cookie lišty (CMP). Jakmile reálný návštěvník klikne na „Přijmout vše", spustí se další vrstva trackerů — analytika, remarketing, konverzní pixely — které testovací scénář nezahrnuje. V praxi bude dopad na uživatele větší, než ukazuje tento report.'
        : 'Measurement ran without the cookie banner (CMP). Once a real visitor clicks "Accept all", more tracker layers wake up — analytics, remarketing, conversion pixels — that this test scenario does not include. Real-world impact on users will be greater.'}
    </div>` : '';

  // Priority-#1 deep layer failed — the whole report is INCOMPLETE and must
  // say so up top, loudly; the headline scenario already refuses any other
  // metric's narrative, this banner makes the state unmissable in the file.
  const incompleteCallout = r.scenario === 'deep-failed-priority' ? `
    <div class="callout" style="border-color:#e5484d;background:rgba(229,72,77,0.08);">
      <strong>${escapeHtml(r.deepFailurePresentation?.title || (lang === 'cs'
        ? `NEKOMPLETNÍ REPORT — hloubková analýza ${r.metricUpper} selhala`
        : `INCOMPLETE REPORT — ${r.metricUpper} deep analysis failed`))}</strong>
      <p><b>${escapeHtml(r.deepFailurePresentation?.labels?.cause || (lang === 'cs' ? 'Pravděpodobná příčina' : 'Likely cause'))}:</b>
        ${escapeHtml(r.deepFailurePresentation?.cause || '')}</p>
      <p><b>${escapeHtml(r.deepFailurePresentation?.labels?.available || (lang === 'cs' ? 'Co je stále k dispozici' : 'Data still available'))}:</b>
        ${escapeHtml(r.deepFailurePresentation?.available || '')}</p>
      <p><b>${escapeHtml(r.deepFailurePresentation?.labels?.next || (lang === 'cs' ? 'Další krok' : 'Next step'))}:</b>
        ${escapeHtml(r.deepFailurePresentation?.action || '')}</p>
    </div>` : '';

  // PSI diagnostics missing (fetch failed) — the report is built only from
  // the deep lab run, so tell the reader why the usual sections are absent.
  const limitedDataCallout = !data.diagnostics ? `
    <div class="callout">
      <strong>${lang === 'cs' ? 'Neúplná data' : 'Incomplete data'}</strong>
      ${lang === 'cs'
        ? 'Nepodařilo se získat diagnostiku z PageSpeed Insights, takže tento report vychází jen z hloubkového laboratorního testu. Chybí rozpad blokujícího času, third-party skripty a doporučené úspory. Spusťte audit znovu — výpadek bývá dočasný.'
        : 'PageSpeed Insights diagnostics could not be fetched, so this report is based only on the deep lab test. Blocking-time breakdown, third-party scripts and savings recommendations are missing. Re-run the audit — the outage is usually temporary.'}
    </div>` : '';

  // Concrete layout-shift element (typically an image without width/height)
  // is often the single most actionable finding of the whole run — surface
  // it in the client audit too, clearly labeled as outside the main metric.
  const clsQuickWinEl = (data.diagnostics?.layoutShiftAttribution?.elements || [])
    .concat(data.diagnostics?.cls?.elements || [])
    .find(el => el && (el.snippet || el.node) );
  const clsQuickWinCallout = (clsQuickWinEl && r.metricLower !== 'cls') ? `
    <div class="callout">
      <strong>${lang === 'cs' ? 'Rychlé vítězství mimo ' + r.metricUpper + ' — posun layoutu (CLS)' : 'Quick win outside ' + r.metricUpper + ' — layout shift (CLS)'}</strong>
      ${lang === 'cs'
        ? `Měření zachytilo konkrétní element, který posouvá layout stránky${clsQuickWinEl.selector ? ` (<code>${escapeHtml(String(clsQuickWinEl.selector).replace(/:nth-of-type\(\d+\)/g, '') || clsQuickWinEl.selector)}</code>)` : ''}:`
        : `The run captured a specific element that shifts the page layout${clsQuickWinEl.selector ? ` (<code>${escapeHtml(String(clsQuickWinEl.selector).replace(/:nth-of-type\(\d+\)/g, '') || clsQuickWinEl.selector)}</code>)` : ''}:`}
      <br><code style="display:block;margin-top:8px;white-space:pre-wrap;word-break:break-all;">${escapeHtml((clsQuickWinEl.snippet || clsQuickWinEl.node || '').slice(0, 300))}</code>
      ${lang === 'cs'
        ? `<br>${/(<img|<image)/i.test(clsQuickWinEl.snippet || clsQuickWinEl.node || '') ? 'U obrázku stačí doplnit atributy <code>width</code> a <code>height</code> (nebo CSS <code>aspect-ratio</code>) — jedna úprava, která stabilizuje layout celé stránky.' : 'Rezervujte elementu místo předem (rozměry nebo CSS <code>aspect-ratio</code>), aby nepohyboval okolním obsahem.'}`
        : `<br>${/(<img|<image)/i.test(clsQuickWinEl.snippet || clsQuickWinEl.node || '') ? 'For an image, adding <code>width</code> and <code>height</code> attributes (or CSS <code>aspect-ratio</code>) is enough — one change that stabilizes the whole page layout.' : 'Reserve space for the element up front (dimensions or CSS <code>aspect-ratio</code>) so it stops moving surrounding content.'}`}
    </div>` : '';

  // CLS measurement-stability callout — the headline lab value is the median
  // across stored runs (same honesty rule as INP), and the report says so.
  const clsSp = r.metricLower === 'cls' ? r.deepCLSResults?.labRunSpread : null;
  const clsSpreadBlock = (clsSp && labSeriesIsVerified(clsSp) && clsSp.min !== clsSp.max) ? `
    <div class="callout">
      <strong>${lang === 'cs' ? 'Stabilita měření CLS' : 'CLS measurement stability'}</strong>
      ${lang === 'cs'
        ? `Rozptyl ${clsSp.runs} uložených běhů (shodu podmínek včetně souhlasu ověřte v evidenci série): ${formatMetricValue('cls', clsSp.min) || '—'}–${formatMetricValue('cls', clsSp.max) || '—'}, medián ${formatMetricValue('cls', clsSp.median) || '—'}. Laboratorní hodnota v tomto reportu je medián přes uložené běhy, ne jeden konkrétní běh — pořadí viníků níže se řídí četností napříč běhy.`
        : `Spread across ${clsSp.runs} stored runs (check the series evidence for comparable conditions including effective consent): ${formatMetricValue('cls', clsSp.min) || '—'}–${formatMetricValue('cls', clsSp.max) || '—'}, median ${formatMetricValue('cls', clsSp.median) || '—'}. The lab value in this report is the median across stored runs, not one specific run — culprit ranking below follows frequency across runs.`}
    </div>` : '';

  const multiPageBlock = r.multiPage ? `
    <div class="callout">
      <strong>${r.multiPage.unavailable
        ? (lang === 'cs' ? `${r.metricUpper} napříč webem — bez platného měření` : `${r.metricUpper} site-wide — no valid measurement`)
        : (lang === 'cs' ? `${r.metricUpper} napříč webem — platně změřeno ${r.multiPage.validPages} z ${r.multiPage.attemptedPages} stránek` : `${r.metricUpper} site-wide — validly measured on ${r.multiPage.validPages} of ${r.multiPage.attemptedPages} pages`)}</strong>
      ${r.multiPage.unavailable ? (lang === 'cs'
        ? `Sweep se pokusil změřit ${r.multiPage.attemptedPages} stránek, ale na žádné nezachytil platnou interakci s ověřenou atribucí. INP proto není k dispozici; nuly nejsou hodnoceny jako „dobré". Z tohoto sweepu nelze tvrdit, že se problém nereprodukoval, ani podle něj zmírnit field verdikt.`
        : `The sweep attempted ${r.multiPage.attemptedPages} pages but captured no valid interaction with verified attribution. INP is therefore unavailable; zeroes are not classified as "good". This sweep cannot support a "not reproduced" claim or soften the field verdict.`)
        : `${r.multiPage.unmeasuredPages > 0 ? (lang === 'cs'
          ? `${r.multiPage.unmeasuredPages} ${r.multiPage.unmeasuredPages === 1 ? 'stránka zůstala nezměřená' : 'stránky zůstaly nezměřené'} a nejsou započteny do statistik.<br>`
          : `${r.multiPage.unmeasuredPages} ${r.multiPage.unmeasuredPages === 1 ? 'page was' : 'pages were'} unmeasured and excluded from statistics.<br>`) : ''}
      ${lang === 'cs'
        ? `Rozložení: ${r.multiPage.goodCount}× dobré · ${r.multiPage.warnCount}× k zlepšení · ${r.multiPage.poorCount}× špatné. Medián ${formatMetricValue(r.metric, r.multiPage.median) || '—'} (průměr ${formatMetricValue(r.metric, r.multiPage.avg) || '—'}), rozsah ${formatMetricValue(r.metric, r.multiPage.best?.value) || '—'} – ${formatMetricValue(r.metric, r.multiPage.worst?.value) || '—'}. Nejhorší stránka: ${escapeHtml(r.multiPage.worst?.url || '—')}.`
        : `Distribution: ${r.multiPage.goodCount}× good · ${r.multiPage.warnCount}× needs improvement · ${r.multiPage.poorCount}× poor. Median ${formatMetricValue(r.metric, r.multiPage.median) || '—'} (average ${formatMetricValue(r.metric, r.multiPage.avg) || '—'}), range ${formatMetricValue(r.metric, r.multiPage.best?.value) || '—'} – ${formatMetricValue(r.metric, r.multiPage.worst?.value) || '—'}. Worst page: ${escapeHtml(r.multiPage.worst?.url || '—')}.`}`}
      ${r.multiPage.worstIsAnalyzedPage ? `<br><br>${lang === 'cs'
        ? `Pozn.: hlavní hloubkový test výše běžel přímo na této nejhorší stránce — ${escapeHtml(r.multiPage.analyzedDeepUrl || r.multiPage.worst?.url || '')} (${formatMetricValue(r.metric, r.multiPage.analyzedLabValue) || '—'}). Rozdíl mezi oběma hodnotami je rozptyl dvou nezávislých běhů téže stránky, ne dvě různé stránky — i proto berte jednotlivá čísla orientačně.`
        : `Note: the main deep test above ran directly on this worst page — ${escapeHtml(r.multiPage.analyzedDeepUrl || r.multiPage.worst?.url || '')} (${formatMetricValue(r.metric, r.multiPage.analyzedLabValue) || '—'}). The difference between the two values is run-to-run variance of the same page, not two different pages — another reason to treat single-run numbers as indicative.`}` : ''}
      ${r.multiPage.wideRange ? `<br><br>${lang === 'cs'
        ? `⚠ Rozsah hodnot je široký (nejhorší stránka je několikanásobek nejlepší). Počty běhů se mezi stránkami liší; průměr a medián napříč stránkami berte orientačně. ${r.multiPage.worstConfidence === 'median-confirmed' ? 'Nejhorší stránka má alespoň 3 validní opakování.' : 'Pořadí nejhorších stránek není potvrzeno srovnatelnými opakováními.'}`
        : `⚠ The value range is wide (worst page is a multiple of the best). Run counts vary across pages; site-wide average and median are indicative. ${r.multiPage.worstConfidence === 'median-confirmed' ? 'The worst page has at least 3 valid repeats.' : 'Worst-page ranking is not confirmed by comparable repeats.'}`}` : ''}
      ${r.multiPageEscalationApplied ? `<br><br>${lang === 'cs'
        ? `⚠ ${r.multiPage.poorCount === 1 ? 'Jedna měřená stránka spadá' : `${r.multiPage.poorCount} měřené stránky spadají`} do pásma „špatné" (${escapeHtml(r.multiPage.worst?.url || '—')}, ${formatMetricValue(r.metric, r.multiPage.worst?.value) || '—'}). Celkový verdikt reportu je proto zpřísněn — reálně špatná stránka se nesmí schovat za dobrý průměr.`
        : `⚠ ${r.multiPage.poorCount === 1 ? 'One measured page falls' : `${r.multiPage.poorCount} measured pages fall`} into the "poor" band (${escapeHtml(r.multiPage.worst?.url || '—')}, ${formatMetricValue(r.metric, r.multiPage.worst?.value) || '—'}). The report's overall verdict is escalated accordingly — a genuinely poor page must not hide behind a good average.`}` : ''}
      ${r.multiPage.worstConfidence === 'repeated-incomplete' ? `<br><br>${escapeHtml(incompleteSweepNote(r.multiPage, lang === 'cs'))}` : r.multiPage.worstConfidence === 'single-run' ? `<br><br>${lang === 'cs'
        ? `Nejhorší stránka byla změřena jen jednou — berte jako orientační. Pro potvrzení je potřeba ≥ 3 validních běhů (opakované měření dá medián).`
        : `The worst page was measured only once — treat as indicative. Confirmation requires ≥ 3 valid runs (repeated measurement yields a median).`}` : r.multiPage.worstConfidence === 'median-confirmed' ? `<br><br>${lang === 'cs'
        ? `Nejhorší stránka byla přeměřena ${r.multiPage.worstRuns || 5}× opakovanými mělkými (sweep) běhy — uvedená hodnota je medián z ${r.multiPage.worstValidRuns || r.multiPage.worstRuns || 5} validních běhů, nikoli verdikt o každém z nich. Případný hloubkový diagnostický běh je samostatná vrstva měření.`
        : `The worst page was re-measured ${r.multiPage.worstRuns || 5}× with repeated shallow (sweep) runs — the value shown is the median of ${r.multiPage.worstValidRuns || r.multiPage.worstRuns || 5} valid runs, not a verdict on each one. Any deep diagnostic run is a separate measurement layer.`}` : ''}
      ${!r.multiPage.unavailable && r.multiPage.matchesMetric && r.multiPage.poorCount === 0 ? `<br><br>${(() => {
        // Ratio-aware wording: "partial headroom" is only honest while good
        // pages outnumber needs-improvement ones. A site with a majority of
        // pages over the target is borderline ACROSS the board — the same
        // sentence must not soften that into "just headroom".
        const borderline = (r.multiPage.warnCount || 0) >= (r.multiPage.goodCount || 0);
        // The sweep is a lab scenario. There is ONE intentional exception to
        // "sweep never changes the verdict": the multi-page severity cap —
        // when the site-wide sweep for the SAME metric finds zero "poor"
        // pages, a "critical" field headline is softened to a warning so the
        // report does not contradict its own multi-page section. EVERY
        // applied cap must be disclosed here — including the borderline
        // (systemic-warning) wording branch — and the "sweep does not change
        // the verdict" claim may only render when the cap truly did not fire.
        const capDisclosure = r.multiPageCapApplied ? (lang === 'cs'
          ? `Field data ukazují kritickou hodnotu ${r.metricUpper}. Sweep nemá stránky v pásmu „špatné", ale ${r.multiPage.warnCount} stránek vyžaduje zlepšení. Zmírněný laboratorní verdikt nemění field výsledek.`
          : `Field data show critical ${r.metricUpper}. The sweep has no "poor" pages, but ${r.multiPage.warnCount} pages need improvement. The softened lab verdict does not change the field result.`) : null;
        if (borderline) {
          // The sweep may ONLY state the distribution and the count of pages
          // over target. The template-vs-page-specific conclusion belongs
          // exclusively to the deep-run culprit comparison section — a sweep
          // distribution and a culprit comparison must never issue two
          // competing verdicts in one report.
          const systemic = lang === 'cs'
            ? `Žádný uložený agregát měřené stránky nespadá do pásma „špatné", ale ve vzorku ${r.multiPage.pages} měřených URL je ${r.multiPage.warnCount} nad cílem. Jde o agregáty stránek, ne o verdikt každého jednotlivého běhu ani celého webu; bez individuálních hodnot nelze vyloučit horší běh. Zda jde o problém šablony, nebo jednotlivých stránek, samotné rozložení sweepu neurčuje.`
            : `No stored measured-page aggregate falls into the "poor" band, but in the sample of ${r.multiPage.pages} measured URLs, ${r.multiPage.warnCount} are over target. These are page aggregates, not a verdict on each individual run or the entire site; without individual values a worse run cannot be ruled out. The sweep distribution alone cannot establish a template-level or page-specific cause.`;
          return capDisclosure ? `${systemic}<br><br>${capDisclosure}` : systemic;
        }
        const sweepIntro = lang === 'cs'
          ? `V aktuálním sweepu nespadá agregát žádné z ${r.multiPage.pages} měřených stránek do pásma „špatné"; ${r.multiPage.warnCount} agregátů vyžaduje zlepšení. Bez individuálních hodnot to neznamená, že každý běh byl dobrý; ani to není důkaz nepřítomnosti zvýšeného ${r.metricUpper}.`
          : `No measured-page aggregate among ${r.multiPage.pages} pages falls into the "poor" band; ${r.multiPage.warnCount} aggregates need improvement. Without individual values this does not mean every run was good, nor does it establish the absence of elevated ${r.metricUpper}.`;
        if (capDisclosure) return `${sweepIntro} ${capDisclosure}`;
        return lang === 'cs'
          ? `${sweepIntro} Sweep popisuje uložená laboratorní měření včetně zaznamenaných opakování; celkové hodnocení metriky vychází z field dat a sweep ho nemění.`
          : `${sweepIntro} The sweep describes recorded lab measurements including recorded repeats; the metric's overall verdict derives from field data and the sweep does not change it.`;
      })()}` : ''}
    </div>
    ${r.metricLower === 'inp'
      ? renderMultiPageInteractionDiagnosticsHtml(
          data.multiPageINPResults?.pages || data.multiPageINPResults?.results || [],
          lang,
          'paper'
        )
      : ''}` : '';

  const verdictItems = r.verdictCards.length ? r.verdictCards : [{
    label: r.metricUpper,
    value: r.formattedMetricValue,
    target: r.statusLabel,
    severity: r.status
  }];

  const recsList = (r.tasks || []).slice(0, 6).map(t => `
      <div class="rec">
        <div class="rec-head">
          <div class="rec-title">${escapeHtml(t.title || t.task || (lang === 'cs' ? 'Doporučení' : 'Recommendation'))}</div>
          <div class="rec-meta">
            ${t.priority === 'critical' || t.priority === 'high'
              ? '<span class="tag impact-high">' + (lang === 'cs' ? 'Dopad: vysoký' : 'Impact: high') + '</span>'
              : t.priority === 'medium'
              ? '<span class="tag impact-med">' + (lang === 'cs' ? 'Dopad: střední' : 'Impact: medium') + '</span>'
              : ''}
            ${t.estimatedTime ? `<span class="tag effort-med">${escapeHtml(t.estimatedTime)}</span>` : ''}
          </div>
        </div>
        ${t.description ? `<p>${escapeHtml(t.description)}</p>` : ''}
        ${t.code ? `<p style="font-family:var(--mono);font-size:12px;background:var(--paper-2);padding:12px;border-left:2px solid var(--accent);white-space:pre-wrap">${escapeHtml(t.code)}</p>` : ''}
      </div>`).join('');

  return embedAuditEvidenceDigest(annotateFirstJargon(`<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="UTF-8">
<title>${lang === 'cs' ? 'Performance audit' : 'Performance audit'} — ${escapeHtml(r.domain)}</title>
<meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,600;9..144,900&family=JetBrains+Mono:wght@400;500;700&family=Inter+Tight:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
:root { --ink: #0e0f12; --paper: #f5f1ea; --paper-2: #ebe5da; --rule: #1a1b1f; --accent: #ff4d1f; --warn: #d4a017; --ok: #2d6a4f; --mono: 'JetBrains Mono', ui-monospace, monospace; --serif: 'Fraunces', Georgia, serif; --sans: 'Inter Tight', system-ui, sans-serif; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { background: var(--paper); color: var(--ink); font-family: var(--sans); line-height: 1.5; -webkit-font-smoothing: antialiased; background-image: radial-gradient(circle at 20% 10%, rgba(255,77,31,0.04) 0%, transparent 40%), radial-gradient(circle at 80% 60%, rgba(45,106,79,0.03) 0%, transparent 45%); }
.page { max-width: 900px; margin: 0 auto; padding: 48px 32px 96px; }
.masthead { border-top: 3px solid var(--ink); border-bottom: 1px solid var(--ink); padding: 20px 0; display: flex; justify-content: space-between; align-items: baseline; font-family: var(--mono); font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; margin-bottom: 48px; }
.masthead .brand { font-weight: 700; }
.masthead .meta { color: #555; }
.headline { font-family: var(--serif); font-weight: 300; font-size: clamp(38px, 6vw, 68px); line-height: 0.98; letter-spacing: -0.03em; margin-bottom: 24px; font-style: italic; }
.headline em { font-style: normal; font-weight: 900; color: var(--accent); }
.subhead { font-family: var(--serif); font-size: 20px; line-height: 1.4; max-width: 640px; margin-bottom: 48px; color: #2a2b30; }
.verdict { background: #fdfcfa; color: var(--ink); padding: 40px; margin: 0 -32px 64px; position: relative; border: 1px solid var(--rule); }
.verdict::before { content: "VERDIKT"; position: absolute; top: 16px; left: 40px; font-family: var(--mono); font-size: 10px; letter-spacing: 0.2em; color: var(--accent); }
.verdict-grid { display: grid; grid-template-columns: repeat(${Math.min(verdictItems.length, 3)}, 1fr); gap: 32px; margin-top: 24px; }
.verdict-item .label { font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.15em; color: #3a3b40; margin-bottom: 8px; }
.verdict-item .value { font-family: var(--serif); font-size: 38px; font-weight: 400; line-height: 1; margin-bottom: 4px; }
.verdict-item .value.bad, .verdict-item .value.critical { color: var(--accent); }
.verdict-item .value.warn, .verdict-item .value.warning { color: #a87c0a; }
.verdict-item .value.ok, .verdict-item .value.good { color: var(--ok); }
.verdict-item .target { font-family: var(--mono); font-size: 11px; color: #4a4b50; line-height: 1.4; }
section { margin-bottom: 56px; }
.section-label { font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.2em; color: var(--accent); margin-bottom: 12px; display: flex; align-items: center; gap: 12px; }
.section-label::before { content: ""; display: block; width: 24px; height: 1px; background: var(--accent); }
.section-title { font-family: var(--serif); font-size: 32px; font-weight: 400; line-height: 1.15; letter-spacing: -0.02em; margin-bottom: 20px; }
.section-title em { font-style: italic; color: var(--accent); }
.lead { font-family: var(--serif); font-size: 18px; line-height: 1.55; margin-bottom: 20px; color: #1a1b1f; }
p { font-size: 15px; line-height: 1.65; margin-bottom: 16px; color: #2a2b30; }
.culprits { border-top: 2px solid var(--ink); margin: 32px 0; }
.culprit { display: grid; grid-template-columns: 32px 1fr auto; gap: 20px; padding: 18px 0; border-bottom: 1px solid #c9c3b6; align-items: center; }
.culprit .rank { font-family: var(--mono); font-size: 14px; color: var(--accent); font-weight: 700; }
.culprit .name { font-family: var(--mono); font-size: 13px; word-break: break-word; color: var(--ink); }
.culprit .name small { display: block; font-family: var(--sans); font-size: 12px; color: #666; margin-top: 4px; font-weight: 500; }
.culprit .ms { font-family: var(--serif); font-size: 26px; font-weight: 600; color: var(--accent); white-space: nowrap; }
.culprit .ms small { font-size: 12px; color: #888; font-family: var(--mono); font-weight: 400; }
.timing-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 0; border-top: 2px solid var(--ink); border-bottom: 2px solid var(--ink); margin-top: 24px; }
.timing-cell { padding: 16px; border-right: 1px solid #c9c3b6; text-align: center; }
.timing-cell:last-child { border-right: none; }
.timing-cell-key { font-family: var(--mono); font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #888; margin-bottom: 8px; }
.timing-cell-val { font-family: var(--serif); font-size: 28px; font-weight: 600; color: var(--ink); }
.timing-cell-val small { font-family: var(--mono); font-size: 12px; color: #888; font-weight: 400; margin-left: 2px; }
.mt-bar-print { height: 18px; display: flex; border: 1px solid var(--ink); margin-top: 24px; }
.mt-seg-print { height: 100%; }
.mt-legend-print { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 12px; font-family: var(--mono); font-size: 11px; }
.mt-leg-print { display: flex; align-items: center; gap: 6px; }
.mt-leg-print .dot { width: 10px; height: 10px; border-radius: 50%; }
.mt-leg-print b { color: var(--ink); }
.budget { border-top: 2px solid var(--ink); border-bottom: 2px solid var(--ink); margin: 24px 0; }
.budget-row-print { display: grid; grid-template-columns: 130px 1fr 200px; gap: 16px; padding: 14px 0; border-bottom: 1px solid #c9c3b6; align-items: center; }
.budget-row-print:last-child { border-bottom: none; }
.budget-row-print .cat { font-family: var(--mono); font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; }
.budget-bar-print { height: 10px; background: var(--paper-2); position: relative; overflow: hidden; border: 1px solid #c9c3b6; }
.budget-bar-print .fill { position: absolute; top: 0; left: 0; bottom: 0; }
.budget-bar-print .limit { position: absolute; top: -2px; bottom: -2px; width: 2px; background: var(--ink); }
.budget-row-print .values { font-family: var(--mono); font-size: 12px; text-align: right; }
.rb-table { border-top: 2px solid var(--ink); border-bottom: 2px solid var(--ink); margin-top: 24px; }
.rb-row-print { display: grid; grid-template-columns: 70px 1fr 90px 90px; gap: 16px; padding: 14px 0; border-bottom: 1px solid #c9c3b6; align-items: center; }
.rb-row-print:last-child { border-bottom: none; }
.rb-type-print { font-family: var(--mono); font-size: 10px; font-weight: 700; padding: 3px 6px; border: 1px solid var(--accent); color: var(--accent); border-radius: 2px; text-align: center; letter-spacing: 0.1em; }
.rb-name-print { font-family: var(--mono); font-size: 12px; word-break: break-all; }
.rb-bytes-print { font-family: var(--mono); font-size: 12px; color: #666; text-align: right; }
.rb-ms-print { font-family: var(--serif); font-size: 22px; font-weight: 600; color: var(--accent); text-align: right; }
.rb-ms-print small { font-family: var(--mono); font-size: 11px; color: #888; font-weight: 400; }
.rt-table { border-top: 2px solid var(--ink); border-bottom: 1px solid var(--ink); margin-top: 24px; }
.rt-row-print { display: grid; grid-template-columns: 90px 1fr 110px; gap: 16px; padding: 12px 0; border-bottom: 1px solid #c9c3b6; align-items: baseline; }
.rt-row-print:last-child { border-bottom: none; }
.rt-type-print { font-family: var(--mono); font-size: 10px; font-weight: 700; padding: 3px 6px; border: 1px solid var(--ink); color: var(--ink); border-radius: 2px; text-align: center; letter-spacing: 0.1em; }
.rt-count-print { font-family: var(--serif); font-size: 18px; font-weight: 500; }
.rt-count-print small { font-family: var(--mono); font-size: 11px; color: #666; font-weight: 400; }
.rt-bytes-print { font-family: var(--mono); font-size: 13px; color: #333; text-align: right; font-weight: 600; }
.rt-third-print { margin-top: 18px; padding: 18px 20px; background: var(--paper-2); border: 1px solid #c9c3b6; }
.rt-third-label { font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.15em; color: var(--accent); margin-bottom: 12px; }
.rt-third-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.rt-third-grid div { display: flex; flex-direction: column; gap: 4px; }
.rt-third-grid b { font-family: var(--serif); font-size: 22px; font-weight: 600; color: var(--ink); }
.rt-third-grid small { font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: #666; }
.recs { counter-reset: rec; padding-left: 60px; margin-top: 32px; }
.rec { border-left: 3px solid var(--ink); padding: 20px 0 20px 24px; margin-bottom: 24px; counter-increment: rec; position: relative; }
.rec::before { content: counter(rec, decimal-leading-zero); position: absolute; top: 20px; left: -60px; font-family: var(--serif); font-size: 32px; font-weight: 300; font-style: italic; color: var(--accent); }
.rec-head { display: flex; align-items: baseline; gap: 16px; margin-bottom: 8px; flex-wrap: wrap; }
.rec-title { font-family: var(--serif); font-size: 22px; font-weight: 600; line-height: 1.2; }
.rec-meta { display: flex; gap: 10px; font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; }
.tag { padding: 3px 8px; border: 1px solid currentColor; border-radius: 2px; }
.tag.impact-high { color: var(--accent); }
.tag.impact-med { color: var(--warn); }
.tag.effort-med { color: var(--warn); }
.tag.sev-high { color: var(--accent); }
.tag.sev-med { color: var(--warn); }
.tag.sev-low { color: var(--ok); }
.cause { border-left: 3px solid var(--ink); padding: 20px 0 20px 24px; margin-bottom: 28px; }
.cause.sev-high { border-left-color: var(--accent); }
.cause.sev-med { border-left-color: var(--warn); }
.cause.sev-low { border-left-color: var(--ok); }
.cause-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; margin-bottom: 8px; font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; }
.cause-title { font-family: var(--serif); font-size: 22px; font-weight: 600; line-height: 1.2; text-transform: none; letter-spacing: -0.01em; }
.cause-detail { font-family: var(--mono); font-size: 12px; color: #666; margin-bottom: 12px; }
.cause-res { margin-bottom: 12px; }
.cause-res code { display: block; font-family: var(--mono); font-size: 11px; color: #333; background: var(--paper-2); border: 1px solid #c9c3b6; padding: 6px 10px; margin-bottom: 4px; word-break: break-all; }
.cause-recs { margin-bottom: 14px; }
.cause-recs-label { font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.15em; color: #888; margin-bottom: 6px; }
.cause-recs ul { margin-left: 20px; font-size: 14px; line-height: 1.7; color: #2a2b30; }
.code-pair { margin-top: 6px; }
.code-block { border: 1px solid #c9c3b6; background: var(--paper-2); padding: 12px 14px; margin-bottom: 8px; }
.code-block pre { font-family: var(--mono); font-size: 12px; line-height: 1.55; white-space: pre-wrap; word-break: break-word; margin: 0; color: var(--ink); }
.code-block .code-cap { font-family: var(--mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.12em; margin-bottom: 8px; }
.code-bad { border-left: 3px solid var(--accent); }
.code-bad .code-cap { color: var(--accent); }
.code-good { border-left: 3px solid var(--ok); }
.code-good .code-cap { color: var(--ok); }
.callout { background: var(--paper-2); border: 1px solid #c9c3b6; padding: 20px 24px; margin: 24px 0; font-family: var(--serif); font-size: 15px; line-height: 1.55; }
.callout strong { font-family: var(--sans); font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: var(--accent); display: block; margin-bottom: 8px; font-weight: 700; }
.pullquote { font-family: var(--serif); font-style: italic; font-size: 28px; line-height: 1.25; letter-spacing: -0.01em; padding: 40px 0 40px 40px; border-left: 4px solid var(--accent); margin: 48px 0; color: var(--ink); }
.pullquote .attr { display: block; margin-top: 16px; font-family: var(--mono); font-style: normal; font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase; color: #888; }
.footer { border-top: 3px solid var(--ink); margin-top: 64px; padding-top: 24px; font-family: var(--mono); font-size: 11px; color: #666; display: flex; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
@media (max-width: 640px) { .page { padding: 24px 20px 64px; } .verdict { margin: 0 -20px 48px; padding: 32px 20px; } .verdict-grid { grid-template-columns: 1fr; gap: 20px; } .recs { padding-left: 16px; } .rec { padding-left: 16px; } .rec::before { position: static; display: block; margin-bottom: 4px; font-size: 22px; } .culprit { grid-template-columns: 24px 1fr; gap: 12px; } .culprit .ms { grid-column: 2; font-size: 22px; } .pullquote { font-size: 22px; padding: 24px 0 24px 20px; } .budget-row-print { grid-template-columns: 100px 1fr; } .budget-row-print .values { grid-column: 2; text-align: left; font-size: 11px; } }
</style>
</head>
<body>
<div class="page">
  ${renderDiagnosticAuditContext(data, lang)}
  <header class="masthead">
    <span class="brand">ControlVitals / ${r.unified ? (lang === 'cs' ? 'Komplexní CWV audit' : 'Complete CWV audit') : 'Audit'}</span>
    <span class="meta">${headerMetaText(r, lang)}</span>
    ${r.devicePriorityMismatch ? `
    <div style="margin-top:10px;padding:10px 14px;border:1px solid var(--accent);background:rgba(200,60,30,0.06);font-family:var(--mono);font-size:12px;line-height:1.5;color:var(--ink);">
      ⚠ ${lang === 'cs'
        ? `Měřeno na zařízení ${escapeHtml(deviceLabel(r.devicePriorityMismatch.measured, lang))}, ale prioritní zařízení vašich uživatelů je ${escapeHtml(deviceLabel(r.devicePriorityMismatch.priority, lang))} — výsledky nemusí reprezentovat většinu návštěvníků. Pro rozhodování změřte i prioritní zařízení.`
        : `Measured on ${escapeHtml(deviceLabel(r.devicePriorityMismatch.measured, lang))}, but your users' priority device is ${escapeHtml(deviceLabel(r.devicePriorityMismatch.priority, lang))} — results may not represent the majority of visitors. Measure the priority device before deciding.`}
    </div>` : ''}
  </header>

  ${renderPartialMeasurementNotice(r)}${renderMeasurementProvenanceNotice(r)}

  <h1 class="headline">${headlineHtml}</h1>

  <p class="subhead">${escapeHtml(copy.subhead)}</p>

  ${renderInpScenarioFailureNotice(data, lang)}${renderExecSummaryAudit(r)}

  <div class="verdict">
    <div class="verdict-grid">
      ${verdictItems.map(c => `
        <div class="verdict-item">
          <div class="label">${escapeHtml(c.label)}</div>
          <div class="value ${c.severity || ''}">${escapeHtml(c.value)}</div>
          <div class="target">${escapeHtml(c.target || '')}</div>
        </div>`).join('')}
    </div>
  </div>

  ${renderFieldOverviewAudit(r)}${lcpReconciliationHtml ? `\n\n  ${lcpReconciliationHtml}` : ''}${labEvidenceHtml ? `\n\n  ${labEvidenceHtml}` : ''}

  <section>
    <div class="section-label">${lang === 'cs' ? 'Hlavní zjištění' : 'Main finding'}</div>
    ${r.aiNarrative
      ? `<h2 class="section-title">${escapeHtml(r.aiNarrative.headline)}</h2>
         ${r.aiNarrative.paragraphs.map((p, i) => i === 0 ? `<p class="lead">${escapeHtml(p)}</p>` : `<p>${escapeHtml(p)}</p>`).join('')}`
      : `<h2 class="section-title">${headlineHtml}</h2>
         ${copy.body.map((p, i) => i === 0 ? `<p class="lead">${escapeHtml(p)}</p>` : `<p>${escapeHtml(p)}</p>`).join('')}`}
    ${incompleteCallout}
    ${limitedDataCallout}
    ${(r.deepAnalyzedUrl && normUrlLoose(r.deepAnalyzedUrl) !== normUrlLoose(r.url)) ? `
    <div class="callout">
      <strong>${lang === 'cs' ? 'Kde běžela hlavní hloubková diagnostika' : 'Where the main deep diagnostics ran'}</strong>
      ${lang === 'cs'
        ? `Zdrojem hlavní hloubkové diagnostiky ${escapeHtml(r.metricUpper)} je vybraná stránka: ${escapeHtml(r.deepAnalyzedUrl)}. Nejde nutně o stránku s nejvyšším agregátem sweepu; pořadí sweepu a výsledky hloubkových běhů jsou odlišná měření. Vstupní stránka (${escapeHtml(r.url || '')}) slouží jako reference${r.entryDeep?.value != null ? ` — naměřeno ${formatMetricValue(r.metric, r.entryDeep.value) || '—'}` : ''}.`
        : `The source of the main ${escapeHtml(r.metricUpper)} deep diagnostics is the selected page: ${escapeHtml(r.deepAnalyzedUrl)}. It is not necessarily the page with the highest sweep aggregate; sweep rankings and deep results are distinct measurements. The entry page (${escapeHtml(r.url || '')}) serves as a reference${r.entryDeep?.value != null ? ` — measured ${formatMetricValue(r.metric, r.entryDeep.value) || '—'}` : ''}.`}
    </div>` : ''}
    ${cmpCallout}
    ${clsSpreadBlock}
    ${multiPageBlock}
    ${clsQuickWinCallout}
    ${renderCulpritsAudit(r)}
  </section>

  ${renderClsFindingsAudit(r)}
  ${renderInpFindingsAudit(r)}
  ${renderLcpFindingsAudit(r)}
  ${renderTtfbFindingsAudit(r)}
  ${renderSweepDeepAudit(r)}
  ${renderDeviceRunsAudit(r)}

  ${renderDualVerdictAudit(r)}

  ${renderDevicePriorityAudit(r)}

  ${copy.pullquote ? `
  <div class="pullquote">
    ${escapeHtml(copy.pullquote)}
    <span class="attr">— ${escapeHtml(r.scenario.replace(/-/g, ' '))} · ${escapeHtml(r.domain)}</span>
  </div>` : ''}

  ${renderLongTasksAudit(r)}

  ${renderInpCompareAudit(r)}

  ${renderCausesAudit(r)}

  ${renderBudgetAudit(r)}

  ${renderServerTimingAudit(r)}

  ${renderRenderBlockingAudit(r)}

  ${renderResourceTimingAudit(r)}

  ${renderMainThreadAudit(r)}

  ${renderFontLoadingAudit(r)}

  ${recsList ? `
  <section>
    <div class="section-label">${lang === 'cs' ? 'Doporučení' : 'Recommendations'}</div>
    <h2 class="section-title">${lang === 'cs' ? 'Co s tím' : 'What to do'} <em>${lang === 'cs' ? 'dělat' : 'about it'}</em>.</h2>
    <p>${lang === 'cs'
      ? 'Doporučení jsou seřazena podle poměru dopadu k náročnosti.'
      : 'Recommendations are ordered by impact-to-effort ratio.'}</p>
    <div class="recs">
      ${recsList}
    </div>
  </section>` : ''}

  ${renderSavingsAudit(r.savingsRecommendations, r.savingsSummary, lang, r.metricLower, r.hasGtmContainer)}

  ${renderHandoverAudit(r)}${inpScenariosHtml}${diagnosticEvidenceHtml ? `\n\n  ${diagnosticEvidenceHtml}` : ''}${contractHtml}

  ${renderMethodologyAudit(r)}

  ${renderFollowUpAudit(lang, r.auditDate)}

  <footer class="footer">
    <span>ControlVitals — ${escapeHtml(r.metricUpper)} ${lang === 'cs' ? 'analýza' : 'analysis'} / ${escapeHtml(r.domain)}</span>
    <span>${escapeHtml(r.dateNow)}</span>
  </footer>
</div>
</body>
</html>`, lang), data.evidenceContract);
};

export const downloadHTMLFile = (html, filename) => {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// =========================================================================
// EXPORTS FOR TESTING
// =========================================================================
export const __testables = {
  tbtSeriesMedian,
  statusOf,
  carriedMpSummary,
  carriedMpLines,
  getCarriedTtfb,
  getCarriedLcp,
  classifyVendor,
  extractRichDiagnostics,
  buildReportData,
  detectScenario,
  getScenarioCopy,
  buildScenarioContext,
  buildVerdictCards,
  labSeriesIsVerified,
  labSeriesDisplayValue,
  labSeriesSummaryText,
  renderLabRunEvidenceAudit,
  renderLcpFieldReconciliation,
  templateVerdict,
  worstPageMeasurementEvidence,
  execOwnerPhrase,
  vendorCategoryLabel,
  execCauseAndOwner,
  renderHandoverAudit,
  deviceRunsMismatchLines,
  getCarriedInp,
  VENDOR_MAP
};
